import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  Container,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  Switch,
  Tooltip,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
// eslint-disable-next-line no-unused-vars
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CropSquareIcon from '@mui/icons-material/CropSquare';

import fittingsData from '../fittingsData';
import CenterFittingsUI from './CenterFittingsUI';
// eslint-disable-next-line no-unused-vars
import AdditionalOutletsUI from './AdditionalOutletsUI';
import { generateCollectorWithSeparator } from './collectorWithSeparatorLogic';
import { computeHydraulicSeparatorTotalHeight, getDrawingGabaritMm } from './hydraulicSeparatorTotalHeight';

// Импортируем ВСЕ готовые функции из collectorUtils
import {
  getAvailableDiameters,
  CIRCUIT_FITTING_CATEGORIES,
  CENTER_FITTING_CATEGORIES,
  getConcentricReducers,
  isAutoConstructorReducerAllowed,
  isEndFittingCompatibleWithMainPipe,
  getCapRequirementForFitting,
  extractDnFromFlangeKey,
  getPipeDiameterForDn,
  findEllipticalCap,
  getNippelRoundOverrideValue,
  isDistributionEndFittingCompatibleWithSquareProfile as isEndFittingCompatibleWithSquareProfile,
  isDistributionCircuitFittingCompatibleWithSquareProfile as isCircuitFittingCompatibleWithSquareProfile,
} from './collectorUtils';

// Импортируем функции для обработки длин фитингов
import {
  shouldShowConnectionLengthField,
  shouldUseConnectionLength,
  getCapUpdatesForConnectionFitting,
} from './endConnectionsLogic';

// Импортируем функции для центральных фитингов
import {
  updateCenterFittingsForDiameter,
} from './centerFittingsLogic';

// ========================================
// ЛОКАЛЬНЫЕ ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
// ========================================

// Для гидроразделителя минимальный диаметр 76.1мм
const separatorAvailableDiameters = getAvailableDiameters().filter(d => d.value >= 76.1);
// Для коллекторов - полный список диаметров
const collectorAvailableDiameters = getAvailableDiameters();
const concentricReducers = getConcentricReducers();

// Доступные квадратные профили
const availableSquareProfiles = [
  { value: '40x40', label: '40×40' },
  { value: '50x50', label: '50×50' },
  { value: '60x60', label: '60×60' },
  { value: '80x80', label: '80×80' },
  { value: '100x100', label: '100×100' },
  { value: '150x150', label: '150×150' },
];

// Доступные квадратные профили для гидроразделителя (все профили)
const separatorAvailableSquareProfiles = availableSquareProfiles;


/**
 * УСТАРЕВШАЯ: Для обратной совместимости
 * Используйте isCircuitFittingCompatibleWithSquareProfile для контуров
 * и isEndFittingCompatibleWithSquareProfile для торцевых подключений
 */
// eslint-disable-next-line no-unused-vars
const isFittingCompatibleWithSquareProfile = (fittingId, profile, category = null) => {
  return isCircuitFittingCompatibleWithSquareProfile(fittingId, profile, category);
};

/**
 * Определяет максимальный допустимый диаметр трубы коллектора
 * на основе диаметра основной трубы гидроразделителя
 * 
 * @param {number} separatorDiameter - диаметр трубы гидроразделителя
 * @returns {number} - максимальный диаметр трубы коллектора
 */
const getMaxCollectorPipeDiameter = (separatorDiameter) => {
  const mapping = {
    76.1: 48.3,
    88.9: 60.3,
    101.6: 60.3,
    114.3: 76.1,
    129: 88.9,
    139.7: 88.9,
    159: 114.3,
    168.3: 114.3,
    219.1: 159,
    273: 168.3,
    323.9: 219.1,
    355.6: 219.1,
    406.4: 273,
    508: 355.6,
    609.6: 406.4,
  };
  
  return mapping[separatorDiameter] || 406.4; // По умолчанию максимальный
};

/**
 * Проверяет совместимость диаметра трубы коллектора с диаметром гидроразделителя
 * 
 * @param {number} collectorDiameter - диаметр трубы коллектора
 * @param {number} separatorDiameter - диаметр трубы гидроразделителя
 * @returns {boolean} - true если диаметр коллектора совместим
 */
const isCollectorDiameterCompatible = (collectorDiameter, separatorDiameter) => {
  const maxDiameter = getMaxCollectorPipeDiameter(separatorDiameter);
  return collectorDiameter <= maxDiameter;
};

/**
 * Проверяет совместимость профиля коллектора с профилем гидроразделителя
 * Для квадратных профилей: профиль коллектора не может быть больше профиля гидроразделителя
 * 
 * @param {string} collectorProfile - профиль коллектора (например, "80x80")
 * @param {string} separatorProfile - профиль гидроразделителя (например, "100x100")
 * @returns {boolean} - true если профиль коллектора совместим
 */
const isCollectorProfileCompatible = (collectorProfile, separatorProfile) => {
  // Парсим профили
  const parseProfile = (profile) => {
    const parts = String(profile).split('x');
    if (parts.length === 2) {
      return Math.min(Number(parts[0]), Number(parts[1])); // Берем меньшую сторону
    }
    return 0;
  };
  
  const collectorSize = parseProfile(collectorProfile);
  const separatorSize = parseProfile(separatorProfile);
  
  // Профиль коллектора не может быть больше профиля гидроразделителя
  return collectorSize <= separatorSize;
};

/**
 * Категории фитингов для подключений гидроразделителя
 */
const connectionFittingCategories = [
  { id: 'Ниппели', label: 'Ниппели' },
  { id: 'Муфты', label: 'Муфты' },
  { id: 'Фланцы', label: 'Фланцы' },
  { id: 'Накидные гайки', label: 'Накидные гайки' },
  { id: 'Шайбы с наружной резьбой', label: 'Шайбы с нар. резьбой' },
];

/**
 * Категории фитингов для контуров коллектора
 */
const circuitFittingCategories = CIRCUIT_FITTING_CATEGORIES;

/**
 * Категории фитингов для центральных фитингов (термометры, лицевые подключения)
 */
const centerFittingCategories = CENTER_FITTING_CATEGORIES;

/**
 * Получаем фитинги по категории
 */
const getFittingsByCategory = (category, mainPipeDiameter = null, compatibilityCheckFn = null) => {
  return Object.entries(fittingsData)
    .filter(([key, data]) => data.category === category)
    .map(([key, data]) => ({
      id: key,
      label: data.displayName,
      diameter: data.diameter,
      length: data.length,
      isCompatible: mainPipeDiameter && compatibilityCheckFn ? compatibilityCheckFn(key, mainPipeDiameter) : true,
    }))
    .sort((a, b) => a.diameter - b.diameter);
};

/**
 * Проверка совместимости фитинга с диаметром основной трубы (для левых подключений гидроразделителя)
 */
const isFittingCompatibleWithMainPipe = (fittingId, mainPipeDiameter) => {
  const fittingData = fittingsData[fittingId];
  if (!fittingData) return true;

  const category = fittingData.category;

  if (mainPipeDiameter === 42.4) {
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2'];
      return allowedMuftas.includes(fittingId);
    }
    if (category === 'Фланцы') return false;
    if (category === 'Накидные гайки') {
      return !['Nak_Gaika_1_1.4', 'Nak_Gaika_1_1.2', 'Nak_Gaika_2'].includes(fittingId);
    }
    if (category === 'Шайбы с наружной резьбой') {
      return !['Shaib_Nar_Rezba_1_1.4', 'Shaib_Nar_Rezba_1_1.2', 'Shaib_Nar_Rezba_2'].includes(fittingId);
    }
  }

  if (mainPipeDiameter === 48.3) {
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4'];
      return allowedMuftas.includes(fittingId);
    }
    if (category === 'Фланцы') return false;
    if (category === 'Накидные гайки') {
      return !['Nak_Gaika_1_1.4', 'Nak_Gaika_1_1.2', 'Nak_Gaika_2'].includes(fittingId);
    }
    if (category === 'Шайбы с наружной резьбой') {
      return !['Shaib_Nar_Rezba_1_1.4', 'Shaib_Nar_Rezba_1_1.2', 'Shaib_Nar_Rezba_2'].includes(fittingId);
    }
  }

  if (mainPipeDiameter === 60.3) {
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1'];
      return allowedNippels.includes(fittingId);
    }
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1'];
      return allowedMuftas.includes(fittingId);
    }
    if (category === 'Фланцы') return fittingId.startsWith('Flanec_25_');
    if (category === 'Накидные гайки') {
      return fittingId !== 'Nak_Gaika_2';
    }
    if (category === 'Шайбы с наружной резьбой') {
      return fittingId !== 'Shaib_Nar_Rezba_2';
    }
  }

  // ========== Ограничения для трубы 76.1мм ==========
  if (mainPipeDiameter === 76.1) {
    // Ниппели: запрещены больше 1 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду40
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для трубы 88.9мм ==========
  if (mainPipeDiameter === 88.9) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4', 'Mufta_1_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для трубы 101.6мм ==========
  if (mainPipeDiameter === 101.6) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для трубы 114.3мм ==========
  if (mainPipeDiameter === 114.3) {
    // Ниппели: запрещены больше 2 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду65
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для трубы 129мм ==========
  if (mainPipeDiameter === 129) {
    // Ниппели: запрещены больше 3
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для трубы 139.7мм ==========
  if (mainPipeDiameter === 139.7) {
    // Ниппели: запрещены больше 3
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для трубы 159мм ==========
  if (mainPipeDiameter === 159) {
    // Ниппели: запрещены больше 4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3', 'Nippel_4'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для трубы 168.3мм ==========
  if (mainPipeDiameter === 168.3) {
    // Ниппели: запрещены больше 4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3', 'Nippel_4'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 219.1мм  ==========
  if (mainPipeDiameter === 219.1) {
    // Фланцы: запрещены больше Ду125
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 323.9мм ==========
  if (mainPipeDiameter === 323.9) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 355.6мм ==========
  if (mainPipeDiameter === 355.6) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 406.4мм ==========
  if (mainPipeDiameter === 406.4) {
    // Фланцы: запрещены больше Ду250
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200') || fittingId.startsWith('Flanec_250')) {
        return true;
      }
      return false;
    }
  }

  return true;
};

/**
 * Проверка совместимости фитинга контура коллектора
 */
const isCircuitFittingCompatible = (fittingId, mainPipeDiameter) => {
  const fittingData = fittingsData[fittingId];
  if (!fittingData) return true;

  const category = fittingData.category;

  // ========== Ограничения для гребенки 42.4мм ==========
  if (mainPipeDiameter === 42.4) {
    // Ниппели: запрещены больше 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены все"
    if (category === 'Шайбы с наружной резьбой') {
      return false;
    }
  }

  // ========== Ограничения для гребенки 48.3мм ==========
  if (mainPipeDiameter === 48.3) {
    // Ниппели: запрещены больше 3/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_1_1.4' || fittingId === 'Shaib_Nar_Rezba_1_1.2' || fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 60.3мм ==========
  if (mainPipeDiameter === 60.3) {
    // Ниппели: запрещены больше 1
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: только Ду25 (труба 33,7 мм — как ниппель 1")
    if (category === 'Фланцы') {
      return fittingId.startsWith('Flanec_25_');
    }

    // Накидные гайки: запрещены больше 1 1/2"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1 1/2"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 76.1мм ==========
  if (mainPipeDiameter === 76.1) {
    // Ниппели: запрещены больше 1 1/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду32
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 88.9мм ==========
  if (mainPipeDiameter === 88.9) {
    // Ниппели: запрещены больше 1 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду40
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 101.6мм ==========
  if (mainPipeDiameter === 101.6) {
    // Ниппели: запрещены больше 1 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду40
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 114.3мм ==========
  if (mainPipeDiameter === 114.3) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 129мм ==========
  if (mainPipeDiameter === 129) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 139.7мм ==========
  if (mainPipeDiameter === 139.7) {
    // Ниппели: запрещены больше 2 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду65
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 159мм ==========
  if (mainPipeDiameter === 159) {
    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 168.3мм ==========
  if (mainPipeDiameter === 168.3) {
    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 219.1мм ==========
  if (mainPipeDiameter === 219.1) {
    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 273мм ==========
  if (mainPipeDiameter === 273) {
    // Фланцы: запрещены больше Ду125
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 323.9мм ==========
  if (mainPipeDiameter === 323.9) {
    // Фланцы: запрещены больше Ду150
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 355.6мм ==========
  if (mainPipeDiameter === 355.6) {
    // Фланцы: запрещены больше Ду150
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 406.4мм ==========
  if (mainPipeDiameter === 406.4) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 508мм ==========
  if (mainPipeDiameter === 508) {
    // Фланцы: запрещены больше Ду250
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200') || fittingId.startsWith('Flanec_250')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 609.6мм ==========
  if (mainPipeDiameter === 609.6) {
    // Фланцы: запрещены больше Ду300
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200') || fittingId.startsWith('Flanec_250') || fittingId.startsWith('Flanec_300')) {
        return true;
      }
      return false;
    }
  }

  return fittingData.diameter <= mainPipeDiameter;
};

/**
 * Проверка совместимости центрального фитинга с диаметром основной трубы
 */
const isCenterFittingCompatible = (fittingId, mainPipeDiameter) => {
  const fittingData = fittingsData[fittingId];
  if (!fittingData) return true;

  const category = fittingData.category;

  // ========== Ограничения для гребенки 42.4мм (центральные фитинги) ==========
  if (mainPipeDiameter === 42.4) {
    // Ниппели: запрещены больше 3/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_1_1.4' || fittingId === 'Shaib_Nar_Rezba_1_1.2' || fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 48.3мм (центральные фитинги) ==========
  if (mainPipeDiameter === 48.3) {
    // Ниппели: запрещены больше 3/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 3/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_1_1.4' || fittingId === 'Shaib_Nar_Rezba_1_1.2' || fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 60.3мм (центральные фитинги) ==========
  if (mainPipeDiameter === 60.3) {
    // Ниппели: запрещены больше 1
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: только Ду25
    if (category === 'Фланцы') {
      return fittingId.startsWith('Flanec_25_');
    }

    // Накидные гайки: запрещены больше 1 1/2"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1 1/2"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 76.1мм (центральные фитинги) ==========
  if (mainPipeDiameter === 76.1) {
    // Ниппели: запрещены больше 1 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду40
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 88.9мм (центральные фитинги) ==========
  if (mainPipeDiameter === 88.9) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4', 'Mufta_1_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 101.6мм (центральные фитинги) ==========
  if (mainPipeDiameter === 101.6) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 114.3мм (центральные фитинги) ==========
  if (mainPipeDiameter === 114.3) {
    // Ниппели: запрещены больше 2 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду65
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 129мм (центральные фитинги) ==========
  if (mainPipeDiameter === 129) {
    // Ниппели: запрещены больше 3
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 139.7мм (центральные фитинги) ==========
  if (mainPipeDiameter === 139.7) {
    // Ниппели: запрещены больше 3
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 159мм (центральные фитинги) ==========
  if (mainPipeDiameter === 159) {
    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 168.3мм (центральные фитинги) ==========
  if (mainPipeDiameter === 168.3) {
    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 219.1мм (центральные фитинги) ==========
  if (mainPipeDiameter === 219.1) {
    // Фланцы: запрещены больше Ду125
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 273мм (центральные фитинги) ==========
  if (mainPipeDiameter === 273) {
    // Фланцы: запрещены больше Ду150
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125')|| fittingId.startsWith('Flanec_150')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 323.9мм (центральные фитинги)==========
  if (mainPipeDiameter === 323.9) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 355.6мм (центральные фитинги)==========
  if (mainPipeDiameter === 355.6) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 406.4мм (центральные фитинги)==========
  if (mainPipeDiameter === 406.4) {
    // Фланцы: запрещены больше Ду250
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200') || fittingId.startsWith('Flanec_250')) {
        return true;
      }
      return false;
    }
  }

  return true;
};

/**
 * Проверка совместимости дополнительного выхода с диаметром основной трубы
 * Для КОРОТКИХ дополнительных выходов
 */
const isShortAdditionalOutletFittingCompatible = (fittingId, mainPipeDiameter) => {
  const fittingData = fittingsData[fittingId];
  if (!fittingData) return true;

  const category = fittingData.category;

  // ========== Ограничения для гребенки 42.4мм (дополнительные выходы) ==========
  if (mainPipeDiameter === 42.4) {
    // Ниппели: запрещены больше 3/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_1_1.4' || fittingId === 'Shaib_Nar_Rezba_1_1.2' || fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 48.3мм (дополнительные выходы) ==========
  if (mainPipeDiameter === 48.3) {
    // Ниппели: запрещены больше 3/4
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 3/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены все
    if (category === 'Фланцы') {
      return false;
    }

    // Накидные гайки: запрещены больше 1"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_1_1.4' || fittingId === 'Nak_Gaika_1_1.2' || fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_1_1.4' || fittingId === 'Shaib_Nar_Rezba_1_1.2' || fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 60.3мм (дополнительные выходы) ==========
  if (mainPipeDiameter === 60.3) {
    // Ниппели: запрещены больше 1
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: только Ду25
    if (category === 'Фланцы') {
      return fittingId.startsWith('Flanec_25_');
    }

    // Накидные гайки: запрещены больше 1 1/2"
    if (category === 'Накидные гайки') {
      if (fittingId === 'Nak_Gaika_2') {
        return false;
      }
      return true;
    }

    // Шайбы с наружной резьбой: запрещены больше 1 1/2"
    if (category === 'Шайбы с наружной резьбой') {
      if (fittingId === 'Shaib_Nar_Rezba_2') {
        return false;
      }
      return true;
    }
  }

  // ========== Ограничения для гребенки 76.1мм (дополнительные выходы) ==========
  if (mainPipeDiameter === 76.1) {
    // Ниппели: запрещены больше 1 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/4
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду40
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 88.9мм (дополнительные выходы) ==========
  if (mainPipeDiameter === 88.9) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Муфты: запрещены больше 1 1/2
    if (category === 'Муфты') {
      const allowedMuftas = ['Mufta_1.4', 'Mufta_3.8', 'Mufta_1.2', 'Mufta_3.4', 'Mufta_1', 'Mufta_1_1.4', 'Mufta_1_1.2'];
      return allowedMuftas.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 101.6мм (дополнительные выходы) ==========
  if (mainPipeDiameter === 101.6) {
    // Ниппели: запрещены больше 2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду50
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 114.3мм (дополнительные выходы) ==========
  if (mainPipeDiameter === 114.3) {
    // Ниппели: запрещены больше 2 1/2
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду65
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 129мм (дополнительные выходы) ==========
  if (mainPipeDiameter === 129) {
    // Ниппели: запрещены больше 3
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 139.7мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 139.7) {
    // Ниппели: запрещены больше 3
    if (category === 'Ниппели') {
      const allowedNippels = ['Nippel_1.4', 'Nippel_3.8', 'Nippel_1.2', 'Nippel_3.4', 'Nippel_1', 'Nippel_1_1.4', 'Nippel_1_1.2', 'Nippel_2', 'Nippel_2_1/2', 'Nippel_3'];
      return allowedNippels.includes(fittingId);
    }

    // Фланцы: запрещены больше Ду80
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 159мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 159) {
    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 168.3мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 168.3) {
    // Фланцы: запрещены больше Ду100
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 219.1мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 219.1) {
    // Фланцы: запрещены больше Ду125
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 273мм (Дополнительные выходы) ==========
  if (mainPipeDiameter === 273) {
    // Фланцы: запрещены больше Ду150
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125')|| fittingId.startsWith('Flanec_150')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 323.9мм (Дополнительные выходы)==========
  if (mainPipeDiameter === 323.9) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 355.6мм (Дополнительные выходы)==========
  if (mainPipeDiameter === 355.6) {
    // Фланцы: запрещены больше Ду200
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200')) {
        return true;
      }
      return false;
    }
  }

  // ========== Ограничения для гребенки 406.4мм (Дополнительные выходы)==========
  if (mainPipeDiameter === 406.4) {
    // Фланцы: запрещены больше Ду250
    if (category === 'Фланцы') {
      if (fittingId.startsWith('Flanec_25_') || fittingId.startsWith('Flanec_32') || fittingId.startsWith('Flanec_40') || fittingId.startsWith('Flanec_50') || fittingId.startsWith('Flanec_65') || fittingId.startsWith('Flanec_80') || fittingId.startsWith('Flanec_100') || fittingId.startsWith('Flanec_125') || fittingId.startsWith('Flanec_150') || fittingId.startsWith('Flanec_200') || fittingId.startsWith('Flanec_250')) {
        return true;
      }
      return false;
    }
  }

  return true;
};

/**
 * Проверка совместимости фитинга с диаметром гребенки для СКВОЗНЫХ ДОПОЛНИТЕЛЬНЫХ ВЫХОДОВ
 * Используем те же ограничения, что и для контуров
 * @param {string} fittingId - ID фитинга
 * @param {number} mainPipeDiameter - Диаметр гребенки
 * @returns {boolean} - true если фитинг совместим
 */
const isThroughAdditionalOutletFittingCompatible = (fittingId, mainPipeDiameter) => {
  return isCircuitFittingCompatible(fittingId, mainPipeDiameter);
};

/**
 * Универсальная функция проверки совместимости для дополнительных выходов
 * Выбирает правильную функцию в зависимости от типа выхода
 * @param {string} fittingId - ID фитинга
 * @param {number} mainPipeDiameter - Диаметр гребенки
 * @param {string} outletType - Тип выхода: 'short' (короткий) или 'through' (сквозной)
 * @returns {boolean} - true если фитинг совместим
 */
const isAdditionalOutletFittingCompatible = (fittingId, mainPipeDiameter, outletType = 'short') => {
  if (outletType === 'through') {
    return isThroughAdditionalOutletFittingCompatible(fittingId, mainPipeDiameter);
  }
  return isShortAdditionalOutletFittingCompatible(fittingId, mainPipeDiameter);
};

// ========================================
// КОМПОНЕНТ РЕДАКТОРА
// ========================================

function CollectorWithSeparatorEditor({ onGenerate, onBack, onBackToDrawing, onBackToCharacteristics, calculationMode, initialParams }) {
  const [tabValue, setTabValue] = useState(() => {
    // Восстанавливаем активную вкладку при возврате из чертежа
    if (initialParams?.tabValue !== undefined) {
      return initialParams.tabValue;
    }
    return 0;
  });
  const [collectorTabValue, setCollectorTabValue] = useState(() => {
    // Восстанавливаем активную подвкладку коллектора при возврате из чертежа
    if (initialParams?.collectorTabValue !== undefined) {
      return initialParams.collectorTabValue;
    }
    return 0;
  }); // Подвкладки внутри вкладки "Коллектор"
  
  const [preBoilerTabValue, setPreBoilerTabValue] = useState(() => {
    // Восстанавливаем активную подвкладку предкотлового коллектора при возврате из чертежа
    if (initialParams?.preBoilerTabValue !== undefined) {
      return initialParams.preBoilerTabValue;
    }
    return 0;
  }); // Подвкладки внутри вкладки "Предкотловой коллектор"

  const getDefaultParams = () => ({
    // === Общий параметр формы для всей системы ===
    shape: 'round', // 'round' или 'square' - применяется ко всей системе
    
    // === Параметры гидроразделителя ===
    mainPipeDiameter: 88.9,
    mainPipeProfile: '80x80', // Профиль для квадратного гидроразделителя
    separatorHeight: 259,
    
    hasTopCap: true,
    hasBottomCap: true,
    topCapElementType: 'cap',
    bottomCapElementType: 'cap',
    topCapType: 'straight',
    bottomCapType: 'straight',
    topReducer: null,
    bottomReducer: null,
    
    hasTopConnection: true,
    topCapFittingCategory: 'Муфты',
    topCapFitting: 'Mufta_1.2',
    topCapFittingLength: null,
    
    hasBottomConnection: true,
    bottomCapFittingCategory: 'Муфты',
    bottomCapFitting: 'Mufta_1.2',
    bottomCapFittingLength: null,
    
    // Левые подключения гидроразделителя
    leftConnectionsPlacementMode: 'spacing', // 'offsets' (отступы) или 'spacing' (межосевое)
    leftConnectionsFirstOffset: 67, // Отступ первого подключения (для режима spacing)
    leftConnectionsLastOffset: 67, // Отступ последнего подключения (для режима spacing)
    leftConnectionsSpacing: 125, // Межосевое расстояние (для режима spacing)
    leftConnections: [
      { id: 'left-1', fittingId: 'Nippel_1', fittingLength: null, offsetFromTop: 67, offsetDirection: 'top', spacingToNext: null },
      { id: 'left-2', fittingId: 'Nippel_1', fittingLength: null, offsetFromTop: 67, offsetDirection: 'bottom', spacingToNext: null },
    ],
    
    // Дополнительные выходы гидроразделителя
    additionalOutlets: [],
    
    // === Параметры распределительного коллектора ===
    // Диаметры коллекторов подачи и обратки
    supplyPipeDiameter: 60.3,
    returnPipeDiameter: 60.3,
    supplyPipeProfile: '80x80', // Профиль для квадратного подающего коллектора
    returnPipeProfile: '80x80', // Профиль для квадратного обратного коллектора
    pipeSpacing: 125,          // Расстояние между гребёнками подачи и обратки
    pipeSpacingMode: 'interaxis', // 'interaxis' или 'offset'
    supplyPipeOffset: 67,      // Отступ подающего коллектора от верха (когда pipeSpacingMode = 'offset')
    returnPipeOffset: 67,      // Отступ обратного коллектора от низа (когда pipeSpacingMode = 'offset')
    
    // Контуры
    circuitCount: 3,
    supplyFirstCircuitOffset: 85,
    supplyFirstCircuitOffsetMode: 'fromEdge', // 'fromEdge' или 'fromReturnCircuit'
    supplyFirstCircuitOffsetReferenceIndex: 0, // индекс контура обратки, от которого отступаем (0 = первый)
    supplyFirstCircuitOffsetSaved: null, // сохранённое значение до включения режима привязки
    supplyFirstCircuitOffsetLastUsed: 0, // последнее использованное смещение в режиме привязки
    supplyLastCircuitOffset: 50,
    returnFirstCircuitOffset: 85,
    returnFirstCircuitOffsetMode: 'fromEdge', // 'fromEdge' или 'fromSupplyCircuit'
    returnFirstCircuitOffsetReferenceIndex: 0, // индекс контура подачи, от которого отступаем (0 = первый)
    returnFirstCircuitOffsetSaved: null, // сохранённое значение до включения режима привязки
    returnFirstCircuitOffsetLastUsed: 0, // последнее использованное смещение в режиме привязки
    returnLastCircuitOffset: 50,
    supplyCircuitSpacing: 125, // межосевое контура подачи по умолчанию
    returnCircuitSpacing: 125, // межосевое контура обратки по умолчанию
    supplySpacingBetweenCircuits: 125, // расстояние между контурами подачи
    returnSpacingBetweenCircuits: 125, // расстояние между контурами обратки
    
    // Подключения подающего коллектора
    hasSupplyLeftConnection: false,
    hasSupplyRightConnection: false,
    supplyLeftConnectionType: 'fitting',
    supplyLeftConnectionFittingCategory: '',
    supplyLeftConnectionFitting: '',
    supplyLeftConnectionFittingLength: null,
    supplyLeftConnectionLength: null,
    supplyLeftConnectionPipeDiameter: 60.3,
    supplyLeftConnectionPipeLength: 100,
    
    supplyRightConnectionType: 'fitting',
    supplyRightConnectionFittingCategory: '',
    supplyRightConnectionFitting: '',
    supplyRightConnectionFittingLength: null,
    supplyRightConnectionLength: null,
    supplyRightConnectionPipeDiameter: 60.3,
    supplyRightConnectionPipeLength: 100,
    
    // Заглушки/переходы подающего коллектора
    hasSupplyLeftCap: true,
    supplyLeftCapType: 'straight',
    supplyLeftCapElementType: 'cap',
    supplyLeftReducer: null,
    
    hasSupplyRightCap: true,
    supplyRightCapType: 'straight',
    supplyRightCapElementType: 'cap',
    supplyRightReducer: null,
    
    // Подключения обратного коллектора
    hasReturnLeftConnection: false,
    hasReturnRightConnection: false,
    returnLeftConnectionType: 'fitting',
    returnLeftConnectionFittingCategory: '',
    returnLeftConnectionFitting: '',
    returnLeftConnectionFittingLength: null,
    returnLeftConnectionLength: null,
    returnLeftConnectionPipeDiameter: 60.3,
    returnLeftConnectionPipeLength: 100,
    
    returnRightConnectionType: 'fitting',
    returnRightConnectionFittingCategory: '',
    returnRightConnectionFitting: '',
    returnRightConnectionFittingLength: null,
    returnRightConnectionLength: null,
    returnRightConnectionPipeDiameter: 60.3,
    returnRightConnectionPipeLength: 100,
    
    // Заглушки/переходы обратного коллектора
    hasReturnLeftCap: true,
    returnLeftCapType: 'straight',
    returnLeftCapElementType: 'cap',
    returnLeftReducer: null,
    
    hasReturnRightCap: true,
    returnRightCapType: 'straight',
    returnRightCapElementType: 'cap',
    returnRightReducer: null,
    
    // Центральные фитинги
    supplyCenterFittingCount: 0,
    returnCenterFittingCount: 0,
    
    // Дополнительные выходы (одиночные, как в этажном коллекторе)
    supplyAdditionalOutletCount: 0,
    returnAdditionalOutletCount: 0,
    supplyAdditionalFirstOutletOffset: 60,
    returnAdditionalFirstOutletOffset: 60,
    supplyAdditionalLastOutletOffset: 60,
    returnAdditionalLastOutletOffset: 60,
    supplyAdditionalOutletSpacing: 100,
    returnAdditionalOutletSpacing: 100,
    
    // Ручное управление длинами коллектора
    useManualLength: false,
    manualSupplyLength: null,
    manualReturnLength: null,
    
    // === Параметры предкотлового коллектора ===
    hasPreBoilerCollector: false,
    preBoilerSupplyPipeDiameter: 60.3,
    preBoilerReturnPipeDiameter: 60.3,
    preBoilerSupplyPipeProfile: '80x80', // Профиль для квадратного подающего предкотлового коллектора
    preBoilerReturnPipeProfile: '80x80', // Профиль для квадратного обратного предкотлового коллектора
    preBoilerPipeSpacing: 125,
    preBoilerPipeSpacingMode: 'interaxis', // 'interaxis' или 'offset'
    preBoilerSupplyPipeOffset: 67,      // Отступ подающего коллектора от верха (когда preBoilerPipeSpacingMode = 'offset')
    preBoilerReturnPipeOffset: 67,      // Отступ обратного коллектора от низа (когда preBoilerPipeSpacingMode = 'offset')
    preBoilerCircuitCount: 3,
    preBoilerSupplyFirstCircuitOffset: 85,
    preBoilerSupplyLastCircuitOffset: 50,
    preBoilerSupplyCircuitSpacing: 125,
    preBoilerSupplySpacingBetweenCircuits: 125,
    preBoilerReturnFirstCircuitOffset: 85,
    preBoilerReturnLastCircuitOffset: 50,
    preBoilerReturnCircuitSpacing: 125,
    preBoilerReturnSpacingBetweenCircuits: 125,
    // Центральные фитинги и дополнительные выходы предкотлового
    preBoilerSupplyCenterFittingCount: 0,
    preBoilerReturnCenterFittingCount: 0,
    preBoilerSupplyAdditionalOutletCount: 0,
    preBoilerReturnAdditionalOutletCount: 0,
    // Левые торцевые подключения предкотлового подающего коллектора
    hasPreBoilerSupplyLeftConnection: false,
    preBoilerSupplyLeftConnectionType: 'pipe',
    preBoilerSupplyLeftConnectionFittingCategory: '',
    preBoilerSupplyLeftConnectionFitting: '',
    preBoilerSupplyLeftConnectionFittingLength: null,
    preBoilerSupplyLeftConnectionLength: null,
    preBoilerSupplyLeftConnectionPipeDiameter: 76.1,
    preBoilerSupplyLeftConnectionPipeLength: 100,
    // Левые торцевые подключения предкотлового обратного коллектора
    hasPreBoilerReturnLeftConnection: false,
    preBoilerReturnLeftConnectionType: 'pipe',
    preBoilerReturnLeftConnectionFittingCategory: '',
    preBoilerReturnLeftConnectionFitting: '',
    preBoilerReturnLeftConnectionFittingLength: null,
    preBoilerReturnLeftConnectionLength: null,
    preBoilerReturnLeftConnectionPipeDiameter: 76.1,
    preBoilerReturnLeftConnectionPipeLength: 100,
    // Левые заглушки предкотлового подающего коллектора
    hasPreBoilerSupplyLeftCap: true,
    preBoilerSupplyLeftCapType: 'straight',
    preBoilerSupplyLeftCapElementType: 'cap',
    preBoilerSupplyLeftReducer: null,
    // Левые заглушки предкотлового обратного коллектора
    hasPreBoilerReturnLeftCap: true,
    preBoilerReturnLeftCapType: 'straight',
    preBoilerReturnLeftCapElementType: 'cap',
    preBoilerReturnLeftReducer: null,
    
    // Ручное управление длинами предкотлового коллектора
    usePreBoilerManualLength: false,
    manualPreBoilerSupplyLength: null,
    manualPreBoilerReturnLength: null,
  });

  const [params, setParams] = useState(() => {
    if (initialParams?.params) {
      return { ...getDefaultParams(), ...initialParams.params };
    }
    return getDefaultParams();
  });

  // === State для контуров коллектора (из DistributionCollectorEditor) ===
  
  // Инициализация контуров подачи из сохранённых или по умолчанию
  const [supplyCircuits, setSupplyCircuits] = useState(() => {
    if (initialParams?.supplyCircuits) {
      return initialParams.supplyCircuits.map(circuit => ({
        ...circuit,
        circuitSpacing: circuit.circuitSpacing ?? 50,
        swapOrder: circuit.swapOrder ?? false,
      }));
    }
    return Array.from({ length: getDefaultParams().circuitCount }, (_, i) => ({
      id: `supply-${i}`,
      fitting: 'Nippel_1',
      fittingLength: null,
      flangePipeLength: null,
      spacingToNext: null,
      circuitSpacing: 125, // межосевое контура по умолчанию
      swapOrder: false,
    }));
  });
  
  // Инициализация контуров обратки из сохранённых или по умолчанию
  const [returnCircuits, setReturnCircuits] = useState(() => {
    if (initialParams?.returnCircuits) {
      return initialParams.returnCircuits.map(circuit => ({
        ...circuit,
        circuitSpacing: circuit.circuitSpacing ?? 50,
        swapOrder: circuit.swapOrder ?? false,
      }));
    }
    return Array.from({ length: getDefaultParams().circuitCount }, (_, i) => ({
      id: `return-${i}`,
      fitting: 'Nippel_1',
      fittingLength: null,
      flangePipeLength: null,
      spacingToNext: null,
      circuitSpacing: 125, // межосевое контура по умолчанию
      swapOrder: false,
    }));
  });
  
  // Центральные фитинги подающего коллектора
  const [supplyCenterFittings, setSupplyCenterFittings] = useState(() => {
    if (initialParams?.supplyCenterFittings && initialParams.supplyCenterFittings.length > 0) {
      return initialParams.supplyCenterFittings;
    }
    return [];
  });
  
  // Центральные фитинги обратного коллектора
  const [returnCenterFittings, setReturnCenterFittings] = useState(() => {
    if (initialParams?.returnCenterFittings && initialParams.returnCenterFittings.length > 0) {
      return initialParams.returnCenterFittings;
    }
    return [];
  });
  
  // Дополнительные выходы подающего коллектора
  const [supplyAdditionalOutlets, setSupplyAdditionalOutlets] = useState(() => {
    if (initialParams?.supplyAdditionalOutlets && initialParams.supplyAdditionalOutlets.length > 0) {
      return initialParams.supplyAdditionalOutlets.map((outlet) => ({
        ...outlet,
        direction: outlet.direction || 'up',
        positionType: outlet.positionType || 'distance',
        distanceFromEdge: outlet.distanceFromEdge ?? 60,
        distanceDirection: outlet.distanceDirection || 'left',
        betweenIndex: outlet.betweenIndex ?? 0,
        outletType: outlet.outletType || 'short', // Тип выхода: 'short' или 'through'
      }));
    }
    return [];
  });
  
  // Дополнительные выходы обратного коллектора
  const [returnAdditionalOutlets, setReturnAdditionalOutlets] = useState(() => {
    if (initialParams?.returnAdditionalOutlets && initialParams.returnAdditionalOutlets.length > 0) {
      return initialParams.returnAdditionalOutlets.map((outlet) => ({
        ...outlet,
        direction: outlet.direction || 'down',
        positionType: outlet.positionType || 'distance',
        distanceFromEdge: outlet.distanceFromEdge ?? 60,
        distanceDirection: outlet.distanceDirection || 'left',
        betweenIndex: outlet.betweenIndex ?? 0,
        outletType: outlet.outletType || 'short', // Тип выхода: 'short' или 'through'
      }));
    }
    return [];
  });
  
  // === State для предкотлового коллектора ===
  const [preBoilerSupplyCircuits, setPreBoilerSupplyCircuits] = useState(() => {
    if (initialParams?.preBoilerSupplyCircuits) {
      return initialParams.preBoilerSupplyCircuits.map(circuit => ({
        ...circuit,
        circuitSpacing: circuit.circuitSpacing ?? 50,
        swapOrder: circuit.swapOrder ?? false,
      }));
    }
    return Array.from({ length: 3 }, (_, i) => ({
      id: `preboiler-supply-${i}`,
      fitting: 'Nippel_1',
      fittingId: 'Nippel_1',
      fittingCategory: 'Ниппели',
      type: 'fitting',
      fittingLength: null,
      flangePipeLength: null,
      spacingToNext: null,
      circuitSpacing: 125,
      swapOrder: false,
    }));
  });
  
  const [preBoilerReturnCircuits, setPreBoilerReturnCircuits] = useState(() => {
    if (initialParams?.preBoilerReturnCircuits) {
      return initialParams.preBoilerReturnCircuits.map(circuit => ({
        ...circuit,
        circuitSpacing: circuit.circuitSpacing ?? 50,
        swapOrder: circuit.swapOrder ?? false,
      }));
    }
    return Array.from({ length: 3 }, (_, i) => ({
      id: `preboiler-return-${i}`,
      fitting: 'Nippel_1',
      fittingId: 'Nippel_1',
      fittingCategory: 'Ниппели',
      type: 'fitting',
      fittingLength: null,
      flangePipeLength: null,
      spacingToNext: null,
      circuitSpacing: 125,
      swapOrder: false,
    }));
  });
  
  const [preBoilerSupplyCenterFittings, setPreBoilerSupplyCenterFittings] = useState(() => {
    if (initialParams?.preBoilerSupplyCenterFittings && initialParams.preBoilerSupplyCenterFittings.length > 0) {
      return initialParams.preBoilerSupplyCenterFittings;
    }
    return [];
  });
  
  const [preBoilerReturnCenterFittings, setPreBoilerReturnCenterFittings] = useState(() => {
    if (initialParams?.preBoilerReturnCenterFittings && initialParams.preBoilerReturnCenterFittings.length > 0) {
      return initialParams.preBoilerReturnCenterFittings;
    }
    return [];
  });
  
  const [preBoilerSupplyAdditionalOutlets, setPreBoilerSupplyAdditionalOutlets] = useState(() => {
    if (initialParams?.preBoilerSupplyAdditionalOutlets && initialParams.preBoilerSupplyAdditionalOutlets.length > 0) {
      return initialParams.preBoilerSupplyAdditionalOutlets.map((outlet) => ({
        ...outlet,
        direction: outlet.direction || 'down',
        positionType: outlet.positionType || 'distance',
        distanceFromEdge: outlet.distanceFromEdge ?? 60,
        distanceDirection: outlet.distanceDirection || 'left',
        betweenIndex: outlet.betweenIndex ?? 0,
        outletType: outlet.outletType || 'short', // Тип выхода: 'short' или 'through'
      }));
    }
    return [];
  });
  
  const [preBoilerReturnAdditionalOutlets, setPreBoilerReturnAdditionalOutlets] = useState(() => {
    if (initialParams?.preBoilerReturnAdditionalOutlets && initialParams.preBoilerReturnAdditionalOutlets.length > 0) {
      return initialParams.preBoilerReturnAdditionalOutlets.map((outlet) => ({
        ...outlet,
        direction: outlet.direction || 'up',
        positionType: outlet.positionType || 'distance',
        distanceFromEdge: outlet.distanceFromEdge ?? 60,
        distanceDirection: outlet.distanceDirection || 'left',
        betweenIndex: outlet.betweenIndex ?? 0,
        outletType: outlet.outletType || 'short', // Тип выхода: 'short' или 'through'
      }));
    }
    return [];
  });

  const [preBoilerDiametersLinked, setPreBoilerDiametersLinked] = useState(true); // Синхронизация диаметров предкотлового
  const [preBoilerLeftConnectionsLinked, setPreBoilerLeftConnectionsLinked] = useState(true); // Синхронизация левых подключений предкотлового

  const [incompatibilityDialog, setIncompatibilityDialog] = useState({
    open: false,
    message: '',
  });

  const [draggedLeftIndex, setDraggedLeftIndex] = useState(null);
  const [draggedSupplyCircuitIndex, setDraggedSupplyCircuitIndex] = useState(null);
  const [draggedReturnCircuitIndex, setDraggedReturnCircuitIndex] = useState(null);
  const [draggedPreBoilerSupplyCircuitIndex, setDraggedPreBoilerSupplyCircuitIndex] = useState(null);
  const [draggedPreBoilerReturnCircuitIndex, setDraggedPreBoilerReturnCircuitIndex] = useState(null);
  const [lengthsEqualized, setLengthsEqualized] = useState(false); // Флаг приравнивания длин
  const [preBoilerLengthsEqualized, setPreBoilerLengthsEqualized] = useState(false); // Флаг приравнивания длин предкотлового
  const [equalizeDialog, setEqualizeDialog] = useState(false); // Диалог предложения приравнять длины
  const [equalizeMainCollector, setEqualizeMainCollector] = useState(true); // Чекбокс для основного коллектора
  const [equalizePreBoilerCollector, setEqualizePreBoilerCollector] = useState(true); // Чекбокс для предкотлового
  const [diametersLinked, setDiametersLinked] = useState(true); // Флаг синхронизации диаметров (по умолчанию включен)
  const [rightConnectionsLinked, setRightConnectionsLinked] = useState(true); // Флаг синхронизации правых торцевых подключений
  const leftConnectionsLinked = true; // Флаг синхронизации левых торцевых подключений (всегда включен, без UI)
  const [hasPreBoilerCollector, setHasPreBoilerCollector] = useState(() => {
    return initialParams?.hasPreBoilerCollector ?? false;
  }); // Предкотловой коллектор
  
  const [isSystemFlipped, setIsSystemFlipped] = useState(() => {
    return initialParams?.isSystemFlipped ?? false;
  }); // Флаг отражения системы (коллектор слева, предкотловой справа)
  
  // Состояние для хранения предыдущих типов заглушек при переключении на квадратную форму
  const [savedCapTypes, setSavedCapTypes] = useState(null);


  const updateParam = (keyOrObj, value) => {
    let updates = {};
    
    if (typeof keyOrObj === 'object') {
      updates = { ...keyOrObj };
      
      // Синхронизация торцевых подключений для объектов
      if (rightConnectionsLinked) {
        const syncUpdates = {};
        
        Object.keys(updates).forEach(key => {
          // Правые подключения
          if (rightConnectionsLinked) {
            // Проверяем ключи для подачи (Supply) - все ключи связанные с правым подключением подачи
            if (key.startsWith('supplyRightConnection') || 
                key.startsWith('hasSupplyRightCap') || 
                key.startsWith('supplyRightCap') ||
                key.startsWith('supplyRightReducer')) {
              const returnKey = key.replace(/supplyRight/g, 'returnRight')
                                   .replace(/SupplyRight/g, 'ReturnRight');
              syncUpdates[returnKey] = updates[key];
            } 
            // Проверяем ключи для обратки (Return) - все ключи связанные с правым подключением обратки
            else if (key.startsWith('returnRightConnection') || 
                     key.startsWith('hasReturnRightCap') || 
                     key.startsWith('returnRightCap') ||
                     key.startsWith('returnRightReducer')) {
              const supplyKey = key.replace(/returnRight/g, 'supplyRight')
                                   .replace(/ReturnRight/g, 'SupplyRight');
              syncUpdates[supplyKey] = updates[key];
            }
            if (key === 'hasSupplyRightConnection') {
              syncUpdates.hasReturnRightConnection = updates[key];
            } else if (key === 'hasReturnRightConnection') {
              syncUpdates.hasSupplyRightConnection = updates[key];
            }
          }
          
          // Левые подключения
          if (leftConnectionsLinked) {
            // Проверяем ключи для подачи (Supply) - все ключи связанные с левым подключением подачи
            if (key.startsWith('supplyLeftConnection') || 
                key.startsWith('hasSupplyLeftCap') || 
                key.startsWith('supplyLeftCap') ||
                key.startsWith('supplyLeftReducer')) {
              const returnKey = key.replace(/supplyLeft/g, 'returnLeft')
                                   .replace(/SupplyLeft/g, 'ReturnLeft');
              syncUpdates[returnKey] = updates[key];
            } 
            // Проверяем ключи для обратки (Return) - все ключи связанные с левым подключением обратки
            else if (key.startsWith('returnLeftConnection') || 
                     key.startsWith('hasReturnLeftCap') || 
                     key.startsWith('returnLeftCap') ||
                     key.startsWith('returnLeftReducer')) {
              const supplyKey = key.replace(/returnLeft/g, 'supplyLeft')
                                   .replace(/ReturnLeft/g, 'SupplyLeft');
              syncUpdates[supplyKey] = updates[key];
            }
            if (key === 'hasSupplyLeftConnection') {
              syncUpdates.hasReturnLeftConnection = updates[key];
            } else if (key === 'hasReturnLeftConnection') {
              syncUpdates.hasSupplyLeftConnection = updates[key];
            }
          }
        });
        
        updates = { ...updates, ...syncUpdates };
      }
      
      // Синхронизация предкотлового коллектора (для объектов)
      if (preBoilerLeftConnectionsLinked) {
        const preBoilerSyncUpdates = {};
        Object.keys(updates).forEach(key => {
          if (key.startsWith('preBoilerSupplyLeftConnection') || 
              key.startsWith('hasPreBoilerSupplyLeftCap') || 
              key.startsWith('preBoilerSupplyLeftCap') ||
              key.startsWith('preBoilerSupplyLeftReducer')) {
            const returnKey = key.replace(/preBoilerSupplyLeft/g, 'preBoilerReturnLeft')
                                 .replace(/PreBoilerSupplyLeft/g, 'PreBoilerReturnLeft');
            preBoilerSyncUpdates[returnKey] = updates[key];
          } 
          else if (key.startsWith('preBoilerReturnLeftConnection') || 
                   key.startsWith('hasPreBoilerReturnLeftCap') || 
                   key.startsWith('preBoilerReturnLeftCap') ||
                   key.startsWith('preBoilerReturnLeftReducer')) {
            const supplyKey = key.replace(/preBoilerReturnLeft/g, 'preBoilerSupplyLeft')
                                 .replace(/PreBoilerReturnLeft/g, 'PreBoilerSupplyLeft');
            preBoilerSyncUpdates[supplyKey] = updates[key];
          }
        });
        updates = { ...updates, ...preBoilerSyncUpdates };
      }
      
      setParams(prev => ({ ...prev, ...updates }));
    } else {
      // Не конвертируем boolean в number
      let finalValue;
      if (typeof value === 'boolean') {
        finalValue = value;
      } else {
        const numValue = Number(value);
        finalValue = !isNaN(numValue) && value !== '' ? numValue : value;
      }
      updates[keyOrObj] = finalValue;
      
      // Синхронизация торцевых подключений
      // Правые подключения
      if (rightConnectionsLinked) {
        if (keyOrObj.startsWith('supplyRightConnection') || 
            keyOrObj.startsWith('hasSupplyRightCap') || 
            keyOrObj.startsWith('supplyRightCap') ||
            keyOrObj.startsWith('supplyRightReducer')) {
          const returnKey = keyOrObj.replace(/supplyRight/g, 'returnRight')
                                     .replace(/SupplyRight/g, 'ReturnRight');
          updates[returnKey] = finalValue;
        } 
        else if (keyOrObj.startsWith('returnRightConnection') || 
                 keyOrObj.startsWith('hasReturnRightCap') || 
                 keyOrObj.startsWith('returnRightCap') ||
                 keyOrObj.startsWith('returnRightReducer')) {
          const supplyKey = keyOrObj.replace(/returnRight/g, 'supplyRight')
                                     .replace(/ReturnRight/g, 'SupplyRight');
          updates[supplyKey] = finalValue;
        }
        if (keyOrObj === 'hasSupplyRightConnection') {
          updates.hasReturnRightConnection = finalValue;
        } else if (keyOrObj === 'hasReturnRightConnection') {
          updates.hasSupplyRightConnection = finalValue;
        }
      }
      
      // Левые подключения
      if (leftConnectionsLinked) {
        if (keyOrObj.startsWith('supplyLeftConnection') || 
            keyOrObj.startsWith('hasSupplyLeftCap') || 
            keyOrObj.startsWith('supplyLeftCap') ||
            keyOrObj.startsWith('supplyLeftReducer')) {
          const returnKey = keyOrObj.replace(/supplyLeft/g, 'returnLeft')
                                     .replace(/SupplyLeft/g, 'ReturnLeft');
          updates[returnKey] = finalValue;
        } 
        else if (keyOrObj.startsWith('returnLeftConnection') || 
                 keyOrObj.startsWith('hasReturnLeftCap') || 
                 keyOrObj.startsWith('returnLeftCap') ||
                 keyOrObj.startsWith('returnLeftReducer')) {
          const supplyKey = keyOrObj.replace(/returnLeft/g, 'supplyLeft')
                                     .replace(/ReturnLeft/g, 'SupplyLeft');
          updates[supplyKey] = finalValue;
        }
        if (keyOrObj === 'hasSupplyLeftConnection') {
          updates.hasReturnLeftConnection = finalValue;
        } else if (keyOrObj === 'hasReturnLeftConnection') {
          updates.hasSupplyLeftConnection = finalValue;
        }
      }
      
      // Синхронизация диаметров
      if (diametersLinked) {
        if (keyOrObj === 'supplyPipeDiameter') {
          updates.returnPipeDiameter = finalValue;
        } else if (keyOrObj === 'returnPipeDiameter') {
          updates.supplyPipeDiameter = finalValue;
        }
      }
      
      // Синхронизация диаметров предкотлового коллектора
      if (preBoilerDiametersLinked) {
        if (keyOrObj === 'preBoilerSupplyPipeDiameter') {
          updates.preBoilerReturnPipeDiameter = finalValue;
        } else if (keyOrObj === 'preBoilerReturnPipeDiameter') {
          updates.preBoilerSupplyPipeDiameter = finalValue;
        }
      }
      
      // Синхронизация левых подключений предкотлового коллектора
      if (preBoilerLeftConnectionsLinked) {
        if (keyOrObj.startsWith('preBoilerSupplyLeftConnection') || 
            keyOrObj.startsWith('hasPreBoilerSupplyLeftCap') || 
            keyOrObj.startsWith('preBoilerSupplyLeftCap') ||
            keyOrObj.startsWith('preBoilerSupplyLeftReducer')) {
          const returnKey = keyOrObj.replace(/preBoilerSupplyLeft/g, 'preBoilerReturnLeft')
                                     .replace(/PreBoilerSupplyLeft/g, 'PreBoilerReturnLeft');
          updates[returnKey] = finalValue;
        } 
        else if (keyOrObj.startsWith('preBoilerReturnLeftConnection') || 
                 keyOrObj.startsWith('hasPreBoilerReturnLeftCap') || 
                 keyOrObj.startsWith('preBoilerReturnLeftCap') ||
                 keyOrObj.startsWith('preBoilerReturnLeftReducer')) {
          const supplyKey = keyOrObj.replace(/preBoilerReturnLeft/g, 'preBoilerSupplyLeft')
                                     .replace(/PreBoilerReturnLeft/g, 'PreBoilerSupplyLeft');
          updates[supplyKey] = finalValue;
        }
        if (keyOrObj === 'hasPreBoilerSupplyLeftConnection') {
          updates.hasPreBoilerReturnLeftConnection = finalValue;
        } else if (keyOrObj === 'hasPreBoilerReturnLeftConnection') {
          updates.hasPreBoilerSupplyLeftConnection = finalValue;
        }
      }
      
      setParams(prev => ({ ...prev, ...updates }));
    }
  };

  // Работа с левыми подключениями
  const updateLeftConnection = (index, key, value) => {
    setParams(prev => {
      const updated = [...prev.leftConnections];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, leftConnections: updated };
    });
  };

  const addLeftConnection = () => {
    setParams(prev => ({
      ...prev,
      leftConnections: [
        ...prev.leftConnections,
        {
          id: `left-${Date.now()}`,
          fittingId: 'Nippel_3.4',
          fittingLength: null,
          offsetFromTop: Math.round(prev.separatorHeight / 2),
          offsetDirection: 'top',
        },
      ],
    }));
  };

  const deleteLeftConnection = (index) => {
    if (params.leftConnections.length <= 1) return;
    setParams(prev => ({
      ...prev,
      leftConnections: prev.leftConnections.filter((_, i) => i !== index),
    }));
  };

  const moveLeftConnection = (fromIndex, toIndex) => {
    setParams(prev => {
      const newConnections = [...prev.leftConnections];
      const [movedItem] = newConnections.splice(fromIndex, 1);
      newConnections.splice(toIndex, 0, movedItem);
      return {
        ...prev,
        leftConnections: newConnections.map((c, i) => ({ ...c, id: `left-${i + 1}` }))
      };
    });
  };

  // ========================================
  // Функции для работы с дополнительными выходами гидроразделителя
  // ========================================

  const addAdditionalOutlet = () => {
    setParams(prev => ({
      ...prev,
      additionalOutlets: [
        ...prev.additionalOutlets,
        {
          id: `outlet-${Date.now()}`,
          fittingCategory: 'Ниппели',
          fittingId: 'Nippel_3.4',
          positionType: 'center',
          side: 'both',
          distanceFromEdge: 50,
          distanceDirection: 'left',
        },
      ],
    }));
  };

  const updateAdditionalOutlet = (index, key, value) => {
    setParams(prev => {
      const updated = [...prev.additionalOutlets];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, additionalOutlets: updated };
    });
  };

  const removeAdditionalOutlet = (index) => {
    setParams(prev => ({
      ...prev,
      additionalOutlets: prev.additionalOutlets.filter((_, i) => i !== index),
    }));
  };

  // ========================================
  // Функция для отражения системы (обмен коллектора и предкотлового)
  // ========================================
  
  const toggleSystemFlip = () => {
    const newFlippedState = !isSystemFlipped;
    setIsSystemFlipped(newFlippedState);
    
    // Если есть предкотловой коллектор, меняем местами параметры коллектора и предкотлового коллектора
    if (!hasPreBoilerCollector) {
      // Если нет предкотлового коллектора, просто переключаем флаг (левые/правые подключения)
      return;
    }
    
    // Меняем местами параметры коллектора и предкотлового коллектора
    setParams(prev => {
      // Сохраняем текущие параметры коллектора
      const tempSupplyDiameter = prev.supplyPipeDiameter;
      const tempReturnDiameter = prev.returnPipeDiameter;
      const tempPipeSpacing = prev.pipeSpacing;
      const tempPipeSpacingMode = prev.pipeSpacingMode;
      const tempSupplyPipeOffset = prev.supplyPipeOffset;
      const tempReturnPipeOffset = prev.returnPipeOffset;
      const tempSupplyFirstCircuitOffset = prev.supplyFirstCircuitOffset;
      const tempSupplyLastCircuitOffset = prev.supplyLastCircuitOffset;
      const tempReturnFirstCircuitOffset = prev.returnFirstCircuitOffset;
      const tempReturnLastCircuitOffset = prev.returnLastCircuitOffset;
      const tempSupplyCircuitSpacing = prev.supplyCircuitSpacing;
      const tempReturnCircuitSpacing = prev.returnCircuitSpacing;
      const tempSupplySpacingBetweenCircuits = prev.supplySpacingBetweenCircuits;
      const tempReturnSpacingBetweenCircuits = prev.returnSpacingBetweenCircuits;
      const tempSupplyCenterFittingCount = prev.supplyCenterFittingCount;
      const tempReturnCenterFittingCount = prev.returnCenterFittingCount;
      const tempSupplyAdditionalOutletCount = prev.supplyAdditionalOutletCount;
      const tempReturnAdditionalOutletCount = prev.returnAdditionalOutletCount;
      const tempUseManualLength = prev.useManualLength;
      const tempManualSupplyLength = prev.manualSupplyLength;
      const tempManualReturnLength = prev.manualReturnLength;
      
      // Меняем местами с предкотловым коллектором
      return {
        ...prev,
        // Основной коллектор получает параметры предкотлового
        supplyPipeDiameter: prev.preBoilerSupplyPipeDiameter,
        returnPipeDiameter: prev.preBoilerReturnPipeDiameter,
        pipeSpacing: prev.preBoilerPipeSpacing,
        pipeSpacingMode: prev.preBoilerPipeSpacingMode,
        supplyPipeOffset: prev.preBoilerSupplyPipeOffset,
        returnPipeOffset: prev.preBoilerReturnPipeOffset,
        supplyFirstCircuitOffset: prev.preBoilerSupplyFirstCircuitOffset,
        supplyLastCircuitOffset: prev.preBoilerSupplyLastCircuitOffset,
        returnFirstCircuitOffset: prev.preBoilerReturnFirstCircuitOffset,
        returnLastCircuitOffset: prev.preBoilerReturnLastCircuitOffset,
        supplyCircuitSpacing: prev.preBoilerSupplyCircuitSpacing,
        returnCircuitSpacing: prev.preBoilerReturnCircuitSpacing,
        supplySpacingBetweenCircuits: prev.preBoilerSupplySpacingBetweenCircuits,
        returnSpacingBetweenCircuits: prev.preBoilerReturnSpacingBetweenCircuits,
        supplyCenterFittingCount: prev.preBoilerSupplyCenterFittingCount,
        returnCenterFittingCount: prev.preBoilerReturnCenterFittingCount,
        supplyAdditionalOutletCount: prev.preBoilerSupplyAdditionalOutletCount,
        returnAdditionalOutletCount: prev.preBoilerReturnAdditionalOutletCount,
        useManualLength: prev.usePreBoilerManualLength,
        manualSupplyLength: prev.manualPreBoilerSupplyLength,
        manualReturnLength: prev.manualPreBoilerReturnLength,
        
        // Предкотловой коллектор получает параметры основного
        preBoilerSupplyPipeDiameter: tempSupplyDiameter,
        preBoilerReturnPipeDiameter: tempReturnDiameter,
        preBoilerPipeSpacing: tempPipeSpacing,
        preBoilerPipeSpacingMode: tempPipeSpacingMode,
        preBoilerSupplyPipeOffset: tempSupplyPipeOffset,
        preBoilerReturnPipeOffset: tempReturnPipeOffset,
        preBoilerSupplyFirstCircuitOffset: tempSupplyFirstCircuitOffset,
        preBoilerSupplyLastCircuitOffset: tempSupplyLastCircuitOffset,
        preBoilerReturnFirstCircuitOffset: tempReturnFirstCircuitOffset,
        preBoilerReturnLastCircuitOffset: tempReturnLastCircuitOffset,
        preBoilerSupplyCircuitSpacing: tempSupplyCircuitSpacing,
        preBoilerReturnCircuitSpacing: tempReturnCircuitSpacing,
        preBoilerSupplySpacingBetweenCircuits: tempSupplySpacingBetweenCircuits,
        preBoilerReturnSpacingBetweenCircuits: tempReturnSpacingBetweenCircuits,
        preBoilerSupplyCenterFittingCount: tempSupplyCenterFittingCount,
        preBoilerReturnCenterFittingCount: tempReturnCenterFittingCount,
        preBoilerSupplyAdditionalOutletCount: tempSupplyAdditionalOutletCount,
        preBoilerReturnAdditionalOutletCount: tempReturnAdditionalOutletCount,
        usePreBoilerManualLength: tempUseManualLength,
        manualPreBoilerSupplyLength: tempManualSupplyLength,
        manualPreBoilerReturnLength: tempManualReturnLength,
      };
    });
    
    // Меняем местами контуры
    const tempSupplyCircuits = [...supplyCircuits];
    const tempReturnCircuits = [...returnCircuits];
    const tempSupplyCenterFittings = [...supplyCenterFittings];
    const tempReturnCenterFittings = [...returnCenterFittings];
    const tempSupplyAdditionalOutlets = [...supplyAdditionalOutlets];
    const tempReturnAdditionalOutlets = [...returnAdditionalOutlets];
    
    setSupplyCircuits([...preBoilerSupplyCircuits]);
    setReturnCircuits([...preBoilerReturnCircuits]);
    setSupplyCenterFittings([...preBoilerSupplyCenterFittings]);
    setReturnCenterFittings([...preBoilerReturnCenterFittings]);
    setSupplyAdditionalOutlets([...preBoilerSupplyAdditionalOutlets]);
    setReturnAdditionalOutlets([...preBoilerReturnAdditionalOutlets]);
    
    setPreBoilerSupplyCircuits(tempSupplyCircuits);
    setPreBoilerReturnCircuits(tempReturnCircuits);
    setPreBoilerSupplyCenterFittings(tempSupplyCenterFittings);
    setPreBoilerReturnCenterFittings(tempReturnCenterFittings);
    setPreBoilerSupplyAdditionalOutlets(tempSupplyAdditionalOutlets);
    setPreBoilerReturnAdditionalOutlets(tempReturnAdditionalOutlets);
  };

  // ========================================
  // Функции для работы с контурами коллектора (из DistributionCollectorEditor)
  // ========================================
  
  // Вычисляем эффективную высоту гидроразделителя (с учетом режима межосевого для левых подключений)
  const calculatedSeparatorHeight = useMemo(() => {
    // При наличии предкотлового коллектора левые подключения отключены — используем ручное значение
    if (hasPreBoilerCollector || params.leftConnectionsPlacementMode !== 'spacing') {
      return params.separatorHeight;
    }
    
    // Вычисляем максимальную позицию левых подключений
    let leftMaxPosition = params.leftConnectionsFirstOffset;
    if (params.leftConnections.length > 1) {
      for (let i = 0; i < params.leftConnections.length - 1; i++) {
        const spacing = Number(params.leftConnections[i].spacingToNext) || params.leftConnectionsSpacing;
        leftMaxPosition += spacing;
      }
    }
    leftMaxPosition += params.leftConnectionsLastOffset;
    
    return Math.round(leftMaxPosition);
  }, [
    hasPreBoilerCollector,
    params.leftConnectionsPlacementMode,
    params.separatorHeight,
    params.leftConnectionsFirstOffset,
    params.leftConnectionsLastOffset,
    params.leftConnectionsSpacing,
    params.leftConnections
  ]);
  
  // Вычисляем эффективный отступ первого контура подачи (может зависеть от контура обратки)
  const effectiveSupplyFirstCircuitOffset = useMemo(() => {
    // Если режим "от контура обратки" включен
    if (params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit') {
      // Проверяем наличие контуров обратки
      if (returnCircuits.length === 0) {
        return params.supplyFirstCircuitOffset;
      }
      
      // Вычисляем позицию выбранного контура обратки
      const referenceIndex = params.supplyFirstCircuitOffsetReferenceIndex || 0;
      
      if (referenceIndex >= returnCircuits.length) {
        return params.returnFirstCircuitOffset + (params.supplyFirstCircuitOffset || 0);
      }
      
      // Вычисляем позицию выбранного контура обратки
      let referenceCircuitPosition = params.returnFirstCircuitOffset;
      
      for (let i = 0; i < referenceIndex; i++) {
        const circuit = returnCircuits[i];
        const circuitSpacing = Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
        referenceCircuitPosition += circuitSpacing;
        
        if (i < referenceIndex) {
          const spacingToNext = Number(circuit.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
          referenceCircuitPosition += spacingToNext;
        }
      }
      
      return referenceCircuitPosition + (params.supplyFirstCircuitOffset || 0);
    }
    
    return params.supplyFirstCircuitOffset;
  }, [
    params.supplyFirstCircuitOffsetMode, 
    params.supplyFirstCircuitOffset, 
    params.supplyFirstCircuitOffsetReferenceIndex,
    params.returnFirstCircuitOffset,
    params.returnCircuitSpacing,
    params.returnSpacingBetweenCircuits,
    returnCircuits
  ]);

  // Вычисляем эффективный отступ первого контура обратки (может зависеть от контура подачи)
  const effectiveReturnFirstCircuitOffset = useMemo(() => {
    // Если режим "от контура подачи" включен
    if (params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit') {
      // Проверяем наличие контуров подачи
      if (supplyCircuits.length === 0) {
        return params.returnFirstCircuitOffset;
      }
      
      // Вычисляем позицию выбранного контура подачи
      const referenceIndex = params.returnFirstCircuitOffsetReferenceIndex || 0;
      
      if (referenceIndex >= supplyCircuits.length) {
        return effectiveSupplyFirstCircuitOffset + (params.returnFirstCircuitOffset || 0);
      }
      
      // Вычисляем позицию выбранного контура подачи
      let referenceCircuitPosition = effectiveSupplyFirstCircuitOffset;
      
      for (let i = 0; i < referenceIndex; i++) {
        const circuit = supplyCircuits[i];
        const circuitSpacing = Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
        referenceCircuitPosition += circuitSpacing;
        
        if (i < referenceIndex) {
          const spacingToNext = Number(circuit.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
          referenceCircuitPosition += spacingToNext;
        }
      }
      
      return referenceCircuitPosition + (params.returnFirstCircuitOffset || 0);
    }
    
    return params.returnFirstCircuitOffset;
  }, [
    params.returnFirstCircuitOffsetMode, 
    params.returnFirstCircuitOffset, 
    params.returnFirstCircuitOffsetReferenceIndex,
    effectiveSupplyFirstCircuitOffset,
    params.supplyCircuitSpacing,
    params.supplySpacingBetweenCircuits,
    supplyCircuits
  ]);
  
  // Расчёт длины коллекторов (БЕЗ прямых заглушек)
  const { supplyCalculatedLength, returnCalculatedLength } = useMemo(() => {
    // Если включен ручной режим, используем заданные значения
    if (params.useManualLength) {
      return {
        supplyCalculatedLength: params.manualSupplyLength || 150,
        returnCalculatedLength: params.manualReturnLength || 150,
      };
    }
    
    // Рассчитываем длину для коллектора подачи
    let supplyLength = effectiveSupplyFirstCircuitOffset + params.supplyLastCircuitOffset;
    
    // Добавляем межосевое внутри каждого контура подачи
    supplyCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
      supplyLength += circuitSpacing; // межосевое внутри контура (между двумя патрубками)
      
      // Добавляем расстояние до следующего контура (кроме последнего)
      if (index < supplyCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
        supplyLength += spacingToNext;
      }
    });
    
    // Рассчитываем длину для коллектора обратки
    let returnLength = effectiveReturnFirstCircuitOffset + params.returnLastCircuitOffset;
    
    // Добавляем межосевое внутри каждого контура обратки
    returnCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
      returnLength += circuitSpacing; // межосевое внутри контура (между двумя патрубками)
      
      // Добавляем расстояние до следующего контура (кроме последнего)
      if (index < returnCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
        returnLength += spacingToNext;
      }
    });
    
    // Применяем минимальную длину
    supplyLength = Math.max(supplyLength, 150);
    returnLength = Math.max(returnLength, 150);
    
    return {
      supplyCalculatedLength: supplyLength,
      returnCalculatedLength: returnLength,
    };
  }, [
    params.useManualLength, 
    params.manualSupplyLength, 
    params.manualReturnLength,
    effectiveSupplyFirstCircuitOffset, 
    params.supplyLastCircuitOffset, 
    effectiveReturnFirstCircuitOffset, 
    params.returnLastCircuitOffset, 
    params.supplySpacingBetweenCircuits, 
    params.returnSpacingBetweenCircuits, 
    params.supplyCircuitSpacing, 
    params.returnCircuitSpacing, 
    supplyCircuits, 
    returnCircuits
  ]);

  // Вычисляем отступ последнего контура в ручном режиме (обратный расчет)
  const { supplyLastCircuitOffsetCalculated, returnLastCircuitOffsetCalculated } = useMemo(() => {
    if (!params.useManualLength) {
      return {
        supplyLastCircuitOffsetCalculated: params.supplyLastCircuitOffset,
        returnLastCircuitOffsetCalculated: params.returnLastCircuitOffset,
      };
    }
    
    // Для подачи
    let supplyRemainder = params.manualSupplyLength || 150;
    supplyRemainder -= effectiveSupplyFirstCircuitOffset;
    
    supplyCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
      supplyRemainder -= circuitSpacing;
      
      if (index < supplyCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
        supplyRemainder -= spacingToNext;
      }
    });
    
    // Для обратки
    let returnRemainder = params.manualReturnLength || 150;
    returnRemainder -= effectiveReturnFirstCircuitOffset;
    
    returnCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
      returnRemainder -= circuitSpacing;
      
      if (index < returnCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
        returnRemainder -= spacingToNext;
      }
    });
    
    return {
      supplyLastCircuitOffsetCalculated: Math.max(supplyRemainder, 0),
      returnLastCircuitOffsetCalculated: Math.max(returnRemainder, 0),
    };
  }, [
    params.useManualLength,
    params.manualSupplyLength,
    params.manualReturnLength,
    effectiveSupplyFirstCircuitOffset,
    effectiveReturnFirstCircuitOffset,
    params.supplyLastCircuitOffset,
    params.returnLastCircuitOffset,
    params.supplySpacingBetweenCircuits,
    params.returnSpacingBetweenCircuits,
    params.supplyCircuitSpacing,
    params.returnCircuitSpacing,
    supplyCircuits,
    returnCircuits
  ]);

  // Автоматическое отключение привязки при удалении всех контуров
  useEffect(() => {
    // Если включена привязка подачи к контуру обратки, но контуров обратки больше нет
    if (params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit' && returnCircuits.length === 0) {
      setParams(prev => ({
        ...prev,
        supplyFirstCircuitOffsetMode: 'fromEdge',
        supplyFirstCircuitOffset: prev.supplyFirstCircuitOffsetSaved !== null 
          ? prev.supplyFirstCircuitOffsetSaved 
          : prev.supplyFirstCircuitOffset,
      }));
    }
    
    // Если включена привязка обратки к контуру подачи, но контуров подачи больше нет
    if (params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit' && supplyCircuits.length === 0) {
      setParams(prev => ({
        ...prev,
        returnFirstCircuitOffsetMode: 'fromEdge',
        returnFirstCircuitOffset: prev.returnFirstCircuitOffsetSaved !== null 
          ? prev.returnFirstCircuitOffsetSaved 
          : prev.returnFirstCircuitOffset,
      }));
    }
  }, [
    returnCircuits.length, 
    supplyCircuits.length,
    params.supplyFirstCircuitOffsetMode, 
    params.returnFirstCircuitOffsetMode,
    params.supplyFirstCircuitOffsetSaved,
    params.returnFirstCircuitOffsetSaved,
    params.supplyFirstCircuitOffset,
    params.returnFirstCircuitOffset,
    setParams
  ]);

  // Вспомогательная функция для расчета длины фитинга (только для UI)
  const getFittingLengthForUI = useCallback((fittingId, fittingLength, mainPipeDiameter) => {
    // Если задана длина вручную, используем её
    if (fittingLength !== null && fittingLength !== undefined) {
      return fittingLength;
    }
    
    if (fittingId?.startsWith('Nippel_')) {
      const fittingData = fittingsData[fittingId];
      const catalogLength = fittingData?.length || 0;
      if (params.shape === 'square') return catalogLength;
      return getNippelRoundOverrideValue(fittingId, mainPipeDiameter, catalogLength);
    }
    
    // Муфты: до 1/2 включительно - вычитаем 5мм, больше 1/2 - вычитаем 10мм
    if (fittingId?.startsWith('Mufta_') || fittingId?.startsWith('Nippel_Mufta_')) {
      const fittingData = fittingsData[fittingId];
      const catalogLength = fittingData?.length || 0;
      // Определяем размер муфты
      // Больше 1/2: Mufta_1, Mufta_1_1.4, Mufta_1_1.2, Mufta_2 и т.д.
      // До 1/2 включительно: Mufta_3.4, Mufta_1.2 (это муфта 1/2) и т.д.
      const isBigMufta = (fittingId === 'Mufta_1') || 
                         (fittingId.includes('_1_1')) || // Mufta_1_1.4, Mufta_1_1.2 и т.д.
                         (fittingId === 'Mufta_2') ||
                         (fittingId.includes('_2_'));
      const subtractValue = isBigMufta ? 10 : 5; // Больше 1/2 - вычитаем 10мм, до 1/2 включительно - 5мм
      return catalogLength - subtractValue;
    }
    
    // Фланцы: проверяем диаметр
    if (fittingId?.startsWith('Flanec_')) {
      const flangeDn = extractDnFromFlangeKey(fittingId);
      const flangePipeDiameter = getPipeDiameterForDn(flangeDn);
      
      // Для квадратных профилей всегда используем дефолтную длину 100 мм
      // (нет логики "одинаковых диаметров")
      if (params.shape === 'square') {
        return 100;
      }
      
      // Для круглых труб: если диаметр фланца совпадает с диаметром основной трубы - 0 мм
      if (flangePipeDiameter && flangePipeDiameter === mainPipeDiameter) {
        return 0;
      }
      
      // Если диаметр фланца меньше диаметра основной трубы - 100 мм
      if (flangePipeDiameter && flangePipeDiameter < mainPipeDiameter) {
        return 100;
      }
      
      // В остальных случаях - 100 мм
      return 100;
    }
    
    // Накидные гайки - дефолт зависит от формы трубы
    if (fittingId?.startsWith('Nak_Gaika_')) {
      return params.shape === 'square' ? 54 : 32; // Квадратные: 54мм, круглые: 32мм
    }
    
    // Шайбы с резьбой - дефолт зависит от формы трубы
    if (fittingId?.startsWith('Shaib_Nar_Rezba_')) {
      return params.shape === 'square' ? 54 : 32; // Квадратные: 54мм, круглые: 32мм
    }
    
    return 0;
  }, [params.shape]);

  // Функция приравнивания длин коллекторов
  const equalizeLengths = () => {
    const maxLength = Math.max(supplyCalculatedLength, returnCalculatedLength);
    
    if (params.useManualLength) {
      // В ручном режиме просто устанавливаем обе длины равными максимальной
      updateParam('manualSupplyLength', Number(maxLength.toFixed(2)));
      updateParam('manualReturnLength', Number(maxLength.toFixed(2)));
    } else {
      // В автоматическом режиме корректируем отступы.
      // Вычисляем сырую длину (до Math.max 150) чтобы правильно определить нужную разницу.
      if (supplyCalculatedLength < maxLength) {
        let rawSupplyLength = effectiveSupplyFirstCircuitOffset + params.supplyLastCircuitOffset;
        supplyCircuits.forEach((circuit, index) => {
          rawSupplyLength += Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
          if (index < supplyCircuits.length - 1) {
            rawSupplyLength += Number(circuit.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
          }
        });
        const difference = maxLength - rawSupplyLength;
        updateParam('supplyLastCircuitOffset', params.supplyLastCircuitOffset + difference);
      } else if (returnCalculatedLength < maxLength) {
        let rawReturnLength = effectiveReturnFirstCircuitOffset + params.returnLastCircuitOffset;
        returnCircuits.forEach((circuit, index) => {
          rawReturnLength += Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
          if (index < returnCircuits.length - 1) {
            rawReturnLength += Number(circuit.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
          }
        });
        const difference = maxLength - rawReturnLength;
        updateParam('returnLastCircuitOffset', params.returnLastCircuitOffset + difference);
      }
    }
    
    setLengthsEqualized(true);
  };

  // Расчёт длины предкотловых коллекторов (БЕЗ прямых заглушек)
  const { preBoilerSupplyCalculatedLength, preBoilerReturnCalculatedLength } = useMemo(() => {
    if (!hasPreBoilerCollector) {
      return { preBoilerSupplyCalculatedLength: 0, preBoilerReturnCalculatedLength: 0 };
    }

    // Если включен ручной режим, используем заданные значения
    if (params.usePreBoilerManualLength) {
      return {
        preBoilerSupplyCalculatedLength: params.manualPreBoilerSupplyLength || 150,
        preBoilerReturnCalculatedLength: params.manualPreBoilerReturnLength || 150,
      };
    }

    // Рассчитываем длину для предкотлового коллектора подачи
    let supplyLength = params.preBoilerSupplyFirstCircuitOffset + params.preBoilerSupplyLastCircuitOffset;
    
    // Добавляем межосевое внутри каждого контура подачи
    preBoilerSupplyCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.preBoilerSupplyCircuitSpacing || 125;
      supplyLength += circuitSpacing; // межосевое внутри контура (между двумя патрубками)
      
      // Добавляем расстояние до следующего контура (кроме последнего)
      if (index < preBoilerSupplyCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.preBoilerSupplySpacingBetweenCircuits || 125;
        supplyLength += spacingToNext;
      }
    });
    
    // Рассчитываем длину для предкотлового коллектора обратки
    let returnLength = params.preBoilerReturnFirstCircuitOffset + params.preBoilerReturnLastCircuitOffset;
    
    // Добавляем межосевое внутри каждого контура обратки
    preBoilerReturnCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.preBoilerReturnCircuitSpacing || 125;
      returnLength += circuitSpacing; // межосевое внутри контура (между двумя патрубками)
      
      // Добавляем расстояние до следующего контура (кроме последнего)
      if (index < preBoilerReturnCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.preBoilerReturnSpacingBetweenCircuits || 125;
        returnLength += spacingToNext;
      }
    });
    
    // Применяем минимальную длину
    supplyLength = Math.max(supplyLength, 150);
    returnLength = Math.max(returnLength, 150);
    
    return {
      preBoilerSupplyCalculatedLength: supplyLength,
      preBoilerReturnCalculatedLength: returnLength,
    };
  }, [
    hasPreBoilerCollector,
    params.usePreBoilerManualLength,
    params.manualPreBoilerSupplyLength,
    params.manualPreBoilerReturnLength,
    params.preBoilerSupplyFirstCircuitOffset, 
    params.preBoilerSupplyLastCircuitOffset, 
    params.preBoilerReturnFirstCircuitOffset, 
    params.preBoilerReturnLastCircuitOffset, 
    params.preBoilerSupplySpacingBetweenCircuits, 
    params.preBoilerReturnSpacingBetweenCircuits, 
    params.preBoilerSupplyCircuitSpacing, 
    params.preBoilerReturnCircuitSpacing, 
    preBoilerSupplyCircuits, 
    preBoilerReturnCircuits
  ]);

  // Расчет общей длины комбинированной системы (для отображения в UI)
  const totalSystemLength = useMemo(() => {
    let totalLength = 0;
    
    // 1. Левые подключения гидроразделителя (только если НЕТ предкотлового коллектора)
    if (!hasPreBoilerCollector) {
      let maxLeftConnectionLength = 0;
      if (params.leftConnections && Array.isArray(params.leftConnections)) {
        params.leftConnections.forEach(conn => {
          const fittingLength = getFittingLengthForUI(conn.fittingId, conn.fittingLength, params.mainPipeDiameter);
          maxLeftConnectionLength = Math.max(maxLeftConnectionLength, fittingLength);
        });
      }
      totalLength += maxLeftConnectionLength;
    }
    
    // 2. Диаметр основной трубы гидроразделителя
    totalLength += params.mainPipeDiameter || 0;
    
    // 3. Правые подключения гидроразделителя (только если НЕТ предкотлового коллектора)
    if (!hasPreBoilerCollector) {
      let maxRightConnectionLength = 0;
      if (params.rightConnections && Array.isArray(params.rightConnections)) {
        params.rightConnections.forEach(conn => {
          const fittingLength = getFittingLengthForUI(conn.fittingId, conn.fittingLength, params.mainPipeDiameter);
          maxRightConnectionLength = Math.max(maxRightConnectionLength, fittingLength);
        });
      }
      totalLength += maxRightConnectionLength;
    }
    
    // 4. Предкотловой коллектор (если есть)
    if (hasPreBoilerCollector) {
      // Длина предкотлового коллектора (максимум из подачи и обратки)
      const maxPreBoilerLength = Math.max(preBoilerSupplyCalculatedLength, preBoilerReturnCalculatedLength);
      totalLength += maxPreBoilerLength;
      
      // Торцевые подключения предкотлового коллектора (левые - максимум из подачи и обратки)
      let preBoilerSupplyLeftLength = params.hasPreBoilerSupplyLeftConnection 
        ? (params.preBoilerSupplyLeftConnectionType === 'pipe' 
            ? (params.preBoilerSupplyLeftConnectionLength || 100)
            : getFittingLengthForUI(params.preBoilerSupplyLeftConnectionFitting, params.preBoilerSupplyLeftConnectionFittingLength, params.preBoilerSupplyPipeDiameter))
        : 0;
      
      let preBoilerReturnLeftLength = params.hasPreBoilerReturnLeftConnection
        ? (params.preBoilerReturnLeftConnectionType === 'pipe'
            ? (params.preBoilerReturnLeftConnectionLength || 100)
            : getFittingLengthForUI(params.preBoilerReturnLeftConnectionFitting, params.preBoilerReturnLeftConnectionFittingLength, params.preBoilerReturnPipeDiameter))
        : 0;
      
      // Вспомогательная функция для проверки, является ли фланец с меньшим диаметром
      const isFlangeWithSmallerDiameter = (connectionFitting, mainDiameter) => {
        if (!connectionFitting?.startsWith('Flanec_')) return false;
        const flangeDn = extractDnFromFlangeKey(connectionFitting);
        const flangePipeDiameter = getPipeDiameterForDn(flangeDn);
        return flangePipeDiameter && flangePipeDiameter < mainDiameter;
      };
      
      // Проверяем наличие фланцев с меньшим диаметром
      const hasPreBoilerSupplyLeftFlangeSmaller = params.hasPreBoilerSupplyLeftConnection && 
                                                   params.preBoilerSupplyLeftConnectionType === 'fitting' &&
                                                   isFlangeWithSmallerDiameter(params.preBoilerSupplyLeftConnectionFitting, params.preBoilerSupplyPipeDiameter);
      
      const hasPreBoilerReturnLeftFlangeSmaller = params.hasPreBoilerReturnLeftConnection && 
                                                   params.preBoilerReturnLeftConnectionType === 'fitting' &&
                                                   isFlangeWithSmallerDiameter(params.preBoilerReturnLeftConnectionFitting, params.preBoilerReturnPipeDiameter);
      
      // Добавляем левые заглушки предкотлового коллектора (только если НЕТ фланца с меньшим диаметром)
      if (params.hasPreBoilerSupplyLeftCap && !hasPreBoilerSupplyLeftFlangeSmaller && params.preBoilerSupplyLeftCapType === 'elliptical') {
        const capKey = `Zaglushka_EL_${params.preBoilerSupplyPipeDiameter}`;
        const capData = fittingsData[capKey];
        if (capData) {
          preBoilerSupplyLeftLength += capData.length;
        }
      }
      
      if (params.hasPreBoilerReturnLeftCap && !hasPreBoilerReturnLeftFlangeSmaller && params.preBoilerReturnLeftCapType === 'elliptical') {
        const capKey = `Zaglushka_EL_${params.preBoilerReturnPipeDiameter}`;
        const capData = fittingsData[capKey];
        if (capData) {
          preBoilerReturnLeftLength += capData.length;
        }
      }
      
      totalLength += Math.max(preBoilerSupplyLeftLength, preBoilerReturnLeftLength);
      
      // Торцевые подключения предкотлового коллектора (правые - максимум из подачи и обратки)
      const preBoilerSupplyRightLength = params.hasPreBoilerSupplyRightConnection
        ? (params.preBoilerSupplyRightConnectionType === 'pipe'
            ? (params.preBoilerSupplyRightConnectionLength || 100)
            : getFittingLengthForUI(params.preBoilerSupplyRightConnectionFitting, params.preBoilerSupplyRightConnectionFittingLength, params.preBoilerSupplyPipeDiameter))
        : 0;
      
      const preBoilerReturnRightLength = params.hasPreBoilerReturnRightConnection
        ? (params.preBoilerReturnRightConnectionType === 'pipe'
            ? (params.preBoilerReturnRightConnectionLength || 100)
            : getFittingLengthForUI(params.preBoilerReturnRightConnectionFitting, params.preBoilerReturnRightConnectionFittingLength, params.preBoilerReturnPipeDiameter))
        : 0;
      
      totalLength += Math.max(preBoilerSupplyRightLength, preBoilerReturnRightLength);
    }
    
    // 5. Длина трубы основного коллектора (максимум из подачи и обратки)
    const maxCollectorLength = Math.max(supplyCalculatedLength, returnCalculatedLength);
    totalLength += maxCollectorLength;
    
    // 6. Торцевые подключения основного коллектора (левые - максимум из подачи и обратки)
    const supplyLeftLength = params.hasSupplyLeftConnection 
      ? (params.supplyLeftConnectionType === 'pipe' 
          ? (params.supplyLeftConnectionLength || 100)
          : getFittingLengthForUI(params.supplyLeftConnectionFitting, params.supplyLeftConnectionFittingLength, params.supplyPipeDiameter))
      : 0;
    
    const returnLeftLength = params.hasReturnLeftConnection
      ? (params.returnLeftConnectionType === 'pipe'
          ? (params.returnLeftConnectionLength || 100)
          : getFittingLengthForUI(params.returnLeftConnectionFitting, params.returnLeftConnectionFittingLength, params.returnPipeDiameter))
      : 0;
    
    totalLength += Math.max(supplyLeftLength, returnLeftLength);
    
    // 7. Торцевые подключения основного коллектора (правые - максимум из подачи и обратки)
    let supplyRightLength = params.hasSupplyRightConnection
      ? (params.supplyRightConnectionType === 'pipe'
          ? (params.supplyRightConnectionLength || 100)
          : getFittingLengthForUI(params.supplyRightConnectionFitting, params.supplyRightConnectionFittingLength, params.supplyPipeDiameter))
      : 0;
    
    let returnRightLength = params.hasReturnRightConnection
      ? (params.returnRightConnectionType === 'pipe'
          ? (params.returnRightConnectionLength || 100)
          : getFittingLengthForUI(params.returnRightConnectionFitting, params.returnRightConnectionFittingLength, params.returnPipeDiameter))
      : 0;
    
    // Вспомогательная функция для проверки, является ли фланец с меньшим диаметром
    const isFlangeWithSmallerDiameter = (connectionFitting, mainDiameter) => {
      if (!connectionFitting?.startsWith('Flanec_')) return false;
      const flangeDn = extractDnFromFlangeKey(connectionFitting);
      const flangePipeDiameter = getPipeDiameterForDn(flangeDn);
      return flangePipeDiameter && flangePipeDiameter < mainDiameter;
    };
    
    // Проверяем наличие фланцев с меньшим диаметром
    const hasSupplyRightFlangeSmaller = params.hasSupplyRightConnection && 
                                        params.supplyRightConnectionType === 'fitting' &&
                                        isFlangeWithSmallerDiameter(params.supplyRightConnectionFitting, params.supplyPipeDiameter);
    
    const hasReturnRightFlangeSmaller = params.hasReturnRightConnection && 
                                        params.returnRightConnectionType === 'fitting' &&
                                        isFlangeWithSmallerDiameter(params.returnRightConnectionFitting, params.returnPipeDiameter);
    
    // Добавляем правые заглушки коллектора (только если НЕТ фланца с меньшим диаметром)
    if (params.hasSupplyRightCap && !hasSupplyRightFlangeSmaller && params.supplyRightCapType === 'elliptical') {
      const capKey = `Zaglushka_EL_${params.supplyPipeDiameter}`;
      const capData = fittingsData[capKey];
      if (capData) {
        supplyRightLength += capData.length;
      }
    }
    
    if (params.hasReturnRightCap && !hasReturnRightFlangeSmaller && params.returnRightCapType === 'elliptical') {
      const capKey = `Zaglushka_EL_${params.returnPipeDiameter}`;
      const capData = fittingsData[capKey];
      if (capData) {
        returnRightLength += capData.length;
      }
    }
    
    totalLength += Math.max(supplyRightLength, returnRightLength);
    
    return Math.round(totalLength);
  }, [
    params.leftConnections,
    params.mainPipeDiameter,
    params.rightConnections,
    hasPreBoilerCollector,
    preBoilerSupplyCalculatedLength,
    preBoilerReturnCalculatedLength,
    params.hasPreBoilerSupplyLeftConnection,
    params.preBoilerSupplyLeftConnectionType,
    params.preBoilerSupplyLeftConnectionLength,
    params.preBoilerSupplyLeftConnectionFitting,
    params.preBoilerSupplyLeftConnectionFittingLength,
    params.hasPreBoilerReturnLeftConnection,
    params.preBoilerReturnLeftConnectionType,
    params.preBoilerReturnLeftConnectionLength,
    params.preBoilerReturnLeftConnectionFitting,
    params.preBoilerReturnLeftConnectionFittingLength,
    params.hasPreBoilerSupplyLeftCap,
    params.preBoilerSupplyLeftCapType,
    params.preBoilerSupplyPipeDiameter,
    params.hasPreBoilerReturnLeftCap,
    params.preBoilerReturnLeftCapType,
    params.preBoilerReturnPipeDiameter,
    params.hasPreBoilerSupplyRightConnection,
    params.preBoilerSupplyRightConnectionType,
    params.preBoilerSupplyRightConnectionLength,
    params.preBoilerSupplyRightConnectionFitting,
    params.preBoilerSupplyRightConnectionFittingLength,
    params.hasPreBoilerReturnRightConnection,
    params.preBoilerReturnRightConnectionType,
    params.preBoilerReturnRightConnectionLength,
    params.preBoilerReturnRightConnectionFitting,
    params.preBoilerReturnRightConnectionFittingLength,
    supplyCalculatedLength,
    returnCalculatedLength,
    params.hasSupplyLeftConnection,
    params.supplyLeftConnectionType,
    params.supplyLeftConnectionLength,
    params.supplyLeftConnectionFitting,
    params.supplyLeftConnectionFittingLength,
    params.hasReturnLeftConnection,
    params.returnLeftConnectionType,
    params.returnLeftConnectionLength,
    params.returnLeftConnectionFitting,
    params.returnLeftConnectionFittingLength,
    params.hasSupplyRightConnection,
    params.supplyRightConnectionType,
    params.supplyRightConnectionLength,
    params.supplyRightConnectionFitting,
    params.supplyRightConnectionFittingLength,
    params.hasReturnRightConnection,
    params.returnRightConnectionType,
    params.returnRightConnectionLength,
    params.returnRightConnectionFitting,
    params.returnRightConnectionFittingLength,
    params.hasSupplyRightCap,
    params.supplyRightCapType,
    params.supplyPipeDiameter,
    params.hasReturnRightCap,
    params.returnRightCapType,
    params.returnPipeDiameter,
    getFittingLengthForUI,
  ]);

  const separatorTotalHeight = useMemo(
    () =>
      computeHydraulicSeparatorTotalHeight(
        {
          bodyHeight: hasPreBoilerCollector ? params.separatorHeight : calculatedSeparatorHeight,
          mainPipeDiameter: params.mainPipeDiameter,
          hasTopConnection: params.hasTopConnection,
          topCapFitting: params.topCapFitting,
          topCapFittingLength: params.topCapFittingLength,
          hasBottomConnection: params.hasBottomConnection,
          bottomCapFitting: params.bottomCapFitting,
          bottomCapFittingLength: params.bottomCapFittingLength,
          hasTopCap: params.hasTopCap,
          topCapType: params.topCapType,
          hasBottomCap: params.hasBottomCap,
          bottomCapType: params.bottomCapType,
        },
        (fittingId, fittingLength) =>
          getFittingLengthForUI(fittingId, fittingLength, params.mainPipeDiameter)
      ),
    [
      hasPreBoilerCollector,
      params.separatorHeight,
      calculatedSeparatorHeight,
      params.mainPipeDiameter,
      params.hasTopConnection,
      params.topCapFitting,
      params.topCapFittingLength,
      params.hasBottomConnection,
      params.bottomCapFitting,
      params.bottomCapFittingLength,
      params.hasTopCap,
      params.topCapType,
      params.hasBottomCap,
      params.bottomCapType,
      getFittingLengthForUI,
    ]
  );

  // Вычисляем отступ последнего контура для предкотлового коллектора в ручном режиме
  const { preBoilerSupplyLastCircuitOffsetCalculated, preBoilerReturnLastCircuitOffsetCalculated } = useMemo(() => {
    if (!hasPreBoilerCollector || !params.usePreBoilerManualLength) {
      return {
        preBoilerSupplyLastCircuitOffsetCalculated: params.preBoilerSupplyLastCircuitOffset,
        preBoilerReturnLastCircuitOffsetCalculated: params.preBoilerReturnLastCircuitOffset,
      };
    }
    
    // Для подачи
    let supplyRemainder = params.manualPreBoilerSupplyLength || 150;
    supplyRemainder -= params.preBoilerSupplyFirstCircuitOffset;
    
    preBoilerSupplyCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.preBoilerSupplyCircuitSpacing || 125;
      supplyRemainder -= circuitSpacing;
      
      if (index < preBoilerSupplyCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.preBoilerSupplySpacingBetweenCircuits || 125;
        supplyRemainder -= spacingToNext;
      }
    });
    
    // Для обратки
    let returnRemainder = params.manualPreBoilerReturnLength || 150;
    returnRemainder -= params.preBoilerReturnFirstCircuitOffset;
    
    preBoilerReturnCircuits.forEach((circuit, index) => {
      const circuitSpacing = Number(circuit.circuitSpacing) || params.preBoilerReturnCircuitSpacing || 125;
      returnRemainder -= circuitSpacing;
      
      if (index < preBoilerReturnCircuits.length - 1) {
        const spacingToNext = Number(circuit.spacingToNext) || params.preBoilerReturnSpacingBetweenCircuits || 125;
        returnRemainder -= spacingToNext;
      }
    });
    
    return {
      preBoilerSupplyLastCircuitOffsetCalculated: Math.max(supplyRemainder, 0),
      preBoilerReturnLastCircuitOffsetCalculated: Math.max(returnRemainder, 0),
    };
  }, [
    hasPreBoilerCollector,
    params.usePreBoilerManualLength,
    params.manualPreBoilerSupplyLength,
    params.manualPreBoilerReturnLength,
    params.preBoilerSupplyFirstCircuitOffset,
    params.preBoilerReturnFirstCircuitOffset,
    params.preBoilerSupplyLastCircuitOffset,
    params.preBoilerReturnLastCircuitOffset,
    params.preBoilerSupplySpacingBetweenCircuits,
    params.preBoilerReturnSpacingBetweenCircuits,
    params.preBoilerSupplyCircuitSpacing,
    params.preBoilerReturnCircuitSpacing,
    preBoilerSupplyCircuits,
    preBoilerReturnCircuits
  ]);

  // Функция приравнивания длин предкотловых коллекторов
  const equalizePreBoilerLengths = () => {
    const maxLength = Math.max(preBoilerSupplyCalculatedLength, preBoilerReturnCalculatedLength);
    
    if (params.usePreBoilerManualLength) {
      // В ручном режиме просто устанавливаем обе длины равными максимальной
      updateParam('manualPreBoilerSupplyLength', Number(maxLength.toFixed(2)));
      updateParam('manualPreBoilerReturnLength', Number(maxLength.toFixed(2)));
    } else {
      // В автоматическом режиме корректируем отступы.
      // Вычисляем сырую длину (до Math.max 150) чтобы правильно определить нужную разницу.
      if (preBoilerSupplyCalculatedLength < maxLength) {
        let rawSupplyLength = params.preBoilerSupplyFirstCircuitOffset + params.preBoilerSupplyLastCircuitOffset;
        preBoilerSupplyCircuits.forEach((circuit, index) => {
          rawSupplyLength += Number(circuit.circuitSpacing) || params.preBoilerSupplyCircuitSpacing || 125;
          if (index < preBoilerSupplyCircuits.length - 1) {
            rawSupplyLength += Number(circuit.spacingToNext) || params.preBoilerSupplySpacingBetweenCircuits || 125;
          }
        });
        const difference = maxLength - rawSupplyLength;
        updateParam('preBoilerSupplyLastCircuitOffset', params.preBoilerSupplyLastCircuitOffset + difference);
      } else if (preBoilerReturnCalculatedLength < maxLength) {
        let rawReturnLength = params.preBoilerReturnFirstCircuitOffset + params.preBoilerReturnLastCircuitOffset;
        preBoilerReturnCircuits.forEach((circuit, index) => {
          rawReturnLength += Number(circuit.circuitSpacing) || params.preBoilerReturnCircuitSpacing || 125;
          if (index < preBoilerReturnCircuits.length - 1) {
            rawReturnLength += Number(circuit.spacingToNext) || params.preBoilerReturnSpacingBetweenCircuits || 125;
          }
        });
        const difference = maxLength - rawReturnLength;
        updateParam('preBoilerReturnLastCircuitOffset', params.preBoilerReturnLastCircuitOffset + difference);
      }
    }
    
    setPreBoilerLengthsEqualized(true);
  };
  
  // ========================================
  // Функции для работы с контурами предкотлового коллектора
  // ========================================
  
  // Добавление контура подачи предкотлового
  const addPreBoilerSupplyCircuit = () => {
    setPreBoilerLengthsEqualized(false);
    setPreBoilerSupplyCircuits(prev => [
      ...prev,
      {
        id: `preboiler-supply-${Date.now()}`,
        fitting: 'Nippel_1',
        fittingId: 'Nippel_1',
        fittingCategory: 'Ниппели',
        type: 'fitting',
        fittingLength: null,
        flangePipeLength: null,
        spacingToNext: null,
        circuitSpacing: params.preBoilerSupplyCircuitSpacing || 125,
        swapOrder: false,
      },
    ]);
  };

  // Добавление контура обратки предкотлового
  const addPreBoilerReturnCircuit = () => {
    setPreBoilerLengthsEqualized(false);
    setPreBoilerReturnCircuits(prev => [
      ...prev,
      {
        id: `preboiler-return-${Date.now()}`,
        fitting: 'Nippel_1',
        fittingId: 'Nippel_1',
        fittingCategory: 'Ниппели',
        type: 'fitting',
        fittingLength: null,
        flangePipeLength: null,
        spacingToNext: null,
        circuitSpacing: params.preBoilerReturnCircuitSpacing || 125,
        swapOrder: false,
      },
    ]);
  };

  // Удаление контура подачи предкотлового
  // eslint-disable-next-line no-unused-vars
  const deletePreBoilerSupplyCircuit = (index) => {
    setPreBoilerLengthsEqualized(false);
    setPreBoilerSupplyCircuits(prev => prev.filter((_, i) => i !== index));
  };

  // Удаление контура обратки предкотлового
  // eslint-disable-next-line no-unused-vars
  const deletePreBoilerReturnCircuit = (index) => {
    setPreBoilerLengthsEqualized(false);
    setPreBoilerReturnCircuits(prev => prev.filter((_, i) => i !== index));
  };

  // Обновление контура подачи предкотлового
  // eslint-disable-next-line no-unused-vars
  const updatePreBoilerSupplyCircuit = (index, keyOrObj, value) => {
    setPreBoilerLengthsEqualized(false);
    setPreBoilerSupplyCircuits(prev => prev.map((circuit, i) => {
      if (i !== index) return circuit;
      if (typeof keyOrObj === 'object') {
        return { ...circuit, ...keyOrObj };
      }
      return { ...circuit, [keyOrObj]: value };
    }));
  };

  // Обновление контура обратки предкотлового
  // eslint-disable-next-line no-unused-vars
  const updatePreBoilerReturnCircuit = (index, keyOrObj, value) => {
    setPreBoilerLengthsEqualized(false);
    setPreBoilerReturnCircuits(prev => prev.map((circuit, i) => {
      if (i !== index) return circuit;
      if (typeof keyOrObj === 'object') {
        return { ...circuit, ...keyOrObj };
      }
      return { ...circuit, [keyOrObj]: value };
    }));
  };

  // Перемещение контура подачи предкотлового
  // eslint-disable-next-line no-unused-vars
  const movePreBoilerSupplyCircuit = (fromIndex, toIndex) => {
    setPreBoilerLengthsEqualized(false);
    setPreBoilerSupplyCircuits(prev => {
      const newCircuits = [...prev];
      const [movedItem] = newCircuits.splice(fromIndex, 1);
      newCircuits.splice(toIndex, 0, movedItem);
      return newCircuits;
    });
  };

  // Перемещение контура обратки предкотлового
  // eslint-disable-next-line no-unused-vars
  const movePreBoilerReturnCircuit = (fromIndex, toIndex) => {
    setPreBoilerLengthsEqualized(false);
    setPreBoilerReturnCircuits(prev => {
      const newCircuits = [...prev];
      const [movedItem] = newCircuits.splice(fromIndex, 1);
      newCircuits.splice(toIndex, 0, movedItem);
      return newCircuits;
    });
  };
  
  // Генерация с приравниванием длин
  const handleGenerateWithEqualize = () => {
    setEqualizeDialog(false);
    
    // Приравниваем выбранные коллекторы
    if (equalizeMainCollector) {
      equalizeLengths();
    }
    if (equalizePreBoilerCollector) {
      equalizePreBoilerLengths();
    }
    
    // Генерируем после небольшой задержки, чтобы состояние успело обновиться
    setTimeout(() => {
      // Генерируем напрямую, устанавливая флаги чтобы не показывать диалог снова
      setLengthsEqualized(true);
      setPreBoilerLengthsEqualized(true);
      
      const effectiveParams = {
        ...params,
        // Используем вычисленную высоту гидроразделителя (при предкотловом — ручное значение)
        separatorHeight: hasPreBoilerCollector ? params.separatorHeight : calculatedSeparatorHeight,
        equalizeLengths: equalizeMainCollector, // Приравнивать только если выбрано
        preBoilerEqualizeLengths: equalizePreBoilerCollector, // Приравнивать только если выбрано
        // Обычный коллектор
        ...(params.useManualLength ? {
          supplyLastCircuitOffset: supplyLastCircuitOffsetCalculated,
          returnLastCircuitOffset: returnLastCircuitOffsetCalculated,
        } : {}),
        // Предкотловой коллектор
        ...(params.usePreBoilerManualLength ? {
          preBoilerSupplyLastCircuitOffset: preBoilerSupplyLastCircuitOffsetCalculated,
          preBoilerReturnLastCircuitOffset: preBoilerReturnLastCircuitOffsetCalculated,
        } : {}),
      };
      
      // Передаём параметры и state в функцию генерации
      const result = generateCollectorWithSeparator({
        ...effectiveParams,
        supplyCircuits,
        returnCircuits,
        supplyCenterFittings,
        returnCenterFittings,
        supplyAdditionalOutlets,
        returnAdditionalOutlets,
        preBoilerSupplyCircuits,
        preBoilerReturnCircuits,
        preBoilerSupplyCenterFittings,
        preBoilerReturnCenterFittings,
        preBoilerSupplyAdditionalOutlets,
        preBoilerReturnAdditionalOutlets,
        hasPreBoilerCollector,
        isSystemFlipped,
      }, effectiveSupplyFirstCircuitOffset, effectiveReturnFirstCircuitOffset);
      
      const drawingData = {
        cylinders: result.cylinders || [],
        fittings: result.fittings || [],
        dimensionLines: result.dimensionLines || [],
        welds: result.welds || [],
        annotations: [],
        balloons: result.balloons || [],
        lines: [],
        objectConstraints: [],
        settings: {
          isLandscape: true,
          zoom: 0.7,
          textZoom: 1,
          positionTextZoom: 1,
        },
        metadata: {
          ...result.metadata,  // Сохраняем metadata из result (supplyCircuitParams, returnCircuitParams и т.д.)
          totalLength: totalSystemLength,
          separatorTotalHeight,
        },
      };
      
      if (onGenerate) {
        onGenerate(drawingData, {
          params,
          supplyCircuits,
          returnCircuits,
          supplyCenterFittings,
          returnCenterFittings,
          supplyAdditionalOutlets,
          returnAdditionalOutlets,
          preBoilerSupplyCircuits,
          preBoilerReturnCircuits,
          preBoilerSupplyCenterFittings,
          preBoilerReturnCenterFittings,
          preBoilerSupplyAdditionalOutlets,
          preBoilerReturnAdditionalOutlets,
          hasPreBoilerCollector,
          isSystemFlipped,
          tabValue,
          collectorTabValue,
          preBoilerTabValue,
        });
      }
    }, 100);
  };
  
  // Генерация без приравнивания длин
  const handleGenerateWithoutEqualize = () => {
    setEqualizeDialog(false);
    setLengthsEqualized(true); // Устанавливаем флаг, чтобы не показывать диалог снова для основного
    setPreBoilerLengthsEqualized(true); // Устанавливаем флаг, чтобы не показывать диалог снова для предкотлового
    
    // НЕ вызываем equalizeLengths() и equalizePreBoilerLengths() - генерируем напрямую
    const effectiveParams = {
      ...params,
      // Используем вычисленную высоту гидроразделителя (при предкотловом — ручное значение)
      separatorHeight: hasPreBoilerCollector ? params.separatorHeight : calculatedSeparatorHeight,
      equalizeLengths: false, // Не приравнивать длины
      preBoilerEqualizeLengths: false, // Не приравнивать длины предкотлового
      // Обычный коллектор
      ...(params.useManualLength ? {
        supplyLastCircuitOffset: supplyLastCircuitOffsetCalculated,
        returnLastCircuitOffset: returnLastCircuitOffsetCalculated,
      } : {}),
      // Предкотловой коллектор
      ...(params.usePreBoilerManualLength ? {
        preBoilerSupplyLastCircuitOffset: preBoilerSupplyLastCircuitOffsetCalculated,
        preBoilerReturnLastCircuitOffset: preBoilerReturnLastCircuitOffsetCalculated,
      } : {}),
    };
    
    // Передаём параметры и state в функцию генерации
    const result = generateCollectorWithSeparator({
      ...effectiveParams,
      supplyCircuits,
      returnCircuits,
      supplyCenterFittings,
      returnCenterFittings,
      supplyAdditionalOutlets,
      returnAdditionalOutlets,
      preBoilerSupplyCircuits,
      preBoilerReturnCircuits,
      preBoilerSupplyCenterFittings,
      preBoilerReturnCenterFittings,
      preBoilerSupplyAdditionalOutlets,
      preBoilerReturnAdditionalOutlets,
      hasPreBoilerCollector,
      isSystemFlipped,
    }, effectiveSupplyFirstCircuitOffset, effectiveReturnFirstCircuitOffset);
    
    const drawingData = {
      cylinders: result.cylinders || [],
      fittings: result.fittings || [],
      dimensionLines: result.dimensionLines || [],
      welds: result.welds || [],
      annotations: [],
      balloons: result.balloons || [],
      lines: [],
      objectConstraints: [],
      settings: {
        isLandscape: true,
        zoom: 0.7,
        textZoom: 1,
        positionTextZoom: 1,
      },
      metadata: {
        ...result.metadata,
        totalLength: totalSystemLength,
        separatorTotalHeight,
      },
    };
    
    if (onGenerate) {
      onGenerate(drawingData, {
        params,
        supplyCircuits,
        returnCircuits,
        supplyCenterFittings,
        returnCenterFittings,
        supplyAdditionalOutlets,
        returnAdditionalOutlets,
        preBoilerSupplyCircuits,
        preBoilerReturnCircuits,
        preBoilerSupplyCenterFittings,
        preBoilerReturnCenterFittings,
        preBoilerSupplyAdditionalOutlets,
        preBoilerReturnAdditionalOutlets,
        hasPreBoilerCollector,
        isSystemFlipped,
        tabValue,
        collectorTabValue,
        preBoilerTabValue,
      });
    }
  };
  
  // Обновление контура подачи
  const updateSupplyCircuit = (index, keyOrObj, value) => {
    setLengthsEqualized(false);
    setSupplyCircuits(prev => {
      const newCircuits = [...prev];
      if (typeof keyOrObj === 'object') {
        newCircuits[index] = { ...newCircuits[index], ...keyOrObj };
      } else {
        newCircuits[index] = { ...newCircuits[index], [keyOrObj]: value };
      }
      return newCircuits;
    });
  };

  // Обновление контура обратки
  const updateReturnCircuit = (index, keyOrObj, value) => {
    setLengthsEqualized(false);
    setReturnCircuits(prev => {
      const newCircuits = [...prev];
      if (typeof keyOrObj === 'object') {
        newCircuits[index] = { ...newCircuits[index], ...keyOrObj };
      } else {
        newCircuits[index] = { ...newCircuits[index], [keyOrObj]: value };
      }
      return newCircuits;
    });
  };

  // Подбирает фитинг для нового контура: если последний контур имеет совместимый фитинг — берём его, иначе максимально совместимый
  const getDefaultFittingForCircuit = (existingCircuits, diameter) => {
    const lastFitting = existingCircuits.length > 0
      ? (existingCircuits[existingCircuits.length - 1].fittingId || existingCircuits[existingCircuits.length - 1].fitting)
      : null;
    if (lastFitting && isCircuitFittingCompatible(lastFitting, diameter)) return lastFitting;
    const fittings = getFittingsByCategory('Ниппели', diameter, isCircuitFittingCompatible);
    const compatible = fittings.filter(f => f.isCompatible);
    if (compatible.length > 0) return compatible[compatible.length - 1].id;
    return 'Nippel_1';
  };

  // Добавление контура подачи
  const addSupplyCircuit = () => {
    setLengthsEqualized(false);
    setSupplyCircuits(prev => {
    const defaultFitting = getDefaultFittingForCircuit(prev, params.supplyPipeDiameter);
    return [
      ...prev,
      {
        id: `supply-${Date.now()}`,
        fitting: defaultFitting,
        fittingLength: null,
        flangePipeLength: null,
        spacingToNext: null,
        circuitSpacing: params.supplyCircuitSpacing || 50,
        swapOrder: false,
      },
    ];
    });
  };

  // Добавление контура обратки
  const addReturnCircuit = () => {
    setLengthsEqualized(false);
    setReturnCircuits(prev => {
      const defaultFitting = getDefaultFittingForCircuit(prev, params.returnPipeDiameter);
      return [
        ...prev,
        {
          id: `return-${Date.now()}`,
          fitting: defaultFitting,
          fittingLength: null,
          flangePipeLength: null,
          spacingToNext: null,
          circuitSpacing: params.returnCircuitSpacing || 50,
          swapOrder: false,
        },
      ];
    });
  };

  // Удаление контура подачи
  const deleteSupplyCircuit = (index) => {
    setLengthsEqualized(false);
    setSupplyCircuits(prev => prev.filter((_, i) => i !== index));
  };

  // Удаление контура обратки
  const deleteReturnCircuit = (index) => {
    setLengthsEqualized(false);
    setReturnCircuits(prev => prev.filter((_, i) => i !== index));
  };

  // Перемещение контура подачи
  const moveSupplyCircuit = (fromIndex, toIndex) => {
    setLengthsEqualized(false);
    setSupplyCircuits(prev => {
      const newCircuits = [...prev];
      const [movedItem] = newCircuits.splice(fromIndex, 1);
      newCircuits.splice(toIndex, 0, movedItem);
      return newCircuits;
    });
  };

  // Перемещение контура обратки
  const moveReturnCircuit = (fromIndex, toIndex) => {
    setLengthsEqualized(false);
    setReturnCircuits(prev => {
      const newCircuits = [...prev];
      const [movedItem] = newCircuits.splice(fromIndex, 1);
      newCircuits.splice(toIndex, 0, movedItem);
      return newCircuits;
    });
  };

  // === Функции для центральных фитингов подающего коллектора ===
  
  const updateSupplyCenterFittingCount = (delta) => {
    const newCount = Math.max(0, params.supplyCenterFittingCount + delta);
    setParams((prev) => ({ ...prev, supplyCenterFittingCount: newCount }));
    
    if (delta > 0) {
      setSupplyCenterFittings((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          fittingCategory: 'Муфты',
          fittingId: 'Mufta_1.2',
          positionType: 'center',
          side: 'both',
          distanceFromStart: 50,
          distanceDirection: 'left',
          betweenCircuits: '1-2',
          referenceRow: 'supply', // 'supply' или 'return' - для распределительного коллектора
        })),
      ]);
    } else {
      setSupplyCenterFittings((prev) => prev.slice(0, newCount));
    }
  };
  
  const updateSupplyCenterFitting = (index, keyOrObj, value) => {
    setSupplyCenterFittings((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        if (typeof keyOrObj === 'object') {
          return { ...f, ...keyOrObj };
        }
        return { ...f, [keyOrObj]: typeof value === 'string' ? value : Number(value) };
      })
    );
  };
  
  const deleteSupplyCenterFitting = (index) => {
    setSupplyCenterFittings((prev) => {
      const newFittings = prev.filter((_, i) => i !== index);
      return newFittings.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, supplyCenterFittingCount: Math.max(0, prev.supplyCenterFittingCount - 1) }));
  };

  // === Функции для центральных фитингов обратного коллектора ===
  
  const updateReturnCenterFittingCount = (delta) => {
    const newCount = Math.max(0, params.returnCenterFittingCount + delta);
    setParams((prev) => ({ ...prev, returnCenterFittingCount: newCount }));
    
    if (delta > 0) {
      setReturnCenterFittings((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          fittingCategory: 'Муфты',
          fittingId: 'Mufta_1.2',
          positionType: 'center',
          side: 'both',
          distanceFromStart: 50,
          distanceDirection: 'left',
          betweenCircuits: '1-2',
          referenceRow: 'return', // 'supply' или 'return' - для распределительного коллектора
        })),
      ]);
    } else {
      setReturnCenterFittings((prev) => prev.slice(0, newCount));
    }
  };
  
  const updateReturnCenterFitting = (index, keyOrObj, value) => {
    setReturnCenterFittings((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        if (typeof keyOrObj === 'object') {
          return { ...f, ...keyOrObj };
        }
        return { ...f, [keyOrObj]: typeof value === 'string' ? value : Number(value) };
      })
    );
  };
  
  const deleteReturnCenterFitting = (index) => {
    setReturnCenterFittings((prev) => {
      const newFittings = prev.filter((_, i) => i !== index);
      return newFittings.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, returnCenterFittingCount: Math.max(0, prev.returnCenterFittingCount - 1) }));
  };

  // === Функции для дополнительных выходов ===
  
  const updateSupplyAdditionalOutletCount = (delta) => {
    const newCount = Math.max(0, params.supplyAdditionalOutletCount + delta);
    setParams((prev) => ({ ...prev, supplyAdditionalOutletCount: newCount }));
    
    if (delta > 0) {
      setSupplyAdditionalOutlets((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          type: 'fitting',
          diameter: 26.9,
          length: 80,
          fittingCategory: 'Ниппели',
          fittingId: 'Nippel_3.4',
          fittingLength: null,
          flangePipeLength: null,
          direction: 'up', // направление по умолчанию вверх для подающего коллектора
          positionType: 'center', // по умолчанию в центре
          distanceFromEdge: undefined, // не задано для центра
          distanceDirection: 'left',
          betweenIndex: 0,
          referenceRow: 'supply', // 'supply' или 'return' - для распределительного коллектора
          outletType: 'short', // тип выхода: 'short' или 'through'
        })),
      ]);
    } else {
      setSupplyAdditionalOutlets((prev) => prev.slice(0, newCount));
    }
  };
  
  const updateSupplyAdditionalOutlet = (index, key, value) => {
    setSupplyAdditionalOutlets(prev => {
      const newOutlets = [...prev];
      newOutlets[index] = { ...newOutlets[index], [key]: value };
      
      // Если изменился positionType на 'distance' и distanceFromEdge не установлен, устанавливаем значение по умолчанию
      if (key === 'positionType' && value === 'distance' && newOutlets[index].distanceFromEdge == null) {
        newOutlets[index].distanceFromEdge = 60;
      }
      // Если изменился positionType на 'center', сбрасываем distanceFromEdge
      if (key === 'positionType' && value === 'center') {
        newOutlets[index].distanceFromEdge = undefined;
      }
      
      return newOutlets;
    });
  };
  
  const deleteSupplyAdditionalOutlet = (index) => {
    setSupplyAdditionalOutlets((prev) => {
      const newOutlets = prev.filter((_, i) => i !== index);
      return newOutlets.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, supplyAdditionalOutletCount: Math.max(0, prev.supplyAdditionalOutletCount - 1) }));
  };
  
  const updateReturnAdditionalOutletCount = (delta) => {
    const newCount = Math.max(0, params.returnAdditionalOutletCount + delta);
    setParams((prev) => ({ ...prev, returnAdditionalOutletCount: newCount }));
    
    if (delta > 0) {
      setReturnAdditionalOutlets((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          type: 'fitting',
          diameter: 26.9,
          length: 80,
          fittingCategory: 'Ниппели',
          fittingId: 'Nippel_3.4',
          fittingLength: null,
          flangePipeLength: null,
          direction: 'down', // направление по умолчанию вниз для обратного коллектора
          positionType: 'center', // по умолчанию в центре
          distanceFromEdge: undefined, // не задано для центра
          distanceDirection: 'left',
          betweenIndex: 0,
          referenceRow: 'return', // 'supply' или 'return' - для распределительного коллектора
          outletType: 'short', // тип выхода: 'short' или 'through'
        })),
      ]);
    } else {
      setReturnAdditionalOutlets((prev) => prev.slice(0, newCount));
    }
  };
  
  const updateReturnAdditionalOutlet = (index, key, value) => {
    setReturnAdditionalOutlets(prev => {
      const newOutlets = [...prev];
      newOutlets[index] = { ...newOutlets[index], [key]: value };
      
      // Если изменился positionType на 'distance' и distanceFromEdge не установлен, устанавливаем значение по умолчанию
      if (key === 'positionType' && value === 'distance' && newOutlets[index].distanceFromEdge == null) {
        newOutlets[index].distanceFromEdge = 60;
      }
      // Если изменился positionType на 'center', сбрасываем distanceFromEdge
      if (key === 'positionType' && value === 'center') {
        newOutlets[index].distanceFromEdge = undefined;
      }
      
      return newOutlets;
    });
  };
  
  const deleteReturnAdditionalOutlet = (index) => {
    setReturnAdditionalOutlets((prev) => {
      const newOutlets = prev.filter((_, i) => i !== index);
      return newOutlets.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, returnAdditionalOutletCount: Math.max(0, prev.returnAdditionalOutletCount - 1) }));
  };

  // ========================================
  // Функции для предкотлового коллектора
  // ========================================

  // Центральные фитинги подачи предкотлового
  const updatePreBoilerSupplyCenterFittingCount = (delta) => {
    const newCount = Math.max(0, params.preBoilerSupplyCenterFittingCount + delta);
    setParams((prev) => ({ ...prev, preBoilerSupplyCenterFittingCount: newCount }));
    
    if (delta > 0) {
      setPreBoilerSupplyCenterFittings((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          fittingCategory: 'Муфты',
          fittingId: 'Mufta_1.2',
          positionType: 'center',
          side: 'both',
          distanceFromStart: 50,
          distanceDirection: 'left',
          betweenCircuits: '1-2',
          referenceRow: 'supply', // 'supply' или 'return' - для распределительного коллектора
        })),
      ]);
    } else {
      setPreBoilerSupplyCenterFittings((prev) => prev.slice(0, newCount));
    }
  };

  const updatePreBoilerSupplyCenterFitting = (index, keyOrObj, value) => {
    setPreBoilerSupplyCenterFittings((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        if (typeof keyOrObj === 'object') {
          return { ...f, ...keyOrObj };
        }
        return { ...f, [keyOrObj]: typeof value === 'string' ? value : Number(value) };
      })
    );
  };

  const deletePreBoilerSupplyCenterFitting = (index) => {
    setPreBoilerSupplyCenterFittings((prev) => {
      const newFittings = prev.filter((_, i) => i !== index);
      return newFittings.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, preBoilerSupplyCenterFittingCount: Math.max(0, prev.preBoilerSupplyCenterFittingCount - 1) }));
  };

  // Центральные фитинги обратки предкотлового
  const updatePreBoilerReturnCenterFittingCount = (delta) => {
    const newCount = Math.max(0, params.preBoilerReturnCenterFittingCount + delta);
    setParams((prev) => ({ ...prev, preBoilerReturnCenterFittingCount: newCount }));
    
    if (delta > 0) {
      setPreBoilerReturnCenterFittings((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          fittingCategory: 'Муфты',
          fittingId: 'Mufta_1.2',
          positionType: 'center',
          side: 'both',
          distanceFromStart: 50,
          distanceDirection: 'left',
          betweenCircuits: '1-2',
          referenceRow: 'return', // 'supply' или 'return' - для распределительного коллектора
        })),
      ]);
    } else {
      setPreBoilerReturnCenterFittings((prev) => prev.slice(0, newCount));
    }
  };

  const updatePreBoilerReturnCenterFitting = (index, keyOrObj, value) => {
    setPreBoilerReturnCenterFittings((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        if (typeof keyOrObj === 'object') {
          return { ...f, ...keyOrObj };
        }
        return { ...f, [keyOrObj]: typeof value === 'string' ? value : Number(value) };
      })
    );
  };

  const deletePreBoilerReturnCenterFitting = (index) => {
    setPreBoilerReturnCenterFittings((prev) => {
      const newFittings = prev.filter((_, i) => i !== index);
      return newFittings.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, preBoilerReturnCenterFittingCount: Math.max(0, prev.preBoilerReturnCenterFittingCount - 1) }));
  };

  // Дополнительные выходы подачи предкотлового
  const updatePreBoilerSupplyAdditionalOutletCount = (delta) => {
    const newCount = Math.max(0, params.preBoilerSupplyAdditionalOutletCount + delta);
    setParams((prev) => ({ ...prev, preBoilerSupplyAdditionalOutletCount: newCount }));
    
    if (delta > 0) {
      setPreBoilerSupplyAdditionalOutlets((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          type: 'fitting',
          diameter: 26.9,
          length: 80,
          fittingCategory: 'Ниппели',
          fittingId: 'Nippel_3.4',
          fittingLength: null,
          flangePipeLength: null,
          direction: 'up', // направление по умолчанию вверх для подающего коллектора
          positionType: 'center', // по умолчанию в центре
          distanceFromEdge: undefined, // не задано для центра
          distanceDirection: 'left',
          betweenIndex: 0,
          referenceRow: 'supply', // 'supply' или 'return' - для распределительного коллектора
          outletType: 'short', // тип выхода: 'short' или 'through'
        })),
      ]);
    } else {
      setPreBoilerSupplyAdditionalOutlets((prev) => prev.slice(0, newCount));
    }
  };

  const updatePreBoilerSupplyAdditionalOutlet = (index, key, value) => {
    setPreBoilerSupplyAdditionalOutlets(prev => {
      const newOutlets = [...prev];
      newOutlets[index] = { ...newOutlets[index], [key]: value };
      
      // Если изменился positionType на 'distance' и distanceFromEdge не установлен, устанавливаем значение по умолчанию
      if (key === 'positionType' && value === 'distance' && newOutlets[index].distanceFromEdge == null) {
        newOutlets[index].distanceFromEdge = 60;
      }
      // Если изменился positionType на 'center', сбрасываем distanceFromEdge
      if (key === 'positionType' && value === 'center') {
        newOutlets[index].distanceFromEdge = undefined;
      }
      
      return newOutlets;
    });
  };

  const deletePreBoilerSupplyAdditionalOutlet = (index) => {
    setPreBoilerSupplyAdditionalOutlets((prev) => {
      const newOutlets = prev.filter((_, i) => i !== index);
      return newOutlets.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, preBoilerSupplyAdditionalOutletCount: Math.max(0, prev.preBoilerSupplyAdditionalOutletCount - 1) }));
  };

  // Дополнительные выходы обратки предкотлового
  const updatePreBoilerReturnAdditionalOutletCount = (delta) => {
    const newCount = Math.max(0, params.preBoilerReturnAdditionalOutletCount + delta);
    setParams((prev) => ({ ...prev, preBoilerReturnAdditionalOutletCount: newCount }));
    
    if (delta > 0) {
      setPreBoilerReturnAdditionalOutlets((prev) => [
        ...prev,
        ...Array(delta).fill(null).map((_, i) => ({
          id: prev.length + i + 1,
          type: 'fitting',
          diameter: 26.9,
          length: 80,
          fittingCategory: 'Ниппели',
          fittingId: 'Nippel_3.4',
          fittingLength: null,
          flangePipeLength: null,
          direction: 'down', // направление по умолчанию вниз для обратного коллектора
          positionType: 'center', // по умолчанию в центре
          distanceFromEdge: undefined, // не задано для центра
          distanceDirection: 'left',
          betweenIndex: 0,
          referenceRow: 'return', // 'supply' или 'return' - для распределительного коллектора
          outletType: 'short', // тип выхода: 'short' или 'through'
        })),
      ]);
    } else {
      setPreBoilerReturnAdditionalOutlets((prev) => prev.slice(0, newCount));
    }
  };

  const updatePreBoilerReturnAdditionalOutlet = (index, key, value) => {
    setPreBoilerReturnAdditionalOutlets(prev => {
      const newOutlets = [...prev];
      newOutlets[index] = { ...newOutlets[index], [key]: value };
      
      // Если изменился positionType на 'distance' и distanceFromEdge не установлен, устанавливаем значение по умолчанию
      if (key === 'positionType' && value === 'distance' && newOutlets[index].distanceFromEdge == null) {
        newOutlets[index].distanceFromEdge = 60;
      }
      // Если изменился positionType на 'center', сбрасываем distanceFromEdge
      if (key === 'positionType' && value === 'center') {
        newOutlets[index].distanceFromEdge = undefined;
      }
      
      return newOutlets;
    });
  };

  const deletePreBoilerReturnAdditionalOutlet = (index) => {
    setPreBoilerReturnAdditionalOutlets((prev) => {
      const newOutlets = prev.filter((_, i) => i !== index);
      return newOutlets.map((f, i) => ({ ...f, id: i + 1 }));
    });
    setParams((prev) => ({ ...prev, preBoilerReturnAdditionalOutletCount: Math.max(0, prev.preBoilerReturnAdditionalOutletCount - 1) }));
  };


  // ========================================
  // Старые функции для контуров (для совместимости)
  // ========================================

  // eslint-disable-next-line no-unused-vars
  const collectorCircuitFittings = useMemo(() => {
    const allFittings = [];
    CIRCUIT_FITTING_CATEGORIES.forEach(cat => {
      allFittings.push(...getFittingsByCategory(cat.id, params.collectorDiameter, isCircuitFittingCompatible));
    });
    return allFittings.filter(f => f.isCompatible);
  }, [params.collectorDiameter]);

  // eslint-disable-next-line no-unused-vars
  const collectorEndFittings = useMemo(() => {
    const allFittings = [];
    CIRCUIT_FITTING_CATEGORIES.forEach(cat => {
      allFittings.push(...getFittingsByCategory(cat.id, params.collectorDiameter, isEndFittingCompatibleWithMainPipe));
    });
    return allFittings.filter(f => f.isCompatible);
  }, [params.collectorDiameter]);

  // Совмещённое превью гидроразделителя и коллектора
  const combinedSchematicPreview = useMemo(() => {
    const padding = 60;
    const scale = 0.85;

    // Вычисляем необходимую длину для обычного коллектора
    const maxCollectorLength = Math.max(supplyCalculatedLength, returnCalculatedLength);
    const collectorLength = maxCollectorLength * scale;
    
    // Вычисляем необходимую длину для предкотлового коллектора
    const maxPreBoilerLength = hasPreBoilerCollector 
      ? Math.max(preBoilerSupplyCalculatedLength, preBoilerReturnCalculatedLength) 
      : 0;
    const preBoilerLength = maxPreBoilerLength * scale;
    
    // Размеры для схемы разделителя: ширина фиксированная, высота динамическая
    const compactScale = 0.25; // Уменьшаем в 4 раза
    const mainPipeHeight = Math.min(125, calculatedSeparatorHeight * scale * compactScale); // Динамическая высота
    
    // Вычисляем расстояние между коллекторами с учетом режима
    let collectorSpacingRaw;
    if (params.pipeSpacingMode === 'offset') {
      // В режиме отступов вычисляем фактическое межосевое расстояние
      // (от центра подачи до центра обратки)
      // Для отображения серых чисел межосевых используем формулу: высота - отступы
      collectorSpacingRaw = params.separatorHeight - params.supplyPipeOffset - params.returnPipeOffset;
    } else {
      // В режиме межосевого используем заданное значение напрямую
      collectorSpacingRaw = params.pipeSpacing;
    }
    const collectorSpacing = Math.min(125, collectorSpacingRaw * scale * compactScale); // Динамическое расстояние
    
    // Аналогично для предкотлового коллектора
    let preBoilerCollectorSpacingRaw;
    if (params.preBoilerPipeSpacingMode === 'offset') {
      // Для отображения серых чисел межосевых используем формулу: высота - отступы
      preBoilerCollectorSpacingRaw = params.separatorHeight - params.preBoilerSupplyPipeOffset - params.preBoilerReturnPipeOffset;
    } else {
      // В режиме межосевого используем заданное значение напрямую
      preBoilerCollectorSpacingRaw = params.preBoilerPipeSpacing;
    }
    const preBoilerCollectorSpacing = Math.min(125, preBoilerCollectorSpacingRaw * scale * compactScale); // Динамическое расстояние
    
    // Динамически вычисляем размер viewBox в зависимости от содержимого
    const separatorWidth = 55; // Ширина для разделителя с подключениями (уменьшена в 4 раза)
    const compactPadding = padding * compactScale;
    const preBoilerLengthCompact = hasPreBoilerCollector ? preBoilerLength * compactScale : 0;
    
    // При отражении без предкотлового коллектор рисуется слева (как предкотловой)
    const isFlippedWithoutPreBoiler = isSystemFlipped && !hasPreBoilerCollector;
    const leftCollectorLength = isFlippedWithoutPreBoiler ? collectorLength * compactScale : preBoilerLengthCompact;
    const rightCollectorLength = isFlippedWithoutPreBoiler ? 0 : collectorLength * compactScale;
    
    const totalWidth = (leftCollectorLength > 0 ? leftCollectorLength + 5 : 0) + separatorWidth + rightCollectorLength + compactPadding * 2 + 25;
    const totalHeight = Math.max(175, Math.max(mainPipeHeight, collectorSpacing) + compactPadding * 2 + 40);
    
    const svgWidth = totalWidth;
    const svgHeight = totalHeight;

    // Гидроразделитель (в центре) - компактные координаты
    const separatorCenterX = (leftCollectorLength > 0 ? leftCollectorLength + 5 : 0) + 55;
    const mainPipeWidth = 12; // Фиксированное значение
    const mainY = compactPadding + 20; // Уменьшен верхний отступ для центрирования
    const mainEndY = mainY + mainPipeHeight;

    // Позиции левых подключений
    // Вычисляем реальные отступы для левых подключений (от верха разделителя)
    const leftConnectionsActualOffsets = params.leftConnections.map((conn, index) => {
      if (params.leftConnectionsPlacementMode === 'spacing') {
        // Режим межосевого: вычисляем от первого отступа + накопленные межосевые
        let currentOffset = params.leftConnectionsFirstOffset || 50;
        for (let i = 0; i < index; i++) {
          const spacing = Number(params.leftConnections[i].spacingToNext) || params.leftConnectionsSpacing || 100;
          currentOffset += spacing;
        }
        return currentOffset;
      } else {
        // Режим отступов
        return conn.offsetDirection === 'bottom'
          ? calculatedSeparatorHeight - conn.offsetFromTop
          : conn.offsetFromTop;
      }
    });

    const leftConnectionsPositions = leftConnectionsActualOffsets.map((actualOffset) => {
      const relativePos = actualOffset / calculatedSeparatorHeight;
      return mainY + mainPipeHeight * relativePos;
    });

    // Позиционируем коллектор по центру разделителя (не по центру SVG!)
    const separatorCenterY = mainY + mainPipeHeight / 2;
    
    // Позиции Y для обычного коллектора с учетом режима
    let collectorSupplyY, collectorReturnY;
    let actualCollectorSpacing; // Фактическое межосевое для длинных патрубков
    if (params.pipeSpacingMode === 'offset') {
      // Режим отступов
      const offsetScale = scale * compactScale;
      collectorSupplyY = mainY + params.supplyPipeOffset * offsetScale;
      collectorReturnY = mainEndY - params.returnPipeOffset * offsetScale;
      // Вычисляем фактическое межосевое (от центра до центра)
      actualCollectorSpacing = collectorReturnY - collectorSupplyY;
    } else {
      // Режим межосевого
      collectorSupplyY = separatorCenterY - collectorSpacing / 2;
      collectorReturnY = separatorCenterY + collectorSpacing / 2;
      actualCollectorSpacing = collectorSpacing;
    }

    // Предкотловой коллектор (слева от разделителя)
    const preBoilerEndX = hasPreBoilerCollector ? separatorCenterX - mainPipeWidth / 2 - 5 : 0;
    const preBoilerStartX = hasPreBoilerCollector ? preBoilerEndX - preBoilerLengthCompact : 0;
    
    // Позиции Y для предкотлового коллектора с учетом режима
    let preBoilerSupplyY, preBoilerReturnY;
    let actualPreBoilerCollectorSpacing; // Фактическое межосевое для длинных патрубков
    if (hasPreBoilerCollector) {
      if (params.preBoilerPipeSpacingMode === 'offset') {
        // Режим отступов
        const offsetScale = scale * compactScale;
        preBoilerSupplyY = mainY + params.preBoilerSupplyPipeOffset * offsetScale;
        preBoilerReturnY = mainEndY - params.preBoilerReturnPipeOffset * offsetScale;
        // Вычисляем фактическое межосевое (от центра до центра)
        actualPreBoilerCollectorSpacing = preBoilerReturnY - preBoilerSupplyY;
      } else {
        // Режим межосевого
        preBoilerSupplyY = separatorCenterY - preBoilerCollectorSpacing / 2;
        preBoilerReturnY = separatorCenterY + preBoilerCollectorSpacing / 2;
        actualPreBoilerCollectorSpacing = preBoilerCollectorSpacing;
      }
    } else {
      preBoilerSupplyY = 0;
      preBoilerReturnY = 0;
      actualPreBoilerCollectorSpacing = 0;
    }
    
    // Рассчитываем позиции контуров предкотлового коллектора
    // Предкотловой коллектор рисуется СЛЕВА НАПРАВО (от источника к разделителю)
    const preBoilerSupplyPositions = hasPreBoilerCollector ? preBoilerSupplyCircuits.map((circuit, index) => {
      let currentX = params.preBoilerSupplyFirstCircuitOffset;
      for (let i = 0; i < index; i++) {
        const circ = preBoilerSupplyCircuits[i];
        const spacing = Number(circ.circuitSpacing) || params.preBoilerSupplyCircuitSpacing || 125;
        const spacingToNext = Number(circ.spacingToNext) || params.preBoilerSupplySpacingBetweenCircuits || 125;
        currentX += spacing + spacingToNext;
      }
      const circuitSpacingValue = Number(circuit.circuitSpacing) || params.preBoilerSupplyCircuitSpacing || 125;
      const circuitSpacingScaled = circuitSpacingValue * scale * compactScale;
      // Для предкотлового: отсчет идет слева направо (от preBoilerStartX)
      const start = preBoilerStartX + currentX * scale * compactScale;
      return { start, spacing: circuitSpacingScaled };
    }) : [];
    
    const preBoilerReturnPositions = hasPreBoilerCollector ? preBoilerReturnCircuits.map((circuit, index) => {
      let currentX = params.preBoilerReturnFirstCircuitOffset;
      for (let i = 0; i < index; i++) {
        const circ = preBoilerReturnCircuits[i];
        const spacing = Number(circ.circuitSpacing) || params.preBoilerReturnCircuitSpacing || 125;
        const spacingToNext = Number(circ.spacingToNext) || params.preBoilerReturnSpacingBetweenCircuits || 125;
        currentX += spacing + spacingToNext;
      }
      const circuitSpacingValue = Number(circuit.circuitSpacing) || params.preBoilerReturnCircuitSpacing || 125;
      const circuitSpacingScaled = circuitSpacingValue * scale * compactScale;
      // Для предкотлового: отсчет идет слева направо (от preBoilerStartX)
      const start = preBoilerStartX + currentX * scale * compactScale;
      return { start, spacing: circuitSpacingScaled };
    }) : [];
    
    // Позиции центральных фитингов предкотлового коллектора
    const preBoilerSupplyCenterFittingPositions = hasPreBoilerCollector ? preBoilerSupplyCenterFittings.map(fitting => {
      let x = preBoilerStartX;
      
      // Определяем какие позиции использовать (для предкотлового: supply=подача, return=обратка)
      const referenceRow = fitting.referenceRow || 'supply';
      const positions = referenceRow === 'return' ? preBoilerReturnPositions : preBoilerSupplyPositions;
      const circuits = referenceRow === 'return' ? preBoilerReturnCircuits : preBoilerSupplyCircuits;
      
      if (fitting.positionType === 'center') {
        x = (preBoilerStartX + preBoilerEndX) / 2;
      } else if (fitting.positionType === 'betweenCircuits' && circuits.length > 1) {
        const idx = fitting.betweenIndex || 0;
        if (idx < positions.length - 1) {
          const pos1End = positions[idx].start + positions[idx].spacing;
          const pos2Start = positions[idx + 1].start;
          x = (pos1End + pos2Start) / 2;
        }
      } else if (fitting.positionType === 'betweenFittings' && circuits.length > 0) {
        const idx = fitting.betweenIndex || 0;
        const allFittings = [];
        positions.forEach(pos => {
          allFittings.push(pos.start);
          allFittings.push(pos.start + pos.spacing);
        });
        if (idx < allFittings.length - 1) {
          x = (allFittings[idx] + allFittings[idx + 1]) / 2;
        }
      } else if (fitting.positionType === 'distance') {
        const dir = fitting.distanceDirection || 'left';
        if (dir === 'left') {
          x = preBoilerStartX + (fitting.distanceFromStart || 50) * scale * compactScale;
        } else {
          x = preBoilerEndX - (fitting.distanceFromStart || 50) * scale * compactScale;
        }
      }
      return { x, fitting };
    }) : [];
    
    const preBoilerReturnCenterFittingPositions = hasPreBoilerCollector ? preBoilerReturnCenterFittings.map(fitting => {
      let x = preBoilerStartX;
      
      // Определяем какие позиции использовать (для предкотлового обратки: return=обратка, supply=подача)
      const referenceRow = fitting.referenceRow || 'return';
      const positions = referenceRow === 'supply' ? preBoilerSupplyPositions : preBoilerReturnPositions;
      const circuits = referenceRow === 'supply' ? preBoilerSupplyCircuits : preBoilerReturnCircuits;
      
      if (fitting.positionType === 'center') {
        x = (preBoilerStartX + preBoilerEndX) / 2;
      } else if (fitting.positionType === 'betweenCircuits' && circuits.length > 1) {
        const idx = fitting.betweenIndex || 0;
        if (idx < positions.length - 1) {
          const pos1End = positions[idx].start + positions[idx].spacing;
          const pos2Start = positions[idx + 1].start;
          x = (pos1End + pos2Start) / 2;
        }
      } else if (fitting.positionType === 'betweenFittings' && circuits.length > 0) {
        const idx = fitting.betweenIndex || 0;
        const allFittings = [];
        positions.forEach(pos => {
          allFittings.push(pos.start);
          allFittings.push(pos.start + pos.spacing);
        });
        if (idx < allFittings.length - 1) {
          x = (allFittings[idx] + allFittings[idx + 1]) / 2;
        }
      } else if (fitting.positionType === 'distance') {
        const dir = fitting.distanceDirection || 'left';
        if (dir === 'left') {
          x = preBoilerStartX + (fitting.distanceFromStart || 50) * scale * compactScale;
        } else {
          x = preBoilerEndX - (fitting.distanceFromStart || 50) * scale * compactScale;
        }
      }
      return { x, fitting };
    }) : [];
    
    // Позиции дополнительных выходов предкотлового коллектора
    const preBoilerSupplyAdditionalPositions = hasPreBoilerCollector ? preBoilerSupplyAdditionalOutlets.map(outlet => {
      let x = preBoilerStartX;
      
      // Определяем какие позиции использовать (для предкотлового: supply=подача, return=обратка)
      const referenceRow = outlet.referenceRow || 'supply';
      const positions = referenceRow === 'return' ? preBoilerReturnPositions : preBoilerSupplyPositions;
      const circuits = referenceRow === 'return' ? preBoilerReturnCircuits : preBoilerSupplyCircuits;
      
      if (outlet.positionType === 'center') {
        x = (preBoilerStartX + preBoilerEndX) / 2;
      } else if (outlet.positionType === 'betweenCircuits' && circuits.length > 1) {
        const idx = outlet.betweenIndex || 0;
        if (idx < positions.length - 1) {
          const pos1End = positions[idx].start + positions[idx].spacing;
          const pos2Start = positions[idx + 1].start;
          x = (pos1End + pos2Start) / 2;
        }
      } else if (outlet.positionType === 'betweenFittings' && circuits.length > 0) {
        const idx = outlet.betweenIndex ?? 0;
        const allFittings = [];
        positions.forEach(pos => {
          allFittings.push(pos.start);
          allFittings.push(pos.start + pos.spacing);
        });
        if (idx < allFittings.length - 1) {
          x = (allFittings[idx] + allFittings[idx + 1]) / 2;
        }
      } else if (outlet.positionType === 'distance') {
        if (outlet.distanceDirection === 'left') {
          x = preBoilerStartX + (outlet.distanceFromEdge || 60) * scale * compactScale;
        } else {
          x = preBoilerEndX - (outlet.distanceFromEdge || 60) * scale * compactScale;
        }
      }
      return { x, outlet };
    }) : [];
    
    const preBoilerReturnAdditionalPositions = hasPreBoilerCollector ? preBoilerReturnAdditionalOutlets.map(outlet => {
      let x = preBoilerStartX;
      
      // Определяем какие позиции использовать (для предкотлового обратки: return=обратка, supply=подача)
      const referenceRow = outlet.referenceRow || 'return';
      const positions = referenceRow === 'supply' ? preBoilerSupplyPositions : preBoilerReturnPositions;
      const circuits = referenceRow === 'supply' ? preBoilerSupplyCircuits : preBoilerReturnCircuits;
      
      if (outlet.positionType === 'center') {
        x = (preBoilerStartX + preBoilerEndX) / 2;
      } else if (outlet.positionType === 'betweenCircuits' && circuits.length > 1) {
        const idx = outlet.betweenIndex || 0;
        if (idx < positions.length - 1) {
          const pos1End = positions[idx].start + positions[idx].spacing;
          const pos2Start = positions[idx + 1].start;
          x = (pos1End + pos2Start) / 2;
        }
      } else if (outlet.positionType === 'betweenFittings' && circuits.length > 0) {
        const idx = outlet.betweenIndex ?? 0;
        const allFittings = [];
        positions.forEach(pos => {
          allFittings.push(pos.start);
          allFittings.push(pos.start + pos.spacing);
        });
        if (idx < allFittings.length - 1) {
          x = (allFittings[idx] + allFittings[idx + 1]) / 2;
        }
      } else if (outlet.positionType === 'distance') {
        if (outlet.distanceDirection === 'left') {
          x = preBoilerStartX + (outlet.distanceFromEdge || 60) * scale * compactScale;
        } else {
          x = preBoilerEndX - (outlet.distanceFromEdge || 60) * scale * compactScale;
        }
      }
      return { x, outlet };
    }) : [];

    // Коллектор (справа или слева при отражении без предкотлового)
    // isFlippedWithoutPreBoiler уже объявлена выше
    const collectorLengthCompact = collectorLength * compactScale;
    
    // При отражении без предкотлового коллектор рисуется слева (как предкотловой)
    // Коллектор должен заканчиваться впритык к гидроразделителю
    const collectorEndX = isFlippedWithoutPreBoiler
      ? separatorCenterX - mainPipeWidth / 2 - 5  // Слева (впритык к разделителю)
      : separatorCenterX + mainPipeWidth / 2 + 5 + collectorLengthCompact;  // Справа
    
    const collectorStartX = isFlippedWithoutPreBoiler
      ? collectorEndX - collectorLengthCompact  // Слева (от конца назад)
      : separatorCenterX + mainPipeWidth / 2 + 5;  // Справа (обычно)
    
    // Рассчитываем позиции контуров для определения позиций между ними
    const supplyPositions = supplyCircuits.map((circuit, index) => {
      let currentX = effectiveSupplyFirstCircuitOffset;
      for (let i = 0; i < index; i++) {
        const circ = supplyCircuits[i];
        const spacing = Number(circ.circuitSpacing) || params.supplyCircuitSpacing || 125;
        const spacingToNext = Number(circ.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
        currentX += spacing + (i < supplyCircuits.length - 1 ? spacingToNext : 0);
      }
      const circuitSpacingValue = Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
      const circuitSpacingScaled = circuitSpacingValue * scale * compactScale;
      const start = collectorStartX + currentX * scale * compactScale;
      return { start, spacing: circuitSpacingScaled };
    });
    
    const returnPositions = returnCircuits.map((circuit, index) => {
      let currentX = effectiveReturnFirstCircuitOffset;
      for (let i = 0; i < index; i++) {
        const circ = returnCircuits[i];
        const spacing = Number(circ.circuitSpacing) || params.returnCircuitSpacing || 125;
        const spacingToNext = Number(circ.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
        currentX += spacing + (i < returnCircuits.length - 1 ? spacingToNext : 0);
      }
      const circuitSpacingValue = Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
      const circuitSpacingScaled = circuitSpacingValue * scale * compactScale;
      const start = collectorStartX + currentX * scale * compactScale;
      return { start, spacing: circuitSpacingScaled };
    });
    
    // Позиции центральных фитингов подачи
    const supplyCenterFittingPositions = supplyCenterFittings.map(fitting => {
      let x = collectorStartX;
      
      // Определяем какие позиции использовать (для подачи: supply=подача, return=обратка)
      const referenceRow = fitting.referenceRow || 'supply';
      const positions = referenceRow === 'return' ? returnPositions : supplyPositions;
      const circuits = referenceRow === 'return' ? returnCircuits : supplyCircuits;
      
      if (fitting.positionType === 'center') {
        x = (collectorStartX + collectorEndX) / 2;
      } else if (fitting.positionType === 'betweenCircuits' && circuits.length > 1) {
        const idx = fitting.betweenIndex || 0;
        if (idx < positions.length - 1) {
          const pos1End = positions[idx].start + positions[idx].spacing;
          const pos2Start = positions[idx + 1].start;
          x = (pos1End + pos2Start) / 2;
        }
      } else if (fitting.positionType === 'betweenFittings' && circuits.length > 0) {
        const idx = fitting.betweenIndex || 0;
        const allFittings = [];
        positions.forEach(pos => {
          allFittings.push(pos.start);
          allFittings.push(pos.start + pos.spacing);
        });
        if (idx < allFittings.length - 1) {
          x = (allFittings[idx] + allFittings[idx + 1]) / 2;
        }
      } else if (fitting.positionType === 'distance') {
        const dir = fitting.distanceDirection || 'left';
        if (dir === 'left') {
          x = collectorStartX + (fitting.distanceFromStart || 50) * scale * compactScale;
        } else {
          x = collectorEndX - (fitting.distanceFromStart || 50) * scale * compactScale;
        }
      }
      return { x, fitting };
    });
    
    // Позиции центральных фитингов обратки
    const returnCenterFittingPositions = returnCenterFittings.map(fitting => {
      let x = collectorStartX;
      
      // Определяем какие позиции использовать (для обратки: return=обратка, supply=подача)
      const referenceRow = fitting.referenceRow || 'return';
      const positions = referenceRow === 'supply' ? supplyPositions : returnPositions;
      const circuits = referenceRow === 'supply' ? supplyCircuits : returnCircuits;
      
      if (fitting.positionType === 'center') {
        x = (collectorStartX + collectorEndX) / 2;
      } else if (fitting.positionType === 'betweenCircuits' && circuits.length > 1) {
        const idx = fitting.betweenIndex || 0;
        if (idx < positions.length - 1) {
          const pos1End = positions[idx].start + positions[idx].spacing;
          const pos2Start = positions[idx + 1].start;
          x = (pos1End + pos2Start) / 2;
        }
      } else if (fitting.positionType === 'betweenFittings' && circuits.length > 0) {
        const idx = fitting.betweenIndex || 0;
        const allFittings = [];
        positions.forEach(pos => {
          allFittings.push(pos.start);
          allFittings.push(pos.start + pos.spacing);
        });
        if (idx < allFittings.length - 1) {
          x = (allFittings[idx] + allFittings[idx + 1]) / 2;
        }
      } else if (fitting.positionType === 'distance') {
        const dir = fitting.distanceDirection || 'left';
        if (dir === 'left') {
          x = collectorStartX + (fitting.distanceFromStart || 50) * scale * compactScale;
        } else {
          x = collectorEndX - (fitting.distanceFromStart || 50) * scale * compactScale;
        }
      }
      return { x, fitting };
    });
    
    // Позиции дополнительных выходов подачи
    const supplyAdditionalPositions = supplyAdditionalOutlets.map(outlet => {
      let x = collectorStartX;
      
      // Определяем какие позиции использовать (для подачи: supply=подача, return=обратка)
      const referenceRow = outlet.referenceRow || 'supply';
      const positions = referenceRow === 'return' ? returnPositions : supplyPositions;
      const circuits = referenceRow === 'return' ? returnCircuits : supplyCircuits;
      
      if (outlet.positionType === 'center') {
        x = (collectorStartX + collectorEndX) / 2;
      } else if (outlet.positionType === 'betweenCircuits' && circuits.length > 1) {
        const idx = outlet.betweenIndex || 0;
        if (idx < positions.length - 1) {
          const pos1End = positions[idx].start + positions[idx].spacing;
          const pos2Start = positions[idx + 1].start;
          x = (pos1End + pos2Start) / 2;
        }
      } else if (outlet.positionType === 'betweenFittings' && circuits.length > 0) {
        const idx = outlet.betweenIndex ?? 0;
        const allFittings = [];
        positions.forEach(pos => {
          allFittings.push(pos.start);
          allFittings.push(pos.start + pos.spacing);
        });
        if (idx < allFittings.length - 1) {
          x = (allFittings[idx] + allFittings[idx + 1]) / 2;
        }
      } else if (outlet.positionType === 'distance') {
        if (outlet.distanceDirection === 'left') {
          x = collectorStartX + (outlet.distanceFromEdge || 60) * scale * compactScale;
        } else {
          x = collectorEndX - (outlet.distanceFromEdge || 60) * scale * compactScale;
        }
      }
      return { x, outlet };
    });
    
    // Позиции дополнительных выходов обратки
    const returnAdditionalPositions = returnAdditionalOutlets.map(outlet => {
      let x = collectorStartX;
      
      // Определяем какие позиции использовать (для обратки: return=обратка, supply=подача)
      const referenceRow = outlet.referenceRow || 'return';
      const positions = referenceRow === 'supply' ? supplyPositions : returnPositions;
      const circuits = referenceRow === 'supply' ? supplyCircuits : returnCircuits;
      
      if (outlet.positionType === 'center') {
        x = (collectorStartX + collectorEndX) / 2;
      } else if (outlet.positionType === 'betweenCircuits' && circuits.length > 1) {
        const idx = outlet.betweenIndex || 0;
        if (idx < positions.length - 1) {
          const pos1End = positions[idx].start + positions[idx].spacing;
          const pos2Start = positions[idx + 1].start;
          x = (pos1End + pos2Start) / 2;
        }
      } else if (outlet.positionType === 'betweenFittings' && circuits.length > 0) {
        const idx = outlet.betweenIndex ?? 0;
        const allFittings = [];
        positions.forEach(pos => {
          allFittings.push(pos.start);
          allFittings.push(pos.start + pos.spacing);
        });
        if (idx < allFittings.length - 1) {
          x = (allFittings[idx] + allFittings[idx + 1]) / 2;
        }
      } else if (outlet.positionType === 'distance') {
        if (outlet.distanceDirection === 'left') {
          x = collectorStartX + (outlet.distanceFromEdge || 60) * scale * compactScale;
        } else {
          x = collectorEndX - (outlet.distanceFromEdge || 60) * scale * compactScale;
        }
      }
      return { x, outlet };
    });

    return (
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: 250, maxHeight: 250 }}
      >
        {/* Гидроразделитель */}
        {/* Верхняя заглушка */}
        <rect
          x={separatorCenterX - mainPipeWidth / 2}
          y={mainY - 4}
          width={mainPipeWidth}
          height={4}
          fill="#bdbdbd"
          rx={params.topCapType === 'elliptical' ? 2 : 0}
        />
        
        {/* Верхний фитинг */}
        {params.topCapFitting && (
          <line
            x1={separatorCenterX}
            y1={mainY - 4}
            x2={separatorCenterX}
            y2={mainY - 14}
            stroke="#424242"
            strokeWidth={2}
            strokeLinecap="round"
          />
        )}
        
        {/* Нижняя заглушка */}
        <rect
          x={separatorCenterX - mainPipeWidth / 2}
          y={mainEndY}
          width={mainPipeWidth}
          height={4}
          fill="#bdbdbd"
          rx={params.bottomCapType === 'elliptical' ? 2 : 0}
        />
        
        {/* Нижний фитинг */}
        {params.bottomCapFitting && (
          <line
            x1={separatorCenterX}
            y1={mainEndY + 4}
            x2={separatorCenterX}
            y2={mainEndY + 14}
            stroke="#424242"
            strokeWidth={2}
            strokeLinecap="round"
          />
        )}
        
        {/* Основная труба разделителя — верхняя половина подача, нижняя обратка */}
        <rect
          x={separatorCenterX - mainPipeWidth / 2}
          y={mainY}
          width={mainPipeWidth}
          height={mainPipeHeight / 2}
          fill="#d32f2f"
          rx={1}
        />
        <rect
          x={separatorCenterX - mainPipeWidth / 2}
          y={mainY + mainPipeHeight / 2}
          width={mainPipeWidth}
          height={mainPipeHeight / 2}
          fill="#212121"
          rx={1}
        />
        
        {/* Левые/Правые подключения (источник) */}
        {!hasPreBoilerCollector && leftConnectionsPositions.map((y, i) => {
          const isSupply = y < mainY + mainPipeHeight / 2;
          const connColor = isSupply ? '#d32f2f' : '#212121';

          const connectionStartX = isSystemFlipped 
            ? separatorCenterX + mainPipeWidth / 2
            : separatorCenterX - mainPipeWidth / 2;
          
          const connectionLength = 42;
          
          const connectionEndX = isSystemFlipped
            ? connectionStartX + connectionLength
            : compactPadding + 8;
          const textX = isSystemFlipped
            ? connectionEndX - 4
            : compactPadding + 4;
          
          return (
            <g key={`left-${i}`}>
              <line
                x1={connectionStartX}
                y1={y}
                x2={connectionEndX}
                y2={y}
                stroke={connColor}
                strokeWidth={2}
                strokeLinecap="round"
              />
              <text
                x={textX}
                y={y - 2}
                fontSize={6}
                fill={connColor}
                textAnchor="middle"
                fontWeight="bold"
              >
                {i + 1}
              </text>
            </g>
          );
        })}
        
        {/* Размерная линия межосевого для левых/правых подключений */}
        {!hasPreBoilerCollector && leftConnectionsPositions.length >= 2 && (() => {
          // При отражении размерная линия справа, иначе слева
          const connectionStartX = isSystemFlipped 
            ? separatorCenterX + mainPipeWidth / 2
            : separatorCenterX - mainPipeWidth / 2;
          
          // Фиксированная длина подключения (примерно 40-45 пикселей)
          const connectionLength = 42;
          
          const connectionEndX = isSystemFlipped
            ? connectionStartX + connectionLength
            : compactPadding + 8;
          
          const dimLineX = isSystemFlipped
            ? connectionEndX + 2  // Справа (у конца подключения)
            : compactPadding + 2; // Слева
          const tickStartX = isSystemFlipped
            ? connectionEndX      // Справа
            : compactPadding;     // Слева
          const tickEndX = isSystemFlipped
            ? connectionEndX + 4  // Справа
            : compactPadding + 4; // Слева
          const textX = isSystemFlipped
            ? connectionEndX + 6  // Справа
            : compactPadding - 2; // Слева
          const textAnchor = isSystemFlipped ? "start" : "end";
          
          return (
          <g>
            {params.leftConnectionsPlacementMode === 'spacing' ? (
              // Режим межосевого: показываем размерные линии между каждой парой соседних подключений
              params.leftConnections.map((conn, index) => {
                if (index === params.leftConnections.length - 1) return null; // Последнее не имеет "следующего"
                
                const y1 = leftConnectionsPositions[index];
                const y2 = leftConnectionsPositions[index + 1];
                const spacing = Number(conn.spacingToNext) || params.leftConnectionsSpacing;
                
                return (
                  <g key={`left-spacing-${index}`}>
                    <line
                      x1={dimLineX}
                      y1={y1}
                      x2={dimLineX}
                      y2={y2}
                      stroke="#666"
                      strokeWidth={0.5}
                    />
                    <line
                      x1={tickStartX}
                      y1={y1}
                      x2={tickEndX}
                      y2={y1}
                      stroke="#666"
                      strokeWidth={0.5}
                    />
                    <line
                      x1={tickStartX}
                      y1={y2}
                      x2={tickEndX}
                      y2={y2}
                      stroke="#666"
                      strokeWidth={0.5}
                    />
                    <text
                      x={textX}
                      y={(y1 + y2) / 2}
                      fontSize={7}
                      fill="#666"
                      textAnchor={textAnchor}
                      dominantBaseline="middle"
                      fontWeight="bold"
                    >
                      {spacing}
                    </text>
                  </g>
                );
              })
            ) : (
              // Режим отступов: сортируем по реальной позиции и показываем между физически соседними
              (() => {
                const positionsWithIndices = params.leftConnections.map((conn, index) => ({
                  index,
                  offset: leftConnectionsActualOffsets[index],
                  yPos: leftConnectionsPositions[index]
                }));
                
                positionsWithIndices.sort((a, b) => a.offset - b.offset);
                
                return positionsWithIndices.map((item, sortedIndex) => {
                  if (sortedIndex === positionsWithIndices.length - 1) return null;
                  
                  const nextItem = positionsWithIndices[sortedIndex + 1];
                  const distance = Math.abs(nextItem.offset - item.offset);
                  
                  return (
                    <g key={`left-offset-${item.index}-${nextItem.index}`}>
                      <line
                        x1={dimLineX}
                        y1={item.yPos}
                        x2={dimLineX}
                        y2={nextItem.yPos}
                        stroke="#666"
                        strokeWidth={0.5}
                      />
                      <line
                        x1={tickStartX}
                        y1={item.yPos}
                        x2={tickEndX}
                        y2={item.yPos}
                        stroke="#666"
                        strokeWidth={0.5}
                      />
                      <line
                        x1={tickStartX}
                        y1={nextItem.yPos}
                        x2={tickEndX}
                        y2={nextItem.yPos}
                        stroke="#666"
                        strokeWidth={0.5}
                      />
                      <text
                        x={textX}
                        y={(item.yPos + nextItem.yPos) / 2}
                        fontSize={7}
                        fill="#666"
                        textAnchor={textAnchor}
                        dominantBaseline="middle"
                        fontWeight="bold"
                      >
                        {Math.round(distance)}
                      </text>
                    </g>
                  );
                });
              })()
            )}
          </g>
          );
        })()}
        
        {/* Предкотловой коллектор (слева от разделителя) */}
        {hasPreBoilerCollector && (
          <>
            {/* Подающая труба предкотлового */}
            <line
              x1={preBoilerStartX}
              y1={preBoilerSupplyY}
              x2={preBoilerEndX}
              y2={preBoilerSupplyY}
              stroke="#d32f2f"
              strokeWidth={2}
              strokeLinecap="round"
            />
            
            {/* Обратная труба предкотлового */}
            <line
              x1={preBoilerStartX}
              y1={preBoilerReturnY}
              x2={preBoilerEndX}
              y2={preBoilerReturnY}
              stroke="#212121"
              strokeWidth={2}
              strokeLinecap="round"
            />
            
            {/* Контуры подачи предкотлового */}
            {preBoilerSupplyCircuits.map((circuit, index) => {
              const pos = preBoilerSupplyPositions[index];
              if (!pos) return null;
              
              const x1 = pos.start;
              const x2 = pos.start + pos.spacing;
              
              const supplySwap = circuit.swapOrder || false;
              const shortX = supplySwap ? x2 : x1;
              const longX = supplySwap ? x1 : x2;
              
              return (
                <g key={`preboiler-supply-${index}`}>
                  <line
                    x1={shortX}
                    y1={preBoilerSupplyY}
                    x2={shortX}
                    y2={preBoilerSupplyY - 6}
                    stroke="#d32f2f"
                    strokeWidth={1}
                    strokeLinecap="round"
                  />
                  <line
                    x1={longX}
                    y1={preBoilerSupplyY - 6}
                    x2={longX}
                    y2={preBoilerSupplyY + actualPreBoilerCollectorSpacing}
                    stroke="#d32f2f"
                    strokeWidth={1}
                    strokeLinecap="round"
                  />
                </g>
              );
            })}
            
            {/* Контуры обратки предкотлового */}
            {preBoilerReturnCircuits.map((circuit, index) => {
              const pos = preBoilerReturnPositions[index];
              if (!pos) return null;
              
              const x1 = pos.start;
              const x2 = pos.start + pos.spacing;
              
              const returnSwap = circuit.swapOrder || false;
              const longX = returnSwap ? x2 : x1;
              const shortX = returnSwap ? x1 : x2;
              
              return (
                <g key={`preboiler-return-${index}`}>
                  <line
                    x1={longX}
                    y1={preBoilerReturnY - actualPreBoilerCollectorSpacing}
                    x2={longX}
                    y2={preBoilerReturnY + 6}
                    stroke="#212121"
                    strokeWidth={1}
                    strokeLinecap="round"
                  />
                  <line
                    x1={shortX}
                    y1={preBoilerReturnY}
                    x2={shortX}
                    y2={preBoilerReturnY + 6}
                    stroke="#212121"
                    strokeWidth={1}
                    strokeLinecap="round"
                  />
                </g>
              );
            })}
            
            {/* Торцевые подключения предкотлового слева */}
            {params.hasPreBoilerSupplyLeftConnection && (
              <line
                x1={preBoilerStartX - 5}
                y1={preBoilerSupplyY}
                x2={preBoilerStartX}
                y2={preBoilerSupplyY}
                stroke="#bdbdbd"
                strokeWidth={1}
                strokeLinecap="round"
              />
            )}
            {params.hasPreBoilerReturnLeftConnection && (
              <line
                x1={preBoilerStartX - 5}
                y1={preBoilerReturnY}
                x2={preBoilerStartX}
                y2={preBoilerReturnY}
                stroke="#bdbdbd"
                strokeWidth={1}
                strokeLinecap="round"
              />
            )}
            
            {/* Торцевые подключения предкотлового справа (к разделителю) */}
            {params.hasPreBoilerSupplyRightConnection && (
              <line
                x1={preBoilerEndX}
                y1={preBoilerSupplyY}
                x2={separatorCenterX - mainPipeWidth / 2}
                y2={preBoilerSupplyY}
                stroke="#bdbdbd"
                strokeWidth={1}
                strokeLinecap="round"
              />
            )}
            {params.hasPreBoilerReturnRightConnection && (
              <line
                x1={preBoilerEndX}
                y1={preBoilerReturnY}
                x2={separatorCenterX - mainPipeWidth / 2}
                y2={preBoilerReturnY}
                stroke="#bdbdbd"
                strokeWidth={1}
                strokeLinecap="round"
              />
            )}
            
            {/* Центральные фитинги предкотлового подачи */}
            {preBoilerSupplyCenterFittingPositions.map((pos, i) => (
              <g key={`preboiler-supply-center-${i}`}>
                <circle cx={pos.x} cy={preBoilerSupplyY} r={2.4} fill="#757575" />
              </g>
            ))}
            
            {/* Центральные фитинги предкотлового обратки */}
            {preBoilerReturnCenterFittingPositions.map((pos, i) => (
              <g key={`preboiler-return-center-${i}`}>
                <circle cx={pos.x} cy={preBoilerReturnY} r={2.4} fill="#757575" />
              </g>
            ))}
            
            {/* Дополнительные выходы предкотлового подачи */}
            {preBoilerSupplyAdditionalPositions.map((pos, i) => {
              const direction = pos.outlet.direction || 'down';
              const outletType = pos.outlet.outletType || 'short';
              const isThrough = outletType === 'through';
              
              if (isThrough) {
                // Для сквозных выходов рисуем две линии
                const shortLength = 8;
                
                // Для предкотлового подачи (сверху, Y меньше): 
                // - направление 'up' = вверх от коллектора, но сквозной проходит ВНИЗ через обратку
                // - направление 'down' = вниз от коллектора (короткая часть вниз, длинная не нужна)
                if (direction === 'up') {
                  // Короткая часть вверх
                  const yShortEnd = preBoilerSupplyY - shortLength;
                  // Длинная часть продолжается вниз до обратки
                  const yLongEnd = preBoilerReturnY;
                  
                  return (
                    <g key={`preboiler-supply-add-${i}`}>
                      {/* Короткая часть */}
                      <line
                        x1={pos.x}
                        y1={preBoilerSupplyY}
                        x2={pos.x}
                        y2={yShortEnd}
                        stroke="#757575"
                        strokeWidth={0.8}
                      />
                      <circle cx={pos.x} cy={yShortEnd - 1} r={1.2} fill="#757575" />
                      
                      {/* Длинная часть вниз до обратки */}
                      <line
                        x1={pos.x}
                        y1={preBoilerSupplyY}
                        x2={pos.x}
                        y2={yLongEnd}
                        stroke="#757575"
                        strokeWidth={0.8}
                      />
                    </g>
                  );
                } else {
                  // direction === 'down': Короткая часть вниз
                  const yShortEnd = preBoilerSupplyY + shortLength;
                  
                  return (
                    <g key={`preboiler-supply-add-${i}`}>
                      <line
                        x1={pos.x}
                        y1={preBoilerSupplyY}
                        x2={pos.x}
                        y2={yShortEnd}
                        stroke="#757575"
                        strokeWidth={0.8}
                      />
                      <circle cx={pos.x} cy={yShortEnd + 1} r={1.2} fill="#757575" />
                    </g>
                  );
                }
              } else {
                // Обычный короткий выход
                const yEnd = direction === 'down' ? preBoilerSupplyY + 8 : preBoilerSupplyY - 8;
                return (
                  <g key={`preboiler-supply-add-${i}`}>
                    <line
                      x1={pos.x}
                      y1={preBoilerSupplyY}
                      x2={pos.x}
                      y2={yEnd}
                      stroke="#757575"
                      strokeWidth={0.8}
                    />
                    <circle cx={pos.x} cy={yEnd + (direction === 'down' ? 1 : -1)} r={1.2} fill="#757575" />
                  </g>
                );
              }
            })}
            
            {/* Дополнительные выходы предкотлового обратки */}
            {preBoilerReturnAdditionalPositions.map((pos, i) => {
              const direction = pos.outlet.direction || 'up';
              const outletType = pos.outlet.outletType || 'short';
              const isThrough = outletType === 'through';
              
              if (isThrough) {
                // Для сквозных выходов рисуем две линии
                const shortLength = 8;
                
                // Для предкотлового обратки (снизу, Y больше):
                // - направление 'down' = вниз от коллектора, но сквозной проходит ВВЕРХ через подачу
                // - направление 'up' = вверх от коллектора (короткая часть вверх, длинная не нужна)
                if (direction === 'down') {
                  // Короткая часть вниз
                  const yShortEnd = preBoilerReturnY + shortLength;
                  // Длинная часть продолжается вверх до подачи
                  const yLongEnd = preBoilerSupplyY;
                  
                  return (
                    <g key={`preboiler-return-add-${i}`}>
                      {/* Короткая часть */}
                      <line
                        x1={pos.x}
                        y1={preBoilerReturnY}
                        x2={pos.x}
                        y2={yShortEnd}
                        stroke="#757575"
                        strokeWidth={0.8}
                      />
                      <circle cx={pos.x} cy={yShortEnd + 1} r={1.2} fill="#757575" />
                      
                      {/* Длинная часть вверх до подачи */}
                      <line
                        x1={pos.x}
                        y1={preBoilerReturnY}
                        x2={pos.x}
                        y2={yLongEnd}
                        stroke="#757575"
                        strokeWidth={0.8}
                      />
                    </g>
                  );
                } else {
                  // direction === 'up': Короткая часть вверх
                  const yShortEnd = preBoilerReturnY - shortLength;
                  
                  return (
                    <g key={`preboiler-return-add-${i}`}>
                      <line
                        x1={pos.x}
                        y1={preBoilerReturnY}
                        x2={pos.x}
                        y2={yShortEnd}
                        stroke="#757575"
                        strokeWidth={0.8}
                      />
                      <circle cx={pos.x} cy={yShortEnd - 1} r={1.2} fill="#757575" />
                    </g>
                  );
                }
              } else {
                // Обычный короткий выход
                const yEnd = direction === 'up' ? preBoilerReturnY - 8 : preBoilerReturnY + 8;
                return (
                  <g key={`preboiler-return-add-${i}`}>
                    <line
                      x1={pos.x}
                      y1={preBoilerReturnY}
                      x2={pos.x}
                      y2={yEnd}
                      stroke="#757575"
                      strokeWidth={0.8}
                    />
                    <circle cx={pos.x} cy={yEnd + (direction === 'up' ? -1 : 1)} r={1.2} fill="#757575" />
                  </g>
                );
              }
            })}
          </>
        )}
        
        {/* Дополнительные выходы гидроразделителя */}
        {params.additionalOutlets.map((outlet, i) => {
          const side = outlet.side || 'both';
          const positionType = outlet.positionType || 'center';
          
          let yPos = mainY + mainPipeHeight / 2;
          if (positionType === 'distance') {
            const distanceFromEdge = (outlet.distanceFromEdge || 50) * scale * compactScale;
            if (outlet.distanceDirection === 'left' || !outlet.distanceDirection) {
              yPos = mainY + distanceFromEdge;
            } else {
              yPos = mainEndY - distanceFromEdge;
            }
          }
          
          const onDarkZone = yPos >= mainY + mainPipeHeight / 2;
          const outletStroke = onDarkZone ? '#e0e0e0' : '#757575';
          const outletFill = onDarkZone ? '#e0e0e0' : '#757575';
          const outletCircleStroke = onDarkZone ? '#424242' : 'none';
          const outletCircleStrokeWidth = onDarkZone ? 0.5 : 0;
          
          const elements = [];
          
          if (side === 'left') {
            elements.push(
              <g key={`separator-add-left-${i}`}>
                <line
                  x1={separatorCenterX - mainPipeWidth / 2}
                  y1={yPos}
                  x2={separatorCenterX - mainPipeWidth / 2 - 6}
                  y2={yPos}
                  stroke={outletStroke}
                  strokeWidth={1}
                />
                <circle 
                  cx={separatorCenterX - mainPipeWidth / 2 - 7} 
                  cy={yPos} 
                  r={1.5} 
                  fill={outletFill}
                  stroke={outletCircleStroke}
                  strokeWidth={outletCircleStrokeWidth}
                />
              </g>
            );
          }
          
          if (side === 'right') {
            elements.push(
              <g key={`separator-add-right-${i}`}>
                <line
                  x1={separatorCenterX + mainPipeWidth / 2}
                  y1={yPos}
                  x2={separatorCenterX + mainPipeWidth / 2 + 6}
                  y2={yPos}
                  stroke={outletStroke}
                  strokeWidth={1}
                />
                <circle 
                  cx={separatorCenterX + mainPipeWidth / 2 + 7} 
                  cy={yPos} 
                  r={1.5} 
                  fill={outletFill}
                  stroke={outletCircleStroke}
                  strokeWidth={outletCircleStrokeWidth}
                />
              </g>
            );
          }
          
          if (side === 'front') {
            elements.push(
              <circle 
                key={`separator-add-front-${i}`}
                cx={separatorCenterX} 
                cy={yPos} 
                r={1.5} 
                fill={outletFill}
                stroke={outletCircleStroke}
                strokeWidth={outletCircleStrokeWidth}
              />
            );
          }
          
          if (side === 'back') {
            elements.push(
              <circle 
                key={`separator-add-back-${i}`}
                cx={separatorCenterX} 
                cy={yPos} 
                r={1.5} 
                fill={outletFill}
                stroke={outletCircleStroke}
                strokeWidth={outletCircleStrokeWidth}
              />
            );
          }
          
          if (side === 'both') {
            elements.push(
              <circle 
                key={`separator-add-both-${i}`}
                cx={separatorCenterX} 
                cy={yPos} 
                r={1.5} 
                fill={outletFill}
                stroke={outletCircleStroke}
                strokeWidth={outletCircleStrokeWidth}
              />
            );
          }
          
          return elements;
        })}
        
        {/* Коллектор (справа от разделителя, или слева при отражении без предкотлового) */}
        {/* Подающая труба */}
        <line
          x1={collectorStartX}
          y1={collectorSupplyY}
          x2={collectorStartX + collectorLengthCompact}
          y2={collectorSupplyY}
          stroke="#d32f2f"
          strokeWidth={2}
          strokeLinecap="round"
        />
        
        {/* Обратная труба */}
        <line
          x1={collectorStartX}
          y1={collectorReturnY}
          x2={collectorStartX + collectorLengthCompact}
          y2={collectorReturnY}
          stroke="#212121"
          strokeWidth={2}
          strokeLinecap="round"
        />
        
        {/* Контуры подачи */}
        {supplyCircuits.map((circuit, index) => {
          let currentX = effectiveSupplyFirstCircuitOffset;
          for (let i = 0; i < index; i++) {
            const circ = supplyCircuits[i];
            const spacing = Number(circ.circuitSpacing) || params.supplyCircuitSpacing || 125;
            const spacingToNext = Number(circ.spacingToNext) || params.supplySpacingBetweenCircuits || 125;
            currentX += spacing + (i < supplyCircuits.length - 1 ? spacingToNext : 0);
          }
          const circuitSpacingValue = Number(circuit.circuitSpacing) || params.supplyCircuitSpacing || 125;
          const circuitSpacingScaled = circuitSpacingValue * scale * compactScale;
          const x1 = collectorStartX + currentX * scale * compactScale;
          const x2 = x1 + circuitSpacingScaled;
          
          // Учитываем swapOrder для подачи (по умолчанию: короткий слева, длинный справа)
          const supplySwap = circuit.swapOrder || false;
          const shortX = supplySwap ? x2 : x1;
          const longX = supplySwap ? x1 : x2;
          
          return (
            <g key={`supply-${index}`}>
              {/* Короткий патрубок */}
              <line
                x1={shortX}
                y1={collectorSupplyY}
                x2={shortX}
                y2={collectorSupplyY - 6}
                stroke="#d32f2f"
                strokeWidth={1}
                strokeLinecap="round"
              />
              {/* Длинный патрубок - доходит до обратки */}
              <line
                x1={longX}
                y1={collectorSupplyY - 6}
                x2={longX}
                y2={collectorSupplyY + actualCollectorSpacing}
                stroke="#d32f2f"
                strokeWidth={1}
                strokeLinecap="round"
              />
            </g>
          );
        })}
        
        {/* Контуры обратки */}
        {returnCircuits.map((circuit, index) => {
          let currentX = effectiveReturnFirstCircuitOffset;
          for (let i = 0; i < index; i++) {
            const circ = returnCircuits[i];
            const spacing = Number(circ.circuitSpacing) || params.returnCircuitSpacing || 125;
            const spacingToNext = Number(circ.spacingToNext) || params.returnSpacingBetweenCircuits || 125;
            currentX += spacing + (i < returnCircuits.length - 1 ? spacingToNext : 0);
          }
          const circuitSpacingValue = Number(circuit.circuitSpacing) || params.returnCircuitSpacing || 125;
          const circuitSpacingScaled = circuitSpacingValue * scale * compactScale;
          const x1 = collectorStartX + currentX * scale * compactScale;
          const x2 = x1 + circuitSpacingScaled;
          
          // Учитываем swapOrder для обратки (по умолчанию: длинный слева, короткий справа)
          const returnSwap = circuit.swapOrder || false;
          const longX = returnSwap ? x2 : x1;
          const shortX = returnSwap ? x1 : x2;
          
          return (
            <g key={`return-${index}`}>
              {/* Длинный патрубок - доходит до подачи */}
              <line
                x1={longX}
                y1={collectorReturnY - actualCollectorSpacing}
                x2={longX}
                y2={collectorReturnY + 6}
                stroke="#212121"
                strokeWidth={1}
                strokeLinecap="round"
              />
              {/* Короткий патрубок */}
              <line
                x1={shortX}
                y1={collectorReturnY}
                x2={shortX}
                y2={collectorReturnY + 6}
                stroke="#212121"
                strokeWidth={1}
                strokeLinecap="round"
              />
            </g>
          );
        })}
        
        {/* Заглушки коллектора */}
        {/* Заглушки подачи */}
        {params.hasSupplyLeftCap && (
          <rect x={collectorStartX - 3} y={collectorSupplyY - 1} width="2" height="2" fill="#bdbdbd" rx="0.5" />
        )}
        {params.hasSupplyRightCap && (
          <rect x={collectorStartX + collectorLengthCompact + 1} y={collectorSupplyY - 1} width="2" height="2" fill="#bdbdbd" rx="0.5" />
        )}
        
        {/* Заглушки обратки */}
        {params.hasReturnLeftCap && (
          <rect x={collectorStartX - 3} y={collectorReturnY - 1} width="2" height="2" fill="#bdbdbd" rx="0.5" />
        )}
        {params.hasReturnRightCap && (
          <rect x={collectorStartX + collectorLengthCompact + 1} y={collectorReturnY - 1} width="2" height="2" fill="#bdbdbd" rx="0.5" />
        )}
        
        {/* Торцевые подключения подачи */}
        {params.hasSupplyLeftConnection && (
          <line
            x1={collectorStartX - 5}
            y1={collectorSupplyY}
            x2={collectorStartX}
            y2={collectorSupplyY}
            stroke="#bdbdbd"
            strokeWidth={1}
            strokeLinecap="round"
          />
        )}
        {params.hasSupplyRightConnection && (
          <line
            x1={collectorStartX + collectorLengthCompact}
            y1={collectorSupplyY}
            x2={collectorStartX + collectorLengthCompact + 5}
            y2={collectorSupplyY}
            stroke="#bdbdbd"
            strokeWidth={1}
            strokeLinecap="round"
          />
        )}
        
        {/* Торцевые подключения обратки */}
        {params.hasReturnLeftConnection && (
          <line
            x1={collectorStartX - 5}
            y1={collectorReturnY}
            x2={collectorStartX}
            y2={collectorReturnY}
            stroke="#bdbdbd"
            strokeWidth={1}
            strokeLinecap="round"
          />
        )}
        {params.hasReturnRightConnection && (
          <line
            x1={collectorStartX + collectorLengthCompact}
            y1={collectorReturnY}
            x2={collectorStartX + collectorLengthCompact + 5}
            y2={collectorReturnY}
            stroke="#bdbdbd"
            strokeWidth={1}
            strokeLinecap="round"
          />
        )}
        
        {/* Дополнительные выходы подачи */}
        {supplyAdditionalPositions.map((pos, i) => {
          const direction = pos.outlet.direction || 'up';
          const outletType = pos.outlet.outletType || 'short';
          const isThrough = outletType === 'through';
          
          if (isThrough) {
            const shortLength = 8;
            const longLength = shortLength + actualCollectorSpacing * 0.8;
            
            if (direction === 'up') {
              const yShortEnd = collectorSupplyY - shortLength;
              const yLongEnd = collectorSupplyY + longLength;
              return (
                <g key={`supply-add-${i}`}>
                  <line x1={pos.x} y1={collectorSupplyY} x2={pos.x} y2={yShortEnd} stroke="#757575" strokeWidth={0.8} />
                  <circle cx={pos.x} cy={yShortEnd - 1} r={1.2} fill="#757575" />
                  <line x1={pos.x} y1={collectorSupplyY} x2={pos.x} y2={yLongEnd} stroke="#757575" strokeWidth={0.8} />
                </g>
              );
            } else {
              const yShortEnd = collectorSupplyY + shortLength;
              return (
                <g key={`supply-add-${i}`}>
                  <line x1={pos.x} y1={collectorSupplyY} x2={pos.x} y2={yShortEnd} stroke="#757575" strokeWidth={0.8} />
                  <circle cx={pos.x} cy={yShortEnd + 1} r={1.2} fill="#757575" />
                </g>
              );
            }
          } else {
            const yEnd = direction === 'up' ? collectorSupplyY - 8 : collectorSupplyY + 8;
            return (
              <g key={`supply-add-${i}`}>
                <line x1={pos.x} y1={collectorSupplyY} x2={pos.x} y2={yEnd} stroke="#757575" strokeWidth={0.8} />
                <circle cx={pos.x} cy={yEnd + (direction === 'up' ? -1 : 1)} r={1.2} fill="#757575" />
              </g>
            );
          }
        })}
        
        {/* Дополнительные выходы обратки */}
        {returnAdditionalPositions.map((pos, i) => {
          const direction = pos.outlet.direction || 'down';
          const outletType = pos.outlet.outletType || 'short';
          const isThrough = outletType === 'through';
          
          if (isThrough) {
            const shortLength = 8;
            const longLength = shortLength + actualCollectorSpacing * 0.8;
            
            if (direction === 'down') {
              const yShortEnd = collectorReturnY + shortLength;
              const yLongEnd = collectorReturnY - longLength;
              return (
                <g key={`return-add-${i}`}>
                  <line x1={pos.x} y1={collectorReturnY} x2={pos.x} y2={yShortEnd} stroke="#424242" strokeWidth={1.5} />
                  <line x1={pos.x} y1={collectorReturnY} x2={pos.x} y2={yShortEnd} stroke="#e0e0e0" strokeWidth={0.8} />
                  <circle cx={pos.x} cy={yShortEnd + 1} r={1.2} fill="#e0e0e0" stroke="#424242" strokeWidth={0.5} />
                  <line x1={pos.x} y1={collectorReturnY} x2={pos.x} y2={yLongEnd} stroke="#424242" strokeWidth={1.5} />
                  <line x1={pos.x} y1={collectorReturnY} x2={pos.x} y2={yLongEnd} stroke="#e0e0e0" strokeWidth={0.8} />
                </g>
              );
            } else {
              const yShortEnd = collectorReturnY - shortLength;
              return (
                <g key={`return-add-${i}`}>
                  <line x1={pos.x} y1={collectorReturnY} x2={pos.x} y2={yShortEnd} stroke="#424242" strokeWidth={1.5} />
                  <line x1={pos.x} y1={collectorReturnY} x2={pos.x} y2={yShortEnd} stroke="#e0e0e0" strokeWidth={0.8} />
                  <circle cx={pos.x} cy={yShortEnd - 1} r={1.2} fill="#e0e0e0" stroke="#424242" strokeWidth={0.5} />
                </g>
              );
            }
          } else {
            const yEnd = direction === 'down' ? collectorReturnY + 8 : collectorReturnY - 8;
            return (
              <g key={`return-add-${i}`}>
                <line x1={pos.x} y1={collectorReturnY} x2={pos.x} y2={yEnd} stroke="#424242" strokeWidth={1.5} />
                <line x1={pos.x} y1={collectorReturnY} x2={pos.x} y2={yEnd} stroke="#e0e0e0" strokeWidth={0.8} />
                <circle cx={pos.x} cy={yEnd + (direction === 'down' ? 1 : -1)} r={1.2} fill="#e0e0e0" stroke="#424242" strokeWidth={0.5} />
              </g>
            );
          }
        })}

        {/* Центральные фитинги подачи — рендерятся поверх доп. выходов */}
        {supplyCenterFittingPositions.map((pos, i) => (
          <g key={`supply-center-${i}`}>
            <circle cx={pos.x} cy={collectorSupplyY} r={2.4} fill="#757575" />
          </g>
        ))}
        
        {/* Центральные фитинги обратки — рендерятся поверх доп. выходов */}
        {returnCenterFittingPositions.map((pos, i) => (
          <g key={`return-center-${i}`}>
            <circle cx={pos.x} cy={collectorReturnY} r={2.4} fill="#e0e0e0" stroke="#424242" strokeWidth={0.5} />
          </g>
        ))}
        
        {/* Размерные линии межосевого расстояния */}
        {/* Для обычного коллектора (справа или слева при отражении) */}
        {collectorStartX && (() => {
          // При отражении без предкотлового межосевое слева, иначе справа
          const dimLineX = isFlippedWithoutPreBoiler
            ? collectorStartX - 8  // Слева (как у предкотлового)
            : collectorStartX + collectorLengthCompact + 8;  // Справа
          const tickStart = isFlippedWithoutPreBoiler
            ? collectorStartX - 10
            : collectorStartX + collectorLengthCompact + 6;
          const tickEnd = isFlippedWithoutPreBoiler
            ? collectorStartX - 6
            : collectorStartX + collectorLengthCompact + 10;
          const textX = isFlippedWithoutPreBoiler
            ? collectorStartX - 12
            : collectorStartX + collectorLengthCompact + 12;
          const textAnchor = isFlippedWithoutPreBoiler ? "end" : "start";
          
          return (
          <g>
            {/* Вертикальная линия */}
            <line
                x1={dimLineX}
              y1={collectorSupplyY}
                x2={dimLineX}
              y2={collectorReturnY}
              stroke="#666"
              strokeWidth={0.5}
            />
            {/* Засечки */}
            <line
                x1={tickStart}
              y1={collectorSupplyY}
                x2={tickEnd}
              y2={collectorSupplyY}
              stroke="#666"
              strokeWidth={0.5}
            />
            <line
                x1={tickStart}
              y1={collectorReturnY}
                x2={tickEnd}
              y2={collectorReturnY}
              stroke="#666"
              strokeWidth={0.5}
            />
            {/* Текст */}
            <text
                x={textX}
              y={(collectorSupplyY + collectorReturnY) / 2}
              fontSize={8}
              fill="#666"
                textAnchor={textAnchor}
              dominantBaseline="middle"
              fontWeight="bold"
            >
              {Math.round(collectorSpacingRaw)}
            </text>
          </g>
          );
        })()}
        
        {/* Для предкотлового коллектора (слева) */}
        {hasPreBoilerCollector && (
          <g>
            {/* Вертикальная линия */}
            <line
              x1={preBoilerStartX - 8}
              y1={preBoilerSupplyY}
              x2={preBoilerStartX - 8}
              y2={preBoilerReturnY}
              stroke="#666"
              strokeWidth={0.5}
            />
            {/* Засечки */}
            <line
              x1={preBoilerStartX - 10}
              y1={preBoilerSupplyY}
              x2={preBoilerStartX - 6}
              y2={preBoilerSupplyY}
              stroke="#666"
              strokeWidth={0.5}
            />
            <line
              x1={preBoilerStartX - 10}
              y1={preBoilerReturnY}
              x2={preBoilerStartX - 6}
              y2={preBoilerReturnY}
              stroke="#666"
              strokeWidth={0.5}
            />
            {/* Текст */}
            <text
              x={preBoilerStartX - 12}
              y={(preBoilerSupplyY + preBoilerReturnY) / 2}
              fontSize={8}
              fill="#666"
              textAnchor="end"
              dominantBaseline="middle"
              fontWeight="bold"
            >
              {Math.round(preBoilerCollectorSpacingRaw)}
            </text>
          </g>
        )}
        
      </svg>
    );
  }, [
    params,
    supplyCircuits, 
    returnCircuits, 
    supplyCalculatedLength, 
    returnCalculatedLength, 
    supplyCenterFittings, 
    returnCenterFittings, 
    supplyAdditionalOutlets, 
    returnAdditionalOutlets,
    hasPreBoilerCollector,
    preBoilerSupplyCircuits,
    preBoilerReturnCircuits,
    preBoilerSupplyCalculatedLength,
    preBoilerReturnCalculatedLength,
    preBoilerSupplyCenterFittings,
    preBoilerReturnCenterFittings,
    effectiveSupplyFirstCircuitOffset,
    effectiveReturnFirstCircuitOffset,
    preBoilerSupplyAdditionalOutlets,
    preBoilerReturnAdditionalOutlets,
    isSystemFlipped,
    calculatedSeparatorHeight
  ]);

  // Генерация чертежа
  const handleGenerate = () => {
    try {
      const incompatibleConnections = [];
      
      // Проверка совместимости левых подключений гидроразделителя
      params.leftConnections.forEach((conn, index) => {
        if (!isFittingCompatibleWithMainPipe(conn.fittingId, params.mainPipeDiameter)) {
          incompatibleConnections.push(`Левое подключение ${index + 1}`);
        }
      });
      
      if (params.topCapFitting && !isEndFittingCompatibleWithMainPipe(params.topCapFitting, params.mainPipeDiameter)) {
        incompatibleConnections.push('Фитинг на верхней заглушке');
      }
      
      if (params.bottomCapFitting && !isEndFittingCompatibleWithMainPipe(params.bottomCapFitting, params.mainPipeDiameter)) {
        incompatibleConnections.push('Фитинг на нижней заглушке');
      }
      
      // Проверка совместимости фитингов контуров подачи
      supplyCircuits.forEach((circuit, index) => {
        if (circuit.fitting) {
          const isCompatible = params.shape === 'square'
            ? isCircuitFittingCompatibleWithSquareProfile(circuit.fitting, params.supplyPipeProfile)
            : isCircuitFittingCompatible(circuit.fitting, params.supplyPipeDiameter);
          
          if (!isCompatible) {
            incompatibleConnections.push(`Контур подачи ${index + 1}`);
          }
        }
      });
      
      // Проверка совместимости фитингов контуров обратки
      returnCircuits.forEach((circuit, index) => {
        if (circuit.fitting) {
          const isCompatible = params.shape === 'square'
            ? isCircuitFittingCompatibleWithSquareProfile(circuit.fitting, params.returnPipeProfile)
            : isCircuitFittingCompatible(circuit.fitting, params.returnPipeDiameter);
          
          if (!isCompatible) {
            incompatibleConnections.push(`Контур обратки ${index + 1}`);
          }
        }
      });
      
      // Проверка совместимости фитингов предкотловых контуров подачи
      if (hasPreBoilerCollector) {
        preBoilerSupplyCircuits.forEach((circuit, index) => {
          if (circuit.fitting) {
            const isCompatible = params.shape === 'square'
              ? isCircuitFittingCompatibleWithSquareProfile(circuit.fitting, params.preBoilerSupplyPipeProfile)
              : isCircuitFittingCompatible(circuit.fitting, params.preBoilerSupplyPipeDiameter);
            
            if (!isCompatible) {
              incompatibleConnections.push(`Предкотловой контур подачи ${index + 1}`);
            }
          }
        });
        
        // Проверка совместимости фитингов предкотловых контуров обратки
        preBoilerReturnCircuits.forEach((circuit, index) => {
          if (circuit.fitting) {
            const isCompatible = params.shape === 'square'
              ? isCircuitFittingCompatibleWithSquareProfile(circuit.fitting, params.preBoilerReturnPipeProfile)
              : isCircuitFittingCompatible(circuit.fitting, params.preBoilerReturnPipeDiameter);
            
            if (!isCompatible) {
              incompatibleConnections.push(`Предкотловой контур обратки ${index + 1}`);
            }
          }
        });
      }
      
      if (incompatibleConnections.length > 0) {
        setIncompatibilityDialog({
          open: true,
          message: `Невозможно сварить изделие, несовместимость фитингов: ${incompatibleConnections.join(', ')}`,
        });
        return;
      }
      
      // Проверка на разницу длин коллекторов
      const lengthDifference = Math.abs(supplyCalculatedLength - returnCalculatedLength);
      const preBoilerLengthDifference = hasPreBoilerCollector 
        ? Math.abs(preBoilerSupplyCalculatedLength - preBoilerReturnCalculatedLength)
        : 0;
      
      const needMainEqualize = lengthDifference > 1 && !lengthsEqualized;
      const needPreBoilerEqualize = hasPreBoilerCollector && preBoilerLengthDifference > 1 && !preBoilerLengthsEqualized;
      
      // Если нужно приравнять хотя бы один коллектор, показываем диалог
      if (needMainEqualize || needPreBoilerEqualize) {
        // Устанавливаем чекбоксы по умолчанию
        setEqualizeMainCollector(needMainEqualize);
        setEqualizePreBoilerCollector(needPreBoilerEqualize);
        setEqualizeDialog(true);
        return;
      }
      
      // Если включен ручной режим, используем вычисленные значения отступов
      const effectiveParams = {
        ...params,
        // Используем вычисленную высоту гидроразделителя
        separatorHeight: calculatedSeparatorHeight,
        equalizeLengths: true, // По умолчанию приравниваем (если диалог не показан, значит длины уже равны или пользователь согласился)
        preBoilerEqualizeLengths: true,
        // Обычный коллектор
        ...(params.useManualLength ? {
          supplyLastCircuitOffset: supplyLastCircuitOffsetCalculated,
          returnLastCircuitOffset: returnLastCircuitOffsetCalculated,
        } : {}),
        // Предкотловой коллектор
        ...(params.usePreBoilerManualLength ? {
          preBoilerSupplyLastCircuitOffset: preBoilerSupplyLastCircuitOffsetCalculated,
          preBoilerReturnLastCircuitOffset: preBoilerReturnLastCircuitOffsetCalculated,
        } : {}),
      };
      
      // Передаём параметры и state в функцию генерации
      const result = generateCollectorWithSeparator({
        ...effectiveParams,
        supplyCircuits,
        returnCircuits,
        supplyCenterFittings,
        returnCenterFittings,
        supplyAdditionalOutlets,
        returnAdditionalOutlets,
        preBoilerSupplyCircuits,
        preBoilerReturnCircuits,
        preBoilerSupplyCenterFittings,
        preBoilerReturnCenterFittings,
        preBoilerSupplyAdditionalOutlets,
        preBoilerReturnAdditionalOutlets,
        hasPreBoilerCollector,
        isSystemFlipped,
      }, effectiveSupplyFirstCircuitOffset, effectiveReturnFirstCircuitOffset);
      
      const drawingData = {
        cylinders: result.cylinders || [],
        fittings: result.fittings || [],
        dimensionLines: result.dimensionLines || [],
        welds: result.welds || [],
        annotations: [],
        balloons: result.balloons || [],
        lines: [],
        objectConstraints: [],
        settings: {
          isLandscape: true,
          zoom: 0.7,
          textZoom: 1,
          positionTextZoom: 1,
        },
        metadata: {
          ...result.metadata,  // Сохраняем metadata из result (supplyCircuitParams, returnCircuitParams и т.д.)
          totalLength: totalSystemLength,
          separatorTotalHeight,
        },
      };
      
      if (onGenerate) {
        onGenerate(drawingData, {
          params,
          supplyCircuits,
          returnCircuits,
          supplyCenterFittings,
          returnCenterFittings,
          supplyAdditionalOutlets,
          returnAdditionalOutlets,
          preBoilerSupplyCircuits,
          preBoilerReturnCircuits,
          preBoilerSupplyCenterFittings,
          preBoilerReturnCenterFittings,
          preBoilerSupplyAdditionalOutlets,
          preBoilerReturnAdditionalOutlets,
          hasPreBoilerCollector,
          isSystemFlipped,
          tabValue, // Сохраняем активную вкладку
          collectorTabValue, // Сохраняем активную подвкладку коллектора
          preBoilerTabValue, // Сохраняем активную подвкладку предкотлового коллектора
        });
      }
    } catch (error) {
      console.error('Ошибка генерации:', error);
      setIncompatibilityDialog({
        open: true,
        message: `Ошибка генерации чертежа: ${error.message}`,
      });
    }
  };

  // Получение фитингов для контуров с проверкой совместимости
  const getSupplyFittings = (category) => {
    if (params.shape === 'square') {
      // Для квадратных профилей используем специальную проверку
      return getFittingsByCategory(category, null, null).map(f => ({
        ...f,
        isCompatible: isCircuitFittingCompatibleWithSquareProfile(f.id, params.supplyPipeProfile)
      }));
    }
    return getFittingsByCategory(category, params.supplyPipeDiameter, isCircuitFittingCompatible);
  };

  const getReturnFittings = (category) => {
    if (params.shape === 'square') {
      // Для квадратных профилей используем специальную проверку
      return getFittingsByCategory(category, null, null).map(f => ({
        ...f,
        isCompatible: isCircuitFittingCompatibleWithSquareProfile(f.id, params.returnPipeProfile)
      }));
    }
    return getFittingsByCategory(category, params.returnPipeDiameter, isCircuitFittingCompatible);
  };

  const getPreBoilerSupplyFittings = (category) => {
    if (params.shape === 'square') {
      return getFittingsByCategory(category, null, null).map(f => ({
        ...f,
        isCompatible: isCircuitFittingCompatibleWithSquareProfile(f.id, params.preBoilerSupplyPipeProfile)
      }));
    }
    return getFittingsByCategory(category, params.preBoilerSupplyPipeDiameter, isCircuitFittingCompatible);
  };

  const getPreBoilerReturnFittings = (category) => {
    if (params.shape === 'square') {
      return getFittingsByCategory(category, null, null).map(f => ({
        ...f,
        isCompatible: isCircuitFittingCompatibleWithSquareProfile(f.id, params.preBoilerReturnPipeProfile)
      }));
    }
    return getFittingsByCategory(category, params.preBoilerReturnPipeDiameter, isCircuitFittingCompatible);
  };

  // РЕНДЕР
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
        py: 3,
        overflow: 'visible',
      }}
    >
      <Container maxWidth="xl" sx={{ overflow: 'visible' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            <IconButton
              onClick={onBack}
              sx={{
                bgcolor: 'white',
                boxShadow: 1,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <HomeIcon />
            </IconButton>
            {onBackToCharacteristics && (
              <IconButton
                onClick={onBackToCharacteristics}
                sx={{
                  bgcolor: 'white',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            {onBackToDrawing && (
              <IconButton
                onClick={onBackToDrawing}
                sx={{
                  bgcolor: 'white',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ArrowForwardIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Коллектор с гидравлическим разделителем
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Комбинированная система
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={params.shape}
              exclusive
              onChange={(e, newShape) => {
                if (newShape !== null) {
                  const updates = { 
                    shape: newShape,
                  };
                  
                  if (newShape === 'square') {
                    // Сохраняем текущие типы заглушек перед переключением на квадратную форму
                    setSavedCapTypes({
                      topCapType: params.topCapType,
                      bottomCapType: params.bottomCapType,
                      supplyLeftCapType: params.supplyLeftCapType,
                      supplyRightCapType: params.supplyRightCapType,
                      returnLeftCapType: params.returnLeftCapType,
                      returnRightCapType: params.returnRightCapType,
                      preBoilerSupplyLeftCapType: params.preBoilerSupplyLeftCapType,
                      preBoilerReturnLeftCapType: params.preBoilerReturnLeftCapType,
                    });
                    
                    // Устанавливаем профили для всех компонентов
                    updates.mainPipeProfile = params.mainPipeProfile || '80x80';
                    updates.supplyPipeProfile = params.supplyPipeProfile || '80x80';
                    updates.returnPipeProfile = params.returnPipeProfile || '80x80';
                    updates.preBoilerSupplyPipeProfile = params.preBoilerSupplyPipeProfile || '80x80';
                    updates.preBoilerReturnPipeProfile = params.preBoilerReturnPipeProfile || '80x80';
                    
                    // Для квадратных только прямые заглушки
                    updates.topCapType = 'straight';
                    updates.bottomCapType = 'straight';
                    updates.topCapElementType = 'cap';
                    updates.bottomCapElementType = 'cap';
                    updates.hasTopCap = true;
                    updates.hasBottomCap = true;
                    
                    updates.supplyLeftCapType = 'straight';
                    updates.supplyRightCapType = 'straight';
                    updates.returnLeftCapType = 'straight';
                    updates.returnRightCapType = 'straight';
                    updates.supplyLeftCapElementType = 'cap';
                    updates.supplyRightCapElementType = 'cap';
                    updates.returnLeftCapElementType = 'cap';
                    updates.returnRightCapElementType = 'cap';
                    updates.hasSupplyLeftCap = true;
                    updates.hasSupplyRightCap = true;
                    updates.hasReturnLeftCap = true;
                    updates.hasReturnRightCap = true;
                    
                    updates.preBoilerSupplyLeftCapType = 'straight';
                    updates.preBoilerReturnLeftCapType = 'straight';
                    updates.preBoilerSupplyLeftCapElementType = 'cap';
                    updates.preBoilerReturnLeftCapElementType = 'cap';
                    updates.hasPreBoilerSupplyLeftCap = true;
                    updates.hasPreBoilerReturnLeftCap = true;
                    
                    // Сбрасываем fittingLength для всех подключений и контуров
                    const updatedLeftConnections = params.leftConnections.map(conn => ({
                      ...conn,
                      fittingLength: null,
                    }));
                    updates.leftConnections = updatedLeftConnections;
                    
                    const newSupplyProfile = params.supplyPipeProfile || '80x80';
                    const newReturnProfile = params.returnPipeProfile || '80x80';
                    const newPreBoilerSupplyProfile = params.preBoilerSupplyPipeProfile || '80x80';
                    const newPreBoilerReturnProfile = params.preBoilerReturnPipeProfile || '80x80';

                    const updateCircuitsForSquareProfile = (circuits, profile) =>
                      circuits.map((circuit) => {
                        const fittingId = circuit.fittingId || circuit.fitting;
                        if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, profile)) {
                          const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                          const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, profile));
                          if (compatibleFittings.length > 0) {
                            const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                            return {
                              ...circuit,
                              fitting: maxCompatibleFitting.id,
                              fittingId: maxCompatibleFitting.id,
                              diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                              fittingLength: null,
                            };
                          }
                        }
                        return { ...circuit, fittingLength: null };
                      });

                    setSupplyCircuits(prev => updateCircuitsForSquareProfile(prev, newSupplyProfile));
                    setReturnCircuits(prev => updateCircuitsForSquareProfile(prev, newReturnProfile));
                    setPreBoilerSupplyCircuits(prev => updateCircuitsForSquareProfile(prev, newPreBoilerSupplyProfile));
                    setPreBoilerReturnCircuits(prev => updateCircuitsForSquareProfile(prev, newPreBoilerReturnProfile));
                  } else {
                    // Переключение на круглый профиль
                    // Восстанавливаем сохраненные типы заглушек, если они были сохранены
                    if (savedCapTypes) {
                      updates.topCapType = savedCapTypes.topCapType;
                      updates.bottomCapType = savedCapTypes.bottomCapType;
                      updates.supplyLeftCapType = savedCapTypes.supplyLeftCapType;
                      updates.supplyRightCapType = savedCapTypes.supplyRightCapType;
                      updates.returnLeftCapType = savedCapTypes.returnLeftCapType;
                      updates.returnRightCapType = savedCapTypes.returnRightCapType;
                      updates.preBoilerSupplyLeftCapType = savedCapTypes.preBoilerSupplyLeftCapType;
                      updates.preBoilerReturnLeftCapType = savedCapTypes.preBoilerReturnLeftCapType;
                      
                      // Очищаем сохраненные значения
                      setSavedCapTypes(null);
                    } else {
                      // Если нет сохраненных значений (первое включение круглой формы), используем автоматическую логику
                      // Для больших диаметров (>101.6) устанавливаем эллиптические заглушки
                      if (params.mainPipeDiameter > 101.6) {
                        updates.topCapType = 'elliptical';
                        updates.bottomCapType = 'elliptical';
                      } else {
                        updates.topCapType = 'straight';
                        updates.bottomCapType = 'straight';
                      }
                      
                      // Обновляем заглушки основного коллектора
                      const ellipticalCapSupply = findEllipticalCap(params.supplyPipeDiameter);
                      if (ellipticalCapSupply && params.supplyLeftCapElementType === 'cap') {
                        updates.supplyLeftCapType = 'elliptical';
                      } else if (params.supplyLeftCapElementType === 'cap') {
                        updates.supplyLeftCapType = 'straight';
                      }
                      if (ellipticalCapSupply && params.supplyRightCapElementType === 'cap') {
                        updates.supplyRightCapType = 'elliptical';
                      } else if (params.supplyRightCapElementType === 'cap') {
                        updates.supplyRightCapType = 'straight';
                      }
                      
                      const ellipticalCapReturn = findEllipticalCap(params.returnPipeDiameter);
                      if (ellipticalCapReturn && params.returnLeftCapElementType === 'cap') {
                        updates.returnLeftCapType = 'elliptical';
                      } else if (params.returnLeftCapElementType === 'cap') {
                        updates.returnLeftCapType = 'straight';
                      }
                      if (ellipticalCapReturn && params.returnRightCapElementType === 'cap') {
                        updates.returnRightCapType = 'elliptical';
                      } else if (params.returnRightCapElementType === 'cap') {
                        updates.returnRightCapType = 'straight';
                      }
                      
                      // Обновляем заглушки предкотлового коллектора
                      const ellipticalCapPreBoilerSupply = findEllipticalCap(params.preBoilerSupplyPipeDiameter);
                      if (ellipticalCapPreBoilerSupply && params.preBoilerSupplyLeftCapElementType === 'cap') {
                        updates.preBoilerSupplyLeftCapType = 'elliptical';
                      } else if (params.preBoilerSupplyLeftCapElementType === 'cap') {
                        updates.preBoilerSupplyLeftCapType = 'straight';
                      }
                      
                      const ellipticalCapPreBoilerReturn = findEllipticalCap(params.preBoilerReturnPipeDiameter);
                      if (ellipticalCapPreBoilerReturn && params.preBoilerReturnLeftCapElementType === 'cap') {
                        updates.preBoilerReturnLeftCapType = 'elliptical';
                      } else if (params.preBoilerReturnLeftCapElementType === 'cap') {
                        updates.preBoilerReturnLeftCapType = 'straight';
                      }
                    }
                  }
                  
                  updateParam(updates);
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="round" aria-label="круглая форма">
                <Tooltip title="Круглая форма">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <RadioButtonUncheckedIcon fontSize="small" />
                    <Typography variant="body2">Круглый</Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="square" aria-label="квадратная форма">
                <Tooltip title="Квадратная форма">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CropSquareIcon fontSize="small" />
                    <Typography variant="body2">Квадратный</Typography>
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Схема наверху на всю ширину */}
        <Paper 
          sx={{ 
            p: 0.75, 
            borderRadius: 1,
            width: '100%',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'white',
            boxShadow: 1,
            mb: 2,
            alignSelf: 'flex-start'
          }}
        >
          <Box
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 0.5,
              p: 0.5,
              height: 250,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '0.0625rem solid',
              borderColor: 'grey.200',
            }}
          >
            {combinedSchematicPreview}
          </Box>
          
          {/* Информация об общей длине */}
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
              <strong>Общая длина системы:</strong> {totalSystemLength} мм
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <strong>Габарит:</strong>{' '}
              {getDrawingGabaritMm({ totalLength: totalSystemLength, separatorTotalHeight })} мм
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Панель параметров на всю ширину */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label="Гидроразделитель" />
                <Tab label="Коллектор" />
                  {hasPreBoilerCollector && <Tab label="Предкотловой коллектор" />}
              </Tabs>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!hasPreBoilerCollector && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setHasPreBoilerCollector(true);
                        updateParam('hasPreBoilerCollector', true);
                        setTabValue(2);
                      }}
                      sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                    >
                      Добавить предкотловой коллектор
                    </Button>
                  )}
                  
                  {hasPreBoilerCollector && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => {
                        setHasPreBoilerCollector(false);
                        updateParam('hasPreBoilerCollector', false);
                        setIsSystemFlipped(false);
                        // Переключаемся на вкладку коллектора
                        setTabValue(1);
                      }}
                      sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
                    >
                      Удалить предкотловой
                    </Button>
                  )}
                  
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={toggleSystemFlip}
                    sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
                  >
                    {isSystemFlipped ? '↶ Вернуть' : '↷ Отразить систему'}
                  </Button>
                </Box>
              </Box>

              {/* ВКЛАДКА 1: ГИДРОРАЗДЕЛИТЕЛЬ */}
              {tabValue === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Параметры гидравлического разделителя
                  </Typography>

                  <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>
                    Основная труба
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{params.shape === 'round' ? 'Диаметр' : 'Профиль'}</InputLabel>
                        <Select
                          value={params.shape === 'round' ? params.mainPipeDiameter : (params.mainPipeProfile || '80x80')}
                          label={params.shape === 'round' ? 'Диаметр' : 'Профиль'}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            
                            if (params.shape === 'round') {
                            const newDiameter = newValue;
                            
                            // Проверяем и сбрасываем несовместимые фитинги в левых подключениях
                            const updatedLeftConnections = params.leftConnections.map((conn) => {
                              if (!isFittingCompatibleWithMainPipe(conn.fittingId, newDiameter)) {
                                // Ищем максимальный совместимый фитинг в той же категории
                                const fittingInfo = fittingsData[conn.fittingId];
                                const category = fittingInfo?.category || 'Ниппели';
                                const fittingsInCategory = getFittingsByCategory(category, newDiameter, isFittingCompatibleWithMainPipe);
                                const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                
                                if (compatibleFittings.length > 0) {
                                  const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                  return {
                                    ...conn,
                                    fittingId: maxCompatibleFitting.id,
                                    fittingLength: null,
                                  };
                                } else {
                                  // Сбрасываем на дефолтный Nippel_3.4 если он совместим
                                  const defaultFittingId = 'Nippel_3.4';
                                  if (isFittingCompatibleWithMainPipe(defaultFittingId, newDiameter)) {
                                    return {
                                      ...conn,
                                      fittingId: defaultFittingId,
                                      fittingLength: null,
                                    };
                                  } else {
                                    // Ищем максимальный совместимый ниппель
                                    const nippelFittings = getFittingsByCategory('Ниппели', newDiameter, isFittingCompatibleWithMainPipe);
                                    const compatibleNippels = nippelFittings.filter(f => f.isCompatible);
                                    if (compatibleNippels.length > 0) {
                                      const maxCompatibleNippel = compatibleNippels[compatibleNippels.length - 1];
                                      return {
                                        ...conn,
                                        fittingId: maxCompatibleNippel.id,
                                    fittingLength: null,
                                  };
                                    }
                                  }
                                }
                              }
                              return conn;
                            });
                            
                            // Проверяем верхний фитинг
                            let updatedTopCapFitting = params.topCapFitting;
                            let updatedTopCapFittingCategory = params.topCapFittingCategory;
                            if (updatedTopCapFitting && !isEndFittingCompatibleWithMainPipe(updatedTopCapFitting, newDiameter)) {
                              if (updatedTopCapFittingCategory) {
                                const fittingsInCategory = getFittingsByCategory(updatedTopCapFittingCategory, newDiameter);
                                const compatibleFittings = fittingsInCategory.filter(f => isEndFittingCompatibleWithMainPipe(f.id, newDiameter));
                                
                                if (compatibleFittings.length > 0) {
                                  const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                  updatedTopCapFitting = maxCompatibleFitting.id;
                                } else {
                                  updatedTopCapFitting = '';
                                }
                              } else {
                                updatedTopCapFitting = '';
                              }
                            } else if (updatedTopCapFitting && updatedTopCapFittingCategory) {
                              const fittingsInCategory = getFittingsByCategory(updatedTopCapFittingCategory, newDiameter);
                              const betterFitting = fittingsInCategory.find(f => {
                                if (f.id.startsWith('Flanec_')) {
                                  const dnMatch = f.id.match(/Flanec_(\d+)/);
                                  if (dnMatch) {
                                    const dn = parseInt(dnMatch[1]);
                                    const DN_TO_PIPE_DIAMETER = {
                                      25: 33.7, 32: 42.4, 40: 48.3, 50: 60.3, 65: 76.1,
                                      80: 88.9, 100: 114.3, 125: 139.7, 150: 168.3,
                                      200: 219.1, 250: 273, 300: 323.9, 350: 355.6,
                                    };
                                    return DN_TO_PIPE_DIAMETER[dn] === newDiameter;
                                  }
                                }
                                return false;
                              });
                              if (betterFitting) {
                                updatedTopCapFitting = betterFitting.id;
                              }
                            }
                            
                            // Проверяем нижний фитинг
                            let updatedBottomCapFitting = params.bottomCapFitting;
                            let updatedBottomCapFittingCategory = params.bottomCapFittingCategory;
                            if (updatedBottomCapFitting && !isEndFittingCompatibleWithMainPipe(updatedBottomCapFitting, newDiameter)) {
                              if (updatedBottomCapFittingCategory) {
                                const fittingsInCategory = getFittingsByCategory(updatedBottomCapFittingCategory, newDiameter);
                                const compatibleFittings = fittingsInCategory.filter(f => isEndFittingCompatibleWithMainPipe(f.id, newDiameter));
                                
                                if (compatibleFittings.length > 0) {
                                  const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                  updatedBottomCapFitting = maxCompatibleFitting.id;
                                } else {
                                  updatedBottomCapFitting = '';
                                }
                              } else {
                                updatedBottomCapFitting = '';
                              }
                            } else if (updatedBottomCapFitting && updatedBottomCapFittingCategory) {
                              const fittingsInCategory = getFittingsByCategory(updatedBottomCapFittingCategory, newDiameter);
                              const betterFitting = fittingsInCategory.find(f => {
                                if (f.id.startsWith('Flanec_')) {
                                  const dnMatch = f.id.match(/Flanec_(\d+)/);
                                  if (dnMatch) {
                                    const dn = parseInt(dnMatch[1]);
                                    const DN_TO_PIPE_DIAMETER = {
                                      25: 33.7, 32: 42.4, 40: 48.3, 50: 60.3, 65: 76.1,
                                      80: 88.9, 100: 114.3, 125: 139.7, 150: 168.3,
                                      200: 219.1, 250: 273, 300: 323.9, 350: 355.6,
                                    };
                                    return DN_TO_PIPE_DIAMETER[dn] === newDiameter;
                                  }
                                }
                                return false;
                              });
                              if (betterFitting) {
                                updatedBottomCapFitting = betterFitting.id;
                              }
                            }
                            
                            // Обновляем заглушки/переходы для торцевых подключений
                            const capUpdates = {};
                            
                            // Верхнее подключение
                            if (params.hasTopConnection && updatedTopCapFitting) {
                              const topCapReq = getCapRequirementForFitting(updatedTopCapFitting, newDiameter);
                              capUpdates.hasTopCap = topCapReq.needsCap;
                              if (topCapReq.needsCap) {
                                if (topCapReq.capType === 'reducer' && topCapReq.reducer) {
                                  capUpdates.topCapElementType = 'reducer';
                                  capUpdates.topReducer = topCapReq.reducer.key;
                                } else {
                                  capUpdates.topCapElementType = 'cap';
                                  // Для квадратных профилей только прямые заглушки
                                  capUpdates.topCapType = params.shape === 'square' ? 'straight' : 'elliptical';
                                  capUpdates.topReducer = null;
                                }
                              } else {
                                capUpdates.topCapElementType = 'cap';
                                capUpdates.topCapType = 'straight';
                                capUpdates.topReducer = null;
                              }
                            }
                            
                            // Нижнее подключение
                            if (params.hasBottomConnection && updatedBottomCapFitting) {
                              const bottomCapReq = getCapRequirementForFitting(updatedBottomCapFitting, newDiameter);
                              capUpdates.hasBottomCap = bottomCapReq.needsCap;
                              if (bottomCapReq.needsCap) {
                                if (bottomCapReq.capType === 'reducer' && bottomCapReq.reducer) {
                                  capUpdates.bottomCapElementType = 'reducer';
                                  capUpdates.bottomReducer = bottomCapReq.reducer.key;
                                } else {
                                  capUpdates.bottomCapElementType = 'cap';
                                  // Для квадратных профилей только прямые заглушки
                                  capUpdates.bottomCapType = params.shape === 'square' ? 'straight' : 'elliptical';
                                  capUpdates.bottomReducer = null;
                                }
                              } else {
                                capUpdates.bottomCapElementType = 'cap';
                                capUpdates.bottomCapType = 'straight';
                                capUpdates.bottomReducer = null;
                              }
                            }
                            
                            // Обновляем тип заглушек для заглушек БЕЗ фитингов
                            // Проверяем, есть ли эллиптическая заглушка для данного диаметра
                            if (params.hasTopCap && params.topCapElementType === 'cap' && !params.hasTopConnection && params.shape !== 'square') {
                              const ellipticalCap = findEllipticalCap(newDiameter);
                              if (ellipticalCap && params.topCapType !== 'elliptical') {
                                capUpdates.topCapType = 'elliptical';
                              }
                            }
                            if (params.hasBottomCap && params.bottomCapElementType === 'cap' && !params.hasBottomConnection && params.shape !== 'square') {
                              const ellipticalCap = findEllipticalCap(newDiameter);
                              if (ellipticalCap && params.bottomCapType !== 'elliptical') {
                                capUpdates.bottomCapType = 'elliptical';
                              }
                            }
                            
                            // Проверяем и обновляем дополнительные выходы
                            const updatedAdditionalOutlets = params.additionalOutlets.map((outlet) => {
                              if (outlet.fittingId && !isFittingCompatibleWithMainPipe(outlet.fittingId, newDiameter)) {
                                // Фитинг несовместим - ищем максимальный совместимый в той же категории
                                const category = outlet.fittingCategory || 'Ниппели';
                                const fittingsInCategory = getFittingsByCategory(category, newDiameter, isFittingCompatibleWithMainPipe);
                                const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                
                                if (compatibleFittings.length > 0) {
                                  // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                                  const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                  return {
                                    ...outlet,
                                    fittingId: maxCompatibleFitting.id,
                                    fittingLength: null,
                                  };
                                } else {
                                  // Нет совместимых фитингов - сбрасываем на дефолтный Nippel_3.4 если он совместим
                                  const defaultFittingId = 'Nippel_3.4';
                                  if (isFittingCompatibleWithMainPipe(defaultFittingId, newDiameter)) {
                                    return {
                                      ...outlet,
                                      fittingId: defaultFittingId,
                                      fittingLength: null,
                                    };
                                  } else {
                                    // Ищем максимальный совместимый ниппель
                                    const nippelFittings = getFittingsByCategory('Ниппели', newDiameter, isFittingCompatibleWithMainPipe);
                                    const compatibleNippels = nippelFittings.filter(f => f.isCompatible);
                                    if (compatibleNippels.length > 0) {
                                      const maxCompatibleNippel = compatibleNippels[compatibleNippels.length - 1];
                                      return {
                                        ...outlet,
                                        fittingId: maxCompatibleNippel.id,
                                        fittingLength: null,
                                      };
                                    }
                                  }
                                }
                              }
                              return outlet;
                            });
                            
                            // Проверяем и корректируем диаметры труб коллекторов
                            const maxCollectorDiameter = getMaxCollectorPipeDiameter(newDiameter);
                            const collectorDiameterUpdates = {};
                            
                            // Переменные для обновленных фитингов и контуров
                            let updatedSupplyCenterFittings = supplyCenterFittings;
                            let updatedSupplyCircuits = supplyCircuits;
                            let updatedSupplyAdditionalOutlets = supplyAdditionalOutlets;
                            let updatedReturnCenterFittings = returnCenterFittings;
                            let updatedReturnCircuits = returnCircuits;
                            let updatedReturnAdditionalOutlets = returnAdditionalOutlets;
                            let updatedPreBoilerSupplyCenterFittings = preBoilerSupplyCenterFittings;
                            let updatedPreBoilerSupplyCircuits = preBoilerSupplyCircuits;
                            let updatedPreBoilerSupplyAdditionalOutlets = preBoilerSupplyAdditionalOutlets;
                            let updatedPreBoilerReturnCenterFittings = preBoilerReturnCenterFittings;
                            let updatedPreBoilerReturnCircuits = preBoilerReturnCircuits;
                            let updatedPreBoilerReturnAdditionalOutlets = preBoilerReturnAdditionalOutlets;
                            
                            // Переменные для обновленных правых подключений (торцевых)
                            const rightConnectionUpdates = {};
                            
                            // Корректируем диаметр подающего коллектора если он превышает максимальный
                            if (params.supplyPipeDiameter > maxCollectorDiameter) {
                              collectorDiameterUpdates.supplyPipeDiameter = maxCollectorDiameter;
                              
                              // Обновляем центральные фитинги
                              updatedSupplyCenterFittings = updateCenterFittingsForDiameter(
                                supplyCenterFittings,
                                maxCollectorDiameter,
                                isCenterFittingCompatible,
                                getFittingsByCategory
                              );
                              
                              // Обновляем контуры
                              updatedSupplyCircuits = supplyCircuits.map((circuit) => {
                                const fittingId = circuit.fittingId || circuit.fitting;
                                if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, maxCollectorDiameter)) {
                                  const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', maxCollectorDiameter, isCircuitFittingCompatible);
                                  const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                  
                                  if (compatibleFittings.length > 0) {
                                    const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                    return {
                                      ...circuit,
                                      fitting: maxCompatibleFitting.id,
                                      fittingId: maxCompatibleFitting.id,
                                      diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                      fittingLength: null,
                                    };
                                  }
                                }
                                return circuit;
                              });
                              
                              // Обновляем правое торцевое подключение подающего коллектора
                              if (params.supplyRightConnectionType === 'fitting' && params.supplyRightConnectionFitting) {
                                if (!isEndFittingCompatibleWithMainPipe(params.supplyRightConnectionFitting, maxCollectorDiameter)) {
                                  const fittingInfo = fittingsData[params.supplyRightConnectionFitting];
                                  const category = fittingInfo?.category || params.supplyRightConnectionFittingCategory || 'Ниппели';
                                  const fittingsInCategory = getFittingsByCategory(category, maxCollectorDiameter, isEndFittingCompatibleWithMainPipe);
                                  const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                  
                                  if (compatibleFittings.length > 0) {
                                    const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                    rightConnectionUpdates.supplyRightConnectionFitting = maxCompatibleFitting.id;
                                    rightConnectionUpdates.supplyRightConnectionFittingLength = null;
                                  }
                                }
                              }
                              
                              // Обновляем дополнительные выходы подающего коллектора
                              updatedSupplyAdditionalOutlets = supplyAdditionalOutlets.map((outlet) => {
                                if (outlet.fittingId && !isCenterFittingCompatible(outlet.fittingId, maxCollectorDiameter)) {
                                  const category = outlet.fittingCategory || 'Ниппели';
                                  const fittingsInCategory = getFittingsByCategory(category, maxCollectorDiameter, isCenterFittingCompatible);
                                  const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                  
                                  if (compatibleFittings.length > 0) {
                                    const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                    return {
                                      ...outlet,
                                      fittingId: maxCompatibleFitting.id,
                                    };
                                  }
                                }
                                return outlet;
                              });
                            }
                            
                            // Корректируем диаметр обратного коллектора если он превышает максимальный
                            if (params.returnPipeDiameter > maxCollectorDiameter) {
                              collectorDiameterUpdates.returnPipeDiameter = maxCollectorDiameter;
                              
                              // Обновляем центральные фитинги
                              updatedReturnCenterFittings = updateCenterFittingsForDiameter(
                                returnCenterFittings,
                                maxCollectorDiameter,
                                isCenterFittingCompatible,
                                getFittingsByCategory
                              );
                              
                              // Обновляем контуры
                              updatedReturnCircuits = returnCircuits.map((circuit) => {
                                const fittingId = circuit.fittingId || circuit.fitting;
                                if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, maxCollectorDiameter)) {
                                  const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', maxCollectorDiameter, isCircuitFittingCompatible);
                                  const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                  
                                  if (compatibleFittings.length > 0) {
                                    const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                    return {
                                      ...circuit,
                                      fitting: maxCompatibleFitting.id,
                                      fittingId: maxCompatibleFitting.id,
                                      diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                      fittingLength: null,
                                    };
                                  }
                                }
                                return circuit;
                              });
                              
                              // Обновляем правое торцевое подключение обратного коллектора
                              if (params.returnRightConnectionType === 'fitting' && params.returnRightConnectionFitting) {
                                if (!isEndFittingCompatibleWithMainPipe(params.returnRightConnectionFitting, maxCollectorDiameter)) {
                                  const fittingInfo = fittingsData[params.returnRightConnectionFitting];
                                  const category = fittingInfo?.category || params.returnRightConnectionFittingCategory || 'Ниппели';
                                  const fittingsInCategory = getFittingsByCategory(category, maxCollectorDiameter, isEndFittingCompatibleWithMainPipe);
                                  const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                  
                                  if (compatibleFittings.length > 0) {
                                    const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                    rightConnectionUpdates.returnRightConnectionFitting = maxCompatibleFitting.id;
                                    rightConnectionUpdates.returnRightConnectionFittingLength = null;
                                  }
                                }
                              }
                              
                              // Обновляем дополнительные выходы обратного коллектора
                              updatedReturnAdditionalOutlets = returnAdditionalOutlets.map((outlet) => {
                                if (outlet.fittingId && !isCenterFittingCompatible(outlet.fittingId, maxCollectorDiameter)) {
                                  const category = outlet.fittingCategory || 'Ниппели';
                                  const fittingsInCategory = getFittingsByCategory(category, maxCollectorDiameter, isCenterFittingCompatible);
                                  const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                  
                                  if (compatibleFittings.length > 0) {
                                    const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                    return {
                                      ...outlet,
                                      fittingId: maxCompatibleFitting.id,
                                    };
                                  }
                                }
                                return outlet;
                              });
                            }
                            
                            // Корректируем диаметры предкотлового коллектора если он есть
                            if (hasPreBoilerCollector) {
                              if (params.preBoilerSupplyPipeDiameter > maxCollectorDiameter) {
                                collectorDiameterUpdates.preBoilerSupplyPipeDiameter = maxCollectorDiameter;
                                
                                // Обновляем центральные фитинги
                                updatedPreBoilerSupplyCenterFittings = updateCenterFittingsForDiameter(
                                  preBoilerSupplyCenterFittings,
                                  maxCollectorDiameter,
                                  isCenterFittingCompatible,
                                  getFittingsByCategory
                                );
                                
                                // Обновляем контуры
                                updatedPreBoilerSupplyCircuits = preBoilerSupplyCircuits.map((circuit) => {
                                  const fittingId = circuit.fittingId || circuit.fitting;
                                  if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, maxCollectorDiameter)) {
                                    const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', maxCollectorDiameter, isCircuitFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...circuit,
                                        fitting: maxCompatibleFitting.id,
                                        fittingId: maxCompatibleFitting.id,
                                        diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                        fittingLength: null,
                                      };
                                    }
                                  }
                                  return circuit;
                                });
                                
                                // Обновляем левое торцевое подключение предкотлового подающего коллектора
                                if (params.preBoilerSupplyLeftConnectionType === 'fitting' && params.preBoilerSupplyLeftConnectionFitting) {
                                  if (!isEndFittingCompatibleWithMainPipe(params.preBoilerSupplyLeftConnectionFitting, maxCollectorDiameter)) {
                                    const fittingInfo = fittingsData[params.preBoilerSupplyLeftConnectionFitting];
                                    const category = fittingInfo?.category || params.preBoilerSupplyLeftConnectionFittingCategory || 'Ниппели';
                                    const fittingsInCategory = getFittingsByCategory(category, maxCollectorDiameter, isEndFittingCompatibleWithMainPipe);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      rightConnectionUpdates.preBoilerSupplyLeftConnectionFitting = maxCompatibleFitting.id;
                                      rightConnectionUpdates.preBoilerSupplyLeftConnectionFittingLength = null;
                                    }
                                  }
                                }
                                
                                // Обновляем дополнительные выходы предкотлового подающего коллектора
                                updatedPreBoilerSupplyAdditionalOutlets = preBoilerSupplyAdditionalOutlets.map((outlet) => {
                                  if (outlet.fittingId && !isCenterFittingCompatible(outlet.fittingId, maxCollectorDiameter)) {
                                    const category = outlet.fittingCategory || 'Ниппели';
                                    const fittingsInCategory = getFittingsByCategory(category, maxCollectorDiameter, isCenterFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...outlet,
                                        fittingId: maxCompatibleFitting.id,
                                      };
                                    }
                                  }
                                  return outlet;
                                });
                              }
                              
                              if (params.preBoilerReturnPipeDiameter > maxCollectorDiameter) {
                                collectorDiameterUpdates.preBoilerReturnPipeDiameter = maxCollectorDiameter;
                                
                                // Обновляем центральные фитинги
                                updatedPreBoilerReturnCenterFittings = updateCenterFittingsForDiameter(
                                  preBoilerReturnCenterFittings,
                                  maxCollectorDiameter,
                                  isCenterFittingCompatible,
                                  getFittingsByCategory
                                );
                                
                                // Обновляем контуры
                                updatedPreBoilerReturnCircuits = preBoilerReturnCircuits.map((circuit) => {
                                  const fittingId = circuit.fittingId || circuit.fitting;
                                  if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, maxCollectorDiameter)) {
                                    const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', maxCollectorDiameter, isCircuitFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...circuit,
                                        fitting: maxCompatibleFitting.id,
                                        fittingId: maxCompatibleFitting.id,
                                        diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                        fittingLength: null,
                                      };
                                    }
                                  }
                                  return circuit;
                                });
                                
                                // Обновляем левое торцевое подключение предкотлового обратного коллектора
                                if (params.preBoilerReturnLeftConnectionType === 'fitting' && params.preBoilerReturnLeftConnectionFitting) {
                                  if (!isEndFittingCompatibleWithMainPipe(params.preBoilerReturnLeftConnectionFitting, maxCollectorDiameter)) {
                                    const fittingInfo = fittingsData[params.preBoilerReturnLeftConnectionFitting];
                                    const category = fittingInfo?.category || params.preBoilerReturnLeftConnectionFittingCategory || 'Ниппели';
                                    const fittingsInCategory = getFittingsByCategory(category, maxCollectorDiameter, isEndFittingCompatibleWithMainPipe);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      rightConnectionUpdates.preBoilerReturnLeftConnectionFitting = maxCompatibleFitting.id;
                                      rightConnectionUpdates.preBoilerReturnLeftConnectionFittingLength = null;
                                    }
                                  }
                                }
                                
                                // Обновляем дополнительные выходы предкотлового обратного коллектора
                                updatedPreBoilerReturnAdditionalOutlets = preBoilerReturnAdditionalOutlets.map((outlet) => {
                                  if (outlet.fittingId && !isCenterFittingCompatible(outlet.fittingId, maxCollectorDiameter)) {
                                    const category = outlet.fittingCategory || 'Ниппели';
                                    const fittingsInCategory = getFittingsByCategory(category, maxCollectorDiameter, isCenterFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...outlet,
                                        fittingId: maxCompatibleFitting.id,
                                      };
                                    }
                                  }
                                  return outlet;
                                });
                              }
                            }
                            
                            // Применяем все изменения состояний для центральных фитингов, контуров и дополнительных выходов
                            if (JSON.stringify(updatedSupplyCenterFittings) !== JSON.stringify(supplyCenterFittings)) {
                              setSupplyCenterFittings(updatedSupplyCenterFittings);
                            }
                            if (JSON.stringify(updatedSupplyCircuits) !== JSON.stringify(supplyCircuits)) {
                              setSupplyCircuits(updatedSupplyCircuits);
                            }
                            if (JSON.stringify(updatedSupplyAdditionalOutlets) !== JSON.stringify(supplyAdditionalOutlets)) {
                              setSupplyAdditionalOutlets(updatedSupplyAdditionalOutlets);
                            }
                            if (JSON.stringify(updatedReturnCenterFittings) !== JSON.stringify(returnCenterFittings)) {
                              setReturnCenterFittings(updatedReturnCenterFittings);
                            }
                            if (JSON.stringify(updatedReturnCircuits) !== JSON.stringify(returnCircuits)) {
                              setReturnCircuits(updatedReturnCircuits);
                            }
                            if (JSON.stringify(updatedReturnAdditionalOutlets) !== JSON.stringify(returnAdditionalOutlets)) {
                              setReturnAdditionalOutlets(updatedReturnAdditionalOutlets);
                            }
                            if (JSON.stringify(updatedPreBoilerSupplyCenterFittings) !== JSON.stringify(preBoilerSupplyCenterFittings)) {
                              setPreBoilerSupplyCenterFittings(updatedPreBoilerSupplyCenterFittings);
                            }
                            if (JSON.stringify(updatedPreBoilerSupplyCircuits) !== JSON.stringify(preBoilerSupplyCircuits)) {
                              setPreBoilerSupplyCircuits(updatedPreBoilerSupplyCircuits);
                            }
                            if (JSON.stringify(updatedPreBoilerSupplyAdditionalOutlets) !== JSON.stringify(preBoilerSupplyAdditionalOutlets)) {
                              setPreBoilerSupplyAdditionalOutlets(updatedPreBoilerSupplyAdditionalOutlets);
                            }
                            if (JSON.stringify(updatedPreBoilerReturnCenterFittings) !== JSON.stringify(preBoilerReturnCenterFittings)) {
                              setPreBoilerReturnCenterFittings(updatedPreBoilerReturnCenterFittings);
                            }
                            if (JSON.stringify(updatedPreBoilerReturnCircuits) !== JSON.stringify(preBoilerReturnCircuits)) {
                              setPreBoilerReturnCircuits(updatedPreBoilerReturnCircuits);
                            }
                            if (JSON.stringify(updatedPreBoilerReturnAdditionalOutlets) !== JSON.stringify(preBoilerReturnAdditionalOutlets)) {
                              setPreBoilerReturnAdditionalOutlets(updatedPreBoilerReturnAdditionalOutlets);
                            }
                            
                            updateParam({
                              mainPipeDiameter: newDiameter,
                              leftConnections: updatedLeftConnections,
                              additionalOutlets: updatedAdditionalOutlets,
                              topCapFitting: updatedTopCapFitting,
                              topCapFittingLength: updatedTopCapFitting === params.topCapFitting ? params.topCapFittingLength : null,
                              bottomCapFitting: updatedBottomCapFitting,
                              bottomCapFittingLength: updatedBottomCapFitting === params.bottomCapFitting ? params.bottomCapFittingLength : null,
                              ...capUpdates,
                              ...collectorDiameterUpdates,
                              ...rightConnectionUpdates,
                            });
                            } else {
                              // Квадратный профиль - обновляем профиль и автопереключаем несовместимые фитинги
                              const newProfile = newValue;
                              
                              // Обновляем левые подключения гидроразделителя
                              const updatedLeftConnections = params.leftConnections.map((conn) => {
                                if (conn.fittingId && !isEndFittingCompatibleWithSquareProfile(conn.fittingId, newProfile)) {
                                  const fittingInfo = fittingsData[conn.fittingId];
                                  const category = fittingInfo?.category || 'Ниппели';
                                  const fittingsInCategory = getFittingsByCategory(category, null, null);
                                  const compatibleFittings = fittingsInCategory.filter(f => 
                                    isEndFittingCompatibleWithSquareProfile(f.id, newProfile)
                                  );
                                  
                                  if (compatibleFittings.length > 0) {
                                    const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                    return {
                                      ...conn,
                                      fittingId: maxCompatibleFitting.id,
                                      fittingLength: null,
                                    };
                                  }
                                }
                                // Сбрасываем fittingLength для всех подключений
                                return {
                                  ...conn,
                                  fittingLength: null,
                                };
                              });
                              
                              // Проверяем и обновляем дополнительные выходы гидроразделителя
                              const updatedAdditionalOutlets = params.additionalOutlets.map((outlet) => {
                                if (outlet.fittingId && !isEndFittingCompatibleWithSquareProfile(outlet.fittingId, newProfile)) {
                                  // Фитинг несовместим - ищем максимальный совместимый в той же категории
                                  const category = outlet.fittingCategory || 'Ниппели';
                                  const fittingsInCategory = getFittingsByCategory(category, null, null);
                                  const compatibleFittings = fittingsInCategory.filter(f => 
                                    isEndFittingCompatibleWithSquareProfile(f.id, newProfile)
                                  );
                                  
                                  if (compatibleFittings.length > 0) {
                                    // Нашли совместимые фитинги - берем ПОСЛЕДНИЙ (максимальный)
                                    const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                    return {
                                      ...outlet,
                                      fittingId: maxCompatibleFitting.id,
                                      fittingLength: null,
                                    };
                                  }
                                }
                                return outlet;
                              });
                              
                              // Проверяем и корректируем профили коллекторов
                              const collectorProfileUpdates = {};
                              
                              // Переменные для обновленных фитингов и контуров
                              let updatedSupplyCenterFittings = supplyCenterFittings;
                              let updatedSupplyCircuits = supplyCircuits;
                              let updatedSupplyAdditionalOutlets = supplyAdditionalOutlets;
                              let updatedReturnCenterFittings = returnCenterFittings;
                              let updatedReturnCircuits = returnCircuits;
                              let updatedReturnAdditionalOutlets = returnAdditionalOutlets;
                              let updatedPreBoilerSupplyCenterFittings = preBoilerSupplyCenterFittings;
                              let updatedPreBoilerSupplyCircuits = preBoilerSupplyCircuits;
                              let updatedPreBoilerSupplyAdditionalOutlets = preBoilerSupplyAdditionalOutlets;
                              let updatedPreBoilerReturnCenterFittings = preBoilerReturnCenterFittings;
                              let updatedPreBoilerReturnCircuits = preBoilerReturnCircuits;
                              let updatedPreBoilerReturnAdditionalOutlets = preBoilerReturnAdditionalOutlets;
                              
                              // Переменные для обновленных правых подключений (торцевых)
                              const rightConnectionUpdates = {};
                              
                              // Корректируем профиль подающего коллектора если он несовместим
                              if (!isCollectorProfileCompatible(params.supplyPipeProfile, newProfile)) {
                                // Находим наибольший совместимый профиль
                                const compatibleProfiles = availableSquareProfiles.filter(p => 
                                  isCollectorProfileCompatible(p.value, newProfile)
                                );
                                if (compatibleProfiles.length > 0) {
                                  const maxCompatibleProfile = compatibleProfiles[compatibleProfiles.length - 1].value;
                                  collectorProfileUpdates.supplyPipeProfile = maxCompatibleProfile;
                                  
                                  // Обновляем центральные фитинги
                                  updatedSupplyCenterFittings = supplyCenterFittings.map((fitting) => {
                                    if (fitting.fittingId && !isEndFittingCompatibleWithSquareProfile(fitting.fittingId, maxCompatibleProfile)) {
                                      const category = fitting.fittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(category, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => 
                                        isEndFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile)
                                      );
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...fitting,
                                          fittingId: maxCompatibleFitting.id,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...fitting,
                                      fittingLength: null,
                                    };
                                  });
                                  
                                  // Обновляем контуры
                                  updatedSupplyCircuits = supplyCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, maxCompatibleProfile)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...circuit,
                                      fittingLength: null,
                                    };
                                  });
                                  
                                  // Обновляем правое торцевое подключение подающего коллектора
                                  if (params.hasSupplyRightConnection && params.supplyRightConnectionType === 'fitting' && params.supplyRightConnectionFitting) {
                                    if (!isEndFittingCompatibleWithSquareProfile(params.supplyRightConnectionFitting, maxCompatibleProfile)) {
                                      const currentCategory = params.supplyRightConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isEndFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        rightConnectionUpdates.supplyRightConnectionFitting = maxCompatibleFitting.id;
                                        rightConnectionUpdates.supplyRightConnectionFittingLength = null;
                                      }
                                    } else {
                                      rightConnectionUpdates.supplyRightConnectionFittingLength = null;
                                    }
                                  }
                                  
                                  // Обновляем дополнительные выходы подающего коллектора
                                  updatedSupplyAdditionalOutlets = supplyAdditionalOutlets.map((outlet) => {
                                    if (outlet.type === 'fitting' && outlet.fittingId && !isEndFittingCompatibleWithSquareProfile(outlet.fittingId, maxCompatibleProfile)) {
                                      const category = outlet.fittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(category, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => 
                                        isEndFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile)
                                      );
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...outlet,
                                          fittingId: maxCompatibleFitting.id,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return outlet;
                                  });
                                }
                              }
                              
                              // Корректируем профиль обратного коллектора если он несовместим
                              if (!isCollectorProfileCompatible(params.returnPipeProfile, newProfile)) {
                                // Находим наибольший совместимый профиль
                                const compatibleProfiles = availableSquareProfiles.filter(p => 
                                  isCollectorProfileCompatible(p.value, newProfile)
                                );
                                if (compatibleProfiles.length > 0) {
                                  const maxCompatibleProfile = compatibleProfiles[compatibleProfiles.length - 1].value;
                                  collectorProfileUpdates.returnPipeProfile = maxCompatibleProfile;
                                  
                                  // Обновляем центральные фитинги
                                  updatedReturnCenterFittings = returnCenterFittings.map((fitting) => {
                                    if (fitting.fittingId && !isEndFittingCompatibleWithSquareProfile(fitting.fittingId, maxCompatibleProfile)) {
                                      const category = fitting.fittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(category, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => 
                                        isEndFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile)
                                      );
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...fitting,
                                          fittingId: maxCompatibleFitting.id,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...fitting,
                                      fittingLength: null,
                                    };
                                  });
                                  
                                  // Обновляем контуры
                                  updatedReturnCircuits = returnCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, maxCompatibleProfile)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...circuit,
                                      fittingLength: null,
                                    };
                                  });
                                  
                                  // Обновляем правое торцевое подключение обратного коллектора
                                  if (params.hasReturnRightConnection && params.returnRightConnectionType === 'fitting' && params.returnRightConnectionFitting) {
                                    if (!isEndFittingCompatibleWithSquareProfile(params.returnRightConnectionFitting, maxCompatibleProfile)) {
                                      const currentCategory = params.returnRightConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isEndFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        rightConnectionUpdates.returnRightConnectionFitting = maxCompatibleFitting.id;
                                        rightConnectionUpdates.returnRightConnectionFittingLength = null;
                                      }
                                    } else {
                                      rightConnectionUpdates.returnRightConnectionFittingLength = null;
                                    }
                                  }
                                  
                                  // Обновляем дополнительные выходы обратного коллектора
                                  updatedReturnAdditionalOutlets = returnAdditionalOutlets.map((outlet) => {
                                    if (outlet.type === 'fitting' && outlet.fittingId && !isEndFittingCompatibleWithSquareProfile(outlet.fittingId, maxCompatibleProfile)) {
                                      const category = outlet.fittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(category, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => 
                                        isEndFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile)
                                      );
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...outlet,
                                          fittingId: maxCompatibleFitting.id,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return outlet;
                                  });
                                }
                              }
                              
                              // Корректируем профиль предкотлового подающего коллектора если он несовместим
                              if (hasPreBoilerCollector && !isCollectorProfileCompatible(params.preBoilerSupplyPipeProfile, newProfile)) {
                                const compatibleProfiles = availableSquareProfiles.filter(p => 
                                  isCollectorProfileCompatible(p.value, newProfile)
                                );
                                if (compatibleProfiles.length > 0) {
                                  const maxCompatibleProfile = compatibleProfiles[compatibleProfiles.length - 1].value;
                                  collectorProfileUpdates.preBoilerSupplyPipeProfile = maxCompatibleProfile;
                                  
                                  // Обновляем центральные фитинги
                                  updatedPreBoilerSupplyCenterFittings = preBoilerSupplyCenterFittings.map((fitting) => {
                                    if (fitting.fittingId && !isEndFittingCompatibleWithSquareProfile(fitting.fittingId, maxCompatibleProfile)) {
                                      const category = fitting.fittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(category, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => 
                                        isEndFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile)
                                      );
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...fitting,
                                          fittingId: maxCompatibleFitting.id,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...fitting,
                                      fittingLength: null,
                                    };
                                  });
                                  
                                  // Обновляем контуры
                                  updatedPreBoilerSupplyCircuits = preBoilerSupplyCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, maxCompatibleProfile)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...circuit,
                                      fittingLength: null,
                                    };
                                  });
                                  
                                  // Обновляем дополнительные выходы предкотлового подающего коллектора
                                  updatedPreBoilerSupplyAdditionalOutlets = preBoilerSupplyAdditionalOutlets.map((outlet) => {
                                    if (outlet.type === 'fitting' && outlet.fittingId && !isEndFittingCompatibleWithSquareProfile(outlet.fittingId, maxCompatibleProfile)) {
                                      const category = outlet.fittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(category, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => 
                                        isEndFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile)
                                      );
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...outlet,
                                          fittingId: maxCompatibleFitting.id,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return outlet;
                                  });
                                }
                              }
                              
                              // Корректируем профиль предкотлового обратного коллектора если он несовместим
                              if (hasPreBoilerCollector && !isCollectorProfileCompatible(params.preBoilerReturnPipeProfile, newProfile)) {
                                const compatibleProfiles = availableSquareProfiles.filter(p => 
                                  isCollectorProfileCompatible(p.value, newProfile)
                                );
                                if (compatibleProfiles.length > 0) {
                                  const maxCompatibleProfile = compatibleProfiles[compatibleProfiles.length - 1].value;
                                  collectorProfileUpdates.preBoilerReturnPipeProfile = maxCompatibleProfile;
                                  
                                  // Обновляем центральные фитинги
                                  updatedPreBoilerReturnCenterFittings = preBoilerReturnCenterFittings.map((fitting) => {
                                    if (fitting.fittingId && !isEndFittingCompatibleWithSquareProfile(fitting.fittingId, maxCompatibleProfile)) {
                                      const category = fitting.fittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(category, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => 
                                        isEndFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile)
                                      );
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...fitting,
                                          fittingId: maxCompatibleFitting.id,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...fitting,
                                      fittingLength: null,
                                    };
                                  });
                                  
                                  // Обновляем контуры
                                  updatedPreBoilerReturnCircuits = preBoilerReturnCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, maxCompatibleProfile)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...circuit,
                                      fittingLength: null,
                                    };
                                  });
                                  
                                  // Обновляем дополнительные выходы предкотлового обратного коллектора
                                  updatedPreBoilerReturnAdditionalOutlets = preBoilerReturnAdditionalOutlets.map((outlet) => {
                                    if (outlet.type === 'fitting' && outlet.fittingId && !isEndFittingCompatibleWithSquareProfile(outlet.fittingId, maxCompatibleProfile)) {
                                      const category = outlet.fittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(category, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => 
                                        isEndFittingCompatibleWithSquareProfile(f.id, maxCompatibleProfile)
                                      );
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...outlet,
                                          fittingId: maxCompatibleFitting.id,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return outlet;
                                  });
                                }
                              }
                              
                              // Применяем все обновления
                              updateParam({
                                mainPipeProfile: newProfile,
                                leftConnections: updatedLeftConnections,
                                additionalOutlets: updatedAdditionalOutlets,
                                ...collectorProfileUpdates,
                                ...rightConnectionUpdates,
                              });
                              
                              // Обновляем состояния контуров и фитингов
                              if (JSON.stringify(updatedSupplyCenterFittings) !== JSON.stringify(supplyCenterFittings)) {
                                setSupplyCenterFittings(updatedSupplyCenterFittings);
                              }
                              if (JSON.stringify(updatedSupplyCircuits) !== JSON.stringify(supplyCircuits)) {
                                setSupplyCircuits(updatedSupplyCircuits);
                              }
                              if (JSON.stringify(updatedSupplyAdditionalOutlets) !== JSON.stringify(supplyAdditionalOutlets)) {
                                setSupplyAdditionalOutlets(updatedSupplyAdditionalOutlets);
                              }
                              if (JSON.stringify(updatedReturnCenterFittings) !== JSON.stringify(returnCenterFittings)) {
                                setReturnCenterFittings(updatedReturnCenterFittings);
                              }
                              if (JSON.stringify(updatedReturnCircuits) !== JSON.stringify(returnCircuits)) {
                                setReturnCircuits(updatedReturnCircuits);
                              }
                              if (JSON.stringify(updatedReturnAdditionalOutlets) !== JSON.stringify(returnAdditionalOutlets)) {
                                setReturnAdditionalOutlets(updatedReturnAdditionalOutlets);
                              }
                              if (hasPreBoilerCollector) {
                                if (JSON.stringify(updatedPreBoilerSupplyCenterFittings) !== JSON.stringify(preBoilerSupplyCenterFittings)) {
                                  setPreBoilerSupplyCenterFittings(updatedPreBoilerSupplyCenterFittings);
                                }
                                if (JSON.stringify(updatedPreBoilerSupplyCircuits) !== JSON.stringify(preBoilerSupplyCircuits)) {
                                  setPreBoilerSupplyCircuits(updatedPreBoilerSupplyCircuits);
                                }
                                if (JSON.stringify(updatedPreBoilerSupplyAdditionalOutlets) !== JSON.stringify(preBoilerSupplyAdditionalOutlets)) {
                                  setPreBoilerSupplyAdditionalOutlets(updatedPreBoilerSupplyAdditionalOutlets);
                                }
                                if (JSON.stringify(updatedPreBoilerReturnCenterFittings) !== JSON.stringify(preBoilerReturnCenterFittings)) {
                                  setPreBoilerReturnCenterFittings(updatedPreBoilerReturnCenterFittings);
                                }
                                if (JSON.stringify(updatedPreBoilerReturnCircuits) !== JSON.stringify(preBoilerReturnCircuits)) {
                                  setPreBoilerReturnCircuits(updatedPreBoilerReturnCircuits);
                                }
                                if (JSON.stringify(updatedPreBoilerReturnAdditionalOutlets) !== JSON.stringify(preBoilerReturnAdditionalOutlets)) {
                                  setPreBoilerReturnAdditionalOutlets(updatedPreBoilerReturnAdditionalOutlets);
                                }
                              }
                            }
                          }}
                        >
                          {params.shape === 'round'
                            ? separatorAvailableDiameters.map((d) => (
                                <MenuItem key={d.value} value={d.value}>
                                  {d.label}
                                </MenuItem>
                              ))
                            : separatorAvailableSquareProfiles.map((p) => (
                                <MenuItem key={p.value} value={p.value}>
                                  {p.label}
                                </MenuItem>
                              ))
                          }
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        label="Высота"
                        type="number"
                        size="small"
                        value={(params.leftConnectionsPlacementMode === 'spacing' && !hasPreBoilerCollector) ? calculatedSeparatorHeight : params.separatorHeight}
                        onChange={(e) => updateParam('separatorHeight', Number(e.target.value))}
                        fullWidth
                        disabled={params.leftConnectionsPlacementMode === 'spacing' && !hasPreBoilerCollector}
                        InputProps={{ 
                          readOnly: params.leftConnectionsPlacementMode === 'spacing' && !hasPreBoilerCollector,
                          endAdornment: <Typography variant="caption">мм</Typography> 
                        }}
                        inputProps={{ min: 200, max: 3000, step: 10 }}
                        helperText={(params.leftConnectionsPlacementMode === 'spacing' && !hasPreBoilerCollector) ? "Авто" : ""}
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                    Заглушки/переходы
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Верхняя заглушка/переход */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={params.hasTopCap}
                                  onChange={(e) => updateParam('hasTopCap', e.target.checked)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2" fontWeight={600}>Заглушка/переход сверху</Typography>}
                            />
                          </Grid>
                          {params.hasTopCap && (
                            <>
                              {/* Для квадратных форм только прямые заглушки */}
                              {params.shape === 'square' ? (
                                <Grid item xs={12}>
                                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    Прямая заглушка {params.mainPipeProfile?.replace('x', '×')}
                                  </Typography>
                                </Grid>
                              ) : (
                                <>
                                  <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Элемент</InputLabel>
                                      <Select
                                        value={params.topCapElementType}
                                        label="Элемент"
                                        onChange={(e) => updateParam('topCapElementType', e.target.value)}
                                      >
                                        <MenuItem value="cap">Заглушка</MenuItem>
                                        <MenuItem value="reducer">Переход</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  {params.topCapElementType === 'cap' ? (
                                    <Grid item xs={6}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Тип</InputLabel>
                                        <Select
                                          value={params.topCapType}
                                          label="Тип"
                                          onChange={(e) => updateParam('topCapType', e.target.value)}
                                        >
                                          {params.shape === 'round' && (
                                            <MenuItem value="elliptical">Эллиптическая</MenuItem>
                                          )}
                                          {(params.shape === 'square' || params.mainPipeDiameter <= 101.6) && (
                                            <MenuItem value="straight">Плоская</MenuItem>
                                          )}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                  ) : (
                                    <Grid item xs={6}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Переход</InputLabel>
                                        <Select
                                          value={params.topReducer || ''}
                                          label="Переход"
                                          onChange={(e) => updateParam('topReducer', e.target.value)}
                                        >
                                          {Object.keys(fittingsData)
                                            .filter(key => key.startsWith('Perehod_Konc_') && isAutoConstructorReducerAllowed(key))
                                            .map(key => (
                                              <MenuItem key={key} value={key}>
                                                {fittingsData[key].label || key.replace('Perehod_Konc_', '')}
                                              </MenuItem>
                                            ))}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                    
                    {/* Нижняя заглушка/переход */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={params.hasBottomCap}
                                  onChange={(e) => updateParam('hasBottomCap', e.target.checked)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2" fontWeight={600}>Заглушка/переход снизу</Typography>}
                            />
                          </Grid>
                          {params.hasBottomCap && (
                            <>
                              {/* Для квадратных форм только прямые заглушки */}
                              {params.shape === 'square' ? (
                                <Grid item xs={12}>
                                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    Прямая заглушка {params.mainPipeProfile?.replace('x', '×')}
                                  </Typography>
                                </Grid>
                              ) : (
                                <>
                                  <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Элемент</InputLabel>
                                      <Select
                                        value={params.bottomCapElementType}
                                        label="Элемент"
                                        onChange={(e) => updateParam('bottomCapElementType', e.target.value)}
                                      >
                                        <MenuItem value="cap">Заглушка</MenuItem>
                                        <MenuItem value="reducer">Переход</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  {params.bottomCapElementType === 'cap' ? (
                                    <Grid item xs={6}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Тип</InputLabel>
                                        <Select
                                          value={params.bottomCapType}
                                          label="Тип"
                                          onChange={(e) => updateParam('bottomCapType', e.target.value)}
                                        >
                                          {params.shape === 'round' && (
                                            <MenuItem value="elliptical">Эллиптическая</MenuItem>
                                          )}
                                          {(params.shape === 'square' || params.mainPipeDiameter <= 101.6) && (
                                            <MenuItem value="straight">Плоская</MenuItem>
                                          )}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                  ) : (
                                    <Grid item xs={6}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Переход</InputLabel>
                                        <Select
                                          value={params.bottomReducer || ''}
                                          label="Переход"
                                          onChange={(e) => updateParam('bottomReducer', e.target.value)}
                                        >
                                          {Object.keys(fittingsData)
                                            .filter(key => key.startsWith('Perehod_Konc_') && isAutoConstructorReducerAllowed(key))
                                            .map(key => (
                                              <MenuItem key={key} value={key}>
                                                {fittingsData[key].label || key.replace('Perehod_Konc_', '')}
                                              </MenuItem>
                                            ))}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                    Торцевые подключения
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={params.hasTopConnection}
                            onChange={(e) => updateParam('hasTopConnection', e.target.checked)}
                          />
                        }
                        label="Верхнее подключение"
                      />
                    </Grid>
                    {params.hasTopConnection && (
                      <>
                        <Grid item xs={6} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel shrink>Категория</InputLabel>
                            <Select
                              value={params.topCapFittingCategory || ''}
                              label="Категория"
                              displayEmpty
                              notched
                              onChange={(e) => {
                                const newCategory = e.target.value;
                                updateParam({
                                  topCapFittingCategory: newCategory,
                                  topCapFitting: '',
                                  topCapFittingLength: null
                                });
                              }}
                            >
                              <MenuItem value="">
                                <em>Выбрать</em>
                              </MenuItem>
                              {connectionFittingCategories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                  {cat.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {params.topCapFittingCategory && (
                          <>
                            <Grid item xs={6} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel shrink>Фитинг</InputLabel>
                                <Select
                                  value={params.topCapFitting || ''}
                                  label="Фитинг"
                                  displayEmpty
                                  notched
                                  onChange={(e) => {
                                    const newFitting = e.target.value;
                                    const isCompatibleFitting = params.shape === 'square'
                                      ? isEndFittingCompatibleWithSquareProfile(newFitting, params.mainPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(newFitting, params.mainPipeDiameter);
                                    
                                    if (newFitting && !isCompatibleFitting) {
                                      // Показываем диалог несовместимости
                                      return;
                                    }
                                    
                                    const updates = { topCapFitting: newFitting, topCapFittingLength: null };
                                    
                                    // Автоматическое переключение заглушек/переходов
                                    if (newFitting) {
                                      const capReq = getCapRequirementForFitting(newFitting, params.mainPipeDiameter);
                                      
                                      if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                        // Нужен переход
                                        updates.hasTopCap = true;
                                        updates.topCapElementType = 'reducer';
                                        updates.topReducer = capReq.reducer.key;
                                      } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                        // Нужна заглушка (прямая для квадратных, эллиптическая для круглых)
                                        updates.hasTopCap = true;
                                        updates.topCapElementType = 'cap';
                                        updates.topCapType = params.shape === 'square' ? 'straight' : 'elliptical';
                                        updates.topReducer = null;
                                      } else if (capReq.fitsFlush) {
                                        // Впритык - закрываем заглушки
                                        updates.hasTopCap = false;
                                        updates.topReducer = null;
                                      }
                                    } else {
                                      // Если фитинг пустой - сбрасываем всё
                                      updates.hasTopCap = false;
                                      updates.topCapElementType = 'cap';
                                      updates.topCapType = 'straight';
                                      updates.topReducer = null;
                                    }
                                    
                                    updateParam(updates);
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Выбрать</em>
                                  </MenuItem>
                                  {getFittingsByCategory(params.topCapFittingCategory, params.mainPipeDiameter).map((f) => {
                                    const isCompatible = params.shape === 'square'
                                      ? isEndFittingCompatibleWithSquareProfile(f.id, params.mainPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(f.id, params.mainPipeDiameter);
                                    return (
                                      <MenuItem 
                                        key={f.id} 
                                        value={f.id}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                            });
                                          }
                                        }}
                                        sx={{
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                          cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                          '&:hover': {
                                            backgroundColor: !isCompatible ? 'transparent' : undefined,
                                          },
                                        }}
                                      >
                                        {f.label}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Grid>
                            {params.topCapFitting && shouldShowConnectionLengthField(params.topCapFitting, params.mainPipeDiameter) && (
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  label="Вылет фитинга"
                                  type="number"
                                  size="small"
                                  value={params.topCapFittingLength ?? ''}
                                  placeholder="Авто"
                                  onChange={(e) => updateParam('topCapFittingLength', e.target.value ? Number(e.target.value) : null)}
                                  fullWidth
                                  InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            )}
                          </>
                        )}
                      </>
                    )}
                    
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={params.hasBottomConnection}
                            onChange={(e) => updateParam('hasBottomConnection', e.target.checked)}
                          />
                        }
                        label="Нижнее подключение"
                      />
                    </Grid>
                    {params.hasBottomConnection && (
                      <>
                        <Grid item xs={6} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel shrink>Категория</InputLabel>
                            <Select
                              value={params.bottomCapFittingCategory || ''}
                              label="Категория"
                              displayEmpty
                              notched
                              onChange={(e) => {
                                const newCategory = e.target.value;
                                updateParam({
                                  bottomCapFittingCategory: newCategory,
                                  bottomCapFitting: '',
                                  bottomCapFittingLength: null
                                });
                              }}
                            >
                              <MenuItem value="">
                                <em>Выбрать</em>
                              </MenuItem>
                              {connectionFittingCategories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                  {cat.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {params.bottomCapFittingCategory && (
                          <>
                            <Grid item xs={6} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel shrink>Фитинг</InputLabel>
                                <Select
                                  value={params.bottomCapFitting || ''}
                                  label="Фитинг"
                                  displayEmpty
                                  notched
                                  onChange={(e) => {
                                    const newFitting = e.target.value;
                                    const isCompatibleFitting = params.shape === 'square'
                                      ? isEndFittingCompatibleWithSquareProfile(newFitting, params.mainPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(newFitting, params.mainPipeDiameter);
                                    
                                    if (newFitting && !isCompatibleFitting) {
                                      // Показываем диалог несовместимости
                                      return;
                                    }
                                    
                                    const updates = { bottomCapFitting: newFitting, bottomCapFittingLength: null };
                                    
                                    // Автоматическое переключение заглушек/переходов
                                    if (newFitting) {
                                      const capReq = getCapRequirementForFitting(newFitting, params.mainPipeDiameter);
                                      
                                      if (capReq.needsCap && capReq.capType === 'reducer' && capReq.reducer) {
                                        // Нужен переход
                                        updates.hasBottomCap = true;
                                        updates.bottomCapElementType = 'reducer';
                                        updates.bottomReducer = capReq.reducer.key;
                                      } else if (capReq.needsCap && capReq.capType === 'elliptical') {
                                        // Нужна заглушка (прямая для квадратных, эллиптическая для круглых)
                                        updates.hasBottomCap = true;
                                        updates.bottomCapElementType = 'cap';
                                        updates.bottomCapType = params.shape === 'square' ? 'straight' : 'elliptical';
                                        updates.bottomReducer = null;
                                      } else if (capReq.fitsFlush) {
                                        // Впритык - закрываем заглушки
                                        updates.hasBottomCap = false;
                                        updates.bottomReducer = null;
                                      }
                                    } else {
                                      // Если фитинг пустой - сбрасываем всё
                                      updates.hasBottomCap = false;
                                      updates.bottomCapElementType = 'cap';
                                      updates.bottomCapType = 'straight';
                                      updates.bottomReducer = null;
                                    }
                                    
                                    updateParam(updates);
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Выбрать</em>
                                  </MenuItem>
                                  {getFittingsByCategory(params.bottomCapFittingCategory, params.mainPipeDiameter).map((f) => {
                                    const isCompatible = params.shape === 'square'
                                      ? isEndFittingCompatibleWithSquareProfile(f.id, params.mainPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(f.id, params.mainPipeDiameter);
                                    return (
                                      <MenuItem 
                                        key={f.id} 
                                        value={f.id}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                            });
                                          }
                                        }}
                                        sx={{
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                          cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                          '&:hover': {
                                            backgroundColor: !isCompatible ? 'transparent' : undefined,
                                          },
                                        }}
                                      >
                                        {f.label}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Grid>
                            {params.bottomCapFitting && shouldShowConnectionLengthField(params.bottomCapFitting, params.mainPipeDiameter) && (
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  label="Вылет фитинга"
                                  type="number"
                                  size="small"
                                  value={params.bottomCapFittingLength ?? ''}
                                  placeholder="Авто"
                                  onChange={(e) => updateParam('bottomCapFittingLength', e.target.value ? Number(e.target.value) : null)}
                                  fullWidth
                                  InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Grid>

                  {/* Левые подключения */}
                  {!hasPreBoilerCollector && (
                    <>
                  <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                    {isSystemFlipped ? 'Правые подключения' : 'Левые подключения'}
                  </Typography>
                  
                  {/* Переключатель режима размещения */}
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          Режим размещения подключений:
                        </Typography>
                        <ToggleButtonGroup
                          value={params.leftConnectionsPlacementMode}
                          exclusive
                          onChange={(e, newMode) => {
                            if (newMode) updateParam('leftConnectionsPlacementMode', newMode);
                          }}
                          size="small"
                        >
                          <ToggleButton value="spacing">
                            Межосевое
                          </ToggleButton>
                          <ToggleButton value="offsets">
                            Отступы
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </FormControl>
                    </Grid>
                  </Grid>
                  
                  {/* Поля для режима межосевого */}
                  {params.leftConnectionsPlacementMode === 'spacing' && (
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          label="Отступ первого"
                          type="number"
                          size="small"
                          value={params.leftConnectionsFirstOffset}
                          onChange={(e) => updateParam('leftConnectionsFirstOffset', Number(e.target.value))}
                          fullWidth
                          InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          label="Отступ последнего"
                          type="number"
                          size="small"
                          value={params.leftConnectionsLastOffset}
                          onChange={(e) => updateParam('leftConnectionsLastOffset', Number(e.target.value))}
                          fullWidth
                          InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          label="Между подключениями"
                          type="number"
                          size="small"
                          value={params.leftConnectionsSpacing}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            updateParam('leftConnectionsSpacing', value);
                            // Применяем ко всем подключениям
                            const updatedConnections = params.leftConnections.map(conn => ({
                              ...conn,
                              spacingToNext: value,
                            }));
                            updateParam('leftConnections', updatedConnections);
                          }}
                          fullWidth
                          InputProps={{ endAdornment: <Typography variant="caption">мм</Typography> }}
                          helperText="Ко всем"
                        />
                      </Grid>
                    </Grid>
                  )}
                  
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">Количество:</Typography>
                        <IconButton
                          size="small"
                          onClick={() => deleteLeftConnection(params.leftConnections.length - 1)}
                          disabled={params.leftConnections.length <= 1}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center' }}>
                          {params.leftConnections.length}
                        </Typography>
                        <IconButton size="small" onClick={addLeftConnection}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <TableContainer sx={{ maxHeight: '12.5rem', border: '0.0625rem solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, p: 0.5, width: 20 }}></TableCell>
                          <TableCell sx={{ fontWeight: 600, p: 0.5, width: 30 }}>№</TableCell>
                          <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Категория</TableCell>
                          <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Фитинг</TableCell>
                          <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Вылет</TableCell>
                          <TableCell sx={{ fontWeight: 600, p: 0.5 }}>
                            {params.leftConnectionsPlacementMode === 'spacing' ? 'До след.' : 'Отступ'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, p: 0.5, width: 40 }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {params.leftConnections.map((conn, index) => {
                          const fittingInfo = fittingsData[conn.fittingId];
                          const category = fittingInfo?.category || 'Ниппели';
                          const fittingsInCategory = params.shape === 'square'
                            ? getFittingsByCategory(category, null, null).map(f => ({
                                ...f,
                                isCompatible: isEndFittingCompatibleWithSquareProfile(f.id, params.mainPipeProfile)
                              }))
                            : getFittingsByCategory(category, params.mainPipeDiameter, isFittingCompatibleWithMainPipe);

                          return (
                            <TableRow 
                              key={conn.id}
                              draggable
                              onDragStart={() => setDraggedLeftIndex(index)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => {
                                if (draggedLeftIndex !== null && draggedLeftIndex !== index) {
                                  moveLeftConnection(draggedLeftIndex, index);
                                }
                                setDraggedLeftIndex(null);
                              }}
                              onDragEnd={() => setDraggedLeftIndex(null)}
                              sx={{
                                cursor: 'move',
                                opacity: draggedLeftIndex === index ? 0.5 : 1,
                                '&:hover': { backgroundColor: 'action.hover' }
                              }}
                            >
                              <TableCell sx={{ p: 0.5 }}>
                                <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'grab' }} />
                              </TableCell>
                              <TableCell sx={{ p: 0.5 }}>{index + 1}</TableCell>
                              <TableCell sx={{ p: 0.5 }}>
                                <Select
                                  size="small"
                                  value={category}
                                  onChange={(e) => {
                                    const newCategory = e.target.value;
                                    const newFittings = params.shape === 'square'
                                      ? getFittingsByCategory(newCategory, null, null)
                                          .filter(f => isEndFittingCompatibleWithSquareProfile(f.id, params.mainPipeProfile))
                                      : getFittingsByCategory(newCategory, params.mainPipeDiameter, isFittingCompatibleWithMainPipe)
                                          .filter(f => f.isCompatible);
                                    if (newFittings.length > 0) {
                                      updateLeftConnection(index, 'fittingId', newFittings[0].id);
                                    }
                                  }}
                                  sx={{ minWidth: 90, fontSize: '0.75rem' }}
                                >
                                  {connectionFittingCategories.map(cat => (
                                    <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>{cat.label}</MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell sx={{ p: 0.5 }}>
                                <Select
                                  size="small"
                                  value={conn.fittingId}
                                  onChange={(e) => {
                                    const newFitting = e.target.value;
                                    const isCompatibleFitting = params.shape === 'square'
                                      ? isEndFittingCompatibleWithSquareProfile(newFitting, params.mainPipeProfile)
                                      : isFittingCompatibleWithMainPipe(newFitting, params.mainPipeDiameter);
                                    
                                    if (!isCompatibleFitting) {
                                      setIncompatibilityDialog({
                                        open: true,
                                        message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                      });
                                      return;
                                    }
                                    updateLeftConnection(index, 'fittingId', newFitting);
                                    updateLeftConnection(index, 'fittingLength', null);
                                  }}
                                  sx={{ minWidth: 90, fontSize: '0.75rem' }}
                                >
                                  {fittingsInCategory.map(f => (
                                    <MenuItem 
                                      key={f.id} 
                                      value={f.id} 
                                      onClick={(e) => {
                                        if (!f.isCompatible) {
                                          e.stopPropagation();
                                          setIncompatibilityDialog({
                                            open: true,
                                            message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                          });
                                        }
                                      }}
                                      sx={{ 
                                        fontSize: '0.8rem',
                                        textDecoration: !f.isCompatible ? 'line-through' : 'none',
                                        color: !f.isCompatible ? 'text.disabled' : 'inherit',
                                        cursor: !f.isCompatible ? 'not-allowed' : 'pointer',
                                        '&:hover': {
                                          backgroundColor: !f.isCompatible ? 'transparent' : undefined,
                                        },
                                      }}
                                    >
                                      {f.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell sx={{ p: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={conn.fittingLength ?? ''}
                                  placeholder="Авто"
                                  onChange={(e) => updateLeftConnection(index, 'fittingLength', e.target.value ? Number(e.target.value) : null)}
                                  sx={{ width: 65 }}
                                  InputProps={{ sx: { fontSize: '0.8rem' } }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </TableCell>
                              <TableCell sx={{ p: 0.5 }}>
                                {params.leftConnectionsPlacementMode === 'spacing' ? (
                                  // Режим межосевого: показываем "До след." или прочерк для последнего
                                  index === params.leftConnections.length - 1 ? (
                                    <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                      —
                                    </Typography>
                                  ) : (
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={conn.spacingToNext ?? params.leftConnectionsSpacing}
                                      onChange={(e) => updateLeftConnection(index, 'spacingToNext', e.target.value === '' ? null : Number(e.target.value))}
                                      placeholder={conn.spacingToNext === null ? 'Общее' : ''}
                                      sx={{ width: 65 }}
                                      InputProps={{ sx: { fontSize: '0.8rem' } }}
                                    />
                                  )
                                ) : (
                                  // Режим отступов: показываем "Отступ" с кнопкой направления
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={conn.offsetFromTop}
                                      onChange={(e) => updateLeftConnection(index, 'offsetFromTop', Number(e.target.value))}
                                      sx={{ width: 65 }}
                                      InputProps={{ sx: { fontSize: '0.8rem' } }}
                                    />
                                    <Tooltip title={(conn.offsetDirection || 'top') === 'top' ? "Отсчёт сверху" : "Отсчёт снизу"}>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const newDirection = (conn.offsetDirection || 'top') === 'top' ? 'bottom' : 'top';
                                          updateLeftConnection(index, 'offsetDirection', newDirection);
                                        }}
                                        sx={{ p: 0.3 }}
                                      >
                                        {(conn.offsetDirection || 'top') === 'top' ? (
                                          <ArrowDownwardIcon fontSize="small" />
                                        ) : (
                                          <ArrowUpwardIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ p: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => deleteLeftConnection(index)}
                                  disabled={params.leftConnections.length <= 1}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                    </>
                  )}

                  {/* Дополнительные выходы */}
                  <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 1 }}>
                    Дополнительные выходы
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">Количество:</Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeAdditionalOutlet(params.additionalOutlets.length - 1)}
                          disabled={params.additionalOutlets.length === 0}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center' }}>
                          {params.additionalOutlets.length}
                        </Typography>
                        <IconButton size="small" onClick={addAdditionalOutlet}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {params.additionalOutlets.length > 0 && (
                    <TableContainer sx={{ maxHeight: '12.5rem', border: '0.0625rem solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, p: 0.5, width: 20 }}></TableCell>
                            <TableCell sx={{ fontWeight: 600, p: 0.5, width: 30 }}>№</TableCell>
                            <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Категория</TableCell>
                            <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Фитинг</TableCell>
                            <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Размещение</TableCell>
                            <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Сторона</TableCell>
                            <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Вылет</TableCell>
                            <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Значение</TableCell>
                            <TableCell sx={{ fontWeight: 600, p: 0.5, width: 40 }}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {params.additionalOutlets.map((outlet, index) => {
                            const category = outlet.fittingCategory || 'Ниппели';
                            const fittingsInCategory = params.shape === 'square'
                              ? getFittingsByCategory(category, null, null).map(f => ({
                                  ...f,
                                  isCompatible: isEndFittingCompatibleWithSquareProfile(f.id, params.mainPipeProfile)
                                }))
                              : getFittingsByCategory(category, params.mainPipeDiameter, isFittingCompatibleWithMainPipe);
                            
                            const allowedCategories = CIRCUIT_FITTING_CATEGORIES;

                            return (
                              <TableRow 
                                key={outlet.id}
                                sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                              >
                                <TableCell sx={{ p: 0.5 }}>
                                  <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </TableCell>
                                <TableCell sx={{ p: 0.5 }}>{index + 1}</TableCell>
                                
                                {/* Категория */}
                                <TableCell sx={{ p: 0.5 }}>
                                  <Select
                                    size="small"
                                    value={category}
                                    onChange={(e) => {
                                      const newCategory = e.target.value;
                                      const newFittings = params.shape === 'square'
                                        ? getFittingsByCategory(newCategory, null, null)
                                            .filter(f => isEndFittingCompatibleWithSquareProfile(f.id, params.mainPipeProfile))
                                        : getFittingsByCategory(newCategory, params.mainPipeDiameter, isFittingCompatibleWithMainPipe)
                                            .filter(f => f.isCompatible);
                                      const compatibleFittings = newFittings;
                                      if (compatibleFittings.length > 0) {
                                        updateAdditionalOutlet(index, 'fittingCategory', newCategory);
                                        updateAdditionalOutlet(index, 'fittingId', compatibleFittings[0].id);
                                      } else if (newFittings.length > 0) {
                                        // Если нет совместимых, берем первый несовместимый
                                        updateAdditionalOutlet(index, 'fittingCategory', newCategory);
                                        updateAdditionalOutlet(index, 'fittingId', newFittings[0].id);
                                      }
                                    }}
                                    sx={{ minWidth: 90, fontSize: '0.75rem' }}
                                  >
                                    {allowedCategories.map(cat => (
                                      <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>{cat.label}</MenuItem>
                                    ))}
                                  </Select>
                                </TableCell>
                                
                                {/* Фитинг */}
                                <TableCell sx={{ p: 0.5 }}>
                                  <Select
                                    size="small"
                                    value={outlet.fittingId}
                                    onChange={(e) => {
                                      const newFitting = e.target.value;
                                      const isCompatibleFitting = params.shape === 'square'
                                        ? isEndFittingCompatibleWithSquareProfile(newFitting, params.mainPipeProfile)
                                        : isFittingCompatibleWithMainPipe(newFitting, params.mainPipeDiameter);
                                      
                                      if (!isCompatibleFitting) {
                                        setIncompatibilityDialog({
                                          open: true,
                                          message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                        });
                                        return;
                                      }
                                      updateAdditionalOutlet(index, 'fittingId', newFitting);
                                    }}
                                    sx={{ minWidth: 100, fontSize: '0.75rem' }}
                                  >
                                    {fittingsInCategory.map(fitting => (
                                      <MenuItem 
                                        key={fitting.id} 
                                        value={fitting.id}
                                        onClick={(e) => {
                                          if (!fitting.isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          fontSize: '0.8rem',
                                          textDecoration: !fitting.isCompatible ? 'line-through' : 'none',
                                          color: !fitting.isCompatible ? 'text.disabled' : 'inherit',
                                          cursor: !fitting.isCompatible ? 'not-allowed' : 'pointer',
                                          '&:hover': {
                                            backgroundColor: !fitting.isCompatible ? 'transparent' : undefined,
                                          },
                                        }}
                                      >
                                        {fitting.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </TableCell>
                                
                                {/* Размещение */}
                                <TableCell sx={{ p: 0.5 }}>
                                  <Select
                                    size="small"
                                    value={outlet.positionType || 'center'}
                                    onChange={(e) => updateAdditionalOutlet(index, 'positionType', e.target.value)}
                                    sx={{ minWidth: 110, fontSize: '0.75rem' }}
                                  >
                                    <MenuItem value="center" sx={{ fontSize: '0.8rem' }}>По середине</MenuItem>
                                    <MenuItem value="distance" sx={{ fontSize: '0.8rem' }}>На расстоянии</MenuItem>
                                  </Select>
                                </TableCell>
                                
                                {/* Сторона */}
                                <TableCell sx={{ p: 0.5 }}>
                                  <Select
                                    size="small"
                                    value={outlet.side || 'both'}
                                    onChange={(e) => updateAdditionalOutlet(index, 'side', e.target.value)}
                                    sx={{ minWidth: 110, fontSize: '0.75rem' }}
                                  >
                                    <MenuItem value="both" sx={{ fontSize: '0.8rem' }}>С 2-х сторон</MenuItem>
                                    <MenuItem value="front" sx={{ fontSize: '0.8rem' }}>Спереди</MenuItem>
                                    <MenuItem value="back" sx={{ fontSize: '0.8rem' }}>Сзади</MenuItem>
                                    <MenuItem value="left" sx={{ fontSize: '0.8rem' }}>Слева</MenuItem>
                                    <MenuItem value="right" sx={{ fontSize: '0.8rem' }}>Справа</MenuItem>
                                  </Select>
                                </TableCell>
                                
                                <TableCell sx={{ p: 0.5 }}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={(outlet.flangePipeLength ?? outlet.fittingLength) ?? ''}
                                    onChange={(e) => {
                                      const next = e.target.value === '' ? null : Number(e.target.value);
                                      updateAdditionalOutlet(index, 'fittingLength', next);
                                      updateAdditionalOutlet(index, 'flangePipeLength', next);
                                    }}
                                    sx={{ width: 65 }}
                                    placeholder="Авто"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                      endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                      sx: { fontSize: '0.8rem' }
                                    }}
                                    title="Вылет от наружной стенки"
                                  />
                                </TableCell>
                                
                                {/* Значение (динамическое поле) */}
                                <TableCell sx={{ p: 0.5 }}>
                                  {outlet.positionType === 'center' ? (
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                      —
                                    </Typography>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <TextField
                                        type="number"
                                        size="small"
                                        value={outlet.distanceFromEdge === 0 ? '' : (outlet.distanceFromEdge ?? 50)}
                                        onChange={(e) => updateAdditionalOutlet(index, 'distanceFromEdge', parseFloat(e.target.value) || 0)}
                                        sx={{ width: 80 }}
                                        InputProps={{ 
                                          endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                          sx: { fontSize: '0.8rem' }
                                        }}
                                        title="Расстояние от края гидроразделителя"
                                      />
                                      <Tooltip title={outlet.distanceDirection === 'right' ? 'Отсчёт снизу' : 'Отсчёт сверху'}>
                                        <IconButton
                                          size="small"
                                          onClick={() => updateAdditionalOutlet(index, 'distanceDirection', outlet.distanceDirection === 'right' ? 'left' : 'right')}
                                          sx={{ 
                                            p: 0.3,
                                            '&:hover': { bgcolor: 'action.hover' }
                                          }}
                                        >
                                          {outlet.distanceDirection === 'right' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  )}
                                </TableCell>
                                
                                {/* Удалить */}
                                <TableCell sx={{ p: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeAdditionalOutlet(index)}
                                    sx={{ color: 'error.main' }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* ВКЛАДКА 2: КОЛЛЕКТОР */}
              {tabValue === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Параметры распределительного коллектора
                  </Typography>

                  {/* Подвкладки для коллектора */}
                  <Tabs 
                    value={collectorTabValue} 
                    onChange={(e, v) => setCollectorTabValue(v)} 
                    sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Tab label="Основные параметры" />
                    <Tab label="Контуры" />
                    <Tab label="Подключения" />
                    <Tab label="Заглушки" />
                  </Tabs>

                  {/* Подвкладка 1: Основные параметры */}
                  {collectorTabValue === 0 && (
                    <Paper sx={{ p: 3, bgcolor: '#fafafa' }} elevation={0}>
                      <Typography variant="h6" gutterBottom>Основные параметры</Typography>
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>{params.shape === 'round' ? 'Диаметр подающего коллектора' : 'Профиль подающего коллектора'}</InputLabel>
                            <Select
                              value={params.shape === 'round' ? params.supplyPipeDiameter : (params.supplyPipeProfile || '80x80')}
                              label={params.shape === 'round' ? 'Диаметр подающего коллектора' : 'Профиль подающего коллектора'}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                
                                if (params.shape === 'round') {
                                const newDiameter = newValue;
                                
                                // Проверяем совместимость выбранного диаметра
                                if (!isCollectorDiameterCompatible(newDiameter, params.mainPipeDiameter)) {
                                  setIncompatibilityDialog({
                                    open: true,
                                    message: `Диаметр трубы коллектора ${newDiameter}мм несовместим с диаметром гидроразделителя ${params.mainPipeDiameter}мм. Максимально допустимый диаметр: ${getMaxCollectorPipeDiameter(params.mainPipeDiameter)}мм`,
                                  });
                                  return; // Прерываем обработку
                                }
                                
                                // Обновляем центральные фитинги подающего коллектора
                                const updatedSupplyCenterFittings = updateCenterFittingsForDiameter(
                                  supplyCenterFittings,
                                  newDiameter,
                                  isCenterFittingCompatible,
                                  getFittingsByCategory
                                );
                                if (JSON.stringify(updatedSupplyCenterFittings) !== JSON.stringify(supplyCenterFittings)) {
                                  setSupplyCenterFittings(updatedSupplyCenterFittings);
                                }
                                
                                // Обновляем контуры подающего коллектора
                                const updatedSupplyCircuits = supplyCircuits.map((circuit) => {
                                  const fittingId = circuit.fittingId || circuit.fitting;
                                  if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, newDiameter)) {
                                    const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', newDiameter, isCircuitFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...circuit,
                                        fitting: maxCompatibleFitting.id,
                                        fittingId: maxCompatibleFitting.id,
                                        diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                        fittingLength: null,
                                      };
                                    } else {
                                      return {
                                        ...circuit,
                                        type: 'pipe',
                                        fitting: null,
                                        fittingId: null,
                                        fittingCategory: null,
                                        fittingLength: null,
                                        diameter: newDiameter,
                                        length: 100,
                                      };
                                    }
                                  }
                                  return circuit;
                                });
                                if (JSON.stringify(updatedSupplyCircuits) !== JSON.stringify(supplyCircuits)) {
                                  setSupplyCircuits(updatedSupplyCircuits);
                                }
                                
                                // Обновляем дополнительные выходы подающего коллектора
                                const updatedSupplyAdditionalOutlets = supplyAdditionalOutlets.map((outlet) => {
                                  if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatible(outlet.fittingId, newDiameter)) {
                                    const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', newDiameter, isAdditionalOutletFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...outlet,
                                        fittingId: maxCompatibleFitting.id,
                                        diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                                        fittingLength: null,
                                      };
                                    } else {
                                      return {
                                        ...outlet,
                                        type: 'pipe',
                                        fittingId: null,
                                        fittingCategory: null,
                                        fittingLength: null,
                                        diameter: newDiameter,
                                        length: 100,
                                      };
                                    }
                                  }
                                  return outlet;
                                  });
                                  if (JSON.stringify(updatedSupplyAdditionalOutlets) !== JSON.stringify(supplyAdditionalOutlets)) {
                                    setSupplyAdditionalOutlets(updatedSupplyAdditionalOutlets);
                                  }
                                  
                                  // Обновляем правое торцевое подключение подающего коллектора
                                  const supplyRightConnectionUpdates = {};
                                  if (params.hasSupplyRightConnection && params.supplyRightConnectionType === 'fitting' && params.supplyRightConnectionFitting) {
                                    if (!isEndFittingCompatibleWithMainPipe(params.supplyRightConnectionFitting, newDiameter)) {
                                      // Фитинг несовместим - пытаемся найти совместимый в той же категории
                                      const currentCategory = params.supplyRightConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        supplyRightConnectionUpdates.supplyRightConnectionFitting = maxCompatibleFitting.id;
                                        supplyRightConnectionUpdates.supplyRightConnectionFittingLength = null;
                                      }
                                    }
                                    
                                    const capUpdates = getCapUpdatesForConnectionFitting(
                                      supplyRightConnectionUpdates.supplyRightConnectionFitting || params.supplyRightConnectionFitting,
                                      newDiameter,
                                      'right',
                                      'supply',
                                      params.shape
                                    );
                                    Object.assign(supplyRightConnectionUpdates, capUpdates);
                                  }
                                  // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                                  else if (params.hasSupplyRightCap && params.supplyRightCapElementType === 'cap' && params.shape !== 'square') {
                                    const ellipticalCap = findEllipticalCap(newDiameter);
                                    if (ellipticalCap && params.supplyRightCapType !== 'elliptical') {
                                      supplyRightConnectionUpdates.supplyRightCapType = 'elliptical';
                                    }
                                  }
                                  
                                  if (Object.keys(supplyRightConnectionUpdates).length > 0) {
                                    updateParam(supplyRightConnectionUpdates);
                                  }
                                  
                                  // Обновляем левое торцевое подключение подающего коллектора
                                  const supplyLeftConnectionUpdates = {};
                                  if (params.hasSupplyLeftConnection && params.supplyLeftConnectionType === 'fitting' && params.supplyLeftConnectionFitting) {
                                    if (!isEndFittingCompatibleWithMainPipe(params.supplyLeftConnectionFitting, newDiameter)) {
                                      // Фитинг несовместим - пытаемся найти совместимый в той же категории
                                      const currentCategory = params.supplyLeftConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        supplyLeftConnectionUpdates.supplyLeftConnectionFitting = maxCompatibleFitting.id;
                                        supplyLeftConnectionUpdates.supplyLeftConnectionFittingLength = null;
                                      }
                                    }
                                    
                                    const capUpdates = getCapUpdatesForConnectionFitting(
                                      supplyLeftConnectionUpdates.supplyLeftConnectionFitting || params.supplyLeftConnectionFitting,
                                      newDiameter,
                                      'left',
                                      'supply',
                                      params.shape
                                    );
                                    Object.assign(supplyLeftConnectionUpdates, capUpdates);
                                  }
                                  // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                                  else if (params.hasSupplyLeftCap && params.supplyLeftCapElementType === 'cap' && params.shape !== 'square') {
                                    const ellipticalCap = findEllipticalCap(newDiameter);
                                    if (ellipticalCap && params.supplyLeftCapType !== 'elliptical') {
                                      supplyLeftConnectionUpdates.supplyLeftCapType = 'elliptical';
                                    }
                                  }
                                  
                                  if (Object.keys(supplyLeftConnectionUpdates).length > 0) {
                                    updateParam(supplyLeftConnectionUpdates);
                                  }
                                  
                                if (diametersLinked) {
                                  updateParam({ supplyPipeDiameter: newDiameter, returnPipeDiameter: newDiameter });
                                  
                                  // Обновляем центральные фитинги обратного коллектора
                                  const updatedReturnCenterFittings = updateCenterFittingsForDiameter(
                                    returnCenterFittings,
                                    newDiameter,
                                    isCenterFittingCompatible,
                                    getFittingsByCategory
                                  );
                                  if (JSON.stringify(updatedReturnCenterFittings) !== JSON.stringify(returnCenterFittings)) {
                                    setReturnCenterFittings(updatedReturnCenterFittings);
                                  }
                                  
                                  // Обновляем контуры обратного коллектора
                                  const updatedReturnCircuits = returnCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, newDiameter)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', newDiameter, isCircuitFittingCompatible);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      } else {
                                        return {
                                          ...circuit,
                                          type: 'pipe',
                                          fitting: null,
                                          fittingId: null,
                                          fittingCategory: null,
                                          fittingLength: null,
                                          diameter: newDiameter,
                                          length: 100,
                                        };
                                      }
                                    }
                                    return circuit;
                                  });
                                  if (JSON.stringify(updatedReturnCircuits) !== JSON.stringify(returnCircuits)) {
                                    setReturnCircuits(updatedReturnCircuits);
                                  }
                                  
                                  // Обновляем дополнительные выходы обратного коллектора
                                  const updatedReturnAdditionalOutlets = returnAdditionalOutlets.map((outlet) => {
                                    if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatible(outlet.fittingId, newDiameter)) {
                                      const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', newDiameter, isAdditionalOutletFittingCompatible);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...outlet,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                                          fittingLength: null,
                                        };
                                      } else {
                                        return {
                                          ...outlet,
                                          type: 'pipe',
                                          fittingId: null,
                                          fittingCategory: null,
                                          fittingLength: null,
                                          diameter: newDiameter,
                                          length: 100,
                                        };
                                      }
                                    }
                                    return outlet;
                                  });
                                  if (JSON.stringify(updatedReturnAdditionalOutlets) !== JSON.stringify(returnAdditionalOutlets)) {
                                    setReturnAdditionalOutlets(updatedReturnAdditionalOutlets);
                                  }
                                  
                                  // Обновляем правое торцевое подключение обратного коллектора
                                  const returnRightConnectionUpdates = {};
                                  if (params.hasReturnRightConnection && params.returnRightConnectionType === 'fitting' && params.returnRightConnectionFitting) {
                                    if (!isEndFittingCompatibleWithMainPipe(params.returnRightConnectionFitting, newDiameter)) {
                                      // Фитинг несовместим - пытаемся найти совместимый в той же категории
                                      const currentCategory = params.returnRightConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        returnRightConnectionUpdates.returnRightConnectionFitting = maxCompatibleFitting.id;
                                        returnRightConnectionUpdates.returnRightConnectionFittingLength = null;
                                      }
                                    }
                                    
                                    const capUpdates = getCapUpdatesForConnectionFitting(
                                      returnRightConnectionUpdates.returnRightConnectionFitting || params.returnRightConnectionFitting,
                                      newDiameter,
                                      'right',
                                      'return',
                                      params.shape
                                    );
                                    Object.assign(returnRightConnectionUpdates, capUpdates);
                                  }
                                  // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                                  else if (params.hasReturnRightCap && params.returnRightCapElementType === 'cap' && params.shape !== 'square') {
                                    const ellipticalCap = findEllipticalCap(newDiameter);
                                    if (ellipticalCap && params.returnRightCapType !== 'elliptical') {
                                      returnRightConnectionUpdates.returnRightCapType = 'elliptical';
                                    }
                                  }
                                  
                                  if (Object.keys(returnRightConnectionUpdates).length > 0) {
                                    updateParam(returnRightConnectionUpdates);
                                  }
                                  
                                  // Обновляем левое торцевое подключение обратного коллектора
                                  const returnLeftConnectionUpdates = {};
                                  if (params.hasReturnLeftConnection && params.returnLeftConnectionType === 'fitting' && params.returnLeftConnectionFitting) {
                                    if (!isEndFittingCompatibleWithMainPipe(params.returnLeftConnectionFitting, newDiameter)) {
                                      // Фитинг несовместим - пытаемся найти совместимый в той же категории
                                      const currentCategory = params.returnLeftConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        returnLeftConnectionUpdates.returnLeftConnectionFitting = maxCompatibleFitting.id;
                                        returnLeftConnectionUpdates.returnLeftConnectionFittingLength = null;
                                      }
                                    }
                                    
                                    const capUpdates = getCapUpdatesForConnectionFitting(
                                      returnLeftConnectionUpdates.returnLeftConnectionFitting || params.returnLeftConnectionFitting,
                                      newDiameter,
                                      'left',
                                      'return',
                                      params.shape
                                    );
                                    Object.assign(returnLeftConnectionUpdates, capUpdates);
                                  }
                                  // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                                  else if (params.hasReturnLeftCap && params.returnLeftCapElementType === 'cap' && params.shape !== 'square') {
                                    const ellipticalCap = findEllipticalCap(newDiameter);
                                    if (ellipticalCap && params.returnLeftCapType !== 'elliptical') {
                                      returnLeftConnectionUpdates.returnLeftCapType = 'elliptical';
                                    }
                                  }
                                  
                                  if (Object.keys(returnLeftConnectionUpdates).length > 0) {
                                    updateParam(returnLeftConnectionUpdates);
                                  }
                                } else {
                                  updateParam('supplyPipeDiameter', newDiameter);
                                }
                                } else {
                                  // Квадратный профиль - обновляем профиль и автопереключаем несовместимые фитинги
                                  const newProfile = newValue;
                                  
                                  // Проверяем совместимость выбранного профиля с профилем гидроразделителя
                                  if (!isCollectorProfileCompatible(newProfile, params.mainPipeProfile)) {
                                    setIncompatibilityDialog({
                                      open: true,
                                      message: `Профиль коллектора ${newProfile} несовместим с профилем гидроразделителя ${params.mainPipeProfile}. Профиль коллектора не может быть больше профиля гидроразделителя.`,
                                    });
                                    return; // Прерываем обработку
                                  }
                                  
                                  // Обновляем контуры подающего коллектора
                                  const updatedSupplyCircuits = supplyCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, newProfile)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...circuit,
                                      fittingLength: null,
                                    };
                                  });
                                  setSupplyCircuits(updatedSupplyCircuits);
                                  
                                  // Обновляем правое подключение подающего коллектора
                                  const supplyRightConnectionUpdates = {};
                                  if (params.hasSupplyRightConnection && params.supplyRightConnectionType === 'fitting' && params.supplyRightConnectionFitting) {
                                    if (!isFittingCompatibleWithSquareProfile(params.supplyRightConnectionFitting, newProfile)) {
                                      const currentCategory = params.supplyRightConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isFittingCompatibleWithSquareProfile(f.id, newProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        supplyRightConnectionUpdates.supplyRightConnectionFitting = maxCompatibleFitting.id;
                                        supplyRightConnectionUpdates.supplyRightConnectionFittingLength = null;
                                      }
                                    } else {
                                      supplyRightConnectionUpdates.supplyRightConnectionFittingLength = null;
                                    }
                                  }
                                  
                                  // Если включена синхронизация - обновляем также обратный коллектор
                                  if (diametersLinked) {
                                    // Обновляем контуры обратного коллектора
                                    const updatedReturnCircuits = returnCircuits.map((circuit) => {
                                      const fittingId = circuit.fittingId || circuit.fitting;
                                      if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, newProfile)) {
                                        const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                                        const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                                        
                                        if (compatibleFittings.length > 0) {
                                          const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                          return {
                                            ...circuit,
                                            fitting: maxCompatibleFitting.id,
                                            fittingId: maxCompatibleFitting.id,
                                            diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                            fittingLength: null,
                                          };
                                        }
                                      }
                                      return {
                                        ...circuit,
                                        fittingLength: null,
                                      };
                                    });
                                    setReturnCircuits(updatedReturnCircuits);
                                    
                                    // Обновляем правое подключение обратного коллектора
                                    const returnRightConnectionUpdates = {};
                                    if (params.hasReturnRightConnection && params.returnRightConnectionType === 'fitting' && params.returnRightConnectionFitting) {
                                      if (!isFittingCompatibleWithSquareProfile(params.returnRightConnectionFitting, newProfile)) {
                                        const currentCategory = params.returnRightConnectionFittingCategory || 'Ниппели';
                                        const fittingsInCategory = getFittingsByCategory(currentCategory, null, null);
                                        const compatibleFittings = fittingsInCategory.filter(f => isFittingCompatibleWithSquareProfile(f.id, newProfile));
                                        
                                        if (compatibleFittings.length > 0) {
                                          const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                          returnRightConnectionUpdates.returnRightConnectionFitting = maxCompatibleFitting.id;
                                          returnRightConnectionUpdates.returnRightConnectionFittingLength = null;
                                        }
                                      } else {
                                        returnRightConnectionUpdates.returnRightConnectionFittingLength = null;
                                      }
                                    }
                                    
                                    updateParam({
                                      supplyPipeProfile: newProfile,
                                      returnPipeProfile: newProfile,
                                      ...supplyRightConnectionUpdates,
                                      ...returnRightConnectionUpdates,
                                    });
                                  } else {
                                    updateParam({
                                      supplyPipeProfile: newProfile,
                                      ...supplyRightConnectionUpdates,
                                    });
                                  }
                                }
                              }}
                            >
                              {params.shape === 'round'
                                ? collectorAvailableDiameters.map(d => {
                                    const isCompatible = isCollectorDiameterCompatible(d.value, params.mainPipeDiameter);
                                    return (
                                      <MenuItem 
                                        key={d.value} 
                                        value={d.value}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: `Диаметр трубы коллектора ${d.label} несовместим с диаметром гидроразделителя ${params.mainPipeDiameter}мм. Максимально допустимый диаметр: ${getMaxCollectorPipeDiameter(params.mainPipeDiameter)}мм`,
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                          cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                          '&:hover': {
                                            backgroundColor: !isCompatible ? 'transparent' : undefined,
                                          },
                                        }}
                                      >
                                        {d.label}
                                      </MenuItem>
                                    );
                                  })
                                : availableSquareProfiles.map((p) => {
                                    const isCompatible = isCollectorProfileCompatible(p.value, params.mainPipeProfile);
                                    return (
                                      <MenuItem 
                                        key={p.value} 
                                        value={p.value}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: `Профиль коллектора ${p.label} несовместим с профилем гидроразделителя ${params.mainPipeProfile}. Профиль коллектора не может быть больше профиля гидроразделителя.`,
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                          cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                          '&:hover': {
                                            backgroundColor: !isCompatible ? 'transparent' : undefined,
                                          },
                                        }}
                                      >
                                        {p.label}
                                      </MenuItem>
                                    );
                                  })
                              }
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        {/* Кнопка синхронизации диаметров */}
                        <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Button
                            variant={diametersLinked ? 'contained' : 'outlined'}
                            onClick={() => {
                              const newLinkedState = !diametersLinked;
                              setDiametersLinked(newLinkedState);
                              if (newLinkedState && params.supplyPipeDiameter !== params.returnPipeDiameter) {
                                updateParam('returnPipeDiameter', params.supplyPipeDiameter);
                              }
                            }}
                            sx={{
                              minWidth: '3rem', // 48px
                              width: '3rem',
                              height: '3rem',
                              borderRadius: '0.25rem', // 4px
                              fontSize: '1.25rem', // 20px
                              fontWeight: 'bold',
                              padding: 0,
                            }}
                            title={diametersLinked ? 'Диаметры синхронизированы' : 'Диаметры независимы'}
                          >
                            =
                          </Button>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>{params.shape === 'round' ? 'Диаметр обратного коллектора' : 'Профиль обратного коллектора'}</InputLabel>
                            <Select
                              value={params.shape === 'round' ? params.returnPipeDiameter : (params.returnPipeProfile || '80x80')}
                              label={params.shape === 'round' ? 'Диаметр обратного коллектора' : 'Профиль обратного коллектора'}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                
                                if (params.shape === 'round') {
                                const newDiameter = newValue;
                                
                                // Проверяем совместимость выбранного диаметра
                                if (!isCollectorDiameterCompatible(newDiameter, params.mainPipeDiameter)) {
                                  setIncompatibilityDialog({
                                    open: true,
                                    message: `Диаметр трубы коллектора ${newDiameter}мм несовместим с диаметром гидроразделителя ${params.mainPipeDiameter}мм. Максимально допустимый диаметр: ${getMaxCollectorPipeDiameter(params.mainPipeDiameter)}мм`,
                                  });
                                  return; // Прерываем обработку
                                }
                                
                                // Обновляем центральные фитинги обратного коллектора
                                const updatedReturnCenterFittings = updateCenterFittingsForDiameter(
                                  returnCenterFittings,
                                  newDiameter,
                                  isCenterFittingCompatible,
                                  getFittingsByCategory
                                );
                                if (JSON.stringify(updatedReturnCenterFittings) !== JSON.stringify(returnCenterFittings)) {
                                  setReturnCenterFittings(updatedReturnCenterFittings);
                                }
                                
                                // Обновляем контуры обратного коллектора
                                const updatedReturnCircuits = returnCircuits.map((circuit) => {
                                  const fittingId = circuit.fittingId || circuit.fitting;
                                  if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, newDiameter)) {
                                    const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', newDiameter, isCircuitFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...circuit,
                                        fitting: maxCompatibleFitting.id,
                                        fittingId: maxCompatibleFitting.id,
                                        diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                        fittingLength: null,
                                      };
                                    } else {
                                      return {
                                        ...circuit,
                                        type: 'pipe',
                                        fitting: null,
                                        fittingId: null,
                                        fittingCategory: null,
                                        fittingLength: null,
                                        diameter: newDiameter,
                                        length: 100,
                                      };
                                    }
                                  }
                                  return circuit;
                                });
                                if (JSON.stringify(updatedReturnCircuits) !== JSON.stringify(returnCircuits)) {
                                  setReturnCircuits(updatedReturnCircuits);
                                }
                                
                                // Обновляем дополнительные выходы обратного коллектора
                                const updatedReturnAdditionalOutlets = returnAdditionalOutlets.map((outlet) => {
                                  if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatible(outlet.fittingId, newDiameter)) {
                                    const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', newDiameter, isAdditionalOutletFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...outlet,
                                        fittingId: maxCompatibleFitting.id,
                                        diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                                        fittingLength: null,
                                      };
                                    } else {
                                      return {
                                        ...outlet,
                                        type: 'pipe',
                                        fittingId: null,
                                        fittingCategory: null,
                                        fittingLength: null,
                                        diameter: newDiameter,
                                        length: 100,
                                      };
                                    }
                                  }
                                  return outlet;
                                });
                                if (JSON.stringify(updatedReturnAdditionalOutlets) !== JSON.stringify(returnAdditionalOutlets)) {
                                  setReturnAdditionalOutlets(updatedReturnAdditionalOutlets);
                                }
                                
                                // Обновляем правое торцевое подключение обратного коллектора
                                const returnRightConnectionUpdates = {};
                                if (params.hasReturnRightConnection && params.returnRightConnectionType === 'fitting' && params.returnRightConnectionFitting) {
                                  if (!isEndFittingCompatibleWithMainPipe(params.returnRightConnectionFitting, newDiameter)) {
                                    // Фитинг несовместим - пытаемся найти совместимый в той же категории
                                    const currentCategory = params.returnRightConnectionFittingCategory || 'Ниппели';
                                    const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      returnRightConnectionUpdates.returnRightConnectionFitting = maxCompatibleFitting.id;
                                      returnRightConnectionUpdates.returnRightConnectionFittingLength = null;
                                    }
                                  }
                                  
                                  const capUpdates = getCapUpdatesForConnectionFitting(
                                    returnRightConnectionUpdates.returnRightConnectionFitting || params.returnRightConnectionFitting,
                                    newDiameter,
                                    'right',
                                    'return',
                                    params.shape
                                  );
                                  Object.assign(returnRightConnectionUpdates, capUpdates);
                                }
                                // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                                else if (params.hasReturnRightCap && params.returnRightCapElementType === 'cap' && params.shape !== 'square') {
                                  const ellipticalCap = findEllipticalCap(newDiameter);
                                  if (ellipticalCap && params.returnRightCapType !== 'elliptical') {
                                    returnRightConnectionUpdates.returnRightCapType = 'elliptical';
                                  }
                                }
                                
                                if (Object.keys(returnRightConnectionUpdates).length > 0) {
                                  updateParam(returnRightConnectionUpdates);
                                }
                                
                                // Обновляем левое торцевое подключение обратного коллектора
                                const returnLeftConnectionUpdates = {};
                                if (params.hasReturnLeftConnection && params.returnLeftConnectionType === 'fitting' && params.returnLeftConnectionFitting) {
                                  if (!isEndFittingCompatibleWithMainPipe(params.returnLeftConnectionFitting, newDiameter)) {
                                    // Фитинг несовместим - пытаемся найти совместимый в той же категории
                                    const currentCategory = params.returnLeftConnectionFittingCategory || 'Ниппели';
                                    const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      returnLeftConnectionUpdates.returnLeftConnectionFitting = maxCompatibleFitting.id;
                                      returnLeftConnectionUpdates.returnLeftConnectionFittingLength = null;
                                    }
                                  }
                                  
                                  const capUpdates = getCapUpdatesForConnectionFitting(
                                    returnLeftConnectionUpdates.returnLeftConnectionFitting || params.returnLeftConnectionFitting,
                                    newDiameter,
                                    'left',
                                    'return',
                                    params.shape
                                  );
                                  Object.assign(returnLeftConnectionUpdates, capUpdates);
                                }
                                // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                                else if (params.hasReturnLeftCap && params.returnLeftCapElementType === 'cap' && params.shape !== 'square') {
                                  const ellipticalCap = findEllipticalCap(newDiameter);
                                  if (ellipticalCap && params.returnLeftCapType !== 'elliptical') {
                                    returnLeftConnectionUpdates.returnLeftCapType = 'elliptical';
                                  }
                                }
                                
                                if (Object.keys(returnLeftConnectionUpdates).length > 0) {
                                  updateParam(returnLeftConnectionUpdates);
                                }
                                
                                if (diametersLinked) {
                                  updateParam({ supplyPipeDiameter: newDiameter, returnPipeDiameter: newDiameter });
                                  
                                  // Обновляем центральные фитинги подающего коллектора
                                  const updatedSupplyCenterFittings = updateCenterFittingsForDiameter(
                                    supplyCenterFittings,
                                    newDiameter,
                                    isCenterFittingCompatible,
                                    getFittingsByCategory
                                  );
                                  if (JSON.stringify(updatedSupplyCenterFittings) !== JSON.stringify(supplyCenterFittings)) {
                                    setSupplyCenterFittings(updatedSupplyCenterFittings);
                                  }
                                  
                                  // Обновляем контуры подающего коллектора
                                  const updatedSupplyCircuits = supplyCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, newDiameter)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', newDiameter, isCircuitFittingCompatible);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      } else {
                                        return {
                                          ...circuit,
                                          type: 'pipe',
                                          fitting: null,
                                          fittingId: null,
                                          fittingCategory: null,
                                          fittingLength: null,
                                          diameter: newDiameter,
                                          length: 100,
                                        };
                                      }
                                    }
                                    return circuit;
                                  });
                                  if (JSON.stringify(updatedSupplyCircuits) !== JSON.stringify(supplyCircuits)) {
                                    setSupplyCircuits(updatedSupplyCircuits);
                                  }
                                  
                                  // Обновляем дополнительные выходы подающего коллектора
                                  const updatedSupplyAdditionalOutlets = supplyAdditionalOutlets.map((outlet) => {
                                    if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatible(outlet.fittingId, newDiameter)) {
                                      const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', newDiameter, isAdditionalOutletFittingCompatible);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...outlet,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                                          fittingLength: null,
                                        };
                                      } else {
                                        return {
                                          ...outlet,
                                          type: 'pipe',
                                          fittingId: null,
                                          fittingCategory: null,
                                          fittingLength: null,
                                          diameter: newDiameter,
                                          length: 100,
                                        };
                                      }
                                    }
                                    return outlet;
                                  });
                                  if (JSON.stringify(updatedSupplyAdditionalOutlets) !== JSON.stringify(supplyAdditionalOutlets)) {
                                    setSupplyAdditionalOutlets(updatedSupplyAdditionalOutlets);
                                  }
                                  
                                  // Обновляем правое торцевое подключение подающего коллектора
                                  const supplyRightConnectionUpdates = {};
                                  if (params.hasSupplyRightConnection && params.supplyRightConnectionType === 'fitting' && params.supplyRightConnectionFitting) {
                                    if (!isEndFittingCompatibleWithMainPipe(params.supplyRightConnectionFitting, newDiameter)) {
                                      // Фитинг несовместим - пытаемся найти совместимый в той же категории
                                      const currentCategory = params.supplyRightConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        supplyRightConnectionUpdates.supplyRightConnectionFitting = maxCompatibleFitting.id;
                                        supplyRightConnectionUpdates.supplyRightConnectionFittingLength = null;
                                      }
                                    }
                                    
                                    const capUpdates = getCapUpdatesForConnectionFitting(
                                      supplyRightConnectionUpdates.supplyRightConnectionFitting || params.supplyRightConnectionFitting,
                                      newDiameter,
                                      'right',
                                      'supply',
                                      params.shape
                                    );
                                    Object.assign(supplyRightConnectionUpdates, capUpdates);
                                  }
                                  
                                  if (Object.keys(supplyRightConnectionUpdates).length > 0) {
                                    updateParam(supplyRightConnectionUpdates);
                                  }
                                  
                                  // Обновляем левое торцевое подключение подающего коллектора
                                  const supplyLeftConnectionUpdates = {};
                                  if (params.hasSupplyLeftConnection && params.supplyLeftConnectionType === 'fitting' && params.supplyLeftConnectionFitting) {
                                    if (!isEndFittingCompatibleWithMainPipe(params.supplyLeftConnectionFitting, newDiameter)) {
                                      // Фитинг несовместим - пытаемся найти совместимый в той же категории
                                      const currentCategory = params.supplyLeftConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        supplyLeftConnectionUpdates.supplyLeftConnectionFitting = maxCompatibleFitting.id;
                                        supplyLeftConnectionUpdates.supplyLeftConnectionFittingLength = null;
                                      }
                                    }
                                    
                                    const capUpdates = getCapUpdatesForConnectionFitting(
                                      supplyLeftConnectionUpdates.supplyLeftConnectionFitting || params.supplyLeftConnectionFitting,
                                      newDiameter,
                                      'left',
                                      'supply',
                                      params.shape
                                    );
                                    Object.assign(supplyLeftConnectionUpdates, capUpdates);
                                  }
                                  // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                                  else if (params.hasSupplyLeftCap && params.supplyLeftCapElementType === 'cap' && params.shape !== 'square') {
                                    const ellipticalCap = findEllipticalCap(newDiameter);
                                    if (ellipticalCap && params.supplyLeftCapType !== 'elliptical') {
                                      supplyLeftConnectionUpdates.supplyLeftCapType = 'elliptical';
                                    }
                                  }
                                  
                                  if (Object.keys(supplyLeftConnectionUpdates).length > 0) {
                                    updateParam(supplyLeftConnectionUpdates);
                                  }
                                } else {
                                  updateParam('returnPipeDiameter', newDiameter);
                                }
                                } else {
                                  // Квадратный профиль - обновляем профиль и автопереключаем несовместимые фитинги
                                  const newProfile = newValue;
                                  
                                  // Обновляем контуры обратного коллектора
                                  const updatedReturnCircuits = returnCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, newProfile)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...circuit,
                                      fittingLength: null,
                                    };
                                  });
                                  setReturnCircuits(updatedReturnCircuits);
                                  
                                  // Обновляем правое подключение обратного коллектора
                                  const returnRightConnectionUpdates = {};
                                  if (params.hasReturnRightConnection && params.returnRightConnectionType === 'fitting' && params.returnRightConnectionFitting) {
                                    if (!isFittingCompatibleWithSquareProfile(params.returnRightConnectionFitting, newProfile)) {
                                      const currentCategory = params.returnRightConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isFittingCompatibleWithSquareProfile(f.id, newProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        returnRightConnectionUpdates.returnRightConnectionFitting = maxCompatibleFitting.id;
                                        returnRightConnectionUpdates.returnRightConnectionFittingLength = null;
                                      }
                                    } else {
                                      returnRightConnectionUpdates.returnRightConnectionFittingLength = null;
                                    }
                                  }
                                  
                                  updateParam({
                                    returnPipeProfile: newProfile,
                                    ...returnRightConnectionUpdates,
                                  });
                                }
                              }}
                            >
                              {params.shape === 'round'
                                ? collectorAvailableDiameters.map(d => {
                                    const isCompatible = isCollectorDiameterCompatible(d.value, params.mainPipeDiameter);
                                    return (
                                      <MenuItem 
                                        key={d.value} 
                                        value={d.value}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: `Диаметр трубы коллектора ${d.label} несовместим с диаметром гидроразделителя ${params.mainPipeDiameter}мм. Максимально допустимый диаметр: ${getMaxCollectorPipeDiameter(params.mainPipeDiameter)}мм`,
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                          cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                          '&:hover': {
                                            backgroundColor: !isCompatible ? 'transparent' : undefined,
                                          },
                                        }}
                                      >
                                        {d.label}
                                      </MenuItem>
                                    );
                                  })
                                : availableSquareProfiles.map((p) => {
                                    const isCompatible = isCollectorProfileCompatible(p.value, params.mainPipeProfile);
                                    return (
                                      <MenuItem 
                                        key={p.value} 
                                        value={p.value}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: `Профиль коллектора ${p.label} несовместим с профилем гидроразделителя ${params.mainPipeProfile}. Профиль коллектора не может быть больше профиля гидроразделителя.`,
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                          cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                          '&:hover': {
                                            backgroundColor: !isCompatible ? 'transparent' : undefined,
                                          },
                                        }}
                                      >
                                        {p.label}
                                      </MenuItem>
                                    );
                                  })
                              }
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Расстояние между коллекторами
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={params.pipeSpacingMode === 'offset'}
                                  onChange={(e) => updateParam('pipeSpacingMode', e.target.checked ? 'offset' : 'interaxis')}
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="caption">
                                  {params.pipeSpacingMode === 'offset' ? 'Режим: Отступы' : 'Режим: Межосевое'}
                                </Typography>
                              }
                            />
                          </Box>
                        </Grid>
                        
                        {params.pipeSpacingMode === 'interaxis' ? (
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                              label="Межосевое расстояние (мм)"
                            type="number"
                            value={params.pipeSpacing}
                            onChange={(e) => updateParam('pipeSpacing', Number(e.target.value))}
                          />
                        </Grid>
                        ) : (
                          <>
                            <Grid item xs={12} md={2}>
                              <TextField
                                fullWidth
                                label="Отступ подачи от верха (мм)"
                                type="number"
                                value={params.supplyPipeOffset}
                                onChange={(e) => updateParam('supplyPipeOffset', Number(e.target.value))}
                                helperText="Отступ до центра трубы подачи"
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField
                                fullWidth
                                label="Отступ обратки от низа (мм)"
                                type="number"
                                value={params.returnPipeOffset}
                                onChange={(e) => updateParam('returnPipeOffset', Number(e.target.value))}
                                helperText="Отступ до центра трубы обратки"
                              />
                            </Grid>
                          </>
                        )}
                        
                        {/* Разрыв строки */}
                        <Grid item xs={12} />
                        
                        <Grid item xs={12} md={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={params.useManualLength}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  updateParam('useManualLength', checked);
                                  if (checked) {
                                    updateParam('manualSupplyLength', Number(supplyCalculatedLength.toFixed(2)));
                                    updateParam('manualReturnLength', Number(returnCalculatedLength.toFixed(2)));
                                  }
                                }}
                              />
                            }
                            label="Ручная длина"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={1.5}>
                          <TextField
                            fullWidth
                            label="Длина подающего (мм)"
                            type="number"
                            value={params.useManualLength ? (params.manualSupplyLength || '') : Number(supplyCalculatedLength.toFixed(2))}
                            onChange={(e) => updateParam('manualSupplyLength', Number(e.target.value))}
                            InputProps={{ readOnly: !params.useManualLength }}
                            helperText={params.useManualLength ? "Ручной ввод" : "Автоматически"}
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Button
                            variant="outlined"
                            onClick={equalizeLengths}
                            disabled={supplyCalculatedLength === returnCalculatedLength}
                            sx={{ 
                              minWidth: '2.5rem', // 40px
                              height: '2.5rem',
                              fontSize: '1.2rem',
                              fontWeight: 'bold',
                              bgcolor: lengthsEqualized ? 'success.light' : 'inherit',
                              '&:hover': {
                                bgcolor: lengthsEqualized ? 'success.main' : 'inherit',
                              }
                            }}
                            title={lengthsEqualized ? 'Длины приравнены' : 'Приравнять длины коллекторов'}
                          >
                            =
                          </Button>
                        </Grid>
                        
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            label="Длина обратного (мм)"
                            type="number"
                            value={params.useManualLength ? (params.manualReturnLength || '') : Number(returnCalculatedLength.toFixed(2))}
                            onChange={(e) => updateParam('manualReturnLength', Number(e.target.value))}
                            InputProps={{ readOnly: !params.useManualLength }}
                            helperText={params.useManualLength ? "Ручной ввод" : "Автоматически"}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  {/* Подвкладка 2: Контуры */}
                  {collectorTabValue === 1 && (
                    <Paper sx={{ p: 3 }} elevation={0}>
                      {/* Две колонки: Подача слева, Обратка справа */}
                      <Grid container spacing={3}>
                        {/* ЛЕВАЯ КОЛОНКА - КОЛЛЕКТОР ПОДАЧИ */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 1.5, height: '100%', bgcolor: '#fff5f5' }}>
                            <Typography variant="subtitle1" color="error" sx={{ mb: 1.5, fontWeight: 600 }}>
                              Коллектор подачи ({supplyCircuits.length})
                            </Typography>
                            
                            {/* Отступы */}
                            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 'bold', fontSize: '0.8rem' }}>
                              Отступы
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                              <TextField
                                label="Первого контура"
                                type="number"
                                size="small"
                                value={params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit' 
                                  ? effectiveSupplyFirstCircuitOffset 
                                  : params.supplyFirstCircuitOffset}
                                onChange={(e) => {
                                  setLengthsEqualized(false);
                                  updateParam('supplyFirstCircuitOffset', e.target.value);
                                }}
                                disabled={params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit'}
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                              />
                              <TextField
                                label="Последнего контура"
                                type="number"
                                size="small"
                                value={params.useManualLength ? Math.round(supplyLastCircuitOffsetCalculated) : params.supplyLastCircuitOffset}
                                onChange={(e) => {
                                  setLengthsEqualized(false);
                                  updateParam('supplyLastCircuitOffset', e.target.value);
                                }}
                                fullWidth
                                InputProps={{ 
                                  readOnly: params.useManualLength,
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                helperText={params.useManualLength ? "Авто (ручная длина)" : undefined}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                            </Box>
                            
                            {/* Переключатель привязки к контуру обратки */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit'}
                                    onChange={(e) => {
                                      const isEnabled = e.target.checked;
                                      if (isEnabled) {
                                        // Включаем режим привязки
                                        setParams(prev => ({
                                          ...prev,
                                          supplyFirstCircuitOffsetMode: 'fromReturnCircuit',
                                          supplyFirstCircuitOffsetSaved: prev.supplyFirstCircuitOffset,
                                          supplyFirstCircuitOffset: prev.supplyFirstCircuitOffsetLastUsed || 0,
                                          // Взаимная исключительность: отключаем привязку обратки
                                          returnFirstCircuitOffsetMode: 'fromEdge',
                                          returnFirstCircuitOffset: prev.returnFirstCircuitOffsetSaved !== null 
                                            ? prev.returnFirstCircuitOffsetSaved 
                                            : prev.returnFirstCircuitOffset,
                                        }));
                                      } else {
                                        // Отключаем режим привязки
                                        setParams(prev => ({
                                          ...prev,
                                          supplyFirstCircuitOffsetMode: 'fromEdge',
                                          supplyFirstCircuitOffsetLastUsed: prev.supplyFirstCircuitOffset,
                                          supplyFirstCircuitOffset: prev.supplyFirstCircuitOffsetSaved !== null 
                                            ? prev.supplyFirstCircuitOffsetSaved 
                                            : prev.supplyFirstCircuitOffset,
                                        }));
                                      }
                                      setLengthsEqualized(false);
                                    }}
                                    disabled={returnCircuits.length === 0}
                                  />
                                }
                                label={
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    Привязать к контуру обратки
                                  </Typography>
                                }
                              />
                            </Box>
                            
                            {/* Поля для выбора контура и отступа от него */}
                            {params.supplyFirstCircuitOffsetMode === 'fromReturnCircuit' && returnCircuits.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <InputLabel sx={{ fontSize: '0.8rem' }}>Контур</InputLabel>
                                  <Select
                                    value={params.supplyFirstCircuitOffsetReferenceIndex || 0}
                                    label="Контур"
                                    onChange={(e) => {
                                      updateParam('supplyFirstCircuitOffsetReferenceIndex', e.target.value);
                                      setLengthsEqualized(false);
                                    }}
                                    sx={{ fontSize: '0.8rem' }}
                                  >
                                    {returnCircuits.map((_, index) => (
                                      <MenuItem key={index} value={index} sx={{ fontSize: '0.8rem' }}>
                                        {index + 1}-й
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <TextField
                                  label="Отступ от контура"
                                  type="number"
                                  size="small"
                                  value={params.supplyFirstCircuitOffset || 0}
                                  onChange={(e) => {
                                    setLengthsEqualized(false);
                                    updateParam('supplyFirstCircuitOffset', e.target.value);
                                  }}
                                  fullWidth
                                  InputProps={{
                                    endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                    sx: { fontSize: '0.8rem' }
                                  }}
                                  InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                />
                              </Box>
                            )}
                            
                            {/* Расстояния */}
                            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                              <TextField
                                label="Межосевое контура"
                                type="number"
                                size="small"
                                value={params.supplyCircuitSpacing}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') return;
                                  const numValue = Number(value);
                                  if (isNaN(numValue)) return;
                                  setLengthsEqualized(false);
                                  updateParam('supplyCircuitSpacing', numValue);
                                  // Применяем ко всем контурам подачи
                                  setSupplyCircuits(prev => prev.map(circuit => ({
                                    ...circuit,
                                    circuitSpacing: numValue,
                                  })));
                                }}
                                helperText="Ко всем"
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                              <TextField
                                label="Между контурами"
                                type="number"
                                size="small"
                                value={params.supplySpacingBetweenCircuits}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') return;
                                  const numValue = Number(value);
                                  if (isNaN(numValue)) return;
                                  setLengthsEqualized(false);
                                  updateParam('supplySpacingBetweenCircuits', numValue);
                                  setSupplyCircuits(prev => prev.map(circuit => ({
                                    ...circuit,
                                    spacingToNext: numValue,
                                  })));
                                }}
                                helperText="Ко всем"
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                            </Box>
                            
                            {/* Кнопка добавить */}
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                              onClick={addSupplyCircuit}
                              size="small"
                              fullWidth
                              sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}
                              color="error"
                            >
                              Добавить контур
                            </Button>
                            
                            {/* Таблица контуров */}
                            <TableContainer>
                              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 0.5, fontSize: '0.75rem' } }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell width="25" sx={{ fontWeight: 600 }}>#</TableCell>
                                    <TableCell width="90" sx={{ fontWeight: 600 }}>Категория</TableCell>
                                    <TableCell width="100" sx={{ fontWeight: 600 }}>Фитинг</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>Вылет</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>Межос.</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>След.</TableCell>
                                    <TableCell width="40" align="center" sx={{ fontWeight: 600 }}>Swap</TableCell>
                                    <TableCell width="35"></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {supplyCircuits.map((circuit, index) => (
                                    <TableRow 
                                      key={circuit.id}
                                      draggable
                                      onDragStart={() => setDraggedSupplyCircuitIndex(index)}
                                      onDragOver={(e) => e.preventDefault()}
                                      onDrop={() => {
                                        if (draggedSupplyCircuitIndex !== null && draggedSupplyCircuitIndex !== index) {
                                          moveSupplyCircuit(draggedSupplyCircuitIndex, index);
                                        }
                                        setDraggedSupplyCircuitIndex(null);
                                      }}
                                      onDragEnd={() => setDraggedSupplyCircuitIndex(null)}
                                      sx={{ 
                                        cursor: 'move',
                                        opacity: draggedSupplyCircuitIndex === index ? 0.5 : 1,
                                        '&:hover': { backgroundColor: 'action.hover' }
                                      }}
                                    >
                                      <TableCell>{index + 1}</TableCell>
                                      
                                      {/* Колонка: Категория */}
                                      <TableCell>
                                        <FormControl fullWidth size="small">
                                          <Select
                                            value={circuit.fittingCategory || 'Ниппели'}
                                            onChange={(e) => {
                                              const newCategory = e.target.value;
                                              const fittingsInNewCategory = getSupplyFittings(newCategory);
                                              const firstCompatibleFitting = fittingsInNewCategory.find(f => f.isCompatible);
                                              
                                              if (!firstCompatibleFitting) {
                                                setIncompatibilityDialog({
                                                  open: true,
                                                  message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                });
                                                return;
                                              }
                                              
                                              // Устанавливаем дефолтное значение flangePipeLength при выборе категории
                                              const updates = {
                                                fittingCategory: newCategory,
                                                fitting: firstCompatibleFitting.id,
                                                fittingLength: null,
                                              };
                                              
                                              if (firstCompatibleFitting.id?.startsWith('Flanec_') || firstCompatibleFitting.id?.startsWith('Nak_Gaika_') || firstCompatibleFitting.id?.startsWith('Shaib_Nar_Rezba_')) {
                                                updates.flangePipeLength = null;
                                              }
                                              
                                              updateSupplyCircuit(index, updates);
                                            }}
                                            sx={{ fontSize: '0.8rem' }}
                                          >
                                            {circuitFittingCategories.map(cat => (
                                              <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>
                                                {cat.label}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      
                                      {/* Колонка: Фитинг */}
                                      <TableCell>
                                        <FormControl fullWidth size="small">
                                          <Select
                                            value={circuit.fitting}
                                            onChange={(e) => {
                                              const newFittingId = e.target.value;
                                              const fittingInfo = getSupplyFittings(circuit.fittingCategory || 'Ниппели').find(f => f.id === newFittingId);
                                              
                                              if (fittingInfo && !fittingInfo.isCompatible) {
                                                setIncompatibilityDialog({
                                                  open: true,
                                                  message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                });
                                                return;
                                              }
                                              
                                              // Сбрасываем flangePipeLength в null при выборе нового фитинга
                                              const updates = { fitting: newFittingId };
                                              if (newFittingId?.startsWith('Flanec_') || newFittingId?.startsWith('Nak_Gaika_') || newFittingId?.startsWith('Shaib_Nar_Rezba_')) {
                                                updates.flangePipeLength = null;
                                              }
                                              
                                              updateSupplyCircuit(index, updates);
                                            }}
                                            sx={{ fontSize: '0.8rem' }}
                                          >
                                            {getSupplyFittings(circuit.fittingCategory || 'Ниппели').map(f => (
                                              <MenuItem 
                                                key={f.id} 
                                                value={f.id} 
                                                onClick={(e) => {
                                                  if (!f.isCompatible) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setIncompatibilityDialog({
                                                      open: true,
                                                      message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                    });
                                                  }
                                                }}
                                                sx={{ 
                                                  fontSize: '0.8rem',
                                                  ...(!f.isCompatible && {
                                                    color: 'text.disabled',
                                                    textDecoration: 'line-through',
                                                    backgroundColor: 'transparent',
                                                  }),
                                                }}
                                              >
                                                {f.label}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      
                                      <TableCell>
                                        {circuit.fitting?.startsWith('Flanec_') || circuit.fitting?.startsWith('Nak_Gaika_') || circuit.fitting?.startsWith('Shaib_Nar_Rezba_') ? (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.flangePipeLength ?? ''}
                                            placeholder="Авто"
                                            onChange={(e) => updateSupplyCircuit(index, 'flangePipeLength', e.target.value ? Number(e.target.value) : null)}
                                            sx={{ width: 75 }}
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        ) : (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.fittingLength ?? ''}
                                            onChange={(e) => updateSupplyCircuit(index, 'fittingLength', e.target.value ? Number(e.target.value) : null)}
                                            placeholder="Авто"
                                            sx={{ width: 75 }}
                                          />
                                        )}
                                      </TableCell>
                                      
                                      <TableCell>
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={circuit.circuitSpacing ?? 50}
                                          onChange={(e) => updateSupplyCircuit(index, 'circuitSpacing', e.target.value ? Number(e.target.value) : 50)}
                                          sx={{ width: 75 }}
                                          title="Межосевое расстояние внутри контура (между двумя патрубками)"
                                        />
                                      </TableCell>
                                      
                                      <TableCell>
                                        {index < supplyCircuits.length - 1 && (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.spacingToNext ?? ''}
                                            onChange={(e) => updateSupplyCircuit(index, 'spacingToNext', e.target.value ? Number(e.target.value) : null)}
                                            placeholder={String(params.supplySpacingBetweenCircuits)}
                                            sx={{ width: 75 }}
                                          />
                                        )}
                                      </TableCell>
                                      
                                      <TableCell align="center">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const currentValue = circuit.swapOrder ?? false;
                                            updateSupplyCircuit(index, 'swapOrder', !currentValue);
                                          }}
                                          color={(circuit.swapOrder ?? false) ? 'error' : 'default'}
                                          title={(circuit.swapOrder ?? false) ? 'Д-К' : 'К-Д'}
                                        >
                                          <SwapHorizIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                      
                                      <TableCell>
                                        <IconButton
                                          size="small"
                                          onClick={() => deleteSupplyCircuit(index)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        </Grid>
                        
                        {/* ПРАВАЯ КОЛОНКА - КОЛЛЕКТОР ОБРАТКИ */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 1.5, height: '100%', bgcolor: '#f5f5ff' }}>
                            <Typography variant="subtitle1" color="primary" sx={{ mb: 1.5, fontWeight: 600 }}>
                              Коллектор обратки ({returnCircuits.length})
                            </Typography>
                            
                            {/* Отступы */}
                            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 'bold', fontSize: '0.8rem' }}>
                              Отступы
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                              <TextField
                                label="Первого контура"
                                type="number"
                                size="small"
                                value={params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit' 
                                  ? effectiveReturnFirstCircuitOffset 
                                  : params.returnFirstCircuitOffset}
                                onChange={(e) => {
                                  setLengthsEqualized(false);
                                  updateParam('returnFirstCircuitOffset', e.target.value);
                                }}
                                disabled={params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit'}
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                              />
                              <TextField
                                label="Последнего контура"
                                type="number"
                                size="small"
                                value={params.useManualLength ? Math.round(returnLastCircuitOffsetCalculated) : params.returnLastCircuitOffset}
                                onChange={(e) => {
                                  setLengthsEqualized(false);
                                  updateParam('returnLastCircuitOffset', e.target.value);
                                }}
                                fullWidth
                                InputProps={{ 
                                  readOnly: params.useManualLength,
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                helperText={params.useManualLength ? "Авто (ручная длина)" : undefined}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                            </Box>
                            
                            {/* Переключатель привязки к контуру подачи */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit'}
                                    onChange={(e) => {
                                      const isEnabled = e.target.checked;
                                      if (isEnabled) {
                                        // Включаем режим привязки
                                        setParams(prev => ({
                                          ...prev,
                                          returnFirstCircuitOffsetMode: 'fromSupplyCircuit',
                                          returnFirstCircuitOffsetSaved: prev.returnFirstCircuitOffset,
                                          returnFirstCircuitOffset: prev.returnFirstCircuitOffsetLastUsed || 0,
                                          // Взаимная исключительность: отключаем привязку подачи
                                          supplyFirstCircuitOffsetMode: 'fromEdge',
                                          supplyFirstCircuitOffset: prev.supplyFirstCircuitOffsetSaved !== null 
                                            ? prev.supplyFirstCircuitOffsetSaved 
                                            : prev.supplyFirstCircuitOffset,
                                        }));
                                      } else {
                                        // Отключаем режим привязки
                                        setParams(prev => ({
                                          ...prev,
                                          returnFirstCircuitOffsetMode: 'fromEdge',
                                          returnFirstCircuitOffsetLastUsed: prev.returnFirstCircuitOffset,
                                          returnFirstCircuitOffset: prev.returnFirstCircuitOffsetSaved !== null 
                                            ? prev.returnFirstCircuitOffsetSaved 
                                            : prev.returnFirstCircuitOffset,
                                        }));
                                      }
                                      setLengthsEqualized(false);
                                    }}
                                    disabled={supplyCircuits.length === 0}
                                  />
                                }
                                label={
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    Привязать к контуру подачи
                                  </Typography>
                                }
                              />
                            </Box>
                            
                            {/* Поля для выбора контура и отступа от него */}
                            {params.returnFirstCircuitOffsetMode === 'fromSupplyCircuit' && supplyCircuits.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <InputLabel sx={{ fontSize: '0.8rem' }}>Контур</InputLabel>
                                  <Select
                                    value={params.returnFirstCircuitOffsetReferenceIndex || 0}
                                    label="Контур"
                                    onChange={(e) => {
                                      updateParam('returnFirstCircuitOffsetReferenceIndex', e.target.value);
                                      setLengthsEqualized(false);
                                    }}
                                    sx={{ fontSize: '0.8rem' }}
                                  >
                                    {supplyCircuits.map((_, index) => (
                                      <MenuItem key={index} value={index} sx={{ fontSize: '0.8rem' }}>
                                        {index + 1}-й
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <TextField
                                  label="Отступ от контура"
                                  type="number"
                                  size="small"
                                  value={params.returnFirstCircuitOffset || 0}
                                  onChange={(e) => {
                                    setLengthsEqualized(false);
                                    updateParam('returnFirstCircuitOffset', e.target.value);
                                  }}
                                  fullWidth
                                  InputProps={{
                                    endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                    sx: { fontSize: '0.8rem' }
                                  }}
                                  InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                />
                              </Box>
                            )}
                            
                            {/* Расстояния */}
                            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                              <TextField
                                label="Межосевое контура"
                                type="number"
                                size="small"
                                value={params.returnCircuitSpacing}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') return;
                                  const numValue = Number(value);
                                  if (isNaN(numValue)) return;
                                  setLengthsEqualized(false);
                                  updateParam('returnCircuitSpacing', numValue);
                                  // Применяем ко всем контурам обратки
                                  setReturnCircuits(prev => prev.map(circuit => ({
                                    ...circuit,
                                    circuitSpacing: numValue,
                                  })));
                                }}
                                helperText="Ко всем"
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                              <TextField
                                label="Между контурами"
                                type="number"
                                size="small"
                                value={params.returnSpacingBetweenCircuits}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') return;
                                  const numValue = Number(value);
                                  if (isNaN(numValue)) return;
                                  setLengthsEqualized(false);
                                  updateParam('returnSpacingBetweenCircuits', numValue);
                                  setReturnCircuits(prev => prev.map(circuit => ({
                                    ...circuit,
                                    spacingToNext: numValue,
                                  })));
                                }}
                                helperText="Ко всем"
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                            </Box>
                            
                            {/* Кнопка добавить */}
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                              onClick={addReturnCircuit}
                              size="small"
                              fullWidth
                              sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}
                              color="primary"
                            >
                              Добавить контур
                            </Button>
                            
                            {/* Таблица контуров */}
                            <TableContainer>
                              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 0.5, fontSize: '0.75rem' } }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell width="25" sx={{ fontWeight: 600 }}>#</TableCell>
                                    <TableCell width="90" sx={{ fontWeight: 600 }}>Категория</TableCell>
                                    <TableCell width="100" sx={{ fontWeight: 600 }}>Фитинг</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>Вылет</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>Межос.</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>След.</TableCell>
                                    <TableCell width="40" align="center" sx={{ fontWeight: 600 }}>Swap</TableCell>
                                    <TableCell width="35"></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {returnCircuits.map((circuit, index) => (
                                    <TableRow 
                                      key={circuit.id}
                                      draggable
                                      onDragStart={() => setDraggedReturnCircuitIndex(index)}
                                      onDragOver={(e) => e.preventDefault()}
                                      onDrop={() => {
                                        if (draggedReturnCircuitIndex !== null && draggedReturnCircuitIndex !== index) {
                                          moveReturnCircuit(draggedReturnCircuitIndex, index);
                                        }
                                        setDraggedReturnCircuitIndex(null);
                                      }}
                                      onDragEnd={() => setDraggedReturnCircuitIndex(null)}
                                      sx={{ 
                                        cursor: 'move',
                                        opacity: draggedReturnCircuitIndex === index ? 0.5 : 1,
                                        '&:hover': { backgroundColor: 'action.hover' }
                                      }}
                                    >
                                      <TableCell>{index + 1}</TableCell>
                                      
                                      {/* Колонка: Категория */}
                                      <TableCell>
                                        <FormControl fullWidth size="small">
                                          <Select
                                            value={circuit.fittingCategory || 'Ниппели'}
                                            onChange={(e) => {
                                              const newCategory = e.target.value;
                                              const fittingsInNewCategory = getReturnFittings(newCategory);
                                              const firstCompatibleFitting = fittingsInNewCategory.find(f => f.isCompatible);
                                              
                                              if (!firstCompatibleFitting) {
                                                setIncompatibilityDialog({
                                                  open: true,
                                                  message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                });
                                                return;
                                              }
                                              
                                              // Устанавливаем дефолтное значение flangePipeLength при выборе категории
                                              const updates = {
                                                fittingCategory: newCategory,
                                                fitting: firstCompatibleFitting.id,
                                                fittingLength: null,
                                              };
                                              
                                              if (firstCompatibleFitting.id?.startsWith('Flanec_') || firstCompatibleFitting.id?.startsWith('Nak_Gaika_') || firstCompatibleFitting.id?.startsWith('Shaib_Nar_Rezba_')) {
                                                updates.flangePipeLength = null;
                                              }
                                              
                                              updateReturnCircuit(index, updates);
                                            }}
                                            sx={{ fontSize: '0.8rem' }}
                                          >
                                            {circuitFittingCategories.map(cat => (
                                              <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>
                                                {cat.label}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      
                                      {/* Колонка: Фитинг */}
                                      <TableCell>
                                        <FormControl fullWidth size="small">
                                          <Select
                                            value={circuit.fitting}
                                            onChange={(e) => {
                                              const newFittingId = e.target.value;
                                              const fittingInfo = getReturnFittings(circuit.fittingCategory || 'Ниппели').find(f => f.id === newFittingId);
                                              
                                              if (fittingInfo && !fittingInfo.isCompatible) {
                                                setIncompatibilityDialog({
                                                  open: true,
                                                  message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                });
                                                return;
                                              }
                                              
                                              // Сбрасываем flangePipeLength в null при выборе нового фитинга
                                              const updates = { fitting: newFittingId };
                                              if (newFittingId?.startsWith('Flanec_') || newFittingId?.startsWith('Nak_Gaika_') || newFittingId?.startsWith('Shaib_Nar_Rezba_')) {
                                                updates.flangePipeLength = null;
                                              }
                                              
                                              updateReturnCircuit(index, updates);
                                            }}
                                            sx={{ fontSize: '0.8rem' }}
                                          >
                                            {getReturnFittings(circuit.fittingCategory || 'Ниппели').map(f => (
                                              <MenuItem 
                                                key={f.id} 
                                                value={f.id} 
                                                onClick={(e) => {
                                                  if (!f.isCompatible) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setIncompatibilityDialog({
                                                      open: true,
                                                      message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                    });
                                                  }
                                                }}
                                                sx={{ 
                                                  fontSize: '0.8rem',
                                                  ...(!f.isCompatible && {
                                                    color: 'text.disabled',
                                                    textDecoration: 'line-through',
                                                    backgroundColor: 'transparent',
                                                  }),
                                                }}
                                              >
                                                {f.label}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      
                                      <TableCell>
                                        {circuit.fitting?.startsWith('Flanec_') || circuit.fitting?.startsWith('Nak_Gaika_') || circuit.fitting?.startsWith('Shaib_Nar_Rezba_') ? (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.flangePipeLength ?? ''}
                                            placeholder="Авто"
                                            onChange={(e) => updateReturnCircuit(index, 'flangePipeLength', e.target.value ? Number(e.target.value) : null)}
                                            sx={{ width: 75 }}
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        ) : (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.fittingLength ?? ''}
                                            onChange={(e) => updateReturnCircuit(index, 'fittingLength', e.target.value ? Number(e.target.value) : null)}
                                            placeholder="Авто"
                                            sx={{ width: 75 }}
                                          />
                                        )}
                                      </TableCell>
                                      
                                      <TableCell>
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={circuit.circuitSpacing ?? 50}
                                          onChange={(e) => updateReturnCircuit(index, 'circuitSpacing', e.target.value ? Number(e.target.value) : 50)}
                                          sx={{ width: 75 }}
                                          title="Межосевое расстояние внутри контура (между двумя патрубками)"
                                        />
                                      </TableCell>
                                      
                                      <TableCell>
                                        {index < returnCircuits.length - 1 && (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.spacingToNext ?? ''}
                                            onChange={(e) => updateReturnCircuit(index, 'spacingToNext', e.target.value ? Number(e.target.value) : null)}
                                            placeholder={String(params.returnSpacingBetweenCircuits)}
                                            sx={{ width: 75 }}
                                          />
                                        )}
                                      </TableCell>
                                      
                                      <TableCell align="center">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const currentValue = circuit.swapOrder ?? false;
                                            updateReturnCircuit(index, 'swapOrder', !currentValue);
                                          }}
                                          color={(circuit.swapOrder ?? false) ? 'primary' : 'default'}
                                          title={(circuit.swapOrder ?? false) ? 'Д-К' : 'К-Д'}
                                        >
                                          <SwapHorizIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                      
                                      <TableCell>
                                        <IconButton
                                          size="small"
                                          onClick={() => deleteReturnCircuit(index)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        </Grid>
                      </Grid>
                      
                      {/* Центральные фитинги (термометры и лицевые подключения) */}
                      <Divider sx={{ my: 3 }} />
                      
                      <Grid container spacing={3}>
                        {/* ЛЕВАЯ КОЛОНКА - ПОДАЮЩИЙ КОЛЛЕКТОР */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 2, bgcolor: '#fff5f5' }}>
                            <Typography variant="subtitle2" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
                              Фитинги термометров и лицевых подключений (подача)
                            </Typography>
                            
                            <CenterFittingsUI
                              centerFittings={supplyCenterFittings}
                              centerFittingCount={params.supplyCenterFittingCount}
                              onUpdateCount={updateSupplyCenterFittingCount}
                              onUpdateFitting={updateSupplyCenterFitting}
                              onDeleteFitting={deleteSupplyCenterFitting}
                              centerFittingCategories={centerFittingCategories}
                              getFittingsByCategory={(cat) => {
                                if (params.shape === 'square') {
                                  return getFittingsByCategory(cat, null, null).map(f => ({
                                    ...f,
                                    isCompatible: isEndFittingCompatibleWithSquareProfile(f.id, params.supplyPipeProfile)
                                  }));
                                }
                                return getFittingsByCategory(cat, params.supplyPipeDiameter, isCenterFittingCompatible);
                              }}
                              isFittingCompatibleWithMainPipe={(fittingId) => params.shape === 'square' 
                                ? isEndFittingCompatibleWithSquareProfile(fittingId, params.supplyPipeProfile)
                                : isCenterFittingCompatible(fittingId, params.supplyPipeDiameter)}
                              isFittingCompatibleWithSquareProfile={isEndFittingCompatibleWithSquareProfile}
                              mainPipeDiameter={params.supplyPipeDiameter}
                              shape={params.shape}
                              mainPipeProfile={params.supplyPipeProfile}
                              circuitCount={supplyCircuits.length}
                              bottomCircuitCount={returnCircuits.length}
                              onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                              mode="distribution"
                              currentRow="supply"
                            />
                          </Paper>
                        </Grid>
                        
                        {/* ПРАВАЯ КОЛОНКА - ОБРАТНЫЙ КОЛЛЕКТОР */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 2, bgcolor: '#f5f5ff' }}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                              Фитинги термометров и лицевых подключений (обратка)
                            </Typography>
                            
                            <CenterFittingsUI
                              centerFittings={returnCenterFittings}
                              centerFittingCount={params.returnCenterFittingCount}
                              onUpdateCount={updateReturnCenterFittingCount}
                              onUpdateFitting={updateReturnCenterFitting}
                              onDeleteFitting={deleteReturnCenterFitting}
                              centerFittingCategories={centerFittingCategories}
                              getFittingsByCategory={(cat) => {
                                if (params.shape === 'square') {
                                  return getFittingsByCategory(cat, null, null).map(f => ({
                                    ...f,
                                    isCompatible: isEndFittingCompatibleWithSquareProfile(f.id, params.returnPipeProfile)
                                  }));
                                }
                                return getFittingsByCategory(cat, params.returnPipeDiameter, isCenterFittingCompatible);
                              }}
                              isFittingCompatibleWithMainPipe={(fittingId) => params.shape === 'square' 
                                ? isEndFittingCompatibleWithSquareProfile(fittingId, params.returnPipeProfile)
                                : isCenterFittingCompatible(fittingId, params.returnPipeDiameter)}
                              isFittingCompatibleWithSquareProfile={isEndFittingCompatibleWithSquareProfile}
                              mainPipeDiameter={params.returnPipeDiameter}
                              shape={params.shape}
                              mainPipeProfile={params.returnPipeProfile}
                              circuitCount={returnCircuits.length}
                              bottomCircuitCount={supplyCircuits.length}
                              onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                              mode="distribution"
                              currentRow="return"
                            />
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Дополнительные выходы */}
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" gutterBottom>Дополнительные выходы</Typography>
                      
                      <Grid container spacing={3}>
                        {/* ЛЕВАЯ КОЛОНКА - ПОДАЮЩИЙ КОЛЛЕКТОР */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 2, bgcolor: '#fff5f5' }}>
                            <Typography variant="subtitle2" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
                              Дополнительные выходы (подача)
                            </Typography>
                            
                            <AdditionalOutletsUI
                              outlets={supplyAdditionalOutlets}
                              outletCount={params.supplyAdditionalOutletCount}
                              onUpdateCount={updateSupplyAdditionalOutletCount}
                              onUpdateOutlet={updateSupplyAdditionalOutlet}
                              onDeleteOutlet={deleteSupplyAdditionalOutlet}
                              fittingCategories={circuitFittingCategories}
                              getFittingsByCategory={(cat, outlet) => {
                                if (params.shape === 'square') {
                                  return getFittingsByCategory(cat, null, null).map(f => ({
                                    ...f,
                                    isCompatible: isEndFittingCompatibleWithSquareProfile(f.id, params.supplyPipeProfile)
                                  }));
                                }
                                const outletType = outlet?.outletType || 'short';
                                return getFittingsByCategory(cat, params.supplyPipeDiameter, (fId) => 
                                  isAdditionalOutletFittingCompatible(fId, params.supplyPipeDiameter, outletType)
                                );
                              }}
                              isFittingCompatibleWithMainPipe={(fittingId, mainPipeDiameter, outlet) => {
                                if (params.shape === 'square') {
                                  return isEndFittingCompatibleWithSquareProfile(fittingId, params.supplyPipeProfile);
                                }
                                const outletType = outlet?.outletType || 'short';
                                return isAdditionalOutletFittingCompatible(fittingId, params.supplyPipeDiameter, outletType);
                              }}
                              mainPipeDiameter={params.supplyPipeDiameter}
                              circuitCount={supplyCircuits.length}
                              bottomCircuitCount={returnCircuits.length}
                              onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                              availableDiameters={collectorAvailableDiameters}
                              fittingsData={fittingsData}
                              collectorType="supply"
                              mode="distribution"
                              currentRow="supply"
                            />
                          </Paper>
                        </Grid>
                        
                        {/* ПРАВАЯ КОЛОНКА - ОБРАТНЫЙ КОЛЛЕКТОР */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 2, bgcolor: '#f5f5ff' }}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                              Дополнительные выходы (обратка)
                            </Typography>
                            
                            <AdditionalOutletsUI
                              outlets={returnAdditionalOutlets}
                              outletCount={params.returnAdditionalOutletCount}
                              onUpdateCount={updateReturnAdditionalOutletCount}
                              onUpdateOutlet={updateReturnAdditionalOutlet}
                              onDeleteOutlet={deleteReturnAdditionalOutlet}
                              fittingCategories={circuitFittingCategories}
                              getFittingsByCategory={(cat, outlet) => {
                                if (params.shape === 'square') {
                                  return getFittingsByCategory(cat, null, null).map(f => ({
                                    ...f,
                                    isCompatible: isEndFittingCompatibleWithSquareProfile(f.id, params.returnPipeProfile)
                                  }));
                                }
                                const outletType = outlet?.outletType || 'short';
                                return getFittingsByCategory(cat, params.returnPipeDiameter, (fId) => 
                                  isAdditionalOutletFittingCompatible(fId, params.returnPipeDiameter, outletType)
                                );
                              }}
                              isFittingCompatibleWithMainPipe={(fittingId, mainPipeDiameter, outlet) => {
                                if (params.shape === 'square') {
                                  return isEndFittingCompatibleWithSquareProfile(fittingId, params.returnPipeProfile);
                                }
                                const outletType = outlet?.outletType || 'short';
                                return isAdditionalOutletFittingCompatible(fittingId, params.returnPipeDiameter, outletType);
                              }}
                              mainPipeDiameter={params.returnPipeDiameter}
                              circuitCount={returnCircuits.length}
                              bottomCircuitCount={supplyCircuits.length}
                              onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                              availableDiameters={collectorAvailableDiameters}
                              fittingsData={fittingsData}
                              collectorType="return"
                              mode="distribution"
                              currentRow="return"
                            />
                          </Paper>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  {/* Подвкладка 3: Подключения */}
                  {collectorTabValue === 2 && (
                    <Paper sx={{ p: 3 }} elevation={0}>
                      <Typography variant="h6" gutterBottom>Торцевые подключения</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Настройка {isSystemFlipped ? 'левых' : 'правых'} торцевых подключений коллекторов ({isSystemFlipped ? 'правая' : 'левая'} сторона соединена с гидроразделителем)
                      </Typography>
                      
                      {/* Кнопка синхронизации торцевых подключений */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
                        <Button
                          variant={rightConnectionsLinked ? 'contained' : 'outlined'}
                          onClick={() => {
                            const newLinkedState = !rightConnectionsLinked;
                            setRightConnectionsLinked(newLinkedState);
                            // Если включаем синхронизацию, копируем настройки подающего в обратный
                            if (newLinkedState) {
                              updateParam({
                                // Правые подключения
                                hasReturnRightConnection: params.hasSupplyRightConnection,
                                returnRightConnectionType: params.supplyRightConnectionType,
                                returnRightConnectionFittingCategory: params.supplyRightConnectionFittingCategory,
                                returnRightConnectionFitting: params.supplyRightConnectionFitting,
                                returnRightConnectionFittingLength: params.supplyRightConnectionFittingLength,
                                returnRightConnectionLength: params.supplyRightConnectionLength,
                                // Правые заглушки и переходы
                                hasReturnRightCap: params.hasSupplyRightCap,
                                returnRightCapType: params.supplyRightCapType,
                                returnRightCapElementType: params.supplyRightCapElementType,
                                returnRightReducer: params.supplyRightReducer,
                              });
                            }
                          }}
                          startIcon={rightConnectionsLinked ? '🔗' : '🔓'}
                          sx={{ mb: 1 }}
                        >
                          {rightConnectionsLinked ? 'Синхронизация ВКЛ' : 'Синхронизация ВЫКЛ'}
                        </Button>
                        {rightConnectionsLinked && (
                          <Chip 
                            label="Изменения в одном коллекторе автоматически применятся к другому" 
                            size="small" 
                            color="info" 
                            sx={{ ml: 2 }} 
                          />
                        )}
                      </Box>
                      
                      <Divider sx={{ mb: 3 }} />
                      
                      {/* Подающий коллектор - справа */}
                      <Typography variant="subtitle2" color="error" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Подающий коллектор - Подключение {isSystemFlipped ? 'слева' : 'справа'}
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4} sm={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={params.hasSupplyRightConnection}
                                onChange={(e) => updateParam('hasSupplyRightConnection', e.target.checked)}
                                size="small"
                              />
                            }
                            label={<Typography variant="body2">Вкл.</Typography>}
                          />
                        </Grid>
                        {params.hasSupplyRightConnection && (
                          <>
                            <Grid item xs={6} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel shrink>Категория</InputLabel>
                                <Select
                                  value={params.supplyRightConnectionFittingCategory || ''}
                                  label="Категория"
                                  displayEmpty
                                  notched
                                  onChange={(e) => {
                                    const newCategory = e.target.value;
                                    updateParam({
                                      supplyRightConnectionFittingCategory: newCategory,
                                      supplyRightConnectionFitting: '',
                                      supplyRightConnectionFittingLength: null
                                    });
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Выбрать</em>
                                  </MenuItem>
                                  {circuitFittingCategories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                      {cat.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel shrink>Фитинг</InputLabel>
                                <Select
                                  value={params.supplyRightConnectionFitting || ''}
                                  label="Фитинг"
                                  displayEmpty
                                  notched
                                  onChange={(e) => {
                                    const newFitting = e.target.value;
                                    
                                    const isSupplyRightFittingCompatible = params.shape === 'square'
                                      ? isEndFittingCompatibleWithSquareProfile(newFitting, params.supplyPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(newFitting, params.supplyPipeDiameter);
                                    if (newFitting && !isSupplyRightFittingCompatible) {
                                      setIncompatibilityDialog({
                                        open: true,
                                        message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                      });
                                      return;
                                    }
                                    
                                    const updates = {
                                      supplyRightConnectionFitting: newFitting,
                                      supplyRightConnectionFittingLength: null,
                                      supplyRightConnectionLength: null
                                    };
                                    
                                    // Автоматически определяем нужны ли заглушки/переходы
                                    const capUpdates = getCapUpdatesForConnectionFitting(
                                      newFitting,
                                      params.supplyPipeDiameter,
                                      'right',
                                      'supply',
                                      params.shape
                                    );
                                    
                                    updateParam({ ...updates, ...capUpdates });
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Выбрать</em>
                                  </MenuItem>
                                  {getFittingsByCategory(params.supplyRightConnectionFittingCategory, params.supplyPipeDiameter).map((f) => {
                                    const isCompatible = params.shape === 'square' 
                                      ? isEndFittingCompatibleWithSquareProfile(f.id, params.supplyPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(f.id, params.supplyPipeDiameter);
                                    return (
                                      <MenuItem 
                                        key={f.id} 
                                        value={f.id}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                        }}
                                      >
                                        {f.label}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Grid>
                            {shouldShowConnectionLengthField(params.supplyRightConnectionFitting, params.supplyPipeDiameter) && (
                              <Grid item xs={6} sm={2}>
                                <TextField
                                  label="Вылет"
                                  type="number"
                                  size="small"
                                  value={(() => {
                                    const useConnectionLength = shouldUseConnectionLength(
                                      params.supplyRightConnectionFitting,
                                      params.hasSupplyRightCap,
                                      params.supplyPipeDiameter
                                    );
                                    
                                    if (useConnectionLength) {
                                      // Для фланцев/гаек/шайб с переходами/заглушками
                                      return params.supplyRightConnectionLength ?? '';
                                    } else {
                                      // Для обычных фитингов
                                      return params.supplyRightConnectionFittingLength ?? '';
                                    }
                                  })()}
                                  placeholder="Авто"
                                  onChange={(e) => {
                                    const useConnectionLength = shouldUseConnectionLength(
                                      params.supplyRightConnectionFitting,
                                      params.hasSupplyRightCap,
                                      params.supplyPipeDiameter
                                    );
                                    
                                    if (useConnectionLength) {
                                      updateParam('supplyRightConnectionLength', e.target.value ? Number(e.target.value) : null);
                                    } else {
                                      updateParam('supplyRightConnectionFittingLength', e.target.value ? Number(e.target.value) : null);
                                    }
                                  }}
                                  InputProps={{ inputProps: { min: 0, step: 1 } }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>

                      <Divider sx={{ my: 3 }} />

                      {/* Обратный коллектор - справа */}
                      <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Обратный коллектор - Подключение {isSystemFlipped ? 'слева' : 'справа'}
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4} sm={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={params.hasReturnRightConnection}
                                onChange={(e) => updateParam('hasReturnRightConnection', e.target.checked)}
                                size="small"
                              />
                            }
                            label={<Typography variant="body2">Вкл.</Typography>}
                          />
                        </Grid>
                        {params.hasReturnRightConnection && (
                          <>
                            <Grid item xs={6} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel shrink>Категория</InputLabel>
                                <Select
                                  value={params.returnRightConnectionFittingCategory || ''}
                                  label="Категория"
                                  displayEmpty
                                  notched
                                  onChange={(e) => {
                                    const newCategory = e.target.value;
                                    updateParam({
                                      returnRightConnectionFittingCategory: newCategory,
                                      returnRightConnectionFitting: '',
                                      returnRightConnectionFittingLength: null
                                    });
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Выбрать</em>
                                  </MenuItem>
                                  {circuitFittingCategories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                      {cat.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel shrink>Фитинг</InputLabel>
                                <Select
                                  value={params.returnRightConnectionFitting || ''}
                                  label="Фитинг"
                                  displayEmpty
                                  notched
                                  onChange={(e) => {
                                    const newFitting = e.target.value;
                                    
                                    const isReturnRightFittingCompatible = params.shape === 'square'
                                      ? isEndFittingCompatibleWithSquareProfile(newFitting, params.returnPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(newFitting, params.returnPipeDiameter);
                                    if (newFitting && !isReturnRightFittingCompatible) {
                                      setIncompatibilityDialog({
                                        open: true,
                                        message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                      });
                                      return;
                                    }
                                    
                                    const updates = {
                                      returnRightConnectionFitting: newFitting,
                                      returnRightConnectionFittingLength: null,
                                      returnRightConnectionLength: null
                                    };
                                    
                                    // Автоматически определяем нужны ли заглушки/переходы
                                    const capUpdates = getCapUpdatesForConnectionFitting(
                                      newFitting,
                                      params.returnPipeDiameter,
                                      'right',
                                      'return',
                                      params.shape
                                    );
                                    
                                    updateParam({ ...updates, ...capUpdates });
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Выбрать</em>
                                  </MenuItem>
                                  {getFittingsByCategory(params.returnRightConnectionFittingCategory, params.returnPipeDiameter).map((f) => {
                                    const isCompatible = params.shape === 'square' 
                                      ? isEndFittingCompatibleWithSquareProfile(f.id, params.returnPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(f.id, params.returnPipeDiameter);
                                    return (
                                      <MenuItem 
                                        key={f.id} 
                                        value={f.id}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                        }}
                                      >
                                        {f.label}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Grid>
                            {shouldShowConnectionLengthField(params.returnRightConnectionFitting, params.returnPipeDiameter) && (
                              <Grid item xs={6} sm={2}>
                                <TextField
                                  label="Вылет"
                                  type="number"
                                  size="small"
                                  value={(() => {
                                    const useConnectionLength = shouldUseConnectionLength(
                                      params.returnRightConnectionFitting,
                                      params.hasReturnRightCap,
                                      params.returnPipeDiameter
                                    );
                                    
                                    if (useConnectionLength) {
                                      // Для фланцев/гаек/шайб с переходами/заглушками
                                      return params.returnRightConnectionLength ?? '';
                                    } else {
                                      // Для обычных фитингов
                                      return params.returnRightConnectionFittingLength ?? '';
                                    }
                                  })()}
                                  placeholder="Авто"
                                  onChange={(e) => {
                                    const useConnectionLength = shouldUseConnectionLength(
                                      params.returnRightConnectionFitting,
                                      params.hasReturnRightCap,
                                      params.returnPipeDiameter
                                    );
                                    
                                    if (useConnectionLength) {
                                      updateParam('returnRightConnectionLength', e.target.value ? Number(e.target.value) : null);
                                    } else {
                                      updateParam('returnRightConnectionFittingLength', e.target.value ? Number(e.target.value) : null);
                                    }
                                  }}
                                  InputProps={{ inputProps: { min: 0, step: 1 } }}
                                  InputLabelProps={{ shrink: true }}
                                />
              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </Paper>
                  )}

                  {/* Подвкладка 4: Заглушки */}
                  {collectorTabValue === 3 && (
                    <Paper sx={{ p: 3 }} elevation={0}>
                      <Typography variant="h6" gutterBottom>Заглушки и переходы конц.</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Настройка {isSystemFlipped ? 'левых' : 'правых'} заглушек/переходов ({isSystemFlipped ? 'правая' : 'левая'} сторона соединена с гидроразделителем)
                      </Typography>
                      
                      {/* Подающий коллектор */}
                      <Typography variant="subtitle2" color="error" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Подающий коллектор - {isSystemFlipped ? 'слева' : 'справа'}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={params.hasSupplyRightCap}
                                  onChange={(e) => updateParam('hasSupplyRightCap', e.target.checked)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Включить</Typography>}
                            />
                            {params.hasSupplyRightCap && (
                              <>
                                {/* Для квадратных форм только прямые заглушки */}
                                {params.shape === 'square' ? (
                                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    Прямая заглушка
                                  </Typography>
                                ) : (
                                  <>
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                      <InputLabel>Элемент</InputLabel>
                                      <Select
                                        value={params.supplyRightCapElementType || 'cap'}
                                        label="Элемент"
                                        onChange={(e) => updateParam('supplyRightCapElementType', e.target.value)}
                                      >
                                        <MenuItem value="cap">Заглушка</MenuItem>
                                        <MenuItem value="reducer">Переход конц.</MenuItem>
                                      </Select>
                                    </FormControl>
                                    {params.supplyRightCapElementType === 'cap' || !params.supplyRightCapElementType ? (
                                      <FormControl size="small" sx={{ minWidth: 130 }}>
                                        <InputLabel>Тип</InputLabel>
                                        <Select
                                          value={params.supplyRightCapType}
                                          label="Тип"
                                          onChange={(e) => updateParam('supplyRightCapType', e.target.value)}
                                        >
                                          {params.supplyPipeDiameter <= 101.6 && (
                                            <MenuItem value="straight">Прямая</MenuItem>
                                          )}
                                          {params.shape === 'round' && (
                                            <MenuItem value="elliptical">Эллиптическая</MenuItem>
                                          )}
                                        </Select>
                                      </FormControl>
                                    ) : (
                                      <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel>Переход</InputLabel>
                                        <Select
                                          value={params.supplyRightReducer || concentricReducers[0]?.id}
                                          label="Переход"
                                          onChange={(e) => updateParam('supplyRightReducer', e.target.value)}
                                        >
                                          {concentricReducers.map((r) => (
                                            <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </Box>
                          {params.hasSupplyRightCap && (
                            <Typography variant="caption" color="text.secondary">
                              {params.supplyRightCapElementType === 'reducer'
                                ? `${fittingsData[params.supplyRightReducer]?.displayName || 'Переход'}`
                                : params.supplyRightCapType === 'straight' 
                                  ? params.shape === 'square'
                                    ? `Заглушка прямая ${params.supplyPipeProfile}`
                                    : `Заглушка прямая ⌀${params.supplyPipeDiameter}`
                                  : `Заглушка эллиптическая ⌀${params.supplyPipeDiameter}`
                              }
                            </Typography>
                          )}
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 3 }} />

                      {/* Обратный коллектор */}
                      <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Обратный коллектор - {isSystemFlipped ? 'слева' : 'справа'}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={params.hasReturnRightCap}
                                  onChange={(e) => updateParam('hasReturnRightCap', e.target.checked)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Включить</Typography>}
                            />
                            {params.hasReturnRightCap && (
                              <>
                                {/* Для квадратных форм только прямые заглушки */}
                                {params.shape === 'square' ? (
                                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    Прямая заглушка
                                  </Typography>
                                ) : (
                                  <>
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                      <InputLabel>Элемент</InputLabel>
                                      <Select
                                        value={params.returnRightCapElementType || 'cap'}
                                        label="Элемент"
                                        onChange={(e) => updateParam('returnRightCapElementType', e.target.value)}
                                      >
                                        <MenuItem value="cap">Заглушка</MenuItem>
                                        <MenuItem value="reducer">Переход конц.</MenuItem>
                                      </Select>
                                    </FormControl>
                                    {params.returnRightCapElementType === 'cap' || !params.returnRightCapElementType ? (
                                      <FormControl size="small" sx={{ minWidth: 130 }}>
                                        <InputLabel>Тип</InputLabel>
                                        <Select
                                          value={params.returnRightCapType}
                                          label="Тип"
                                          onChange={(e) => updateParam('returnRightCapType', e.target.value)}
                                        >
                                          {params.returnPipeDiameter <= 101.6 && (
                                            <MenuItem value="straight">Прямая</MenuItem>
                                          )}
                                          {params.shape === 'round' && (
                                            <MenuItem value="elliptical">Эллиптическая</MenuItem>
                                          )}
                                        </Select>
                                      </FormControl>
                                    ) : (
                                      <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel>Переход</InputLabel>
                                        <Select
                                          value={params.returnRightReducer || concentricReducers[0]?.id}
                                          label="Переход"
                                          onChange={(e) => updateParam('returnRightReducer', e.target.value)}
                                        >
                                          {concentricReducers.map((r) => (
                                            <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </Box>
                          {params.hasReturnRightCap && (
                            <Typography variant="caption" color="text.secondary">
                              {params.returnRightCapElementType === 'reducer'
                                ? `${fittingsData[params.returnRightReducer]?.displayName || 'Переход'}`
                                : params.returnRightCapType === 'straight' 
                                  ? params.shape === 'square'
                                    ? `Заглушка прямая ${params.returnPipeProfile}`
                                    : `Заглушка прямая ⌀${params.returnPipeDiameter}`
                                  : `Заглушка эллиптическая ⌀${params.returnPipeDiameter}`
                              }
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </Box>
              )}

              {/* ВКЛАДКА 3: ПРЕДКОТЛОВОЙ КОЛЛЕКТОР */}
              {tabValue === 2 && hasPreBoilerCollector && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Настройка предкотлового коллектора
                  </Typography>

                  {/* Подвкладки для предкотлового коллектора */}
                  <Tabs 
                    value={preBoilerTabValue} 
                    onChange={(e, v) => setPreBoilerTabValue(v)} 
                    sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Tab label="Основные параметры" />
                    <Tab label="Контуры" />
                    <Tab label="Подключения" />
                    <Tab label="Заглушки" />
                  </Tabs>

                  {/* Подвкладка 1: Основные параметры */}
                  {preBoilerTabValue === 0 && (
                    <Paper sx={{ p: 3, bgcolor: '#fafafa' }} elevation={0}>
                      <Typography variant="h6" gutterBottom>Основные параметры</Typography>
                      <Grid container spacing={3} alignItems="center">
                        {/* Первая строка: диаметры */}
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>{params.shape === 'round' ? 'Диаметр подающего коллектора' : 'Профиль подающего коллектора'}</InputLabel>
                            <Select
                              value={params.shape === 'round' ? params.preBoilerSupplyPipeDiameter : (params.preBoilerSupplyPipeProfile || '80x80')}
                              label={params.shape === 'round' ? 'Диаметр подающего коллектора' : 'Профиль подающего коллектора'}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                
                                if (params.shape === 'round') {
                                const newDiameter = newValue;
                                
                                // Проверяем совместимость выбранного диаметра
                                if (!isCollectorDiameterCompatible(newDiameter, params.mainPipeDiameter)) {
                                  setIncompatibilityDialog({
                                    open: true,
                                    message: `Диаметр трубы коллектора ${newDiameter}мм несовместим с диаметром гидроразделителя ${params.mainPipeDiameter}мм. Максимально допустимый диаметр: ${getMaxCollectorPipeDiameter(params.mainPipeDiameter)}мм`,
                                  });
                                  return; // Прерываем обработку
                                }
                                
                                // Обновляем центральные фитинги подающего коллектора
                                const updatedSupplyCenterFittings = updateCenterFittingsForDiameter(
                                  preBoilerSupplyCenterFittings,
                                  newDiameter,
                                  isCenterFittingCompatible,
                                  getFittingsByCategory
                                );
                                if (JSON.stringify(updatedSupplyCenterFittings) !== JSON.stringify(preBoilerSupplyCenterFittings)) {
                                  setPreBoilerSupplyCenterFittings(updatedSupplyCenterFittings);
                                }
                                
                                // Обновляем контуры подающего коллектора
                                const updatedSupplyCircuits = preBoilerSupplyCircuits.map((circuit) => {
                                  const fittingId = circuit.fittingId || circuit.fitting;
                                  if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, newDiameter)) {
                                    const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', newDiameter, isCircuitFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...circuit,
                                        fitting: maxCompatibleFitting.id,
                                        fittingId: maxCompatibleFitting.id,
                                        diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                        fittingLength: null,
                                      };
                                    } else {
                                      return {
                                        ...circuit,
                                        type: 'pipe',
                                        fitting: null,
                                        fittingId: null,
                                        fittingCategory: null,
                                        fittingLength: null,
                                        diameter: newDiameter,
                                        length: 100,
                                      };
                                    }
                                  }
                                  return circuit;
                                });
                                if (JSON.stringify(updatedSupplyCircuits) !== JSON.stringify(preBoilerSupplyCircuits)) {
                                  setPreBoilerSupplyCircuits(updatedSupplyCircuits);
                                }
                                
                                // Обновляем дополнительные выходы подающего коллектора
                                const updatedSupplyAdditionalOutlets = preBoilerSupplyAdditionalOutlets.map((outlet) => {
                                  if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatible(outlet.fittingId, newDiameter)) {
                                    const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', newDiameter, isAdditionalOutletFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...outlet,
                                        fittingId: maxCompatibleFitting.id,
                                        diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                                        fittingLength: null,
                                      };
                                    } else {
                                      return {
                                        ...outlet,
                                        type: 'pipe',
                                        fittingId: null,
                                        fittingCategory: null,
                                        fittingLength: null,
                                        diameter: newDiameter,
                                        length: 100,
                                      };
                                    }
                                  }
                                  return outlet;
                                });
                                if (JSON.stringify(updatedSupplyAdditionalOutlets) !== JSON.stringify(preBoilerSupplyAdditionalOutlets)) {
                                  setPreBoilerSupplyAdditionalOutlets(updatedSupplyAdditionalOutlets);
                                }
                                
                                // Обновляем левое торцевое подключение подающего коллектора
                                const supplyLeftConnectionUpdates = {};
                                if (params.hasPreBoilerSupplyLeftConnection && params.preBoilerSupplyLeftConnectionType === 'fitting' && params.preBoilerSupplyLeftConnectionFitting) {
                                  if (!isEndFittingCompatibleWithMainPipe(params.preBoilerSupplyLeftConnectionFitting, newDiameter)) {
                                    // Фитинг несовместим - пытаемся найти совместимый в той же категории
                                    const currentCategory = params.preBoilerSupplyLeftConnectionFittingCategory || 'Ниппели';
                                    const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      supplyLeftConnectionUpdates.preBoilerSupplyLeftConnectionFitting = maxCompatibleFitting.id;
                                      supplyLeftConnectionUpdates.preBoilerSupplyLeftConnectionFittingLength = null;
                                    }
                                  }
                                  
                                  const capUpdates = getCapUpdatesForConnectionFitting(
                                    supplyLeftConnectionUpdates.preBoilerSupplyLeftConnectionFitting || params.preBoilerSupplyLeftConnectionFitting,
                                    newDiameter,
                                    'left',
                                    'preBoilerSupply',
                                    params.shape
                                  );
                                  Object.assign(supplyLeftConnectionUpdates, capUpdates);
                                }
                                // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                                else if (params.hasPreBoilerSupplyLeftCap && params.preBoilerSupplyLeftCapElementType === 'cap' && params.shape !== 'square') {
                                  const ellipticalCap = findEllipticalCap(newDiameter);
                                  if (ellipticalCap && params.preBoilerSupplyLeftCapType !== 'elliptical') {
                                    supplyLeftConnectionUpdates.preBoilerSupplyLeftCapType = 'elliptical';
                                  }
                                }
                                
                                if (Object.keys(supplyLeftConnectionUpdates).length > 0) {
                                  updateParam(supplyLeftConnectionUpdates);
                                }
                                
                                // Синхронизация с обраткой если включена
                                if (preBoilerDiametersLinked) {
                                  updateParam({ preBoilerSupplyPipeDiameter: newDiameter, preBoilerReturnPipeDiameter: newDiameter });
                                  
                                  // Обновляем центральные фитинги обратки
                                  const updatedReturnCenterFittings = updateCenterFittingsForDiameter(
                                    preBoilerReturnCenterFittings,
                                    newDiameter,
                                    isCenterFittingCompatible,
                                    getFittingsByCategory
                                  );
                                  if (JSON.stringify(updatedReturnCenterFittings) !== JSON.stringify(preBoilerReturnCenterFittings)) {
                                    setPreBoilerReturnCenterFittings(updatedReturnCenterFittings);
                                  }
                                  
                                  // Обновляем контуры обратки
                                  const updatedReturnCircuits = preBoilerReturnCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, newDiameter)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', newDiameter, isCircuitFittingCompatible);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      } else {
                                        return {
                                          ...circuit,
                                          type: 'pipe',
                                          fitting: null,
                                          fittingId: null,
                                          fittingCategory: null,
                                          fittingLength: null,
                                          diameter: newDiameter,
                                          length: 100,
                                        };
                                      }
                                    }
                                    return circuit;
                                  });
                                  if (JSON.stringify(updatedReturnCircuits) !== JSON.stringify(preBoilerReturnCircuits)) {
                                    setPreBoilerReturnCircuits(updatedReturnCircuits);
                                  }
                                  
                                  // Обновляем дополнительные выходы обратки
                                  const updatedReturnAdditionalOutlets = preBoilerReturnAdditionalOutlets.map((outlet) => {
                                    if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatible(outlet.fittingId, newDiameter)) {
                                      const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', newDiameter, isAdditionalOutletFittingCompatible);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...outlet,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                                          fittingLength: null,
                                        };
                                      } else {
                                        return {
                                          ...outlet,
                                          type: 'pipe',
                                          fittingId: null,
                                          fittingCategory: null,
                                          fittingLength: null,
                                          diameter: newDiameter,
                                          length: 100,
                                        };
                                      }
                                    }
                                    return outlet;
                                  });
                                  if (JSON.stringify(updatedReturnAdditionalOutlets) !== JSON.stringify(preBoilerReturnAdditionalOutlets)) {
                                    setPreBoilerReturnAdditionalOutlets(updatedReturnAdditionalOutlets);
                                  }
                                  
                                  // Обновляем левое торцевое подключение обратного коллектора
                                  const returnLeftConnectionUpdates = {};
                                  if (params.hasPreBoilerReturnLeftConnection && params.preBoilerReturnLeftConnectionType === 'fitting' && params.preBoilerReturnLeftConnectionFitting) {
                                    if (!isEndFittingCompatibleWithMainPipe(params.preBoilerReturnLeftConnectionFitting, newDiameter)) {
                                      // Фитинг несовместим - пытаемся найти совместимый в той же категории
                                      const currentCategory = params.preBoilerReturnLeftConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                                      const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        returnLeftConnectionUpdates.preBoilerReturnLeftConnectionFitting = maxCompatibleFitting.id;
                                        returnLeftConnectionUpdates.preBoilerReturnLeftConnectionFittingLength = null;
                                      }
                                    }
                                    
                                    const capUpdates = getCapUpdatesForConnectionFitting(
                                      returnLeftConnectionUpdates.preBoilerReturnLeftConnectionFitting || params.preBoilerReturnLeftConnectionFitting,
                                      newDiameter,
                                      'left',
                                      'preBoilerReturn',
                                      params.shape
                                    );
                                    Object.assign(returnLeftConnectionUpdates, capUpdates);
                                  }
                                  // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                                  else if (params.hasPreBoilerReturnLeftCap && params.preBoilerReturnLeftCapElementType === 'cap' && params.shape !== 'square') {
                                    const ellipticalCap = findEllipticalCap(newDiameter);
                                    if (ellipticalCap && params.preBoilerReturnLeftCapType !== 'elliptical') {
                                      returnLeftConnectionUpdates.preBoilerReturnLeftCapType = 'elliptical';
                                    }
                                  }
                                  
                                  if (Object.keys(returnLeftConnectionUpdates).length > 0) {
                                    updateParam(returnLeftConnectionUpdates);
                                  }
                                } else {
                                  updateParam('preBoilerSupplyPipeDiameter', newDiameter);
                                }
                                } else {
                                  // Квадратный профиль - обновляем профиль и автопереключаем несовместимые фитинги
                                  const newProfile = newValue;
                                  
                                  // Обновляем контуры подающего предкотлового коллектора
                                  const updatedPreBoilerSupplyCircuits = preBoilerSupplyCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, newProfile)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...circuit,
                                      fittingLength: null,
                                    };
                                  });
                                  setPreBoilerSupplyCircuits(updatedPreBoilerSupplyCircuits);
                                  
                                  // Обновляем левое подключение подающего предкотлового коллектора
                                  const preBoilerSupplyLeftConnectionUpdates = {};
                                  if (params.hasPreBoilerSupplyLeftConnection && params.preBoilerSupplyLeftConnectionType === 'fitting' && params.preBoilerSupplyLeftConnectionFitting) {
                                    if (!isFittingCompatibleWithSquareProfile(params.preBoilerSupplyLeftConnectionFitting, newProfile)) {
                                      const currentCategory = params.preBoilerSupplyLeftConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isFittingCompatibleWithSquareProfile(f.id, newProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        preBoilerSupplyLeftConnectionUpdates.preBoilerSupplyLeftConnectionFitting = maxCompatibleFitting.id;
                                        preBoilerSupplyLeftConnectionUpdates.preBoilerSupplyLeftConnectionFittingLength = null;
                                      }
                                    } else {
                                      preBoilerSupplyLeftConnectionUpdates.preBoilerSupplyLeftConnectionFittingLength = null;
                                    }
                                  }
                                  
                                  updateParam({
                                    preBoilerSupplyPipeProfile: newProfile,
                                    ...preBoilerSupplyLeftConnectionUpdates,
                                  });
                                  
                                  // Синхронизация с обраткой если включена
                                  if (preBoilerDiametersLinked) {
                                    setPreBoilerReturnCircuits(updatedPreBoilerSupplyCircuits);
                                    updateParam('preBoilerReturnPipeProfile', newProfile);
                                  }
                                }
                              }}
                            >
                              {params.shape === 'round'
                                ? collectorAvailableDiameters.map(d => {
                                    const isCompatible = isCollectorDiameterCompatible(d.value, params.mainPipeDiameter);
                                    return (
                                      <MenuItem 
                                        key={d.value} 
                                        value={d.value}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: `Диаметр трубы коллектора ${d.label} несовместим с диаметром гидроразделителя ${params.mainPipeDiameter}мм. Максимально допустимый диаметр: ${getMaxCollectorPipeDiameter(params.mainPipeDiameter)}мм`,
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                          cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                          '&:hover': {
                                            backgroundColor: !isCompatible ? 'transparent' : undefined,
                                          },
                                        }}
                                      >
                                        {d.label}
                                      </MenuItem>
                                    );
                                  })
                                : availableSquareProfiles.map((p) => {
                                    const isCompatible = isCollectorProfileCompatible(p.value, params.mainPipeProfile);
                                    return (
                                      <MenuItem 
                                        key={p.value} 
                                        value={p.value}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: `Профиль коллектора ${p.label} несовместим с профилем гидроразделителя ${params.mainPipeProfile}. Профиль коллектора не может быть больше профиля гидроразделителя.`,
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                          cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                          '&:hover': {
                                            backgroundColor: !isCompatible ? 'transparent' : undefined,
                                          },
                                        }}
                                      >
                                        {p.label}
                                      </MenuItem>
                                    );
                                  })
                              }
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        {/* Кнопка синхронизации диаметров */}
                        <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Button
                            variant={preBoilerDiametersLinked ? 'contained' : 'outlined'}
                            onClick={() => {
                              const newLinkedState = !preBoilerDiametersLinked;
                              setPreBoilerDiametersLinked(newLinkedState);
                              if (newLinkedState) {
                                if (params.shape === 'round' && params.preBoilerSupplyPipeDiameter !== params.preBoilerReturnPipeDiameter) {
                                  updateParam('preBoilerReturnPipeDiameter', params.preBoilerSupplyPipeDiameter);
                                } else if (params.shape === 'square' && params.preBoilerSupplyPipeProfile !== params.preBoilerReturnPipeProfile) {
                                  const profile = params.preBoilerSupplyPipeProfile;
                                  const updatedReturnCircuits = preBoilerReturnCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, profile)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, profile));
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return { ...circuit, fittingLength: null };
                                  });
                                  setPreBoilerReturnCircuits(updatedReturnCircuits);
                                  updateParam('preBoilerReturnPipeProfile', profile);
                                }
                              }
                            }}
                            sx={{
                              minWidth: '3rem', // 48px
                              width: '3rem',
                              height: '3rem',
                              borderRadius: '0.25rem', // 4px
                              fontSize: '1.25rem', // 20px
                              fontWeight: 'bold',
                              padding: 0,
                            }}
                            title={preBoilerDiametersLinked ? 'Профили синхронизированы' : 'Профили независимы'}
                          >
                            =
                          </Button>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>{params.shape === 'round' ? 'Диаметр обратного коллектора' : 'Профиль обратного коллектора'}</InputLabel>
                            <Select
                              value={params.shape === 'round' ? params.preBoilerReturnPipeDiameter : (params.preBoilerReturnPipeProfile || '80x80')}
                              label={params.shape === 'round' ? 'Диаметр обратного коллектора' : 'Профиль обратного коллектора'}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                
                                if (params.shape === 'round') {
                                const newDiameter = newValue;
                                
                                // Проверяем совместимость выбранного диаметра
                                if (!isCollectorDiameterCompatible(newDiameter, params.mainPipeDiameter)) {
                                  setIncompatibilityDialog({
                                    open: true,
                                    message: `Диаметр трубы коллектора ${newDiameter}мм несовместим с диаметром гидроразделителя ${params.mainPipeDiameter}мм. Максимально допустимый диаметр: ${getMaxCollectorPipeDiameter(params.mainPipeDiameter)}мм`,
                                  });
                                  return; // Прерываем обработку
                                }
                                
                                // Аналогично подающему, но для обратки
                                const updatedReturnCenterFittings = updateCenterFittingsForDiameter(
                                  preBoilerReturnCenterFittings,
                                  newDiameter,
                                  isCenterFittingCompatible,
                                  getFittingsByCategory
                                );
                                if (JSON.stringify(updatedReturnCenterFittings) !== JSON.stringify(preBoilerReturnCenterFittings)) {
                                  setPreBoilerReturnCenterFittings(updatedReturnCenterFittings);
                                }
                                
                                const updatedReturnCircuits = preBoilerReturnCircuits.map((circuit) => {
                                  const fittingId = circuit.fittingId || circuit.fitting;
                                  if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatible(fittingId, newDiameter)) {
                                    const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', newDiameter, isCircuitFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...circuit,
                                        fitting: maxCompatibleFitting.id,
                                        fittingId: maxCompatibleFitting.id,
                                        diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                        fittingLength: null,
                                      };
                                    } else {
                                      return {
                                        ...circuit,
                                        type: 'pipe',
                                        fitting: null,
                                        fittingId: null,
                                        fittingCategory: null,
                                        fittingLength: null,
                                        diameter: newDiameter,
                                        length: 100,
                                      };
                                    }
                                  }
                                  return circuit;
                                });
                                if (JSON.stringify(updatedReturnCircuits) !== JSON.stringify(preBoilerReturnCircuits)) {
                                  setPreBoilerReturnCircuits(updatedReturnCircuits);
                                }
                                
                                const updatedReturnAdditionalOutlets = preBoilerReturnAdditionalOutlets.map((outlet) => {
                                  if (outlet.type === 'fitting' && outlet.fittingId && !isAdditionalOutletFittingCompatible(outlet.fittingId, newDiameter)) {
                                    const fittingsInCategory = getFittingsByCategory(outlet.fittingCategory || 'Ниппели', newDiameter, isAdditionalOutletFittingCompatible);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      return {
                                        ...outlet,
                                        fittingId: maxCompatibleFitting.id,
                                        diameter: fittingsData[maxCompatibleFitting.id]?.diameter || outlet.diameter,
                                        fittingLength: null,
                                      };
                                    } else {
                                      return {
                                        ...outlet,
                                        type: 'pipe',
                                        fittingId: null,
                                        fittingCategory: null,
                                        fittingLength: null,
                                        diameter: newDiameter,
                                        length: 100,
                                      };
                                    }
                                  }
                                  return outlet;
                                });
                                if (JSON.stringify(updatedReturnAdditionalOutlets) !== JSON.stringify(preBoilerReturnAdditionalOutlets)) {
                                  setPreBoilerReturnAdditionalOutlets(updatedReturnAdditionalOutlets);
                                }
                                
                                // Обновляем левое торцевое подключение обратного коллектора
                                const returnLeftConnectionUpdates = {};
                                if (params.hasPreBoilerReturnLeftConnection && params.preBoilerReturnLeftConnectionType === 'fitting' && params.preBoilerReturnLeftConnectionFitting) {
                                  if (!isEndFittingCompatibleWithMainPipe(params.preBoilerReturnLeftConnectionFitting, newDiameter)) {
                                    // Фитинг несовместим - пытаемся найти совместимый в той же категории
                                    const currentCategory = params.preBoilerReturnLeftConnectionFittingCategory || 'Ниппели';
                                    const fittingsInCategory = getFittingsByCategory(currentCategory, newDiameter, isEndFittingCompatibleWithMainPipe);
                                    const compatibleFittings = fittingsInCategory.filter(f => f.isCompatible);
                                    
                                    if (compatibleFittings.length > 0) {
                                      const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                      returnLeftConnectionUpdates.preBoilerReturnLeftConnectionFitting = maxCompatibleFitting.id;
                                      returnLeftConnectionUpdates.preBoilerReturnLeftConnectionFittingLength = null;
                                    }
                                  }
                                  
                                  const capUpdates = getCapUpdatesForConnectionFitting(
                                    returnLeftConnectionUpdates.preBoilerReturnLeftConnectionFitting || params.preBoilerReturnLeftConnectionFitting,
                                    newDiameter,
                                    'left',
                                    'preBoilerReturn'
                                  );
                                  Object.assign(returnLeftConnectionUpdates, capUpdates);
                                }
                                // Если заглушка установлена, но нет фитинга - обновляем тип заглушки на эллиптическую для больших диаметров
                                else if (params.hasPreBoilerReturnLeftCap && params.preBoilerReturnLeftCapElementType === 'cap' && params.shape !== 'square') {
                                  const ellipticalCap = findEllipticalCap(newDiameter);
                                  if (ellipticalCap && params.preBoilerReturnLeftCapType !== 'elliptical') {
                                    returnLeftConnectionUpdates.preBoilerReturnLeftCapType = 'elliptical';
                                  }
                                }
                                
                                // Обновляем диаметр и фитинг в одном вызове
                                if (Object.keys(returnLeftConnectionUpdates).length > 0) {
                                  updateParam({ preBoilerReturnPipeDiameter: newDiameter, ...returnLeftConnectionUpdates });
                                } else {
                                updateParam('preBoilerReturnPipeDiameter', newDiameter);
                                }
                                
                                // Синхронизация с подачей если включена
                                if (preBoilerDiametersLinked) {
                                  updateParam('preBoilerSupplyPipeDiameter', newDiameter);
                                  // ... и обновление подачи (код выше)
                                }
                                } else {
                                  // Квадратный профиль - обновляем профиль и автопереключаем несовместимые фитинги
                                  const newProfile = newValue;
                                  
                                  // Обновляем контуры обратного предкотлового коллектора
                                  const updatedPreBoilerReturnCircuits = preBoilerReturnCircuits.map((circuit) => {
                                    const fittingId = circuit.fittingId || circuit.fitting;
                                    if (fittingId && circuit.type !== 'pipe' && !isCircuitFittingCompatibleWithSquareProfile(fittingId, newProfile)) {
                                      const fittingsInCategory = getFittingsByCategory(circuit.fittingCategory || 'Ниппели', null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isCircuitFittingCompatibleWithSquareProfile(f.id, newProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        return {
                                          ...circuit,
                                          fitting: maxCompatibleFitting.id,
                                          fittingId: maxCompatibleFitting.id,
                                          diameter: fittingsData[maxCompatibleFitting.id]?.diameter || circuit.diameter,
                                          fittingLength: null,
                                        };
                                      }
                                    }
                                    return {
                                      ...circuit,
                                      fittingLength: null,
                                    };
                                  });
                                  setPreBoilerReturnCircuits(updatedPreBoilerReturnCircuits);
                                  
                                  // Обновляем левое подключение обратного предкотлового коллектора
                                  const preBoilerReturnLeftConnectionUpdates = {};
                                  if (params.hasPreBoilerReturnLeftConnection && params.preBoilerReturnLeftConnectionType === 'fitting' && params.preBoilerReturnLeftConnectionFitting) {
                                    if (!isFittingCompatibleWithSquareProfile(params.preBoilerReturnLeftConnectionFitting, newProfile)) {
                                      const currentCategory = params.preBoilerReturnLeftConnectionFittingCategory || 'Ниппели';
                                      const fittingsInCategory = getFittingsByCategory(currentCategory, null, null);
                                      const compatibleFittings = fittingsInCategory.filter(f => isFittingCompatibleWithSquareProfile(f.id, newProfile));
                                      
                                      if (compatibleFittings.length > 0) {
                                        const maxCompatibleFitting = compatibleFittings[compatibleFittings.length - 1];
                                        preBoilerReturnLeftConnectionUpdates.preBoilerReturnLeftConnectionFitting = maxCompatibleFitting.id;
                                        preBoilerReturnLeftConnectionUpdates.preBoilerReturnLeftConnectionFittingLength = null;
                                      }
                                    } else {
                                      preBoilerReturnLeftConnectionUpdates.preBoilerReturnLeftConnectionFittingLength = null;
                                    }
                                  }
                                  
                                  updateParam({
                                    preBoilerReturnPipeProfile: newProfile,
                                    ...preBoilerReturnLeftConnectionUpdates,
                                  });
                                  
                                  // Синхронизация с подачей если включена
                                  if (preBoilerDiametersLinked) {
                                    setPreBoilerSupplyCircuits(updatedPreBoilerReturnCircuits);
                                    updateParam('preBoilerSupplyPipeProfile', newProfile);
                                  }
                                }
                              }}
                            >
                              {params.shape === 'round'
                                ? collectorAvailableDiameters.map(d => {
                                    const isCompatible = isCollectorDiameterCompatible(d.value, params.mainPipeDiameter);
                                    return (
                                      <MenuItem 
                                        key={d.value} 
                                        value={d.value}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: `Диаметр трубы коллектора ${d.label} несовместим с диаметром гидроразделителя ${params.mainPipeDiameter}мм. Максимально допустимый диаметр: ${getMaxCollectorPipeDiameter(params.mainPipeDiameter)}мм`,
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                          cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                          '&:hover': {
                                            backgroundColor: !isCompatible ? 'transparent' : undefined,
                                          },
                                        }}
                                      >
                                        {d.label}
                                      </MenuItem>
                                    );
                                  })
                                : availableSquareProfiles.map((p) => {
                                    const isCompatible = isCollectorProfileCompatible(p.value, params.mainPipeProfile);
                                    return (
                                      <MenuItem 
                                        key={p.value} 
                                        value={p.value}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: `Профиль коллектора ${p.label} несовместим с профилем гидроразделителя ${params.mainPipeProfile}. Профиль коллектора не может быть больше профиля гидроразделителя.`,
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                          cursor: !isCompatible ? 'not-allowed' : 'pointer',
                                          '&:hover': {
                                            backgroundColor: !isCompatible ? 'transparent' : undefined,
                                          },
                                        }}
                                      >
                                        {p.label}
                                      </MenuItem>
                                    );
                                  })
                              }
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        {/* Вторая строка: расстояние и длины */}
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Расстояние между коллекторами
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={params.preBoilerPipeSpacingMode === 'offset'}
                                  onChange={(e) => updateParam('preBoilerPipeSpacingMode', e.target.checked ? 'offset' : 'interaxis')}
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="caption">
                                  {params.preBoilerPipeSpacingMode === 'offset' ? 'Режим: Отступы' : 'Режим: Межосевое'}
                                </Typography>
                              }
                            />
                          </Box>
                        </Grid>
                        
                        {params.preBoilerPipeSpacingMode === 'interaxis' ? (
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                              label="Межосевое расстояние (мм)"
                            type="number"
                            value={params.preBoilerPipeSpacing}
                            onChange={(e) => updateParam('preBoilerPipeSpacing', Number(e.target.value))}
                          />
                        </Grid>
                        ) : (
                          <>
                            <Grid item xs={12} md={2}>
                              <TextField
                                fullWidth
                                label="Отступ подачи от верха (мм)"
                                type="number"
                                value={params.preBoilerSupplyPipeOffset}
                                onChange={(e) => updateParam('preBoilerSupplyPipeOffset', Number(e.target.value))}
                                helperText="Отступ до центра трубы подачи"
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField
                                fullWidth
                                label="Отступ обратки от низа (мм)"
                                type="number"
                                value={params.preBoilerReturnPipeOffset}
                                onChange={(e) => updateParam('preBoilerReturnPipeOffset', Number(e.target.value))}
                                helperText="Отступ до центра трубы обратки"
                              />
                            </Grid>
                          </>
                        )}
                        
                        {/* Разрыв строки */}
                        <Grid item xs={12} />
                        
                        <Grid item xs={12} md={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={params.usePreBoilerManualLength}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  updateParam('usePreBoilerManualLength', checked);
                                  if (checked) {
                                    updateParam('manualPreBoilerSupplyLength', Number(preBoilerSupplyCalculatedLength.toFixed(2)));
                                    updateParam('manualPreBoilerReturnLength', Number(preBoilerReturnCalculatedLength.toFixed(2)));
                                  }
                                }}
                              />
                            }
                            label="Ручная длина"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={1.5}>
                          <TextField
                            fullWidth
                            label="Длина подающего (мм)"
                            type="number"
                            value={params.usePreBoilerManualLength ? (params.manualPreBoilerSupplyLength || '') : Number(preBoilerSupplyCalculatedLength.toFixed(2))}
                            onChange={(e) => updateParam('manualPreBoilerSupplyLength', Number(e.target.value))}
                            InputProps={{ readOnly: !params.usePreBoilerManualLength }}
                            helperText={params.usePreBoilerManualLength ? "Ручной ввод" : "Автоматически"}
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Button
                            variant="outlined"
                            onClick={equalizePreBoilerLengths}
                            disabled={preBoilerSupplyCalculatedLength === preBoilerReturnCalculatedLength}
                            sx={{ 
                              minWidth: '2.5rem', // 40px
                              height: '2.5rem',
                              fontSize: '1.2rem',
                              fontWeight: 'bold',
                              bgcolor: preBoilerLengthsEqualized ? 'success.light' : 'inherit',
                              '&:hover': {
                                bgcolor: preBoilerLengthsEqualized ? 'success.main' : 'inherit',
                              }
                            }}
                            title={preBoilerLengthsEqualized ? 'Длины приравнены' : 'Приравнять длины коллекторов'}
                          >
                            =
                          </Button>
                        </Grid>
                        
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            label="Длина обратного (мм)"
                            type="number"
                            value={params.usePreBoilerManualLength ? (params.manualPreBoilerReturnLength || '') : Number(preBoilerReturnCalculatedLength.toFixed(2))}
                            onChange={(e) => updateParam('manualPreBoilerReturnLength', Number(e.target.value))}
                            InputProps={{ readOnly: !params.usePreBoilerManualLength }}
                            helperText={params.usePreBoilerManualLength ? "Ручной ввод" : "Автоматически"}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  {/* Подвкладка 2: Контуры */}
                  {preBoilerTabValue === 1 && (
                    <Paper sx={{ p: 3, bgcolor: '#fafafa' }} elevation={0}>
                      <Typography variant="h6" gutterBottom>Контуры предкотловых коллекторов</Typography>
                      
                      <Grid container spacing={3}>
                        {/* Подача */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 1.5, bgcolor: '#fff5f5' }}>
                            <Typography variant="subtitle1" color="error" sx={{ mb: 1.5, fontWeight: 600 }}>
                              Подающий коллектор ({preBoilerSupplyCircuits.length})
                            </Typography>
                            
                            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 'bold', fontSize: '0.8rem' }}>
                              Отступы
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                              <TextField
                                label="Первого контура"
                                type="number"
                                size="small"
                                value={params.preBoilerSupplyFirstCircuitOffset}
                                onChange={(e) => {
                                  setPreBoilerLengthsEqualized(false);
                                  updateParam('preBoilerSupplyFirstCircuitOffset', e.target.value);
                                }}
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                              />
                              <TextField
                                label="Последнего контура"
                                type="number"
                                size="small"
                                value={params.usePreBoilerManualLength ? Math.round(preBoilerSupplyLastCircuitOffsetCalculated) : params.preBoilerSupplyLastCircuitOffset}
                                onChange={(e) => {
                                  setPreBoilerLengthsEqualized(false);
                                  updateParam('preBoilerSupplyLastCircuitOffset', e.target.value);
                                }}
                                fullWidth
                                InputProps={{ 
                                  readOnly: params.usePreBoilerManualLength,
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                helperText={params.usePreBoilerManualLength ? "Авто (ручная длина)" : undefined}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                            </Box>
                            
                            {/* Расстояния */}
                            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                              <TextField
                                label="Межосевое контура"
                                type="number"
                                size="small"
                                value={params.preBoilerSupplyCircuitSpacing}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') return;
                                  const numValue = Number(value);
                                  if (isNaN(numValue)) return;
                                  setPreBoilerLengthsEqualized(false);
                                  updateParam('preBoilerSupplyCircuitSpacing', numValue);
                                  // Применяем ко всем контурам подачи
                                  setPreBoilerSupplyCircuits(prev => prev.map(circuit => ({
                                    ...circuit,
                                    circuitSpacing: numValue,
                                  })));
                                }}
                                helperText="Ко всем"
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                              <TextField
                                label="Между контурами"
                                type="number"
                                size="small"
                                value={params.preBoilerSupplySpacingBetweenCircuits}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') return;
                                  const numValue = Number(value);
                                  if (isNaN(numValue)) return;
                                  setPreBoilerLengthsEqualized(false);
                                  updateParam('preBoilerSupplySpacingBetweenCircuits', numValue);
                                  setPreBoilerSupplyCircuits(prev => prev.map(circuit => ({
                                    ...circuit,
                                    spacingToNext: numValue,
                                  })));
                                }}
                                helperText="Ко всем"
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                            </Box>
                            
                            {/* Кнопка добавить */}
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                              onClick={addPreBoilerSupplyCircuit}
                              size="small"
                              fullWidth
                              sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}
                              color="error"
                            >
                              Добавить контур
                            </Button>
                            
                            {/* Таблица контуров */}
                            <TableContainer>
                              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 0.5, fontSize: '0.75rem' } }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell width="25" sx={{ fontWeight: 600 }}>#</TableCell>
                                    <TableCell width="90" sx={{ fontWeight: 600 }}>Категория</TableCell>
                                    <TableCell width="100" sx={{ fontWeight: 600 }}>Фитинг</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>Вылет</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>Межос.</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>След.</TableCell>
                                    <TableCell width="40" align="center" sx={{ fontWeight: 600 }}>Swap</TableCell>
                                    <TableCell width="35"></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {preBoilerSupplyCircuits.map((circuit, index) => (
                                    <TableRow 
                                      key={circuit.id}
                                      draggable
                                      onDragStart={() => setDraggedPreBoilerSupplyCircuitIndex(index)}
                                      onDragOver={(e) => e.preventDefault()}
                                      onDrop={() => {
                                        if (draggedPreBoilerSupplyCircuitIndex !== null && draggedPreBoilerSupplyCircuitIndex !== index) {
                                          movePreBoilerSupplyCircuit(draggedPreBoilerSupplyCircuitIndex, index);
                                        }
                                        setDraggedPreBoilerSupplyCircuitIndex(null);
                                      }}
                                      onDragEnd={() => setDraggedPreBoilerSupplyCircuitIndex(null)}
                                      sx={{ 
                                        cursor: 'move',
                                        opacity: draggedPreBoilerSupplyCircuitIndex === index ? 0.5 : 1,
                                        '&:hover': { backgroundColor: 'action.hover' }
                                      }}
                                    >
                                      <TableCell>{index + 1}</TableCell>
                                      
                                      {/* Колонка: Категория */}
                                      <TableCell>
                                        <FormControl fullWidth size="small">
                                          <Select
                                            value={circuit.fittingCategory || 'Ниппели'}
                                            onChange={(e) => {
                                              const newCategory = e.target.value;
                                              const fittingsInNewCategory = getPreBoilerSupplyFittings(newCategory);
                                              const firstCompatibleFitting = fittingsInNewCategory.find(f => f.isCompatible);
                                              
                                              if (!firstCompatibleFitting) {
                                                setIncompatibilityDialog({
                                                  open: true,
                                                  message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                });
                                                return;
                                              }
                                              
                                              // Устанавливаем дефолтное значение flangePipeLength при выборе категории
                                              const updates = {
                                                fittingCategory: newCategory,
                                                fitting: firstCompatibleFitting.id,
                                                fittingId: firstCompatibleFitting.id,
                                                fittingLength: null,
                                              };
                                              
                                              if (firstCompatibleFitting.id?.startsWith('Flanec_') || firstCompatibleFitting.id?.startsWith('Nak_Gaika_') || firstCompatibleFitting.id?.startsWith('Shaib_Nar_Rezba_')) {
                                                updates.flangePipeLength = null;
                                              }
                                              
                                              updatePreBoilerSupplyCircuit(index, updates);
                                            }}
                                            sx={{ fontSize: '0.8rem' }}
                                          >
                                            {circuitFittingCategories.map(cat => (
                                              <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>
                                                {cat.label}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      
                                      {/* Колонка: Фитинг */}
                                      <TableCell>
                                        <FormControl fullWidth size="small">
                                          <Select
                                            value={circuit.fittingId || circuit.fitting}
                                            onChange={(e) => {
                                              const newFittingId = e.target.value;
                                              const fittingInfo = getPreBoilerSupplyFittings(circuit.fittingCategory || 'Ниппели').find(f => f.id === newFittingId);
                                              
                                              if (fittingInfo && !fittingInfo.isCompatible) {
                                                setIncompatibilityDialog({
                                                  open: true,
                                                  message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                });
                                                return;
                                              }
                                              
                                              // Устанавливаем дефолтное значение flangePipeLength при выборе фитинга
                                              const updates = {
                                                fitting: newFittingId,
                                                fittingId: newFittingId,
                                                diameter: fittingsData[newFittingId]?.diameter || circuit.diameter,
                                                fittingLength: null,
                                              };
                                              
                                              if (newFittingId?.startsWith('Flanec_') || newFittingId?.startsWith('Nak_Gaika_') || newFittingId?.startsWith('Shaib_Nar_Rezba_')) {
                                                updates.flangePipeLength = null;
                                              }
                                              
                                              updatePreBoilerSupplyCircuit(index, updates);
                                            }}
                                            sx={{ fontSize: '0.8rem' }}
                                          >
                                            {getPreBoilerSupplyFittings(circuit.fittingCategory || 'Ниппели').map(f => (
                                              <MenuItem 
                                                key={f.id} 
                                                value={f.id} 
                                                onClick={(e) => {
                                                  if (!f.isCompatible) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setIncompatibilityDialog({
                                                      open: true,
                                                      message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                    });
                                                  }
                                                }}
                                                sx={{ 
                                                  fontSize: '0.8rem',
                                                  ...(!f.isCompatible && {
                                                    color: 'text.disabled',
                                                    textDecoration: 'line-through',
                                                    backgroundColor: 'transparent',
                                                  }),
                                                }}
                                              >
                                                {f.label}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      
                                      <TableCell>
                                        {(circuit.fittingId || circuit.fitting)?.startsWith('Flanec_') || (circuit.fittingId || circuit.fitting)?.startsWith('Nak_Gaika_') || (circuit.fittingId || circuit.fitting)?.startsWith('Shaib_Nar_Rezba_') ? (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.flangePipeLength ?? ''}
                                            placeholder="Авто"
                                            onChange={(e) => updatePreBoilerSupplyCircuit(index, 'flangePipeLength', e.target.value ? Number(e.target.value) : null)}
                                            sx={{ width: 75 }}
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        ) : (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.fittingLength ?? ''}
                                            onChange={(e) => updatePreBoilerSupplyCircuit(index, 'fittingLength', e.target.value ? Number(e.target.value) : null)}
                                            placeholder="Авто"
                                            sx={{ width: 75 }}
                                          />
                                        )}
                                      </TableCell>
                                      
                                      <TableCell>
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={circuit.circuitSpacing ?? 50}
                                          onChange={(e) => updatePreBoilerSupplyCircuit(index, 'circuitSpacing', e.target.value ? Number(e.target.value) : 50)}
                                          sx={{ width: 75 }}
                                          title="Межосевое расстояние внутри контура (между двумя патрубками)"
                                        />
                                      </TableCell>
                                      
                                      <TableCell>
                                        {index < preBoilerSupplyCircuits.length - 1 && (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.spacingToNext ?? ''}
                                            onChange={(e) => updatePreBoilerSupplyCircuit(index, 'spacingToNext', e.target.value ? Number(e.target.value) : null)}
                                            placeholder={String(params.preBoilerSupplySpacingBetweenCircuits)}
                                            sx={{ width: 75 }}
                                          />
                                        )}
                                      </TableCell>
                                      
                                      <TableCell align="center">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const currentValue = circuit.swapOrder ?? false;
                                            updatePreBoilerSupplyCircuit(index, 'swapOrder', !currentValue);
                                          }}
                                          color={(circuit.swapOrder ?? false) ? 'error' : 'default'}
                                          title={(circuit.swapOrder ?? false) ? 'Д-К' : 'К-Д'}
                                        >
                                          <SwapHorizIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                      
                                      <TableCell>
                                        <IconButton
                                          size="small"
                                          onClick={() => deletePreBoilerSupplyCircuit(index)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        </Grid>
                        
                        {/* Обратка */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 1.5, height: '100%', bgcolor: '#f5f5ff' }}>
                            <Typography variant="subtitle1" color="primary" sx={{ mb: 1.5, fontWeight: 600 }}>
                              Коллектор обратки ({preBoilerReturnCircuits.length})
                            </Typography>
                            
                            {/* Отступы */}
                            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 'bold', fontSize: '0.8rem' }}>
                              Отступы
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                              <TextField
                                label="Первого контура"
                                type="number"
                                size="small"
                                value={params.preBoilerReturnFirstCircuitOffset}
                                onChange={(e) => {
                                  setPreBoilerLengthsEqualized(false);
                                  updateParam('preBoilerReturnFirstCircuitOffset', e.target.value);
                                }}
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                              />
                              <TextField
                                label="Последнего контура"
                                type="number"
                                size="small"
                                value={params.usePreBoilerManualLength ? Math.round(preBoilerReturnLastCircuitOffsetCalculated) : params.preBoilerReturnLastCircuitOffset}
                                onChange={(e) => {
                                  setPreBoilerLengthsEqualized(false);
                                  updateParam('preBoilerReturnLastCircuitOffset', e.target.value);
                                }}
                                fullWidth
                                InputProps={{ 
                                  readOnly: params.usePreBoilerManualLength,
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                helperText={params.usePreBoilerManualLength ? "Авто (ручная длина)" : undefined}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                            </Box>
                            
                            {/* Расстояния */}
                            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                              <TextField
                                label="Межосевое контура"
                                type="number"
                                size="small"
                                value={params.preBoilerReturnCircuitSpacing}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') return;
                                  const numValue = Number(value);
                                  if (isNaN(numValue)) return;
                                  setPreBoilerLengthsEqualized(false);
                                  updateParam('preBoilerReturnCircuitSpacing', numValue);
                                  // Применяем ко всем контурам обратки
                                  setPreBoilerReturnCircuits(prev => prev.map(circuit => ({
                                    ...circuit,
                                    circuitSpacing: numValue,
                                  })));
                                }}
                                helperText="Ко всем"
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                              <TextField
                                label="Между контурами"
                                type="number"
                                size="small"
                                value={params.preBoilerReturnSpacingBetweenCircuits}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') return;
                                  const numValue = Number(value);
                                  if (isNaN(numValue)) return;
                                  setPreBoilerLengthsEqualized(false);
                                  updateParam('preBoilerReturnSpacingBetweenCircuits', numValue);
                                  setPreBoilerReturnCircuits(prev => prev.map(circuit => ({
                                    ...circuit,
                                    spacingToNext: numValue,
                                  })));
                                }}
                                helperText="Ко всем"
                                fullWidth
                                InputProps={{ 
                                  endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                                  sx: { fontSize: '0.8rem' }
                                }}
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.25 } }}
                              />
                            </Box>
                            
                            {/* Кнопка добавить */}
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                              onClick={addPreBoilerReturnCircuit}
                              size="small"
                              fullWidth
                              sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}
                              color="primary"
                            >
                              Добавить контур
                            </Button>
                            
                            {/* Таблица контуров */}
                            <TableContainer>
                              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 0.5, fontSize: '0.75rem' } }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell width="25" sx={{ fontWeight: 600 }}>#</TableCell>
                                    <TableCell width="90" sx={{ fontWeight: 600 }}>Категория</TableCell>
                                    <TableCell width="100" sx={{ fontWeight: 600 }}>Фитинг</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>Вылет</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>Межос.</TableCell>
                                    <TableCell width="70" sx={{ fontWeight: 600 }}>След.</TableCell>
                                    <TableCell width="40" align="center" sx={{ fontWeight: 600 }}>Swap</TableCell>
                                    <TableCell width="35"></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {preBoilerReturnCircuits.map((circuit, index) => (
                                    <TableRow 
                                      key={circuit.id}
                                      draggable
                                      onDragStart={() => setDraggedPreBoilerReturnCircuitIndex(index)}
                                      onDragOver={(e) => e.preventDefault()}
                                      onDrop={() => {
                                        if (draggedPreBoilerReturnCircuitIndex !== null && draggedPreBoilerReturnCircuitIndex !== index) {
                                          movePreBoilerReturnCircuit(draggedPreBoilerReturnCircuitIndex, index);
                                        }
                                        setDraggedPreBoilerReturnCircuitIndex(null);
                                      }}
                                      onDragEnd={() => setDraggedPreBoilerReturnCircuitIndex(null)}
                                      sx={{ 
                                        cursor: 'move',
                                        opacity: draggedPreBoilerReturnCircuitIndex === index ? 0.5 : 1,
                                        '&:hover': { backgroundColor: 'action.hover' }
                                      }}
                                    >
                                      <TableCell>{index + 1}</TableCell>
                                      
                                      {/* Колонка: Категория */}
                                      <TableCell>
                                        <FormControl fullWidth size="small">
                                          <Select
                                            value={circuit.fittingCategory || 'Ниппели'}
                                            onChange={(e) => {
                                              const newCategory = e.target.value;
                                              const fittingsInNewCategory = getPreBoilerReturnFittings(newCategory);
                                              const firstCompatibleFitting = fittingsInNewCategory.find(f => f.isCompatible);
                                              
                                              if (!firstCompatibleFitting) {
                                                setIncompatibilityDialog({
                                                  open: true,
                                                  message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                });
                                                return;
                                              }
                                              
                                              // Устанавливаем дефолтное значение flangePipeLength при выборе категории
                                              const updates = {
                                                fittingCategory: newCategory,
                                                fitting: firstCompatibleFitting.id,
                                                fittingId: firstCompatibleFitting.id,
                                                fittingLength: null,
                                              };
                                              
                                              if (firstCompatibleFitting.id?.startsWith('Flanec_') || firstCompatibleFitting.id?.startsWith('Nak_Gaika_') || firstCompatibleFitting.id?.startsWith('Shaib_Nar_Rezba_')) {
                                                updates.flangePipeLength = null;
                                              }
                                              
                                              updatePreBoilerReturnCircuit(index, updates);
                                            }}
                                            sx={{ fontSize: '0.8rem' }}
                                          >
                                            {circuitFittingCategories.map(cat => (
                                              <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>
                                                {cat.label}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      
                                      {/* Колонка: Фитинг */}
                                      <TableCell>
                                        <FormControl fullWidth size="small">
                                          <Select
                                            value={circuit.fittingId || circuit.fitting}
                                            onChange={(e) => {
                                              const newFittingId = e.target.value;
                                              const fittingInfo = getPreBoilerReturnFittings(circuit.fittingCategory || 'Ниппели').find(f => f.id === newFittingId);
                                              
                                              if (fittingInfo && !fittingInfo.isCompatible) {
                                                setIncompatibilityDialog({
                                                  open: true,
                                                  message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                });
                                                return;
                                              }
                                              
                                              // Устанавливаем дефолтное значение flangePipeLength при выборе фитинга
                                              const updates = {
                                                fitting: newFittingId,
                                                fittingId: newFittingId,
                                                diameter: fittingsData[newFittingId]?.diameter || circuit.diameter,
                                                fittingLength: null,
                                              };
                                              
                                              if (newFittingId?.startsWith('Flanec_') || newFittingId?.startsWith('Nak_Gaika_') || newFittingId?.startsWith('Shaib_Nar_Rezba_')) {
                                                updates.flangePipeLength = null;
                                              }
                                              
                                              updatePreBoilerReturnCircuit(index, updates);
                                            }}
                                            sx={{ fontSize: '0.8rem' }}
                                          >
                                            {getPreBoilerReturnFittings(circuit.fittingCategory || 'Ниппели').map(f => (
                                              <MenuItem 
                                                key={f.id} 
                                                value={f.id} 
                                                onClick={(e) => {
                                                  if (!f.isCompatible) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setIncompatibilityDialog({
                                                      open: true,
                                                      message: 'Невозможно сварить изделие, в связи с ограничением потока',
                                                    });
                                                  }
                                                }}
                                                sx={{ 
                                                  fontSize: '0.8rem',
                                                  ...(!f.isCompatible && {
                                                    color: 'text.disabled',
                                                    textDecoration: 'line-through',
                                                    backgroundColor: 'transparent',
                                                  }),
                                                }}
                                              >
                                                {f.label}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      
                                      <TableCell>
                                        {(circuit.fittingId || circuit.fitting)?.startsWith('Flanec_') || (circuit.fittingId || circuit.fitting)?.startsWith('Nak_Gaika_') || (circuit.fittingId || circuit.fitting)?.startsWith('Shaib_Nar_Rezba_') ? (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.flangePipeLength ?? ''}
                                            placeholder="Авто"
                                            onChange={(e) => updatePreBoilerReturnCircuit(index, 'flangePipeLength', e.target.value ? Number(e.target.value) : null)}
                                            sx={{ width: 75 }}
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        ) : (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.fittingLength ?? ''}
                                            onChange={(e) => updatePreBoilerReturnCircuit(index, 'fittingLength', e.target.value ? Number(e.target.value) : null)}
                                            placeholder="Авто"
                                            sx={{ width: 75 }}
                                          />
                                        )}
                                      </TableCell>
                                      
                                      <TableCell>
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={circuit.circuitSpacing ?? 50}
                                          onChange={(e) => updatePreBoilerReturnCircuit(index, 'circuitSpacing', e.target.value ? Number(e.target.value) : 50)}
                                          sx={{ width: 75 }}
                                          title="Межосевое расстояние внутри контура (между двумя патрубками)"
                                        />
                                      </TableCell>
                                      
                                      <TableCell>
                                        {index < preBoilerReturnCircuits.length - 1 && (
                                          <TextField
                                            size="small"
                                            type="number"
                                            value={circuit.spacingToNext ?? ''}
                                            onChange={(e) => updatePreBoilerReturnCircuit(index, 'spacingToNext', e.target.value ? Number(e.target.value) : null)}
                                            placeholder={String(params.preBoilerReturnSpacingBetweenCircuits)}
                                            sx={{ width: 75 }}
                                          />
                                        )}
                                      </TableCell>
                                      
                                      <TableCell align="center">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const currentValue = circuit.swapOrder ?? false;
                                            updatePreBoilerReturnCircuit(index, 'swapOrder', !currentValue);
                                          }}
                                          color={(circuit.swapOrder ?? false) ? 'primary' : 'default'}
                                          title={(circuit.swapOrder ?? false) ? 'Д-К' : 'К-Д'}
                                        >
                                          <SwapHorizIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                      
                                      <TableCell>
                                        <IconButton
                                          size="small"
                                          onClick={() => deletePreBoilerReturnCircuit(index)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Центральные фитинги */}
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" gutterBottom>Центральные фитинги (термометры и лицевые подключения)</Typography>
                      
                      <Grid container spacing={2}>
                        {/* ЛЕВАЯ КОЛОНКА - ПОДАЮЩИЙ КОЛЛЕКТОР */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 2, bgcolor: '#fff5f5' }}>
                            <Typography variant="subtitle2" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
                              Фитинги термометров и лицевых подключений (подача)
                            </Typography>
                            
                            <CenterFittingsUI
                              centerFittings={preBoilerSupplyCenterFittings}
                              centerFittingCount={params.preBoilerSupplyCenterFittingCount}
                              onUpdateCount={updatePreBoilerSupplyCenterFittingCount}
                              onUpdateFitting={updatePreBoilerSupplyCenterFitting}
                              onDeleteFitting={deletePreBoilerSupplyCenterFitting}
                              centerFittingCategories={centerFittingCategories}
                              getFittingsByCategory={(cat) => {
                                if (params.shape === 'square') {
                                  return getFittingsByCategory(cat, null, null).map(f => ({
                                    ...f,
                                    isCompatible: isEndFittingCompatibleWithSquareProfile(f.id, params.preBoilerSupplyPipeProfile)
                                  }));
                                }
                                return getFittingsByCategory(cat, params.preBoilerSupplyPipeDiameter, isCenterFittingCompatible);
                              }}
                              isFittingCompatibleWithMainPipe={(fittingId) => params.shape === 'square' 
                                ? isEndFittingCompatibleWithSquareProfile(fittingId, params.preBoilerSupplyPipeProfile)
                                : isCenterFittingCompatible(fittingId, params.preBoilerSupplyPipeDiameter)}
                              isFittingCompatibleWithSquareProfile={isEndFittingCompatibleWithSquareProfile}
                              mainPipeDiameter={params.preBoilerSupplyPipeDiameter}
                              shape={params.shape}
                              mainPipeProfile={params.preBoilerSupplyPipeProfile}
                              circuitCount={preBoilerSupplyCircuits.length}
                              bottomCircuitCount={preBoilerReturnCircuits.length}
                              onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                              mode="distribution"
                              currentRow="supply"
                            />
                          </Paper>
                        </Grid>
                        
                        {/* ПРАВАЯ КОЛОНКА - ОБРАТНЫЙ КОЛЛЕКТОР */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 2, bgcolor: '#f5f5ff' }}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                              Фитинги термометров и лицевых подключений (обратка)
                            </Typography>
                            
                            <CenterFittingsUI
                              centerFittings={preBoilerReturnCenterFittings}
                              centerFittingCount={params.preBoilerReturnCenterFittingCount}
                              onUpdateCount={updatePreBoilerReturnCenterFittingCount}
                              onUpdateFitting={updatePreBoilerReturnCenterFitting}
                              onDeleteFitting={deletePreBoilerReturnCenterFitting}
                              centerFittingCategories={centerFittingCategories}
                              getFittingsByCategory={(cat) => {
                                if (params.shape === 'square') {
                                  return getFittingsByCategory(cat, null, null).map(f => ({
                                    ...f,
                                    isCompatible: isEndFittingCompatibleWithSquareProfile(f.id, params.preBoilerReturnPipeProfile)
                                  }));
                                }
                                return getFittingsByCategory(cat, params.preBoilerReturnPipeDiameter, isCenterFittingCompatible);
                              }}
                              isFittingCompatibleWithMainPipe={(fittingId) => params.shape === 'square' 
                                ? isEndFittingCompatibleWithSquareProfile(fittingId, params.preBoilerReturnPipeProfile)
                                : isCenterFittingCompatible(fittingId, params.preBoilerReturnPipeDiameter)}
                              isFittingCompatibleWithSquareProfile={isEndFittingCompatibleWithSquareProfile}
                              mainPipeDiameter={params.preBoilerReturnPipeDiameter}
                              shape={params.shape}
                              mainPipeProfile={params.preBoilerReturnPipeProfile}
                              circuitCount={preBoilerReturnCircuits.length}
                              bottomCircuitCount={preBoilerSupplyCircuits.length}
                              onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                              mode="distribution"
                              currentRow="return"
                            />
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Дополнительные выходы */}
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" gutterBottom>Дополнительные выходы</Typography>
                      
                      <Grid container spacing={2}>
                        {/* ЛЕВАЯ КОЛОНКА - ПОДАЮЩИЙ КОЛЛЕКТОР */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 2, bgcolor: '#fff5f5' }}>
                            <Typography variant="subtitle2" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
                              Дополнительные выходы (подача)
                            </Typography>
                            
                            <AdditionalOutletsUI
                              outlets={preBoilerSupplyAdditionalOutlets}
                              outletCount={params.preBoilerSupplyAdditionalOutletCount}
                              onUpdateCount={updatePreBoilerSupplyAdditionalOutletCount}
                              onUpdateOutlet={updatePreBoilerSupplyAdditionalOutlet}
                              onDeleteOutlet={deletePreBoilerSupplyAdditionalOutlet}
                              fittingCategories={circuitFittingCategories}
                              getFittingsByCategory={(cat, outlet) => {
                                if (params.shape === 'square') {
                                  return getFittingsByCategory(cat, null, null).map(f => ({
                                    ...f,
                                    isCompatible: isEndFittingCompatibleWithSquareProfile(f.id, params.preBoilerSupplyPipeProfile)
                                  }));
                                }
                                const outletType = outlet?.outletType || 'short';
                                return getFittingsByCategory(cat, params.preBoilerSupplyPipeDiameter, (fId) => 
                                  isAdditionalOutletFittingCompatible(fId, params.preBoilerSupplyPipeDiameter, outletType)
                                );
                              }}
                              isFittingCompatibleWithMainPipe={(fittingId, mainPipeDiameter, outlet) => {
                                if (params.shape === 'square') {
                                  return isEndFittingCompatibleWithSquareProfile(fittingId, params.preBoilerSupplyPipeProfile);
                                }
                                const outletType = outlet?.outletType || 'short';
                                return isAdditionalOutletFittingCompatible(fittingId, params.preBoilerSupplyPipeDiameter, outletType);
                              }}
                              mainPipeDiameter={params.preBoilerSupplyPipeDiameter}
                              circuitCount={preBoilerSupplyCircuits.length}
                              bottomCircuitCount={preBoilerReturnCircuits.length}
                              onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                              availableDiameters={collectorAvailableDiameters}
                              fittingsData={fittingsData}
                              collectorType="supply"
                              mode="distribution"
                              currentRow="supply"
                            />
                          </Paper>
                        </Grid>
                        
                        {/* ПРАВАЯ КОЛОНКА - ОБРАТНЫЙ КОЛЛЕКТОР */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 2, bgcolor: '#f5f5ff' }}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                              Дополнительные выходы (обратка)
                            </Typography>
                            
                            <AdditionalOutletsUI
                              outlets={preBoilerReturnAdditionalOutlets}
                              outletCount={params.preBoilerReturnAdditionalOutletCount}
                              onUpdateCount={updatePreBoilerReturnAdditionalOutletCount}
                              onUpdateOutlet={updatePreBoilerReturnAdditionalOutlet}
                              onDeleteOutlet={deletePreBoilerReturnAdditionalOutlet}
                              fittingCategories={circuitFittingCategories}
                              getFittingsByCategory={(cat, outlet) => {
                                if (params.shape === 'square') {
                                  return getFittingsByCategory(cat, null, null).map(f => ({
                                    ...f,
                                    isCompatible: isEndFittingCompatibleWithSquareProfile(f.id, params.preBoilerReturnPipeProfile)
                                  }));
                                }
                                const outletType = outlet?.outletType || 'short';
                                return getFittingsByCategory(cat, params.preBoilerReturnPipeDiameter, (fId) => 
                                  isAdditionalOutletFittingCompatible(fId, params.preBoilerReturnPipeDiameter, outletType)
                                );
                              }}
                              isFittingCompatibleWithMainPipe={(fittingId, mainPipeDiameter, outlet) => {
                                if (params.shape === 'square') {
                                  return isEndFittingCompatibleWithSquareProfile(fittingId, params.preBoilerReturnPipeProfile);
                                }
                                const outletType = outlet?.outletType || 'short';
                                return isAdditionalOutletFittingCompatible(fittingId, params.preBoilerReturnPipeDiameter, outletType);
                              }}
                              mainPipeDiameter={params.preBoilerReturnPipeDiameter}
                              circuitCount={preBoilerReturnCircuits.length}
                              bottomCircuitCount={preBoilerSupplyCircuits.length}
                              onIncompatibilityWarning={(message) => setIncompatibilityDialog({ open: true, message })}
                              availableDiameters={collectorAvailableDiameters}
                              fittingsData={fittingsData}
                              collectorType="return"
                              mode="distribution"
                              currentRow="return"
                            />
                          </Paper>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  {/* Подвкладка 3: Подключения */}
                  {preBoilerTabValue === 2 && (
                    <Paper sx={{ p: 3, bgcolor: '#fafafa' }} elevation={0}>
                      <Typography variant="h6" gutterBottom>{isSystemFlipped ? 'Правые' : 'Левые'} торцевые подключения</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Настройка {isSystemFlipped ? 'правых' : 'левых'} торцевых подключений предкотловых коллекторов ({isSystemFlipped ? 'левая' : 'правая'} сторона подключена к гидроразделителю)
                      </Typography>
                      
                      {/* Кнопка синхронизации */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
                        <Button
                          variant={preBoilerLeftConnectionsLinked ? 'contained' : 'outlined'}
                          onClick={() => {
                            const newLinkedState = !preBoilerLeftConnectionsLinked;
                            setPreBoilerLeftConnectionsLinked(newLinkedState);
                            if (newLinkedState) {
                              updateParam({
                                hasPreBoilerReturnLeftConnection: params.hasPreBoilerSupplyLeftConnection,
                                preBoilerReturnLeftConnectionType: params.preBoilerSupplyLeftConnectionType,
                                preBoilerReturnLeftConnectionFittingCategory: params.preBoilerSupplyLeftConnectionFittingCategory,
                                preBoilerReturnLeftConnectionFitting: params.preBoilerSupplyLeftConnectionFitting,
                                preBoilerReturnLeftConnectionFittingLength: params.preBoilerSupplyLeftConnectionFittingLength,
                              });
                            }
                          }}
                          startIcon={preBoilerLeftConnectionsLinked ? '🔗' : '🔓'}
                          sx={{ mb: 1 }}
                        >
                          {preBoilerLeftConnectionsLinked ? 'Синхронизация ВКЛ' : 'Синхронизация ВЫКЛ'}
                        </Button>
                      </Box>
                      
                      <Divider sx={{ mb: 3 }} />
                      
                      {/* Подающий коллектор */}
                      <Typography variant="subtitle2" color="error" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Подающий коллектор - Подключение {isSystemFlipped ? 'справа' : 'слева'}
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4} sm={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={params.hasPreBoilerSupplyLeftConnection}
                                onChange={(e) => updateParam('hasPreBoilerSupplyLeftConnection', e.target.checked)}
                                size="small"
                              />
                            }
                            label={<Typography variant="body2">Вкл.</Typography>}
                          />
                        </Grid>
                        {params.hasPreBoilerSupplyLeftConnection && (
                          <>
                            <Grid item xs={6} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel shrink>Категория</InputLabel>
                                <Select
                                  value={params.preBoilerSupplyLeftConnectionFittingCategory || ''}
                                  label="Категория"
                                  displayEmpty
                                  notched
                                  onChange={(e) => {
                                    const newCategory = e.target.value;
                                    updateParam({
                                      preBoilerSupplyLeftConnectionFittingCategory: newCategory,
                                      preBoilerSupplyLeftConnectionFitting: '',
                                      preBoilerSupplyLeftConnectionFittingLength: null
                                    });
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Выбрать</em>
                                  </MenuItem>
                                  {circuitFittingCategories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                      {cat.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel shrink>Фитинг</InputLabel>
                                <Select
                                  value={params.preBoilerSupplyLeftConnectionFitting || ''}
                                  label="Фитинг"
                                  displayEmpty
                                  notched
                                  onChange={(e) => {
                                    const newFitting = e.target.value;
                                    
                                    const isPreBoilerSupplyFittingCompatible = params.shape === 'square'
                                      ? isEndFittingCompatibleWithSquareProfile(newFitting, params.preBoilerSupplyPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(newFitting, params.preBoilerSupplyPipeDiameter);
                                    if (newFitting && !isPreBoilerSupplyFittingCompatible) {
                                      setIncompatibilityDialog({
                                        open: true,
                                        message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                      });
                                      return;
                                    }
                                    
                                    const updates = {
                                      preBoilerSupplyLeftConnectionType: 'fitting',
                                      preBoilerSupplyLeftConnectionFitting: newFitting,
                                      preBoilerSupplyLeftConnectionFittingLength: null,
                                      preBoilerSupplyLeftConnectionLength: null
                                    };
                                    
                                    // Автоматически определяем нужны ли заглушки/переходы
                                    const capUpdates = getCapUpdatesForConnectionFitting(
                                      newFitting,
                                      params.preBoilerSupplyPipeDiameter,
                                      'left',
                                      'preBoilerSupply',
                                      params.shape
                                    );
                                    
                                    updateParam({ ...updates, ...capUpdates });
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Выбрать</em>
                                  </MenuItem>
                                  {getFittingsByCategory(params.preBoilerSupplyLeftConnectionFittingCategory, params.preBoilerSupplyPipeDiameter).map((f) => {
                                    const isCompatible = params.shape === 'square' 
                                      ? isEndFittingCompatibleWithSquareProfile(f.id, params.preBoilerSupplyPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(f.id, params.preBoilerSupplyPipeDiameter);
                                    return (
                                      <MenuItem 
                                        key={f.id} 
                                        value={f.id}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                        }}
                                      >
                                        {f.label}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Grid>
                            {shouldShowConnectionLengthField(params.preBoilerSupplyLeftConnectionFitting, params.preBoilerSupplyPipeDiameter) && (
                              <Grid item xs={6} sm={2}>
                                <TextField
                                  label="Вылет"
                                  type="number"
                                  size="small"
                                  value={(() => {
                                    const useConnectionLength = shouldUseConnectionLength(
                                      params.preBoilerSupplyLeftConnectionFitting,
                                      params.hasPreBoilerSupplyLeftCap,
                                      params.preBoilerSupplyPipeDiameter
                                    );
                                    
                                    if (useConnectionLength) {
                                      return params.preBoilerSupplyLeftConnectionLength ?? '';
                                    } else {
                                      return params.preBoilerSupplyLeftConnectionFittingLength ?? '';
                                    }
                                  })()}
                                  placeholder="Авто"
                                  onChange={(e) => {
                                    const useConnectionLength = shouldUseConnectionLength(
                                      params.preBoilerSupplyLeftConnectionFitting,
                                      params.hasPreBoilerSupplyLeftCap,
                                      params.preBoilerSupplyPipeDiameter
                                    );
                                    
                                    if (useConnectionLength) {
                                      updateParam('preBoilerSupplyLeftConnectionLength', e.target.value ? Number(e.target.value) : null);
                                    } else {
                                      updateParam('preBoilerSupplyLeftConnectionFittingLength', e.target.value ? Number(e.target.value) : null);
                                    }
                                  }}
                                  InputProps={{ inputProps: { min: 0, step: 1 } }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                      
                      <Divider sx={{ my: 3 }} />
                      
                      {/* Обратный коллектор */}
                      <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Обратный коллектор - Подключение {isSystemFlipped ? 'справа' : 'слева'}
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4} sm={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={params.hasPreBoilerReturnLeftConnection}
                                onChange={(e) => updateParam('hasPreBoilerReturnLeftConnection', e.target.checked)}
                                size="small"
                              />
                            }
                            label={<Typography variant="body2">Вкл.</Typography>}
                          />
                        </Grid>
                        {params.hasPreBoilerReturnLeftConnection && (
                          <>
                            <Grid item xs={6} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel shrink>Категория</InputLabel>
                                <Select
                                  value={params.preBoilerReturnLeftConnectionFittingCategory || ''}
                                  label="Категория"
                                  displayEmpty
                                  notched
                                  onChange={(e) => {
                                    const newCategory = e.target.value;
                                    updateParam({
                                      preBoilerReturnLeftConnectionFittingCategory: newCategory,
                                      preBoilerReturnLeftConnectionFitting: '',
                                      preBoilerReturnLeftConnectionFittingLength: null
                                    });
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Выбрать</em>
                                  </MenuItem>
                                  {circuitFittingCategories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                      {cat.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel shrink>Фитинг</InputLabel>
                                <Select
                                  value={params.preBoilerReturnLeftConnectionFitting || ''}
                                  label="Фитинг"
                                  displayEmpty
                                  notched
                                  onChange={(e) => {
                                    const newFitting = e.target.value;
                                    
                                    const isPreBoilerReturnFittingCompatible = params.shape === 'square'
                                      ? isEndFittingCompatibleWithSquareProfile(newFitting, params.preBoilerReturnPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(newFitting, params.preBoilerReturnPipeDiameter);
                                    if (newFitting && !isPreBoilerReturnFittingCompatible) {
                                      setIncompatibilityDialog({
                                        open: true,
                                        message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                      });
                                      return;
                                    }
                                    
                                    const updates = {
                                      preBoilerReturnLeftConnectionType: 'fitting',
                                      preBoilerReturnLeftConnectionFitting: newFitting,
                                      preBoilerReturnLeftConnectionFittingLength: null,
                                      preBoilerReturnLeftConnectionLength: null
                                    };
                                    
                                    // Автоматически определяем нужны ли заглушки/переходы
                                    const capUpdates = getCapUpdatesForConnectionFitting(
                                      newFitting,
                                      params.preBoilerReturnPipeDiameter,
                                      'left',
                                      'preBoilerReturn',
                                      params.shape
                                    );
                                    
                                    updateParam({ ...updates, ...capUpdates });
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Выбрать</em>
                                  </MenuItem>
                                  {getFittingsByCategory(params.preBoilerReturnLeftConnectionFittingCategory, params.preBoilerReturnPipeDiameter).map((f) => {
                                    const isCompatible = params.shape === 'square' 
                                      ? isEndFittingCompatibleWithSquareProfile(f.id, params.preBoilerReturnPipeProfile)
                                      : isEndFittingCompatibleWithMainPipe(f.id, params.preBoilerReturnPipeDiameter);
                                    return (
                                      <MenuItem 
                                        key={f.id} 
                                        value={f.id}
                                        onClick={(e) => {
                                          if (!isCompatible) {
                                            e.stopPropagation();
                                            setIncompatibilityDialog({
                                              open: true,
                                              message: 'Невозможно сварить изделие, в связи с основным диаметром гребенки',
                                            });
                                          }
                                        }}
                                        sx={{ 
                                          textDecoration: !isCompatible ? 'line-through' : 'none',
                                          color: !isCompatible ? 'text.disabled' : 'inherit',
                                        }}
                                      >
                                        {f.label}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            </Grid>
                            {shouldShowConnectionLengthField(params.preBoilerReturnLeftConnectionFitting, params.preBoilerReturnPipeDiameter) && (
                              <Grid item xs={6} sm={2}>
                                <TextField
                                  label="Вылет"
                                  type="number"
                                  size="small"
                                  value={(() => {
                                    const useConnectionLength = shouldUseConnectionLength(
                                      params.preBoilerReturnLeftConnectionFitting,
                                      params.hasPreBoilerReturnLeftCap,
                                      params.preBoilerReturnPipeDiameter
                                    );
                                    
                                    if (useConnectionLength) {
                                      return params.preBoilerReturnLeftConnectionLength ?? '';
                                    } else {
                                      return params.preBoilerReturnLeftConnectionFittingLength ?? '';
                                    }
                                  })()}
                                  placeholder="Авто"
                                  onChange={(e) => {
                                    const useConnectionLength = shouldUseConnectionLength(
                                      params.preBoilerReturnLeftConnectionFitting,
                                      params.hasPreBoilerReturnLeftCap,
                                      params.preBoilerReturnPipeDiameter
                                    );
                                    
                                    if (useConnectionLength) {
                                      updateParam('preBoilerReturnLeftConnectionLength', e.target.value ? Number(e.target.value) : null);
                                    } else {
                                      updateParam('preBoilerReturnLeftConnectionFittingLength', e.target.value ? Number(e.target.value) : null);
                                    }
                                  }}
                                  InputProps={{ inputProps: { min: 0, step: 1 } }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </Paper>
                  )}

                  {/* Подвкладка 4: Заглушки */}
                  {preBoilerTabValue === 3 && (
                    <Paper sx={{ p: 3, bgcolor: '#fafafa' }} elevation={0}>
                      <Typography variant="h6" gutterBottom>Заглушки и переходы конц.</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Настройка {isSystemFlipped ? 'правых' : 'левых'} заглушек/переходов ({isSystemFlipped ? 'левая' : 'правая'} сторона соединена с гидроразделителем)
                      </Typography>
                      
                      {/* Подающий коллектор */}
                      <Typography variant="subtitle2" color="error" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Подающий коллектор - {isSystemFlipped ? 'справа' : 'слева'}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={params.hasPreBoilerSupplyLeftCap}
                                  onChange={(e) => updateParam('hasPreBoilerSupplyLeftCap', e.target.checked)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Включить</Typography>}
                            />
                            {params.hasPreBoilerSupplyLeftCap && (
                              <>
                                {/* Для квадратных форм только прямые заглушки */}
                                {params.shape === 'square' ? (
                                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    Прямая заглушка
                                  </Typography>
                                ) : (
                                  <>
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                      <InputLabel>Элемент</InputLabel>
                                      <Select
                                        value={params.preBoilerSupplyLeftCapElementType || 'cap'}
                                        label="Элемент"
                                        onChange={(e) => updateParam('preBoilerSupplyLeftCapElementType', e.target.value)}
                                      >
                                        <MenuItem value="cap">Заглушка</MenuItem>
                                        <MenuItem value="reducer">Переход конц.</MenuItem>
                                      </Select>
                                    </FormControl>
                                    {params.preBoilerSupplyLeftCapElementType === 'cap' || !params.preBoilerSupplyLeftCapElementType ? (
                                      <FormControl size="small" sx={{ minWidth: 130 }}>
                                        <InputLabel>Тип</InputLabel>
                                        <Select
                                          value={params.preBoilerSupplyLeftCapType}
                                          label="Тип"
                                          onChange={(e) => updateParam('preBoilerSupplyLeftCapType', e.target.value)}
                                        >
                                          {params.preBoilerSupplyPipeDiameter <= 101.6 && (
                                            <MenuItem value="straight">Прямая</MenuItem>
                                          )}
                                          {params.shape === 'round' && (
                                            <MenuItem value="elliptical">Эллиптическая</MenuItem>
                                          )}
                                        </Select>
                                      </FormControl>
                                    ) : (
                                      <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel>Переход</InputLabel>
                                        <Select
                                          value={params.preBoilerSupplyLeftReducer || concentricReducers[0]?.id}
                                          label="Переход"
                                          onChange={(e) => updateParam('preBoilerSupplyLeftReducer', e.target.value)}
                                        >
                                          {concentricReducers.map((r) => (
                                            <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </Box>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 3 }} />

                      {/* Обратный коллектор */}
                      <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Обратный коллектор - {isSystemFlipped ? 'справа' : 'слева'}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={params.hasPreBoilerReturnLeftCap}
                                  onChange={(e) => updateParam('hasPreBoilerReturnLeftCap', e.target.checked)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Включить</Typography>}
                            />
                            {params.hasPreBoilerReturnLeftCap && (
                              <>
                                {/* Для квадратных форм только прямые заглушки */}
                                {params.shape === 'square' ? (
                                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    Прямая заглушка
                                  </Typography>
                                ) : (
                                  <>
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                      <InputLabel>Элемент</InputLabel>
                                      <Select
                                        value={params.preBoilerReturnLeftCapElementType || 'cap'}
                                        label="Элемент"
                                        onChange={(e) => updateParam('preBoilerReturnLeftCapElementType', e.target.value)}
                                      >
                                        <MenuItem value="cap">Заглушка</MenuItem>
                                        <MenuItem value="reducer">Переход конц.</MenuItem>
                                      </Select>
                                    </FormControl>
                                    {params.preBoilerReturnLeftCapElementType === 'cap' || !params.preBoilerReturnLeftCapElementType ? (
                                      <FormControl size="small" sx={{ minWidth: 130 }}>
                                        <InputLabel>Тип</InputLabel>
                                        <Select
                                          value={params.preBoilerReturnLeftCapType}
                                          label="Тип"
                                          onChange={(e) => updateParam('preBoilerReturnLeftCapType', e.target.value)}
                                        >
                                          {params.preBoilerReturnPipeDiameter <= 101.6 && (
                                            <MenuItem value="straight">Прямая</MenuItem>
                                          )}
                                          {params.shape === 'round' && (
                                            <MenuItem value="elliptical">Эллиптическая</MenuItem>
                                          )}
                                        </Select>
                                      </FormControl>
                                    ) : (
                                      <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel>Переход</InputLabel>
                                        <Select
                                          value={params.preBoilerReturnLeftReducer || concentricReducers[0]?.id}
                                          label="Переход"
                                          onChange={(e) => updateParam('preBoilerReturnLeftReducer', e.target.value)}
                                        >
                                          {concentricReducers.map((r) => (
                                            <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </Box>
              )}

              {/* Кнопка генерации */}
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleGenerate}
                >
                  Сгенерировать чертёж
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Диалог предложения приравнять длины */}
        <Dialog open={equalizeDialog} onClose={() => setEqualizeDialog(false)}>
          <DialogTitle>Приравнять длины коллекторов?</DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              {(() => {
                const lengthDiff = Math.abs(supplyCalculatedLength - returnCalculatedLength);
                const preBoilerLengthDiff = hasPreBoilerCollector 
                  ? Math.abs(preBoilerSupplyCalculatedLength - preBoilerReturnCalculatedLength)
                  : 0;
                
                const needMainEqualize = lengthDiff > 1;
                const needPreBoilerEqualize = hasPreBoilerCollector && preBoilerLengthDiff > 1;
                
                return (
                  <Box>
                    {needMainEqualize && (
                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={equalizeMainCollector}
                              onChange={(e) => setEqualizeMainCollector(e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <strong>Основной коллектор:</strong><br/>
                              • Подача: {supplyCalculatedLength.toFixed(2)} мм<br/>
                              • Обратка: {returnCalculatedLength.toFixed(2)} мм<br/>
                              Приравнять по максимальной ({Math.max(supplyCalculatedLength, returnCalculatedLength).toFixed(2)} мм)
                            </Box>
                          }
                        />
                      </Box>
                    )}
                    {needPreBoilerEqualize && (
                      <Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={equalizePreBoilerCollector}
                              onChange={(e) => setEqualizePreBoilerCollector(e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <strong>Предкотловой коллектор:</strong><br/>
                              • Подача: {preBoilerSupplyCalculatedLength.toFixed(2)} мм<br/>
                              • Обратка: {preBoilerReturnCalculatedLength.toFixed(2)} мм<br/>
                              Приравнять по максимальной ({Math.max(preBoilerSupplyCalculatedLength, preBoilerReturnCalculatedLength).toFixed(2)} мм)
                            </Box>
                          }
                        />
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleGenerateWithoutEqualize} 
              color="inherit"
            >
              Оставить как есть
            </Button>
            <Button 
              onClick={handleGenerateWithEqualize} 
              variant="contained" 
              color="primary"
              disabled={!equalizeMainCollector && !equalizePreBoilerCollector}
            >
              Применить
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <Dialog
        open={incompatibilityDialog.open}
        onClose={() => setIncompatibilityDialog({ open: false, message: '' })}
      >
        <DialogTitle>Предупреждение о несовместимости</DialogTitle>
        <DialogContent>
          <DialogContentText>{incompatibilityDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncompatibilityDialog({ open: false, message: '' })}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CollectorWithSeparatorEditor;
